// Starbucks AI Lab mock pack — the demo's scripted questions and their full
// typed-block responses (block kinds → pages/Lab/blocks/index.js).
//
// Authoring rules (locked design, memory strategy-lab-design.md):
// - every response leads with a `readout` block — the VERDICT ALONE. Its
//   `body`, a paragraph restating the evidence, was dropped from every
//   scripted entry on 2026-09-08 (user call: "unnecessary") — the blocks
//   that follow carry that evidence, and the modelView carries the read.
//   The field stays optional in the DTO and the renderer for a live
//   answer; do not author one back into a pack;
// - copy speaks CMO cultural-capital vocabulary, NEVER engine internals
//   (no half-life / grace / multiplier / rubric talk);
// - proposals are NAMED concepts, never generic formats;
// - `modelView` is the model's opinion: gold-badged and contains NO digits;
// - provenance: "observed" (rival facts) / "projected" (engine counterfactual)
//   / "measured" (this brand's own record).
//
// Numbers + references are calibrated to the LIVE dev DB (2026-08-24 run):
// Starbucks CIQ 81.3, rank #4 of 20, +1.2 on the window; McDonald's #2 at
// 83.0. Cited precedents are REAL measured items on Starbucks' shelf: the
// POP MART MOLLY collab, the Mofusand collection, the Pink Bearista™ cup,
// Unicorn Frappuccino Returns, the Reserve Roastery Milan exhibition, the
// thisisneverthat Seoul collab, the Malaysia Kopi Creator Kolektif series.
//
// The audience/geo/forecast entries mock the 2026-09 stakeholder brief
// (demographic + geographic impact tracking, short-lead vs long-lead
// prediction): illustrative numbers on real precedent items. The "demo
// preview … illustrative numbers" note blocks that used to close each entry
// are GONE (user call 2026-09-06: the UI never calls itself a demo — the
// same rule as the briefing footnote and the share dialog); the fixture in
// apps/api/modules/lab/test/fixtures was regenerated in the same change.
//
// The FANS question (2026-09-21, user brief: "What is the product fans vs
// brand fans ratio?" — use the insights and charts of the Starbucks Fans Lab
// artifact, as many as are relevant): type `fans`, its own think script,
// FIRST in the pack so it is the first starter chip. The artifact's whole
// analysis is carried block by block — its "Main insight" under every chart
// as the block's `insight` sentence, its reads as the tap-read cards — on
// two block kinds authored for it (`stacks`, 100% share rows; `pairs`, A
// against B per row) beside the existing ones. Basis (the artifact, built
// 2026-09-20 on the 2026-08-24 run + dated public facts; RE-SYNCED with it
// 2026-09-23 — the top rung was renamed Collectors → SUPERFANS there and
// the loyalty-and-community row left the pull chart, so both moved here,
// user call: "make changes so it matches the original fans artifact"): the
// fan LADDER per 100 US customers — 24 passers-by / 52 product fans /
// 19 brand fans / 5 superfans; visits a month 1.5 / 5 / 9 / 11, ticket $5.20 / $6.90 /
// $8.40 / $9.00, merch a month $0 / $0 / $4 / $15 → share of visits
// 7 / 50 / 33 / 11, share of spend 5 / 44 / 37 / 14 (brand fans and
// superfans: a quarter of customers, 44% of visits, 51% of spend). Public
// proof: 38% of app users hold one standing order and 54% rotate a few,
// 41% of transactions are non-members, 17.7M Instagram vs 35.8M US 90-day
// loyalty actives. The six segments (Starbucks' own Product Innovation
// Workshop deck): Clean Connoisseurs 21% (62/38, index 130), Budget Baddies
// 18% (70/30, 125), Hype Hunters 18% (25/75, 85), Flavor Fiends 15% (80/20,
// 80), Rushed Regulars 14% (92/8, 60), Frugal Faithfuls 15% (88/12, 70);
// sixteen of nineteen brand leaders are Clean Connoisseurs. The community
// assessment (May–Aug 2026, lane 71): crowd-built dimensions in the 80s and
// 90s (resale depth 97, secret-menu canon 96, the cup in hand 94, rituals
// 84), brand-owned ones under 45 (status progression 41, engagement without
// incentives 38, exclusivity 23, owned spaces 22, owned channel 19).
// Strategies on 131 plays: fandom 18 / net reception 21 / valence +9 (11
// merch, 3 events, 2 drinks, 1 collab, 1 film), scarcity 29 / 18 / +8,
// aesthetic innovation 84 / 16 / +8, identity signal 29 / 14 / +7, opacity
// 15 / 13 / +7. Generations × rungs (pass/prod/brand/coll): Alpha 3% of the
// base 30/30/25/15, Gen Z 27% 18/42/30/10, Millennials 31% 20/52/23/5,
// Gen X 24% 28/60/10/2, Boomers 15% 38/56/5/1 → rung composition product
// fans 2/22/32/28/16 (median age 40, 56% women), brand fans 4/42/37/13/4
// (29, 68%), superfans 8/51/29/9/3 (25, 72%), passers-by 4/20/25/28/23
// (41, 52%). Pull by product type, product fans / brand fans: returns
// 88/55, menu drops 62/58, places 40/80, content 35/70, collabs 30/85,
// merch 25/92, events 15/72 — SEVEN types; the loyalty-and-community row
// (55/90) was dropped 2026-09-23 to match the artifact, which carries the
// seven product types the shelf actually uses. The bridge
// (MEASURED pairs): Pink Bearista 40/+17 vs FIFA Bearista 27/+4, Dandy +
// drinks 90/+53 vs Dandy alone 53/+42, Unicorn return 90/+13 vs the "Made
// in Starbucks" film 20/0. The 2028 ladder 23/47/23/7 = +6.0% visits,
// +9.0% sales on the rung economics; five moves +1.2 / +1.5 / +0.9 / +0.6 /
// +0.6 = +4.8 → ~86. Product-fan wants (want / gap): a smaller cheaper size
// 78/85, at home 71/60, protein or zero-sugar 69/35, the customisation as a
// named drink 64/55, a streak or subscription 61/80, the seasonal all year
// 58/70. Drink × format propensity: PSL 74/82/55/68/60/38, Pink Drink
// 86/70/78/45/72/52, matcha 66/80/40/35/64/30 (drinkware / at home /
// collectible / event / content / apparel). Brand fans + superfans, share
// of customers, H2 2026 → H2 2028: with the moves 24 → 30, today's course
// 24 → 25.5. The three fandoms by generation (Alpha/Z/Mill/X/Boom): Pink
// Drink 12/48/28/9/3 (58% product fans, 28 brand, 14 superfans), matcha
// 3/46/34/12/5 (48/42/10), PSL 1/20/41/26/12 (66/27/7). Ladder, segments,
// generations, pull, wants, propensity and the curve are MODELLED
// (projected); the bridge pairs and the strategy record are measured; the
// five moves are the ONE recommendation set — the drink-level opportunities
// (the pink house, matcha for the kitchen, PSL Day) are their detail.

// The FANDOM question (2026-09-21, user brief: "Where is the biggest
// opportunity to capitalize on fandom?" — charts a CMO can decide on: how
// fandom is built in the industry and which product types carry it, how
// Starbucks' own breaks down, which recent industry plays moved MONEY,
// early signals, the gap against direct competitors, one priced
// recommendation): type `fandom`, its own think script, SECOND in the pack
// (the second starter chip). No new block kinds — `arena`, `quadrant` and
// `playsTimeline` join this pack from the Tiffany / UGG ones. Public basis
// gathered 2026-09-21: the Glass Bearista (6 Nov 2025, Facteus card panel)
// $64M incremental spend in two weeks, launch day $74.7M = +58.2% spend /
// +36.5% transactions on the day before, ticket +15.9%, 5M extra
// transactions, week one $53M / week two $11M, the following week +16.3%
// over the pre-launch week, five days over 4M transactions vs one a year
// earlier; retail $29.95, resale $250–$1,000, Niccol's "record holiday
// launch week"; Pink Bearista 13 Jul 2026 mostly gone by 7 a.m.; Snoopy
// Bearista (Peanuts) 15 Sep 2026, ten pieces, two per customer, resale to
// $150; drops Rewards-account-gated online, Reserve early access. Starbucks
// Q3 FY26: US comps +7.9% (transactions +4.2%, ticket +3.6%), 35.8M 90-day
// Rewards actives ≈ 60% of US company-operated revenue, FY26 US comp guide
// "a little more than 6%"; Piper Sandler spring 2025: teens' #1 coffee
// chain at 37%. McDonald's: Minecraft Movie meal (26 Mar–6 May 2025,
// "largest global campaign yet") US comps −3.6% → +2.5% in a quarter,
// lapping it turned April 2026 negative (Q2 2026 call); KPop Demon Hunters
// dueling meals (31 Mar 2026) sold out faster than planned, photocard sets
// ≈ $100 on resale, US Q2 2026 comps +0.8%; FIFA (June 2026)
// "underperformed versus expectations"; Grimace relaunch in Germany 57M
// views; ~220M 90-day loyalty actives, $40B TTM loyalty sales; history:
// Grimace's birthday (Jun 2023) in a +10.3% US quarter. Dutch Bros Q2 2026:
// company comps +8.3% (transactions +3.4%), 73% of transactions through
// Dutch Rewards, the state sticker and the frog charm "the strongest
// merchant sticker drops of the year … meaningful sales lift". Pop Mart H1
// 2026: revenue +23.8% but overseas −11.1%, the Americas −16.5%, MONSTERS
// (Labubu) down to 26% of the group. Stanley $73M (2019) → $750M (2023);
// the Starbucks × Stanley pink Quencher (Jan 2024) Target frenzy, $200
// resale; teens list the Quencher among trends on their way out. The pack's
// own record (measured, the fans entry above): fandom 18 of 131 plays (11
// merch, 3 events, 2 drinks, 1 collab, 1 film), net reception 21, +9; merch
// fandom 11 / 26 / +12. MEASURED here: the strategy record, the Bearista
// card-panel week, the earnings-call figures. MODELLED (projected): the
// week-after lifts of every other play, the arena's fandom-fit bands and
// dots, the format × metric grid, the competitor lane grid, the calendar's
// effects and the cumulative revenue curve. ONE recommendation set: the
// platform (four dated owned-character drops, a drink in each) = the fans
// entry's first and third moves priced once, +2.1; the card +0.7; the
// bear-suit rule +0.4 → +3.2, 81.3 → ~84.5, ≈ $100M a year over course.
//
// The COMMUNITY question (2026-09-21, user brief: "How to activate Starbucks
// community?" — the state of community in food & drink and what builds the
// strongest ones, Starbucks' own anatomy, the crowds that answer first,
// recommendations valued in CIQ with the read of how each changes the
// culture and the lane): type `community`, its own think script, THIRD in
// the pack — it takes the third starter chip from the next-store question,
// which stays in the pack (its renderers and tests read it) but off the
// chips. Kinds new to a flagship answer here: `radar` and `cohorts`. Public
// basis gathered 2026-09-21: Back to Starbucks at two years — cup writing,
// condiment bars, ceramic mugs and free refills back, ceramic "sit and stay"
// choices up more than 3× in three weeks (US), 1,000+ coffeehouse uplifts by
// Q3 FY26 ahead of plan (~$150K each, overnight, 1,500+ by fiscal year-end,
// "transaction lift across access points, dayparts, formats and customer
// segments"), Green Apron Service at 4+ shots in two-thirds of company
// cafés (+40 pts since Oct 2025), coffeehouse-leader tenure +7 pts, brand
// affinity / consideration / purchase intent at five-year highs; Rewards
// relaunch 10 Mar 2026 — Green / Gold (500 Stars) / Reserve (2,500 more)
// tiers, Free Mod Monday (1 in 3 who tried a mod reordered it), members
// levelling up within four months, the Reserve card, merch-shop first
// access and the Global Coffee Experience (12 members to the Tokyo Roastery,
// fall 2026); Secret Menu in the app 14 Jul 2025 with a $25,000
// customer-creation contest voted on Instagram; Community Stores ~150 toward
// 1,000 by 2030; Odyssey (Dec 2022 → closed 15 Mar 2024, "never held hands
// with Rewards", the $100 collection unsold); Starbucks Workers United — the
// longest strike, 13 Nov 2025 → Feb 2026, ~4,500 baristas at 230 stores,
// bargaining resumed April 2026, no contract; ACSI 2025 Starbucks 80 (first
// in the Northeast), Chick-fil-A 83 for an eleventh year. Dutch Bros: 72–73%
// of transactions through Dutch Rewards, 15M+ members, Q2 2026 comps +8.3%
// (transactions +3.4%), same-store visits +13.4% in 2025 while Starbucks'
// fell; Drink One for Dane 15 May 2026 (19th year) — $1 a drink, a sticker
// with every cup, $1.7M in a day, $20M lifetime. Blank Street Regulars: $22
// a week for up to 14 drinks, app-only, invite/waitlist, ≈5,000 paying and
// ≈4,000 in line. Erewhon: ≈60,000 members at up to $200 a year, a drink a
// month, $40K a smoothie collab, On × Erewhon Wellness Club (May 2026). Taco
// Bell Live Más Live 10 Mar 2026 (LA Rewards members got tickets first);
// Chipotle Rewards on Repeat 13 Apr 2026, 21M actives, monthly free drops;
// Dunkin' Boosted Status — 12 visits in a month unlock three months of
// more; McDonald's ~220M 90-day loyalty actives, $40B loyalty sales.
// The crowd: Eventbrite 2026 Social Study "coffee clubbing" +478% YoY,
// sober-curious +92%; Strava run-club membership +59%, new clubs 3.5×; US
// drinking at its lowest since 1939. THE BOARD'S OWN RECORD (dev DB,
// queried 2026-09-21; the food industry's brands — Diageo left out, user
// call): community assessments of 2026-08-10, rubric dimensions 0–100 —
// status_progression_mechanics 20 for McDonald's, Coca-Cola, Red Bull and
// 7-Eleven (Starbucks 40), community_exclusivity 0 / 0 / 20 / 0,
// owned_community_spaces 20 / 60 / 20 / 20, owned_channel_engagement 40 /
// 40 / 20 / 20, membership_signaling 60 / 100 / 60 / 60, superfan_depth
// McDonald's 100, Red Bull membership_program_strength 20; lane scores
// 60.8 / 62.6 / 52.4 / 51.0 against Starbucks 70.9. Best-received
// community-adjacent plays since 2025-09 (avg positive pulse): Coca-Cola's
// Jacob's Soda Fountain pop-up at The Battery Atlanta (experience, 11 Aug
// 2026) 73; Red Bull New York's special day for one fan (event, 10 Aug
// 2026, scarcity .8) 73; McDonald's Spider-Man cinema takeover (event, 9
// Jul 2026, fandom/identity) 56; 7-Eleven's Slurpee Day 60th (11 Jul 2026,
// archive) 11; Coca-Cola's Share a Coke return (Jul 2026) 0; Starbucks'
// Barista Championship NA (event, opacity/scarcity) 25; Mofusand Rewards
// member early access (collab, identity .6). STRATEGY RECORD since 2025-09
// (plays / avg reception / avg valence, per item): Starbucks aesthetic
// innovation 85 / 16.9 / +11.1, scarcity 29 / 17.4 / +10.6, identity signal
// 29 / 12.5 / +10.4, fandom 21 / 16.8 / +3.6, opacity 16 / 8.8 / +6.0;
// McDonald's 127 / 11.9, 41 / 18.9, 37 / 20.9, 21 / 17.6, 22 / 13.9. By
// type on Starbucks: aesthetic innovation × experience valence +19.7 (the
// best pairing), scarcity × collab +21.8, fandom × event −16.4 (the worst),
// opacity's best plays the secret-menu drinks; NEITHER community item on
// any brand's shelf carries a strategy. Engine multipliers (ciq_config):
// fandom 1.35, identity_signal 1.35, aesthetic_innovation 1.28, scarcity
// 1.22, opacity 1.15. MEASURED here: the assessments (Starbucks' as the
// fans entry calibrated it), the strategy record, the board's reception of
// the rivals' plays, the Rewards and café record. OBSERVED: the public
// record of the brands the board does not measure yet (Dutch Bros,
// Dunkin', Blank Street, Erewhon, Chick-fil-A). MODELLED (projected): the
// lane bands and dots, reach × depth, the mechanic × metric grid, the
// strategy × mechanic grid, the cohorts' scores, the calendar's effects,
// the lane curve. ONE recommendation set: Regulars (a standing by visits;
// identity signal .8 / scarcity .5 / opacity .4) = the fans entry's second
// move, +1.5; the Regulars' Hour (aesthetic innovation .7) = its fourth
// move dated, +0.6; Starbucks Day (identity .6 / scarcity .5 — NOT fandom,
// the worst-received pairing on fan days; new) +0.5 → +2.6, 81.3 → ~83.9,
// lane 71 → ~85.
//
// The COLLAB question (2026-09-22, user brief: "one more mock question so
// there is one more in recommended" + the Starbucks Collabs Lab artifact,
// "use the most important charts"): type `collab` — ALREADY registered by
// the UGG pack, so no new think script, tag or art — inserted FOURTH in the
// pack, which makes it the fourth starter chip (Starters.FEATURED 3 → 4 the
// same day; chips are now fans · fandom · community · collab). The artifact
// was built the same day off the 2026-08-24 run and its numbers are the
// entry's spine. MEASURED (the board's own record): collab weight 2.0
// joint-highest, Starbucks collab avg base 34.4 / avg eff 21.11 / mean
// reception 12.5 on 18 plays — the best of its seven types, and the fewest
// run bar one (McDonald's 32, 7-Eleven 43, Red Bull 28, Coca-Cola 27; food
// is the most collab-dense industry at 18.1%). THE FINDING: of 451 read
// launches 69% register NOTHING; sorted into paper-grade fifths the ODDS
// double (19.8 → 46.7%) while the SIZE when it lands is flat (51.7, 44.5,
// 49.3, 51.4, 55.1) — a partner is a bet on whether there will be a moment.
// (The `pairs` chart that drew those fifths was CUT 2026-09-23 on the user's
// call; the finding stays the answer's rule and is carried by the shapes
// arena and the brand quadrant, which is where the odds are readable.)
// Starbucks lands hardest in food (62.6) and least often (31.3%) against
// McDonald's 51.9 / 54.7; Hermès 81.8, LV 50, Zara 47.1, Nike 37.5, 7-Eleven
// 17.1 (diageo EXCLUDED, standing user rule). Shapes: made-to-be-kept +
// built-for-a-scene 57.1% noticed / 28.6% kept (3 Starbucks plays), craft
// 41.7 / 27.4 (0), the partner made it 41.3 / 23.1 (0), scarce 40 / 16.7
// (0), a documented story 31.4 / 25.3 (0), top tier 26.9 / 19.2 (8),
// formulaic 28.8 / 15.3 (5), adjacent world 22 / 17.4 (2); a named person
// 39.8 vs a partner brand 26.7. Strategy on collabs: fandom 13.8 on 8% of
// tags (the best, the least spent — Starbucks has ONE, it scored 0),
// identity 10.8 on 42.7%, scarcity 10.8 on 15.8%, aesthetic 10.0 on 33.4%,
// OPACITY never tagged on a collab at all (10.6% off that shelf, 6.4).
// Rubric gaps: a documented shared story 23.3 vs board 36.8, the partner
// made it 33.3 vs 50.3, distance 46.7 vs 38.7 (a strength). Items cited are
// real: POP MART MOLLY 43.5 / eff 58.5 (the highest-scoring item it owns),
// Dandy road trip 53, Dandy 41.5, Jay Chou 23.3, Miffy 11.2, and the zeros
// — Stanley (42.9 on paper, the year's highest grade, 0 across 5 reads),
// thisisneverthat, Harry Potter keychains, the FIFA cup sleeve; rivals'
// McDonald's × BT21 60, One Piece 49.5, Spider-Man 46.5, Pokémon Japan 42.3.
// OBSERVED (public reading, no coffee or tea chain is on the board): Luckin
// × Moutai 5.42m cups day one, Luckin × Black Myth, Heytea × Fendi 1.5m in
// three days, Tim Hortons × Bieber, Dunkin' × e.l.f. MODELLED (projected):
// the nine candidates' two clocks, the metric grid, the calendar's effects,
// the lane curve. ONE recommendation set, NAMED partners (user rule from the
// artifact): Pokémon on the cup (identity .8 / scarcity .6) +1.1; BT21 with
// a drop rhythm (identity .8 / scarcity .4 / fandom .3) +0.8; a coffee
// master in residence (aesthetic .6 / identity .6 / scarcity .4) +0.5 →
// +2.4, 81.3 → ~83.7, the shelf's reception 12.5 → low twenties. The lane
// is nearly saturated — one collab alone is worth about +0.2 — so the
// saturation caution sits in the plays footnote and the impact body, NOT in
// the headline (user call: price it like the other labs).

//
// The COLLAGEN SIGNAL question (2026-10-23, the Home's second Early Signal
// card "Collagen Is Shaking Things Up" — its `labQ` landed on the creatine
// audience answer until this entry; plan docs/plans/collagen-signal-lab):
// "Is collagen about to shake up the beverage bar?" on a NEW question type
// `signal` ("Signal lab", cyan #22D3EE, a sparkle over a rising pulse line
// in Starters.js; think script in stages.js: the signal's own trail → where
// the ritual came from → the field's functional plays on the shelf → PERF
// → IMPACT), item type `capsule`, appended LAST so the four chips (fans ·
// fandom · community · collab) are untouched; the card and the aliases
// reach it. VERDICT: real, early, arriving through the beauty aisle — the
// café ritual is unclaimed. Public basis (verified 2026-10-23, URLs in the
// plan): collagen drinks ≈ $431M worldwide in 2026 (Mordor: $393M 2025 →
// $671M 2031, 9.3%/yr; the wider collagen-infused beverage category $1.66B
// 2025 → $2.95B 2034, GMI/Symrise); 80% of Gen Z drink a functional
// beverage at least every two weeks vs 65% of all adults (EY, Mar 2026);
// 71% of Gen A/Z "function-forward", 58% pick drinks that reflect
// identity, 63% drink what the feed drinks, 56% want limited drops (KDP
// State of Beverages, 6 May 2026); SPINS 33% take a supplement for skin /
// hair / beauty, Innova collagen #2 ingredient on beauty-claim launches,
// Cadent 1 in 3 collagen users want new formats; Starbucks protein cold
// foam (US 29 Sep 2025, EMEA 2 Apr 2026, APAC 11 Jun 2026) the
// most-popular add-on, a protein option on ~90% of the drinks on the
// menu (AVAILABILITY, not a share of orders — user caught the misread
// 2026-10-23), customisation ≈ $1B
// (Axios, 26 Mar 2026); Vital Proteins (Nestlé) collagen sparkling water
// late Mar 2026 after its RTD shake (Aug 2025); Clöud Café — Korean-style
// collagen coffee / matcha / milk tea — opened at Ulta Beauty 19 Mar 2026
// (Ube Latte, Pistachio Matcha exclusives); Monster and Unwell collagen
// cans; Nexus at-home collagen cold foam; Shiseido The Collagen (1996) and
// Meiji Amino Collagen (2002, #1 Japan) the origin; ~40% skeptical of
// functional claims, EFSA declines collagen health claims (Symrise, Jul
// 2026); TikTok #collagen 4.6B views across 845.8K posts and #collagendrink
// 313.1K posts (hashtagradar.com, live counts read 2026-10-23 — the 2023
// "438M peak" reading was DROPPED as stale, user call); café trend reports call collagen adds "no longer
// fringe, still incremental, not core"; NO Starbucks collagen item in any
// market. The beauty houses (Sephora / Dior / Rhode / Estée Lauder) were
// CUT from the matrix 2026-10-23 (user call: food and beverage brands only;
// the beauty column rescaled to Starbucks' two plays = 100). OFF THE BOARD (the matrix's last three rows, public record read
// on the board's per-play step, added 2026-10-23 on the user's ask for
// 2–3 brands that have collagen): Joe & the Juice "Maya Jama's Matcha", a
// limited-edition collagen matcha (black sesame, sea salt, agave, Sproud
// Zero) across all 91 UK sites from mid-Apr 2026 — UK MD: "the addition
// of collagen taps into growing demand for more functional options"
// (Restaurant Online 16 Apr 2026; chain 500+ stores / 25 markets, World
// Coffee Portal); 7 Brew Protein Boost — "100% collagen protein from a
// porcine source", unflavoured, 15 g, $2 on any drink, DFW 1 Aug 2026 →
// every stand 1 Oct 2026 (900+ stands, 38 states; the nationwide release
// never says "collagen" — 7brew.com/protein FAQ, QSR, Sporked); Erewhon
// smoothie bar (14 SoCal stores, Grocery Dive) — Hailey Bieber's Strawberry Glaze Skin
// Smoothie (2022, Vital Proteins vanilla collagen peptides, still on the
// standing menu), Coconut Cloud (vanilla collagen), Winnie Harlow's glow
// smoothie (19 Mar 2024, $22), "Liquid Steel" × Simplehuman (1 Jul 2026,
// $12, creatine + collagen). CONSIDERED, NOT ADDED: Monster FLRT (women's
// energy, nationwide 2026, 200 mg caffeine, "marketed to support collagen
// production" — biotin/zinc/vitamins, NO collagen in the can, BevNET
// brand page; backlash over the gendered pitch); Clöud Café and Vital
// Proteins (packaged brands, already the entry's story, not a bar); Blank
// Street "collagen latte" (menu aggregators only, no official source);
// Dutch Bros (none official — protein milk only). The `trendCompare` is REAL Google Trends — ONE comparative
// SerpApi query ("collagen coffee,protein coffee,matcha latte", worldwide,
// 2024-01-01 → 2026-10-22, the attention adapter's engine and key), folded
// into monthly means, Jan 2024 → Sep 2026 (October a part month, left
// off), scaled so the busiest month of the three (protein coffee, Apr
// 2026) reads 100 — on the shared scale collagen coffee peaks at 17 in
// Apr 2026 from 1–2 through 2024, ≈ 8× (recipe in memory
// starbucks-collagen-signal-question). THE DB BASIS (dev DB, queried
// 2026-10-23; the served run per (brand, as_of) = the completed GLOBAL run
// with the latest executed_at; mocked / deprecated items and signalless
// pulses excluded; never Diageo): Starbucks served 2026-10-05 CIQ 84.95,
// raw 86.72, capital K 70.26, k 0.45, rank 1 in food (McDonald's 83.0,
// Coca-Cola 79.1, Red Bull 73.3, 7-Eleven 71.0). Strategy record on 220
// real items since 21 Apr (plays / reception / valence): aesthetic 131 /
// 41.5 / +28.9, identity 44 / 31.0 / +23.9, scarcity 38 / 39.0 / +28.4,
// fandom 34 / 30.8 / +9.0, opacity 19 / 33.1 / +24.7; ON DRINKS (capsule):
// aesthetic 55 / 44.2 / +25.0, opacity 13 / 34.3 / +24.2, scarcity 7 /
// 37.9 / +29.9, fandom 3 / 60.0 / +37.7, identity 0 — the strategy
// quadrant's dots (x = plays as a share of 55, y = reception). NOBODY on
// the board has a collagen play (0 of ~3,300 items); near-signal counts
// (matcha / functional / beauty-coded, regex over title + summary) —
// Starbucks 6 / 10 / 2, 7-Eleven 3 / 1 / 1, Red Bull 0 / 21 / 0, Coca-Cola
// 0 / 3 / 1, McDonald's 0 / 1 / 0, Sephora 1 / 1 / 54, Dior 1 / 1 / 26,
// Rhode 0 / 0 / 23, Estée Lauder 0 / 0 / 48 — the matrix, each column
// scaled to its max. Food-board capsule bands per lane (median / p75 of
// base, one SQL): matcha 7 plays 29.9 / 33.3, energy 18 plays 26.4 / 29.3,
// protein 2 plays 25.7 / 26.1 (both Starbucks — no other food brand has a
// protein drop), wellness objects and beauty-in-a-cup from the board's
// beauty-house precedents (Dior Haute Wellness capsule 25.7 / +47, Dior
// "Facials and Matcha" day 24.5 / +7, Aesop × Wonderbrew kombucha 27.1,
// Sephora free matcha pop-up 15.4, Sephora Summer Club 42.2 / +53, Netflix
// × Anua KPop Demon Hunters skincare 31.9 / +40, 7-Eleven H2Coco matcha
// launch 20.2, McDonald's × Red Bull Dragonberry Energizer 32.0 / +13).
// Starbucks' own wellness-adjacent shelf (base / best presence / net
// valence per read window): Orange Cream collection 35.0 / 73 / +73, +26;
// Lavender Matcha 33.4 / 53 / +20; Blue Coconut 33.2 / 80 / +27, −7, +6,
// +20; Iced Pumpkin Cream Matcha 29.9 / 73 / +46; Iced Mango Cream Matcha
// 29.8 / 67 / +7, 0, +26; Caramelised Banana Matcha 28.6 / 60 / +27, +40,
// +14; New York Energy Refresher 49.8 never read; Fanatics Fest Refresher
// 40.9 / 53 / +53, +40; Blended Pink Energy 24.8 / 47 / 0, +47; Passionfruit
// Guava Energy 23.7 / 60 / +60; Mango Dragonfruit Energy 21.0 / 60 / +13,
// +13; Pink Energy campaign 26.2 / 53 / +14, +33, +53; SuperYou × Tata
// protein coffee (India, collab) 32.0 / 27 / −6; Weighted Vest collab 34.4
// unread, Weighted Vest capsule 26.5 / 47 / −13; Inner Light aura
// activation 30.3 unread; Chicken Bacon Protein Pocket 24.8 unread. READ:
// function as a flavour is received; function as a gym claim is not — the
// pairs chart. MEASURED here: the strategy record, the matrix counts, the
// arena bands and dots, the pairs. OBSERVED: the stats, the shift rows, the
// Trends chart, the where-collagen-lives quadrant. MODELLED (projected):
// the cohorts and the three moves — Glow Cold Foam (capsule; aesthetic .6
// / opacity .4, the secret-menu door) +0.9; Strawberry Matcha Glow
// (capsule; aesthetic .6 / scarcity .4) +0.6; Rhode × Starbucks Glow Cup
// (collab with `rhode`, on the board at 57.7; identity .4 / fandom .4)
// +0.7 → +2.2, 84.95 → ~87.2. No move carries a skin claim.
// 2026-10-28: the Home's second Early Signal card is the SWAVORY signal
// now (`swavory-signal`, below); this entry stays reachable by its aliases
// and `?lab=collagen-signal`.

//
// The MICRO-ORDERS SIGNAL question (2026-10-23, the Home's FIRST Early
// Signal card "Micro-orders are having a moment" — its `labQ` landed on
// the generations audience answer until this entry; plan
// docs/plans/micro-orders-signal-lab): "Are micro-orders the next shape
// of the cup?" on the `signal` type (the collagen entry's — ONE think
// script for the type, generalised in stages.js the same day), item type
// `capsule` (the lead move is a drink size), appended LAST so the four
// chips stay fans · fandom · community · collab; the card and the aliases
// reach it. VERDICT: real, and already in the cup — the micro-order is a
// smaller, stronger, faster ORDER, not a smaller cup; Starbucks owns the
// drink (the Aerocano, the cortado) and nobody owns the ritual (the small
// cup at an ENTRY PRICE, the afternoon second coffee, the little treat).
// Public basis (verified 2026-10-23, URLs in the plan): NCA Fall 2026
// (n=1,882, Jun 2026) past-week espresso-based drinks a record 47% of US
// coffee drinkers (+17.5% on 2022), past-day 29%, 25–39s 60%, specialty
// outsells traditional, app ordering 40%, 22% drink in the afternoon,
// cups per drinker 2.6 from 2.8 (cortado is NOT an NCA line); Toast POS
// 2025 vs 2024 (~164k tills) espresso shots +3.3%, lattes +4.0%, frappés
// −0.6%, drip −3.3%, cold brew −2.2%, energy +8.7%; the AEROCANO
// (aerated Blonde Espresso over ice, Grande = 3 shots, 15 cal, no new
// equipment) launched in Korea 26 Feb 2026 as CORE and sold 1M cups in
// its first week (≈9,500 an hour, the fastest iced launch there; >70% of
// Korea's Americanos are iced), then LATAM 15 May, Singapore 25 May,
// Australia by Jul, US + Canada 29 Sep (National Coffee Day), UK 1 Oct —
// no Japan / mainland-China launch verified; cold ≈ 60% of global
// beverage sales; the CORTADO a permanent core item since 3 Jan 2025 (3
// Blonde ristretto shots, the 8-oz Short ONLY, hot only — the Short's
// first menu-board appearance; off-menu since at least 2006), Pecan
// Oatmilk Cortado 26 Aug 2025 back 25 Aug 2026, Pistachio Cortado 6 Jan
// 2026; the 10-oz Mini Frappuccino ran May 2015 and May 2016 only;
// Coca-Cola 7.5-oz mini cans as singles in US c-stores from 1 Jan 2026 at
// $1.29, Q1 2026 mini-can volume "high single digits" ahead of total unit
// cases (+3%), the CEO: "the lowest entry price"; Red Bull's 250-ml can
// 13.97bn cans in 2025 (+10.2%); McDonald's Canada $1 small McCafé coffee
// locked for all of 2026; Italy's standing espresso €1.29 (Aug 2026);
// Costa's 8-oz "Mini" since Sep 2020 from £1.66 (£1.41 with a reusable
// cup); Greggs overtook Costa in 2026 on a £1.80 Americano; Luckin's
// $1.99 app cup, 36,310 stores, same-store −5.3% (Q2 2026); BofA (Feb
// 2026, Gen Z 18–29, n=1,133) 92% buy little treats, 52% weekly+; BofA
// Institute card data (Aug 2026) Gen Z spend at small and mid-sized
// coffee chains +25% YoY in June, the strongest generation; SurveyMonkey
// (Feb 2026, n=2,038) 62% buy a small treat monthly+, 43% daily/weekly,
// food and drink the top category (65%); Circana (Apr 2025) median ~$5 a
// treat, coffee the #3 beverage treat (41%), 40% buy small because they
// cannot afford bigger (Gen Z 53%); NRA 2026 half of Gen Z make a
// beverage-only purchase weekly, ~69% of beverage-only purchases at lunch
// or mid-afternoon; Circana (Apr 2026) 68% sometimes enjoy a beverage as
// a snack (+7 pts on 2021); Starbucks Investor Day 29 Jan 2026 "a new
// afternoon ritual", "a true second peak", customisation ≈ $1B; US Q3
// FY26 transactions +4.2%, ticket +3.6%, food attach a record "with the
// strongest gains in the afternoon"; PwC/Numerator (May 2026, n=3,089)
// 21% of US households include a GLP-1 user (9% Jan 2025), 35% of users
// who say restaurants have not adapted want smaller portions at
// proportionally lower prices, 32% half-portions without an upcharge;
// Cornell/Numerator GLP-1 users ≈ −8% at limited-service restaurants;
// Datassential nine in ten GLP-1 users interested in mini portions; a
// summer poll (OnePulse, Aug 2026, n=500) 30% prefer coffee "smaller and
// stronger", 41% larger and milkier, 58% never had a cortado, 69%
// interested once described, 26% "may not feel worth the price", 21%
// "looks too small"; the US Aerocano priced ABOVE the Americano (one
// store: Grande $4.55 vs $3.95); Niccol (Oct 2025) pricing "a last
// resort", no value menu; energy drinks on 26.5% of drive-thru menus
// (+187% in four years) while energy SHOTS shrink (Circana $845M, −4%);
// Dunkin's 2026 espresso went BIGGER (15-oz double can); Square UK
// (summer 2025) cortado sales down while matcha +114%; China's 9.9-yuan
// war cooling; one indie's 32-oz "coffee buckets" >30% of orders; NO
// chain launched a mini cup size in 2024–26; TikTok (hashtagradar.com,
// read 2026-10-23) #espresso 388.4K posts / 4.8B views, #cortado 8.9K /
// 49.9M, #shakenespresso 8.2K / 145.9M, #littletreat 3.4K / 64.4M (19K
// views a post, the set's highest), #treatyourself 959.1K / 3.5B. The
// `trendCompare` is REAL Google Trends — ONE comparative SerpApi query
// ("cortado,iced americano,frappuccino", worldwide, 2024-01-01 →
// 2026-10-22, the attention adapter's engine and key), folded into
// monthly means Jan 2024 → Sep 2026 (October and the one December 2023
// week left off as part months), scaled so the busiest month of the
// three reads 100 — frappuccino's Aug 2026 (the Unicorn's return, two
// weeks at 90 and 100); on that scale cortado climbs 49 → 88 (Jan 2024 →
// Sep 2026; 77 in Jan 2025, the cortado's launch; 72 → 78 across the
// Aerocano's Korea weeks), iced americano reads 2–9 in English search
// all the way (the world says Americano; Korea asks in Korean), the
// frappuccino 29–52 outside its spike. THE DB BASIS (dev DB, queried
// 2026-10-23; the served run per (brand, as_of) = the completed GLOBAL
// run with the latest executed_at; mocked / deprecated items and
// signalless pulses excluded; never Diageo): Starbucks served 2026-10-19
// (executed 2026-10-23) CIQ 86.14, raw 86.48, capital K 71.66, rank 1 in
// food (McDonald's 83.04, Coca-Cola 79.07, Red Bull 73.25, 7-Eleven 70.96,
// 24 Aug runs). Strategy record on 270 real items since 21 Apr (plays /
// reception / valence): aesthetic 145 / 42.7 / +30.3, identity 58 / 32.0
// / +25.4, scarcity 53 / 37.9 / +30.3, fandom 52 / 33.0 / +19.0, opacity
// 21 / 30.4 / +22.4; ON DRINKS (56 capsules): aesthetic 56 / 44.6 /
// +25.3, opacity 14 / 33.0 / +24.4, scarcity 7 / 37.9 / +29.9, fandom 3 /
// 60.0 / +43.3, identity 0 — the strategy quadrant (x = plays as a share
// of 56, y = reception as a share of fandom's 60, w = valence). The small
// shelf (base / best presence / net valence per read window): Aerocano
// APAC 2 Jun 24.5 / 67 / +20, +60, +67, +14 (FOUR windows); Honey Ruby
// Grapefruit Aerocano APAC 23.8 / 53 / 0, +27, +53; Aerocano LATAM 15 May
// 26.1 / 40 / +40, −14; Aerocano NA 29 Sep 27.8 unread; Jay Chou Aerocano
// Series (content, APAC 18 Sep) 28.5 unread; Pecan Cortado return
// (archive, NA 5 Oct) 27.4 unread; The Devil Wears Prada 2 menu (GLOBAL
// 30 Apr, character ORDERS) 29.5 / 67 / +7, −13; Seasonal Mini Pies (NA
// 29 Sep) 27.1 unread; Mini Bearista Blind Box (merch, APAC 30 Jun, an
// add-on with any handcrafted drink) 26.5 / 53 / +47, +53, +40; the
// shaken-espresso lane for scale — Iced Pumpkin Cream Shaken Espresso (NA
// 25 Aug) 40.9 / 60 / +60, Iced Horchata Shaken Espresso 31.3 / 73 (FIVE
// reads), Crème Brûlée range 26.3 unread. Food-board capsule lanes since
// April (regex over title + summary; n / Starbucks' n / median / p75 /
// max received): espresso-forward 7 / 7 / 27.7 / 28.7 / 67 — EVERY
// espresso-forward drink on the food board is Starbucks'; shaken espresso
// 3 / 3 / 31.3 / 36.1 / 73; frappuccino 12 / 11 / 29.1 / 34.0 / 80;
// refresher-energy 14 / 8 / 28.8 / 30.2 / 80; latte-cold brew 10 / 6 /
// 30.0 / 33.3 / 67; matcha 5 / 4 / 29.8 / 29.9 / 73; mini format 12 / 1 /
// 30.0 / 31.7 / 73. The board's mini-format plays: Coca-Cola Cherry Float
// 7.5-oz mini cans (capsule, 21 Apr) 28.8 / 73 / +26, 0, +34; Coca-Cola
// America250 mini-cans (merch) 25.8 / 67 (five reads); America250 custom
// bottles 27.2 / 67; McDonald's Mini Hash Browns return (archive, 17 Aug,
// UK) 18.8 / 67 / +67 — the lowest-graded play on the list and one of
// the best read; Lil McDonald's Happy Meal (capsule, 1 Jul) 35.2 / 47;
// McDonald's Mini Lamps 27.1 / 0; 7-Eleven Mini-City 32.7 / 47; 7-Eleven ×
// New Balance mini sneaker blind boxes 42.3 unread; ANNA SUI × Sanrio mini
// boxes 35.1 unread; 7-Eleven × Yasaki 45th 30.7 unread; Burger King ×
// Coca-Cola KingCam mini camera 31.3 / 60. READ: "mini" as an OBJECT is
// read across the board; "mini" as a PORTION is read when it is a loved
// thing returning; nobody on the board has a mini DRINK play but
// Coca-Cola's can and Starbucks' Aerocano; 0 of ~3,400 items is a size
// play. THE MATRIX takes seven rows (user call 2026-10-23, the collagen
// precedent): the five food-board brands plus LUCKIN and COSTA from the
// public record on the board's per-play step, treated AS MEASURED — no
// "off the board" wording anywhere the reader sees; each column scaled
// to its busiest brand (espresso-forward: Starbucks 7 = 100, Luckin and
// Costa one step = 14; mini format: 7-Eleven 4 = 100, Coca-Cola /
// McDonald's / Starbucks 3 = 75, Costa 1 = 25; entry price: McDonald's 2
// = 100, Luckin / Coca-Cola / Costa 50, STARBUCKS 0 — the empty cell;
// small as a ritual: Starbucks' Korea Aerocano 100, Costa's Mini 25).
// MEASURED here: the strategy record, the matrix counts, the arena bands
// and dots, the pairs. OBSERVED: the stats, the shift rows, the Trends
// chart, the where-the-small-cup-lives quadrant. MODELLED (projected):
// the cohorts and the three moves — Aerocano Short (capsule; identity .5
// / aesthetic .4) +0.9; The Espresso Flight (experience; aesthetic .6 /
// opacity .4) +0.6; Little Treat Drop (merch; fandom .5 / scarcity .4)
// +0.7 → +2.2, 86.14 → ~88.3. No move is a value menu: each is a ritual
// at an entry price, and none cuts the price of a drink on the board.
// 2026-10-28: the Home's FIRST Early Signal card is the CAFÉ MIXOLOGY
// signal now (`cafe-mixology-signal`, below); this entry stays reachable
// by its aliases and `?lab=micro-orders-signal`.

//
// The SWAVORY SIGNAL question (2026-10-28, the Home's second Early Signal
// card "Swavory Is Seasoning the Cup" — the collagen card stood there
// 2026-10-22 → 2026-10-28 and its entry stays in the pack; plan
// docs/plans/starbucks-swavory-signal-lab): "Is swavory about to reshape
// the flavour bar?" on the `signal` type (the shared think script), item
// type `capsule` (the lead moves are drinks), appended LAST so the four
// chips stay fans · fandom · community · collab; the card and the aliases
// reach it. VERDICT: yes, and half of it already happened in the cup —
// salt has FINISHED the pistachio drinks since Jan 2021 and the cold foam
// since Jul 2018, so swavory is a topping being promoted to the name of
// the drink; the signal now is the CHEF END (miso caramel, black sesame,
// salted pistachio), doubling on search and on a specialty café's menu
// this autumn, and no chain at Starbucks' scale has named a drink after
// it; the counter-signal is Oleato (savoury that LED the drink, pulled).
// Public basis (verified 2026-10-28, URLs in the plan): "swavory" coined
// by Monin's 2026 flavour trends (Dec 2025 / Jan 2026), Nestlé's trend
// lead naming miso caramel, chile plum and tahini (Jan 2026); hot honey
// on 11% of US restaurant menus, +197% in four years, 75% of US consumers
// aware and 48% have tried it (Datassential, 2026), the world's #1
// trending recipe search of 2025; pistachio beverages +100% on US menus
// in four years, +164% forecast by 2029 (Datassential menu data via
// Monin); pistachio-filled chocolate units +1,234% in the year to Jun
// 2025 (NielsenIQ via AP); Yelp black sesame matcha searches +147% 2023
// → 2025, black sesame lattes +23%; KDP State of Beverages (May 2026)
// Gen Z / Gen Alpha 58% want unexpected flavours, 57% globally inspired,
// 75% still sweet or indulgent; EY (Mar 2026, n=1,511) 66% of US adults
// choose lower-sugar drinks; NCA Spring 2026 (n=1,850) 25–39s 69%
// past-week specialty; cold foam "our leading modifier", platform sales
// +40% in Q2 FY26 (CFO, 28 Apr 2026), customisation ≈ $1B with cold foam
// a third (Investor Day, Feb 2026), Q3 FY26 ticket +3.6% naming beverage
// modifications (29 Jul 2026). STARBUCKS' OWN TRAIL (history, public
// record): Pistachio Latte with a "salted brown buttery topping" since 5
// Jan 2021, Pistachio Cream Cold Brew with salted brown-butter sprinkles
// since 3 Jan 2023, Salted Cream Cold Foam Cold Brew permanent since 10
// Jul 2018, the winter-2026 menu's Pistachio Cortado and Iced Dubai
// Chocolate drinks with brown-butter crunch (6 Jan 2026), the Reserve's
// Hot Honey Espresso Martini with black sesame (Mar 2024) and Hot Honey
// Cortado (Jan 2025), Starbucks Korea's Sea Salt Caramel Cold Brew (23
// Jun 2026), Starbucks Japan's olive-oil-and-salt Frozen Crush Coffee
// Lemonade (24 Jun 2026); OLEATO — "the next revolution in coffee"
// (Schultz, Feb 2023), US-wide Jan 2024, pulled from US menus Nov 2024;
// Coca-Cola Spiced phased out after six months (Sep 2024); Korea's
// Dubai-cookie boom turned to bust (Jan 2026). THE THREE OFF-BOARD ROWS
// (the matrix's last three, public record read on the board's per-play
// step, treated AS MEASURED everywhere the reader sees — the collagen
// precedent): BLUE BOTTLE Miso Caramel Latte / Iced / Shakerato, US cafés
// autumn 2026 (first seen 5 Aug 2026, still listed on its US ordering
// menus 2026-10-28 at ≈ $9, "a savory twist on a fall classic"; Japan
// with Hatcho miso from 3 Sep 2026, ¥850) — the card's own flavour;
// BLANK STREET Salted Pistachio Matcha (pistachio milk + pink salt, SoHo
// Feb 2025 → the US summer menu Jul 2025, off it by Oct 2026) — the
// card's other flavour; LUCKIN Salted Cheese Soy Milk Latte (2025, the
// series back 14 Sep 2026 across 36,310 stores, 15M+ social views in
// days) — savoury in the cup at scale. CONSIDERED, NOT ADDED: Dutch Bros
// (brown butter is sweet), Gregorys (salted-egg-yolk latte, ~50 cafés),
// Joe & the Juice (Sesame Storm, already a collagen-matrix row), Dunkin'
// (plain pistachio), Shake Shack / Crumbl (pistachio sweets, on the
// where-it-lives quadrant only). The `trendCompare` is REAL Google Trends
// — TWO comparative SerpApi calls through the lab-plan skill's trends.py
// (worldwide, 2024-01-01 → 2026-10-27, monthly means, October and the
// one Dec-2023 week dropped as part months): call A anchored on the
// mainstream ("miso caramel,hot honey,pistachio latte,salted caramel";
// hot honey May 2026 = 100, first-12-month mean 43.6 → last-12 86.8, ×2.0;
// salted caramel ×1.4; pistachio latte 2.8 → 4.6 peaking every January;
// miso caramel flat at 0–1) feeds the insight and one shift row, NOT
// drawn; call B anchored on the brand's own door ("pistachio latte,miso
// caramel,salted pistachio,black sesame latte,hojicha latte"; pistachio
// latte Jan 2026 = 100) is THE CHART — pistachio latte 26.8 → 47.2
// (×1.8, the ghost), hojicha latte 12.2 → 34.0 (×2.8, peak 41 Sep 2026),
// salted pistachio 9.4 → 14.7 (×1.6, peak 18 Apr 2026), miso caramel 5.2
// → 10.2 (×2.0, peak 15 Sep 2026), black sesame latte 2.6 → 7.7 (×3.0,
// insight only). Never mix the two calls' points. THE DB BASIS (dev DB,
// re-queried 2026-10-28 at build, identical to the plan; the served run
// per (brand, as_of) = the completed GLOBAL run with the latest
// executed_at; mocked / deprecated items and signalless pulses excluded;
// never Diageo): Starbucks served 2026-10-19 (executed 2026-10-23) CIQ
// 86.14, raw 86.48, capital K 71.66, rank 1 in food (McDonald's 83.04,
// Coca-Cola 79.07, Red Bull 73.25, 7-Eleven 70.96, 24 Aug runs); capsule
// 99.5 / 58. Strategy record on 270 real items since 21 Apr (plays /
// reception / valence): aesthetic 145 / 40.4 / +28.3, identity 58 / 34.5
// / +28.2, scarcity 53 / 36.4 / +29.7, fandom 52 / 38.6 / +29.9, opacity
// 21 / 29.6 / +19.4; ON DRINKS (56 capsules): aesthetic 56 / 41.0 /
// +23.0, opacity 14 / 31.4 / +19.7, scarcity 7 / 39.4 / +28.3, fandom 3 /
// 60.0 / +43.3, identity 0 — the strategy quadrant (x = plays as a share
// of 56, y = reception as a share of fandom's 60, w = valence). The
// flavour shelf (base / best presence / net valence per read window):
// Pistachio Oatmilk Crunch Frappuccino (GLOBAL 27 Sep) 26.8 unread — a
// social-media announcement with no public source found, a MEASURED dot
// only; Crème Brûlée range (23 Sep) 26.3 unread; Tiramisu Velvet (5 Oct)
// 28.1 / 40 / +40; Pecan Cortado return (archive, 5 Oct) 27.4 unread;
// Pikachu Honey Cream Latte (APAC 30 Sep) 34.2 / 47 / +20; Honey Ruby
// Grapefruit Aerocano (APAC 2 Jun) 23.8 / 53 / 0, +27, +53; Chill & Relax
// Soda Yuzu Citrus (APAC 24 Apr) 23.8 / 60 / +60, +47; Kueh Salat
// Cheesecake (archive, APAC 15 Jul) 33.4 / 60 / +60; Braised Short Rib
// Grilled Cheese + Turkey & Stuffing (5 Oct) 27.7 / 40 / +13; Chicken
// Bacon Protein Pocket (NA 25 Aug) 24.8 unread; Caramelised Banana drinks
// (25 Jun) 28.6 / 60 / +27, +40, +14, 0; Iced Horchata Shaken Espresso
// (NA 12 May) 31.3 / 73 / +7, +20, +47, +27, +73 (FIVE windows); Orange
// Cream collection (NA 28 Jul) 35.0 / 73 / +73, +26; the pumpkin shelf
// (NA Aug): Fall menu 43.3 / 67, Iced Pumpkin Cream Shaken Espresso 40.9
// / 60 / +60, Pumpkin Spice Chai 30.1 / 73 / +20, Chaider 30.1 / 67 /
// +14, +40, Iced Pumpkin Cream Matcha 29.9 / 73 / +46; CHARLIE BROWN
// MOCHA FRAPPUCCINO (NA 15 Sep) 36.5 / 0 / −27 — the one autumn drink
// read below zero, sweet on sweet. No Oleato, miso, black sesame, hojicha
// or salted-caramel item on the record since April (0 matches): the
// January pistachio menu and Oleato are HISTORY, public record only. The
// board's nearest plays: McDonald's UK Hot Honey range (14 Jul) 30.9 / 60
// / 0, 0; Honey Brown Butter Biscuit (21 Jul) 26.2 / 67 / +14; Stroopwafel
// McFlurry 31.1 / 67 / +33, +27; Sweet Corn McShaker Fries 25.7 / 47 /
// −27, +47; 7-Eleven Magnum Dubai Pistachio & Kunafa (DB 12 Aug 2026;
// public: 7-Eleven Singapore, Dec 2025 — a MEASURED dot only) 30.8 / 33 /
// +33; Cadbury Dubai chocolate (10 Jul) 31.7 unread; ROASTED CORN
// SMOOTHIE (28 Jul) 29.5 / 53 / +13, 0 and FRUIT GAZPACHO SMOOTHIE (28
// Jul) 32.0 / 0 — the board's only two savoury DRINKS; Red Bull Sudachi
// Lime 30.2 / 67 / +20, +60; Coca-Cola nothing savoury-sweet. MATRIX
// counts (regex over title + summary since 21 Apr; Pistachio / Salt as a
// finish / Honey / Savoury in the cup): Starbucks 1 / 0 / 4 / 0,
// McDonald's 0 / 0 / 3 / 0, 7-Eleven 2 / 0 / 0 / 2, Red Bull 0 / 0 / 1 /
// 0, Coca-Cola 0, Blue Bottle 0 / 0 / 0 / 1, Blank Street 1 / 1 / 0 / 0,
// Luckin 0 / 0 / 0 / 2 — Pistachio scaled to 7-Eleven's 2, Salt as a
// finish ONE play on the whole grid shown as a 25 step and said so, Honey
// to Starbucks' 4 (the grid opens on that cell, the nearest thing the
// brand owns), Savoury in the cup to 2. LANE BANDS (food-board capsules
// since 21 Apr; n / Starbucks n / median / p75 / max presence): nut &
// caramel 14 / 7 / 28.7 / 31.5 / 67; honey 5 / 2 / 30.8 / 30.9 / 67;
// spice sold as a season 26 / 11 / 31.1 / 35.2 / 73; savoury on a plate
// 28 / 6 / 29.8 / 32.0 / 60 (the 80 in the raw query is the Unicorn
// Frappuccino, a regex false hit); savoury in the cup 2 / 0 / 29.5 / 32.0
// / 53 (both 7-Eleven's smoothies). MEASURED here: the strategy record,
// the matrix counts, the arena bands and dots, the pairs. OBSERVED: the
// stats, the shift rows, the Trends chart, the where-swavory-lives
// quadrant. MODELLED (projected): the cohorts and the three moves —
// Salted Pistachio Cold Foam (capsule; aesthetic .6 / opacity .3) +0.9;
// Miso Caramel Cortado (capsule; aesthetic .5 / scarcity .5) +0.8; Hot
// Honey Sesame Shaken Espresso (capsule; identity .4 / scarcity .3) +0.6
// → +2.3, 86.14 → ~88.4. No move leads with savoury: salt and the chef's
// flavour finish a sweet drink. Re-check after the Q4 FY26 call
// (2026-10-29) and the January 2027 pistachio menu.

//
// The CAFÉ MIXOLOGY SIGNAL question (2026-10-28, the Home's FIRST Early
// Signal card "Café Mixology Is Remixing the Cup" — the micro-orders card
// stood there 2026-10-22 → 2026-10-28 and its entry stays in the pack;
// plan docs/plans/starbucks-cafe-mixology-signal-lab): "Is café mixology
// about to remix the cold bar?" on the `signal` type (the shared think
// script), item type `capsule` (the lead moves are drinks), appended LAST
// so the four chips stay fans · fandom · community · collab; the card and
// the aliases reach it. VERDICT: yes, and the fizz reached the cup from
// two directions — the chains built the non-alcoholic bar as a DIRTY
// SODA this spring, and Starbucks is already testing coffee in the fizz
// (the Spritzers test, Spain's Coffee Tonic, Japan's add-espresso soda);
// the signal is the AFTERNOON the bar is losing, not the word "mocktail"
// (nobody searches it); the empty cell is coffee IN the fizz at scale in
// the US; the caution is Fizzio (sparkling as its own menu, gone in two
// years). Public basis (verified 2026-10-28, URLs in the plan): Gallup
// Consumption Habits (n=1,200, Jul 2026) 54% of US adults drink, a tie
// with the record low, 18–34s 50%, 17% chose a non-alcoholic alternative
// instead of a drink in the past year; IWSR US beverage-alcohol volume
// −5% in 2025, no-alcohol beer +15%; Attest (US Gen Z, n=800, Feb 2026)
// bars / clubs as the main drinking place 45% → 23%, not drinking 17% →
// 24%; a Starbucks-commissioned Morning Consult poll (n=2,201, Nov 2025)
// 70% of Gen Z planned a coffee-shop visit in Thanksgiving week vs 33% a
// bar; Tastewise (vendor) menus with a mocktail +35% in a year;
// Datassential mocktail median menu price $9; Toast (same-store, ~164k
// locations, 2025) energy drinks +8.7%, diet soda +7.4%, regular soda
// −2.3%, cold brew −2.2%; NCA Fall 2026 (n=1,882) past-week
// espresso-based 47% (record), cold coffee 21% of past-day; NCA Spring
// 2026 (n=1,850) 22% of past-day drinkers drink in the afternoon, 11% in
// the evening; Square matcha orders +36.5% YoY (Q3 2025); Japan green tea
// exports +42% in volume (FY to Mar 2026), Japan importing tea; Drive
// Research (n=1,000, Jul 2026) 55% of coffee drinkers will not pay $5+;
// YouGov Starbucks net Value −15.1; hashtagradar TikTok counters (read
// 2026-10-28): #dirtysoda 3.7K posts / 165.7M views / 45K per post,
// #mocktail 67.6K / 1.4B, #matchalatte 175.9K / 1.8B, and NO cached page
// for #espressotonic / #dirtymatcha / #coffeesoda / #coffeemocktail (the
// signal's own tags are too small to count — the shift row says so). THE
// CHAINS' MOVES (public record): Dunkin' Dirty Soda nationwide 29 Apr
// 2026 (Pepsi + coffee milk + foam — the April search spike's event);
// McDonald's three crafted sodas + three caffeinated Refreshers in ~13,700
// US restaurants from 6 May 2026, a beverage-specialist role, Red Bull
// Dragonberry Energizer from 17 Aug, CFO: more than half of
// beverage-platform traffic after lunch (Q2, 11 Aug 2026); CosMc's (8
// units) closed Jun 2025, "might be too complex"; Taco Bell dirty sips
// permanent (Mar 2026), 39 Live Más Cafés (Oct 2026); Sonic dirty Dr
// Pepper (24 Aug 2026); Swig >160 stores, a new one every six days,
// visits +30.7% in Feb 2026, no coffee; 7 Brew visits +41.6% Jan–Aug
// 2026, traffic noon–8 pm; Poppi's retail sales fell in H1 2026; Club
// Soda London closed Jan 2026, three LA zero-proof venues closed or
// online-only. STARBUCKS' OWN TRAIL (public record): the SPRITZERS test
// from 28 Jul 2026, ~100 stores in Austin, San Antonio and St. Louis,
// sparkling Energy Refreshers and Spritzes incl. a Sparkling Cold Brew
// Sour Spritz and a Strawberry Matcha Spritz, Niccol on the Q3 FY26 call
// "we're calling it Spritzers", the CFO: the strongest gains in the
// afternoon where people "take no caffeine oftentimes"; Spain and
// Portugal's Coffee Tonic with Schweppes (Espresso Tonic, Cold Brew
// Tonic, Matcha Tonic), ~250 stores, May–Aug 2026, barista-made; Japan's
// Chillax Soda nationwide from 24 Apr 2026 with an official "add
// espresso" customisation (the DB's "Chill & Relax Soda, APAC" items ARE
// Japan — say "Japan"), Orange & Mango from 22 Jul; Korea Reserve's
// Sparkling Citrus Espresso (Mar 2024); Tokyo Roastery's Espresso Rosso
// Tonic (4 Sep 2026); the Roasteries' Arriviamo bars' spirit-free
// spritzes since 2018–19 (Chicago's All Day Spritz $10); the Aerocano on
// the US core menu from 29 Sep 2026 (Aerocano + Caramel only, no yuzu /
// grapefruit in the US), Korea 26 Feb 2026, Australia's Honey Ruby
// Grapefruit Aerocano Jul 2026; Energy Refreshers permanent 7 Apr 2026,
// Blended 14 Jul 2026; "$11 billion after 11 a.m." and the Investor Day
// "new afternoon ritual" line (Jan 2026), Refreshers ≈ $2B, cold ≈
// two-thirds of US beverages; Q3 FY26 US comps +7.9% (transactions
// +4.2%); the holiday 2026 menu (5 Nov) carries NOTHING sparkling, tonic
// or matcha-espresso; FIZZIO handcrafted sodas, ~3,500 cafés summer 2014,
// "no longer a part of our menu" Oct 2016; the espresso tonic's origin
// Oslo / Koppi 2007, Caffè Nero × Fever-Tree the first UK chain tonic
// (2019). THE THREE OFF-BOARD ROWS (the matrix's last three, public
// record read on the board's per-play step, treated AS MEASURED
// everywhere the reader sees — the collagen / swavory precedent): BLUE
// BOTTLE Kyoto-Style Espresso across all cafés worldwide from 16 Jun
// 2026, the base for its Espresso Tonic (US $8.50 vs Cold Brew $7.00 on
// a delivery listing — "about a dollar and a half over its cold brew",
// never the listing) — the specialty tonic; DUTCH BROS 1,225 shops (Q2
// 2026, revenue +32.5%, same-shop +5.8%), Rebel energy ≈ a quarter of
// sales (relayed — "about a quarter"), Myst Energy Refresher permanent
// (5 Aug 2026) — the soda bar at scale; LUCKIN 36,310 stores (Q2 2026),
// ~20 NYC stores, a fruity Americano series and an iced matcha ($6.45)
// on the US menu, a bottled Grapefruit C Americano >1M bottles in 24 h
// (company figure), two alcohol-infused drinks (May 2026), NO coffee
// soda found — the largest scale. CONSIDERED, NOT ADDED: Blank Street
// (no verified mixology play), Peet's / Tim Hortons (sparkling lines,
// no coffee in them), Costa (nothing sparkling), Taco Bell / Swig /
// Sonic (dirty soda, no coffee — on the where-it-lives quadrant only).
// REJECTED (never in copy): Blue Bottle "Sparkling Yuzu Espresso" (does
// not exist), a Luckin coffee soda (none), a US Starbucks espresso tonic
// or coffee soda (none ever), a named Starbucks "dirty matcha" (blog
// only), Taco Bell's "40% lift" (third-hand), every report-seller market
// size. The `trendCompare` is REAL Google Trends — TWO comparative
// SerpApi calls through the lab-plan skill's trends.py (worldwide,
// 2024-01-01 → 2026-10-27, monthly means, October and the one Dec-2023
// week dropped as part months): call A ("espresso tonic,coffee
// soda,dirty matcha,coffee mocktail") is THE CHART — coffee soda 100 in
// Apr 2026 (93 May, 76 Jun; first-12-month mean 31.7 → last-12 59.2,
// ×1.9, the Dunkin' / McDonald's spring), espresso tonic three summers
// each higher (peaks 25 / 28 / 34, Jun 2026; 12.1 → 18.0, ×1.5, back to
// 5–10 every winter), dirty matcha peak 9 (3.2 → 7.4, ×2.3 — people
// search "iced matcha latte", not the bar's name), coffee mocktail peak
// 4 (the ghost); call B ("espresso tonic,dirty matcha,coffee soda,shaken
// espresso,cold brew") is the COMPRESSION CHECK, not drawn — cold brew
// 100 (Feb 2026, ×2.1), shaken espresso 8, coffee soda 6, espresso tonic
// 2, dirty matcha 0: on the cold brew scale every signal term reads under
// six, said in the chart's note, never swapped for a flattering anchor.
// Never mix the two calls' points. THE DB BASIS (dev DB, re-queried
// 2026-10-28 at build, identical to the plan; the served run per (brand,
// as_of) = the completed GLOBAL run with the latest executed_at; mocked /
// deprecated items and signalless pulses excluded; never Diageo):
// Starbucks served 2026-10-19 (executed 2026-10-23) CIQ 86.14, raw
// 86.48, capital K 71.66, rank 1 in food (McDonald's 83.04, Coca-Cola
// 79.07, Red Bull 73.25, 7-Eleven 70.96, 24 Aug runs); capsule 99.5 /
// 58; 270 items since 21 Apr (NA 93 · APAC 85 · GLOBAL 56 · EMEA 25 ·
// LATAM 11). Strategy record (plays / reception / valence): aesthetic
// 145 / 40.4 / +28.3, identity 58 / 34.5 / +28.2, scarcity 53 / 36.4 /
// +29.7, fandom 52 / 38.6 / +29.9, opacity 21 / 29.6 / +19.4; ON DRINKS
// (56 capsules): aesthetic 56 / 41.0 / +23.0, opacity 14 / 31.4 / +19.7,
// scarcity 7 / 39.4 / +28.3, fandom 3 / 60.0 / +43.3, identity 0 — the
// strategy quadrant (x = plays as a share of 56, y = reception as a share
// of fandom's 60, w = valence). The cold shelf near the topic (base /
// best presence / net valence per read window): SODA — Chill & Relax
// Soda Yuzu Citrus (APAC 24 Apr) 23.8 / 60 / +60, +47; Chill & Relax Soda
// Strawberry (APAC) 31.2 / 0 (a return, read once at nothing); no US
// soda, no espresso tonic in any market. ESPRESSO REMIXED (13, no rival
// has one) — Iced Pumpkin Cream Shaken Espresso 40.9 / 60; Okayama Peach
// Apple Cold Brew (Philippines) 33.5 / 60 / +33, +60; Iced Horchata
// Shaken Espresso 31.3 / 73 / +7, +20, +47, +27, +73; Aerocano NA (29
// Sep) 27.8 unread; Aerocano APAC 24.5 / 67 / +20, +60, +67, +14; Honey
// Ruby Grapefruit Aerocano APAC 23.8 / 53 / 0, +27, +53; Aerocano LATAM
// 26.1 / 40 / +40, −14; Aerocano × Jay Chou 28.5 / 47; Kopi Siew Dai 26.5
// / 0. MATCHA (7, the best-read lane) — Blue Coconut beverages 33.2 / 80
// / +27, −7, +6, +20 (the one negative window); Lavender Matcha 33.4 /
// 53; Iced Pumpkin Cream Matcha 29.9 / 73 / +46, 0; Iced Mango Cream
// Matcha 29.8 / 67 / +7, 0, +26; Caramelised Banana Matcha 28.6 / 60;
// Protein Cold Foam Matcha (Singapore) 34.7 unread; NO matcha-espresso
// hybrid on the record. ENERGY & REFRESHERS (12) — New York Energy
// Refresher 49.8 unread (event exclusive); Fanatics Fest Refresher 40.9
// / 53; Tropical Butterfly Refresher 30.3 / 73 (five windows); Blended
// Pink Energy 24.8 / 47; the Refreshers Residency tour 20.2 / 67, the
// Refresher pop-up 17.6 / 73. THE MOCKTAIL ITSELF: 0 — no Starbucks item
// names a mocktail, a cocktail, mixology or the Arriviamo bars. The
// board's nearest: 7-Eleven's Slurpee 60th year (Blueberry Lemonade
// Slurpee 46.8 unread, Slurpee Date content 44.7 / 87, Collective Arts
// Slurpee Soda 27.7, AriZona Slurpee "Fruit Juice Cocktail" 36.0 / 47 —
// the board's only "cocktail"); Coca-Cola's Jacob's Soda Fountain pop-up
// 39.6 / 73 (the one soda bar, read well), Sprite + Tea 34.2 / 60;
// McDonald's × Red Bull Dragonberry Energizer 32.0 / 73; Red Bull
// Energizers at McDonald's 36.0 unread. MATRIX counts (regex over title +
// summary since 21 Apr; sparkling & soda / espresso remixed / matcha /
// the mocktail itself, NARROW mocktail regex — the wide `bar` regex
// caught ice-cream bars): Starbucks 2 / 13 / 7 / 0, 7-Eleven 36 / 0 / 3 /
// 2, Coca-Cola 7 / 0 / 0 / 1, McDonald's 1 / 0 / 0 / 0 (our shelf MISSED
// its May national sodas — the cell note says "a limit of our search"),
// Red Bull 2 / 0 / 0 / 0, Blue Bottle 0 / 1 / 0 / 0, Dutch Bros 2 / 0 / 0
// / 0, Luckin 0 / 1 / 1 / 0 (public record) — each column scaled to its
// busiest brand (the grid opens on Starbucks' espresso cell, the first
// 100). LANE BANDS (food-board capsules since 21 Apr; n / Starbucks n /
// median / p75 / max presence): soda 10 / 2 / 32.0 / 36.2 / 60; espresso
// 16 / 15 / 30.0 / 33.8 / 73; matcha 8 / 7 / 31.5 / 33.7 / 80; energy 18 /
// 12 / 27.0 / 29.8 / 80; mocktail 1 / 0 / 36.0 / 36.0 / 47 (ONE play, so
// typical = ceiling, said in the note). `similar` slugs: coca-cola /
// mcdonalds / 7-eleven / red-bull only (`blue-bottle` and `luckin` are
// not in BRAND_STYLE). MEASURED here: the strategy record, the matrix
// counts, the arena bands and dots, the pairs. OBSERVED: the stats, the
// shift rows, the Trends chart, the where-café-mixology-lives quadrant.
// MODELLED (projected): the cohorts and the three moves — Aerocano Tonic
// (capsule; aesthetic .6 / scarcity .4) +0.9; Iced Dirty Matcha
// (capsule; aesthetic .5 / fandom .3) +0.8; After Five Spritz (capsule;
// identity .5 / opacity .3) +0.6 → +2.3, 86.14 → ~88.4. No move is a new
// menu: the fizz is a modifier on a drink already sold, priced like one.
// STALE readings kept OUT of the blocks (header only): the Q3 FY23 "75%
// cold", the 2022 mocktail menu figures, the 2023 #dirtysoda views, the
// Kyoto tencha prices, the Fall 2025 NCA Gen Z cut, the Jan 2025 dry-year
// intent polls. Re-check after the Q4 FY26 call (2026-10-29 — the first
// Spritzers read-out may be on it), Yum / Dutch Bros / McDonald's Q3
// (early Nov), the holiday menu (5 Nov) and the Spritzers test's fate.

export const STARBUCKS_LAB = {
  brand: "starbucks",
  name: "Starbucks",
  // the demo persona greeted on the Lab — every mock pack carries one
  person: "Alex",
  // THE WEEKLY PERSPECTIVE's cards (2026-10-21, the user's reference
  // screen — pages/Leaderboard/WeeklyOne; the Weekly THREE from 2026-10-14
  // until then, three cards without previews; since 2026-10-22 the pack
  // lists as many cards as it wants, mock/lab/index.js `weeklyFor`): the
  // week's one priority, the fandom lab under its own wording (an alias of
  // the entry, so the card asks with these words and lands on the scripted
  // answer) and the entry's own type, with a PREVIEW drawn from the
  // answer's own blocks — the trend is the cumulative fandom spend with
  // the calendar against today's course (+77% by next September, the
  // "Fandom revenue" series), the ring the share of incremental spend the
  // cups moved ("Where your own fandom comes from"), the bars the
  // reception each lane earned ("How the industry builds fandom"). The
  // overline's count is the pack's.
  weekly: {
    sources: 3118402,
    cards: [
      {
        entry: "fandom-opportunity",
        question: "Where can fandom create the most value?",
        measured: "2026-10-19",
        preview: {
          trend: {
            value: "+77%",
            label: "Fandom spend",
            series: [0, 70, 88, 92, 128, 138, 172, 182, 205, 212, 258, 265],
          },
          ring: { value: 81, label: "Spend moved by cups" },
          bars: {
            title: "Reception by lane",
            rows: [
              { label: "Menu drops", value: 90 },
              { label: "Borrowed IP", value: 76 },
              { label: "Cups & merch", value: 62 },
            ],
          },
        },
      },
    ],
  },
  entries: [
    {
      id: "fans-ratio",
      // the ladder lands on loyalty & community — the one lane that never
      // resets and the lowest on the board
      itemType: "community",
      type: "fans",
      question: "What is the product fans vs brand fans ratio?",
      aliases: [
        "what is the product fans vs brand fans ratio",
        "what is our product fans vs brand fans ratio",
        "product fans vs brand fans ratio",
        "what is the ratio of product fans to brand fans",
        "how many of our fans are product fans vs brand fans",
        "product fans versus brand fans",
        "what is the product fan vs brand fan ratio",
        "do we have more product fans or brand fans",
        // the Weekly Perspective card's wording (2026-10-14)
        "whats the ratio of product fans to brand fans",
      ],
      askedOn: "2026-09-21",
      blocks: [
        {
          kind: "readout",
          provenance: "projected",
          verdict:
            "Two to one. Fifty-two in a hundred love a drink, twenty-four love the place — and those twenty-four carry half the spend.",
        },
        {
          kind: "stacks",
          insight:
            "Fifty-two of every hundred customers are product fans — more than twice the brand fans and superfans together — and the minority carries half the money; that is the whole argument for the ladder.",
          title: "The fan ladder",
          provenance: "projected",
          note: "Every 100 Starbucks customers in the United States sorted onto four rungs by behaviour — repertoire, app use, sitting down, following, collecting — then the same rungs as a share of visits and of spend. Tap a row for what each rung does.",
          legend: [
            { key: "pass", label: "Passers-by", color: "#94A3B8" },
            { key: "prod", label: "Product fans", color: "#60A5FA" },
            { key: "brand", label: "Brand fans", color: "#4ADE80" },
            { key: "coll", label: "Superfans", color: "#FF9F43" },
          ],
          rows: [
            {
              id: "customers",
              label: "Customers",
              sub: "share of the base",
              parts: [
                { key: "pass", v: 24 },
                { key: "prod", v: 52 },
                { key: "brand", v: 19 },
                { key: "coll", v: 5 },
              ],
              note: "Passers-by: fewer than two visits a month, no app or a card they forgot, they order what the person in front ordered. Product fans: one drink, the same way, five times a month; the app as a till; the seasonal version of their drink and nothing beside it. Brand fans: four or more drinks plus food, a barista's name, a feed followed, a cup owned, the new thing in launch week — posted. Superfans: all of that plus the drops — the Bearista queue, blind boxes, resale, the Reserve tier, two visits a week.",
            },
            {
              id: "visits",
              label: "Visits",
              sub: "share of visits",
              parts: [
                { key: "pass", v: 7 },
                { key: "prod", v: 50 },
                { key: "brand", v: 33 },
                { key: "coll", v: 11 },
              ],
              note: "A passer-by comes one and a half times a month, a product fan five, a brand fan nine, a superfan eleven. Half of all visits are product fans buying their one drink; the top two rungs, a quarter of the base, make almost half the visits.",
            },
            {
              id: "spend",
              label: "Spend",
              sub: "share of money",
              parts: [
                { key: "pass", v: 5 },
                { key: "prod", v: 44 },
                { key: "brand", v: 37 },
                { key: "coll", v: 14 },
              ],
              note: "Ticket climbs the ladder — $5.20, $6.90, $8.40, $9.00 — and merch arrives at the top: four dollars a month from a brand fan, fifteen from a superfan. Brand fans and superfans are 24 of every 100 customers and 51 of every 100 dollars.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "Starbucks over-indexes exactly where the product fans are and under-indexes where brand fans are made: the brand is run by its product fans and is short of the one segment — the Hype Hunters — that makes brand fans.",
          title: "Six segments, how you hold each",
          provenance: "projected",
          note: "Your six away-from-home coffee segments: each row's share of coffee drinkers, how its members split once inside Starbucks, and the index of how well you hold it against the chains it skews to — 100 is your fair share, below it the segment goes elsewhere. Tap a row for the reasoning.",
          mode: "split",
          a: { label: "Product fans", color: "#60A5FA" },
          b: { label: "Brand fans", color: "#4ADE80" },
          rows: [
            {
              id: "clean",
              label: "Clean Connoisseurs",
              sub: "21% of coffee drinkers · craft, morning",
              a: 62,
              b: 38,
              tail: "130",
              note: "Your strongest segment and the one your own leadership belongs to — sixteen of the nineteen brand leaders on the segment map are Clean Connoisseurs. Product fans of the craft drink, an Americano or a latte done well, who become brand fans through the café, Reserve and the coffee itself — never through a cup drop. The ladder for them is a chair and a ceramic mug. Skews to independents and Starbucks.",
            },
            {
              id: "budget",
              label: "Budget Baddies",
              sub: "18% · savvy, value, morning",
              a: 70,
              b: 30,
              tail: "125",
              note: "Over-indexed at Starbucks and mostly product fans: they have the app because the app has the deal. The cheapest first rung to climb — a free customisation on Mondays, reload stars and a personal offer on the drink they already buy move them without a single new product. Skews to Starbucks, Peet's, Caribou.",
            },
            {
              id: "hype",
              label: "Hype Hunters",
              sub: "18% · social, trendy, afternoon",
              a: 25,
              b: 75,
              tail: "85",
              tailLow: true,
              note: "The segment that makes brand fans — social, limited-time, merch-minded, posting — and the one where you hold less than your fair share. Dutch Bros built its growth on exactly these people. Every Bearista queue was a Hype Hunter queue; you just do not own the segment between drops. Recruiting them is the single biggest source of new brand fans available.",
            },
            {
              id: "flavor",
              label: "Flavor Fiends",
              sub: "15% · spontaneous, indulgent",
              a: 80,
              b: 20,
              tail: "80",
              tailLow: true,
              note: "The Pumpkin Spice Latte segment: they love a flavour, they indulge, and they leave. Four in five are product fans and they go wherever the flavour is — a McDonald's pumpkin or a Dutch Bros seasonal costs you a visit. They climb the ladder sideways, through the second drink, never through the brand.",
            },
            {
              id: "rushed",
              label: "Rushed Regulars",
              sub: "14% · routine, early, fast",
              a: 92,
              b: 8,
              tail: "60",
              tailLow: true,
              note: "The purest product fan there is: one drink, before seven, every day, and a strong preference for whoever is fastest. You under-index here and should not try to make them brand fans — make them faster: mobile order, a pick-up shelf, a streak. Their value is frequency, not fandom.",
            },
            {
              id: "frugal",
              label: "Frugal Faithfuls",
              sub: "15% · deal-led, tried and true",
              a: 88,
              b: 12,
              tail: "70",
              tailLow: true,
              note: "Product fans of the Mocha and the Frappuccino, deal-driven and predictable. They come for the price and stay for the drink. A brand fan will not be made here; a loyal product fan can be kept with a value tier and a reason to visit in the afternoon.",
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "The fandom exists and the brand does not own it: everything the crowd does scores in the eighties and nineties, everything the brand would have to build scores under forty-five — the fans organise themselves and the brand gives them nowhere to stand.",
          title: "Crowd-built, brand-owned",
          provenance: "measured",
          note: "Your community assessment, May to August 2026, on the dimensions that decide a fandom. Sorted by score: the crowd-built ones first, the brand-owned ones last. Tap a row for the read.",
          rows: [
            {
              id: "resale",
              label: "Resale market depth",
              context: "the crowd built it",
              score: 97,
              role: "the cups",
              drivers: [
                "The Bearista resold at ten times its price within a day; a secondary market this deep is a fandom's clearest fingerprint, and the brand did nothing to make it.",
              ],
            },
            {
              id: "canon",
              label: "Fan creativity",
              context: "the crowd built it",
              score: 96,
              role: "the secret-menu canon",
              drivers: [
                "The Pink Drink was a customer hack for a year before it was a product. The next one is on TikTok now — the crowd names it before Starbucks does.",
              ],
            },
            {
              id: "badge",
              label: "Membership signalling",
              context: "the crowd built it",
              score: 94,
              role: "the cup in hand",
              drivers: [
                "The cup says who you are before you have ordered anything — the one identity signal on the shelf that costs the brand nothing.",
              ],
            },
            {
              id: "rituals",
              label: "Community rituals",
              context: "the crowd built it",
              score: 84,
              role: "the seasonal return",
              drivers: [
                "Launch day for the Pumpkin Spice Latte was the biggest fall day in North American history: a ritual the fans perform without being asked.",
              ],
            },
            {
              id: "status",
              label: "Status progression",
              context: "only the brand can build it",
              score: 41,
              role: "the weakest owned mechanic",
              drivers: [
                "The programme rewards spend, not standing; the new Reserve tier is the first owned status mechanic in years and the right lever, aimed at exactly this score.",
              ],
            },
            {
              id: "unpaid",
              label: "Engagement without incentives",
              context: "only the brand can build it",
              score: 38,
              role: "the app as a till",
              drivers: [
                "Our own assessment says the app functions as a transactional till rather than a community hub; the score fell two points after the Odyssey programme was shut.",
              ],
            },
            {
              id: "exclusive",
              label: "Community exclusivity",
              context: "only the brand can build it",
              score: 23,
              role: "nothing to be inside of",
              drivers: [
                "A drop that sells out in an hour makes resellers, not members. Member-first access and a published calendar turn the queue into belonging.",
              ],
            },
            {
              id: "spaces",
              label: "Owned community spaces",
              context: "only the brand can build it",
              score: 22,
              role: "nowhere to stand",
              drivers: [
                "The fans meet on TikTok and in the resale market, never on anything the brand runs. The third-place remodels are the first owned room in years.",
              ],
            },
            {
              id: "channel",
              label: "Owned channel engagement",
              context: "only the brand can build it",
              score: 19,
              role: "the feed nobody follows",
              drivers: [
                "The brand's own feed is followed by half as many people as use its app; the creator-network pilot is the right instinct — fans make the content the brand cannot.",
              ],
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "Fandom is played one time in seven and rewarded best on both counts; where it is played it is almost all merch, the brand fan's format — none of the three drink fandoms has ever been played as fandom.",
          title: "Fandom as a strategy",
          provenance: "measured",
          note: "The five strategies a play can use across this year's 131 plays: the bar is net reception — attention counted the way the index counts it, so a badly received play counts against its strategy — with the play count and the warmth of the reaction beside it. Tap a row to read it.",
          rows: [
            {
              id: "fandom",
              label: "Fandom",
              context: "18 plays of 131 · valence +9",
              score: 21,
              role: "least used, best received",
              drivers: [
                "The strategy the index rewards most and the warmest on the shelf — and the least used of the five.",
                "When it is merch it works hardest: eleven plays, net reception 26, warmth +12 — the Bearista family, the blind boxes, the collection rack.",
                "The two drinks tagged fandom are the two that travelled furthest this year: the Unicorn's global return, received at 90, and the New York Energy Refresher.",
              ],
            },
            {
              id: "scarcity",
              label: "Scarcity",
              context: "29 plays · valence +8",
              score: 18,
              role: "the safest lever",
              drivers: [
                "Limited runs, drops, seasonal returns. Works on both fan types, because the product fan's ritual is also a scarcity play.",
              ],
            },
            {
              id: "aesthetic",
              label: "Aesthetic innovation",
              context: "84 plays · valence +8",
              score: 16,
              role: "two-thirds of the shelf",
              drivers: [
                "A new colour, a new flavour — most of what Starbucks did this year. Reaches product fans weakly and brand fans barely; the lane is at its ceiling.",
              ],
            },
            {
              id: "identity",
              label: "Identity signal",
              context: "29 plays · valence +7",
              score: 14,
              role: "borrowed identities",
              drivers: [
                "Mostly character collabs — Miffy, Peanuts, POP MART — the cup that says who you are. Brand-fan territory, on someone else's character.",
              ],
            },
            {
              id: "opacity",
              label: "Opacity",
              context: "15 plays · valence +7",
              score: 13,
              role: "the crowd runs it for free",
              drivers: [
                "Blind boxes and secret menus. Small — and the one strategy the crowd already runs without the brand: the secret-menu canon is opacity Starbucks did not make.",
              ],
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "Brand fans are a young phenomenon — four in ten Gen Z and Gen Alpha customers against three in ten Millennials, one in ten Gen X — and product fans are everyone; the conversion pool is Millennials, the largest cohort, one rung below the brand fans they used to be.",
          title: "Rungs by generation",
          provenance: "projected",
          note: "Five generations down the side, four rungs across; each cell is the share of that generation on that rung, so a row adds to one hundred. Gen Z is 27% of the base, Millennials 31%, Gen X 24%, Boomers 15%, Gen Alpha 3% — children ordering through a parent's app. Tap a cell for the reading.",
          cols: ["Passers-by", "Product fans", "Brand fans", "Superfans"],
          rows: [
            {
              label: "Gen Alpha",
              cells: [
                { v: 30 },
                { v: 30 },
                {
                  v: 25,
                  note: "Children under thirteen ordering through a parent's app: the only generation where superfans reach fifteen in a hundred — their first Starbucks is a cup or a bear, not a coffee, and the Pink Drink is the entry product.",
                },
                {
                  v: 15,
                  note: "The highest superfan share of any generation: the Bearista, the blind boxes and the Pink Drink cup are how the youngest customers arrive.",
                },
              ],
            },
            {
              label: "Gen Z",
              cells: [
                { v: 18 },
                {
                  v: 42,
                  note: "Product fans here are drink-specific — matcha, the Pink Drink, a refresher — rather than coffee-specific.",
                },
                {
                  v: 30,
                  note: "The most brand-fan-heavy adult cohort: four in ten are brand fans or superfans, and the most likely to use the app at all.",
                },
                { v: 10 },
              ],
            },
            {
              label: "Millennials",
              cells: [
                { v: 20 },
                {
                  v: 52,
                  note: "The Pumpkin Spice Latte's home: half are product fans of long standing — the drink became a routine ten years ago. They already have the habit; they need a second drink and a reason to sit.",
                },
                {
                  v: 23,
                  note: "A quarter are brand fans who grew up with the third place — the best conversion pool on the page, one rung below where they used to be.",
                },
                { v: 5 },
              ],
            },
            {
              label: "Gen X",
              cells: [
                { v: 28 },
                {
                  v: 60,
                  note: "Six in ten are product fans: the quiet regular — highest income, earliest visit, least interested in the feed. Nothing on the shelf this year spoke to them except the stores.",
                },
                { v: 10 },
                { v: 2 },
              ],
            },
            {
              label: "Boomers",
              cells: [
                {
                  v: 38,
                  note: "Mostly passers-by and product fans of a plain coffee; consideration is a third of Gen Z's. A gap to know about, not a fandom to build — the value is in keeping the routine easy.",
                },
                { v: 56 },
                { v: 5 },
                { v: 1 },
              ],
            },
          ],
        },
        {
          kind: "stacks",
          insight:
            "Product fans look like the country and brand fans look like a campus: every cultural product aimed at brand fans is, in practice, aimed at women under thirty-five, and every product aimed at product fans has to work for a forty-year-old too.",
          title: "Generations by rung",
          provenance: "projected",
          note: "The same numbers turned around: each rung as a bar coloured by the generations it is made of, with median age and the share of women beside the name. Tap a rung for the read.",
          legend: [
            { key: "alpha", label: "Gen Alpha", color: "#FF7A52" },
            { key: "z", label: "Gen Z", color: "#8b7bff" },
            { key: "mill", label: "Millennials", color: "#4FC3F7" },
            { key: "x", label: "Gen X", color: "#94A3B8" },
            { key: "boom", label: "Boomers", color: "#FDE047" },
          ],
          rows: [
            {
              id: "pass",
              label: "Passers-by",
              sub: "median age 41 · 52% women",
              parts: [
                { key: "alpha", v: 4 },
                { key: "z", v: 20 },
                { key: "mill", v: 25 },
                { key: "x", v: 28 },
                { key: "boom", v: 23 },
              ],
              note: "The oldest rung and the most evenly spread: a quarter Gen X, a quarter Millennial, a fifth Boomer. Nobody here is a fan of anything yet.",
            },
            {
              id: "prod",
              label: "Product fans",
              sub: "median age 40 · 56% women",
              parts: [
                { key: "alpha", v: 2 },
                { key: "z", v: 22 },
                { key: "mill", v: 32 },
                { key: "x", v: 28 },
                { key: "boom", v: 16 },
              ],
              note: "Product fans look like the country: a third Millennial, a quarter Gen X, a fifth Gen Z, a sixth Boomer, median age forty, a slight female skew. Anything built for them has to work for a forty-year-old.",
            },
            {
              id: "brand",
              label: "Brand fans",
              sub: "median age 29 · 68% women",
              parts: [
                { key: "alpha", v: 4 },
                { key: "z", v: 42 },
                { key: "mill", v: 37 },
                { key: "x", v: 13 },
                { key: "boom", v: 4 },
              ],
              note: "Brand fans look like a campus: four in ten Gen Z, nearly four in ten Millennial, two-thirds women, median age twenty-nine. Every cultural product aimed at them is, in practice, aimed at women under thirty-five.",
            },
            {
              id: "coll",
              label: "Superfans",
              sub: "median age 25 · 72% women",
              parts: [
                { key: "alpha", v: 8 },
                { key: "z", v: 51 },
                { key: "mill", v: 29 },
                { key: "x", v: 9 },
                { key: "boom", v: 3 },
              ],
              note: "Younger still — half Gen Z, one in twelve a child. The Bearista queue, drawn as a bar.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "Only one product type reaches product fans more than brand fans — the drink coming back; everything else is brand-fan territory, which is why thirty-eight menu drops moved the index less than a handful of cups.",
          title: "Which cultural products work on whom",
          provenance: "projected",
          note: "Seven product types, each with two bars: how strongly it pulls product fans and brand fans, 0 to 100 — built from what each type earned this summer, how warm the reaction was, and who showed up for it. Tap a row for the measured figures behind it.",
          mode: "pair",
          a: { label: "Product fans", color: "#60A5FA" },
          b: { label: "Brand fans", color: "#4ADE80" },
          rows: [
            {
              id: "archive",
              label: "Returning favourites",
              a: 88,
              b: 55,
              note: "Fifteen plays this year, attention 23, net reception 25, valence +9 — the Pumpkin Spice Latte return and the Unicorn Frappuccino return, received at 90. The most-noticed things Starbucks did all year, and the crowd was product fans: people who wanted their drink, not the brand.",
            },
            {
              id: "capsule",
              label: "Menu drops",
              a: 62,
              b: 58,
              note: "Thirty-eight plays, attention 16, net reception 17, valence +7 — Blue Coconut, Lavender Matcha, the energy refreshers. Reaches both, weakly: a third of the shelf for a sliver of the index.",
            },
            {
              id: "experience",
              label: "Places & Reserve",
              a: 40,
              b: 80,
              note: "Thirteen plays, attention 14, net reception 16, valence +12 — the warmest reception of any type: Reserve openings in India, the reimagined Pike Place. The Clean Connoisseur's way up the ladder.",
            },
            {
              id: "content",
              label: "Content & creators",
              a: 35,
              b: 70,
              note: "Fourteen plays, attention 14, net reception 15, valence +7 — Martha Stewart's PSL campaign, the creator-network pilot, the secret-menu guides. Lands on product fans only when it carries their drink.",
            },
            {
              id: "collab",
              label: "Character & IP collabs",
              a: 30,
              b: 85,
              note: "Eighteen plays, attention 12, net reception 13, valence +7 — POP MART, Peanuts, Miffy, Dandy Worldwide. Brand-fan territory on borrowed characters.",
            },
            {
              id: "merch",
              label: "Merch & collectibles",
              a: 25,
              b: 92,
              note: "Fourteen plays, attention 20, net reception 22, valence +10 — the Pink Bearista, the FIFA Bearista, the blind boxes. The strongest pull on brand fans of any type, and next to none on product fans unless the cup is their drink's.",
            },
            {
              id: "event",
              label: "Events & fan moments",
              a: 15,
              b: 72,
              note: "Nineteen plays, attention 9, net reception 10, valence +6 — Fanatics Fest, the Coachella Unicorn drop, the Santa Monica pop-up. Someone else's crowd for a weekend; a product fan does not go.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "The anchored version wins every time, by a lot: a cultural product carries a product fan when it carries their drink — without a named beverage at the centre, cultural bets float.",
          title: "The bridge: with a drink, without",
          provenance: "measured",
          note: "Three like-for-like pairs from this year's shelf, measured the same way: the version anchored to a named drink against the version that was not. The bars are reception, attention on a 0 to 100 scale; the read carries the warmth. Tap a pair.",
          mode: "pair",
          a: { label: "Anchored to a drink", color: "#4ADE80" },
          b: { label: "No drink at the centre", color: "#94A3B8" },
          rows: [
            {
              id: "merch",
              label: "Merch",
              sub: "Pink Bearista vs FIFA Bearista",
              a: 40,
              b: 27,
              note: "The Pink Bearista glass — a Pink Drink cup — was received at 40 with a warmth of +17; the FIFA World Cup Bearista, a bear with no drink, at 27 and +4. Same bear, same summer: the drink's cup beat the football cup on attention and on warmth.",
            },
            {
              id: "collab",
              label: "Collab",
              sub: "Dandy + Orange Cream vs Dandy alone",
              a: 90,
              b: 53,
              note: "Dandy Worldwide merch with the Orange Cream drinks attached was received at 90 and +53; the same partner's merch alone, three weeks earlier, at 53 and +42. The partner did not change — the drink did.",
            },
            {
              id: "content",
              label: "Content",
              sub: "Unicorn return vs the brand film",
              a: 90,
              b: 20,
              note: 'The Unicorn Frappuccino\'s global return — the drink is the story — was received at 90; the "Made in Starbucks" brand film, no drink, at 20 with no warmth either way. The return out-drew the film more than four to one.',
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "The biggest wants are not new drinks; they are their drink, made easier — cheaper in a smaller size, at home, on a subscription, all year — and the one want already answered, protein, is the one that grew fastest.",
          title: "What product fans want and lack",
          provenance: "projected",
          note: "Six things product fans ask for, sorted by how many of them want it; the role names how far Starbucks is from giving it today. Tap a row for why.",
          rows: [
            {
              id: "small",
              label: "A smaller, cheaper size",
              context: "a four-dollar size of the thing I already buy",
              score: 78,
              role: "nothing exists — gap 85",
              drivers: [
                "The value tier exists as a promotion, not as a size. Rushed Regulars and Frugal Faithfuls leave for the price, not the taste.",
              ],
            },
            {
              id: "home",
              label: "My drink at home",
              context: "the Pink Drink in a bottle exists; the matcha does not",
              score: 71,
              role: "half-built — gap 60",
              drivers: [
                "Bottled Pink Drink and pumpkin pods are on the shelf; there is no Starbucks matcha for the kitchen and no cold foam. Seventy-one percent of PSL buyers buy pumpkin spice nowhere else — they would.",
              ],
            },
            {
              id: "protein",
              label: "A protein or zero-sugar version",
              context: "the modifier as the default, not the add-on",
              score: 69,
              role: "mostly answered — gap 35",
              drivers: [
                "Protein cold foam and lattes arrived in 2025 and became the fastest-growing modifier; the gap is that it is still a build, not a named drink for each hero.",
              ],
            },
            {
              id: "named",
              label: "My customisation, named",
              context: "the secret menu made official",
              score: 64,
              role: "half-built — gap 55",
              drivers: [
                "The Pink Drink was a secret-menu order for a year before it was a product. The next one is on TikTok now; the crowd names it before Starbucks does.",
              ],
            },
            {
              id: "streak",
              label: "A streak or a subscription",
              context: "pay once a month, order without thinking",
              score: 61,
              role: "nothing exists — gap 80",
              drivers: [
                "The programme rewards spend, not routine. A product fan's habit is the asset; nothing in the app celebrates it.",
              ],
            },
            {
              id: "allyear",
              label: "My seasonal drink all year",
              context: "PSL in March, S'mores in November",
              score: 58,
              role: "against the calendar — gap 70",
              drivers: [
                "Scarcity is the point for the brand; for the product fan it is a four-month absence. A members-only off-season week is the compromise.",
              ],
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "A product fan will buy a cultural product — but only one about their drink, and each fandom wants a different kind: Pink Drink fans the object, PSL fans the ritual, matcha fans the ingredient; nobody wears a drink.",
          title: "What a product fan would buy",
          provenance: "projected",
          note: "Three drinks down the side, six kinds of cultural product across; each cell is the share of that drink's product fans who would buy that kind of thing if it carried their drink and nothing else. Tap a cell for the read.",
          cols: [
            "Drinkware",
            "At home",
            "Collectible",
            "Event or day",
            "Content",
            "Apparel",
          ],
          rows: [
            {
              label: "Pumpkin Spice Latte",
              cells: [
                {
                  v: 74,
                  note: "A cup with the pumpkin on it: the Great Pumpkin Bearista sold out because it was the drink's season, not because it was a bear.",
                },
                {
                  v: 82,
                  note: "PSL fans buy the at-home version — the format that extends the ritual past the season.",
                },
                { v: 55 },
                {
                  v: 68,
                  note: "Launch day: a quarter of PSL fans already buy on the day it lands. A members-only morning is a ritual the crowd already performs.",
                },
                { v: 60 },
                { v: 38, note: "They will not wear it." },
              ],
            },
            {
              label: "Pink Drink",
              cells: [
                {
                  v: 86,
                  note: "The one fandom where a cup outsells the drink's own at-home bottle: the Pink Vibes collection was mostly gone by seven in the morning.",
                },
                { v: 70 },
                {
                  v: 78,
                  note: "Pink Drink fans buy the object — the collectible and the content around it.",
                },
                { v: 45 },
                { v: 72 },
                { v: 52 },
              ],
            },
            {
              label: "Matcha Latte",
              cells: [
                {
                  v: 66,
                  note: "Drinkware works if it is the ritual bowl and whisk, not a tumbler.",
                },
                {
                  v: 80,
                  note: "Matcha fans want it at home more than any object — and there is nothing to buy. The least collectible fandom and the most ingredient-literate.",
                },
                { v: 40 },
                { v: 35 },
                { v: 64 },
                { v: 30 },
              ],
            },
          ],
        },
        {
          kind: "stacks",
          insight:
            "Three drinks, three different fandoms: the Pink Drink is the youngest and the most object-minded, the matcha the fastest-growing and the most brand-fan-rich, the PSL the biggest, the oldest and the purest product fandom — and it is peaking.",
          title: "Three drinks, three fandoms",
          provenance: "projected",
          note: "The three drinks people are fans of by name, each split by generation; the line under the name is its split by rung. Tap a drink for what we know and the opportunity its fandom points at.",
          legend: [
            { key: "alpha", label: "Gen Alpha", color: "#FF7A52" },
            { key: "z", label: "Gen Z", color: "#8b7bff" },
            { key: "mill", label: "Millennials", color: "#4FC3F7" },
            { key: "x", label: "Gen X", color: "#94A3B8" },
            { key: "boom", label: "Boomers", color: "#FDE047" },
          ],
          rows: [
            {
              id: "pink",
              label: "Pink Drink",
              sub: "58% product fans · 28% brand fans · 14% superfans",
              parts: [
                { key: "alpha", v: 12 },
                { key: "z", v: 48 },
                { key: "mill", v: 28 },
                { key: "x", v: 9 },
                { key: "boom", v: 3 },
              ],
              note: "Born as a customer hack in spring 2016, on the menu by 2017, in a grocery bottle by 2023, a Pink Cannon Ball and a pink energy line by 2026 — every step the crowd's idea first. The youngest fandom on the page: half Gen Z, one in eight a child, 76% women, four in five not coffee drinkers, 45 mg of caffeine — the entry drink, photographed before it is drunk. The only fandom whose cup out-performs its drink. The opportunity: the child's Pink Drink — caffeine-free, smaller, cheaper, for under-thirteens — and a pink house of cups, straws and the bear on a calendar; the message is \"the first Starbucks\".",
            },
            {
              id: "matcha",
              label: "Matcha Latte",
              sub: "48% product fans · 42% brand fans · 10% superfans",
              parts: [
                { key: "alpha", v: 3 },
                { key: "z", v: 46 },
                { key: "mill", v: 34 },
                { key: "x", v: 12 },
                { key: "boom", v: 5 },
              ],
              note: 'The fastest-growing fandom and the most ingredient-literate: the switch to unsweetened Japanese matcha in January 2025 was demanded by the fans and celebrated by them, and the year-round line has tripled since. Search up 65% on the year, tea revenue up 70% in US company stores, 70% women; nearly half Gen Z, drinking it for calm energy and the colour. The fandom with the most brand fans in it — they follow creators, know grades and origins, and sit down — and the one with a supply story: Japan\'s tencha harvest cannot keep up. The opportunity: matcha for the kitchen — a tin, a bowl, a whisk — and a named-origin Reserve matcha with the supply secured and said out loud; the message is "calm energy", the morning ritual.',
            },
            {
              id: "psl",
              label: "Pumpkin Spice Latte",
              sub: "66% product fans · 27% brand fans · 7% superfans",
              parts: [
                { key: "alpha", v: 1 },
                { key: "z", v: 20 },
                { key: "mill", v: 41 },
                { key: "x", v: 26 },
                { key: "boom", v: 12 },
              ],
              note: 'The Millennial ritual since 2003, now ageing with them into Gen X. Half of Starbucks shoppers have bought one, 64% women, 23% buy the day it lands, 71% buy pumpkin spice nowhere else — they are not pumpkin people, they are PSL people: a Starbucks fan of one drink, and the best ladder candidate on the page. And it is peaking: the search led in 48 states in 2024, 32 in 2025 and 3 this August, while apple, caramel, pecan and matcha rise. The opportunity: PSL Day — a members-only launch morning, an at-home kit that matches the cup, and the family of second drinks for the younger fan who wants the season without the sweetness; the message is "where my routine reignites".',
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "A small move on the ladder is a large move in the till, because a brand fan visits nine times a month to a product fan's five and spends a fifth more each time: this shift alone is worth six percent more visits and nine percent more sales, with no price rise and no new stores.",
          title: "The ladder, now and in 2028",
          provenance: "projected",
          note: "Each rung's share of customers today and at the end of 2028 if five in every hundred product fans and one passer-by climb one rung; the tail is the change. Tap a rung for what moves it.",
          mode: "pair",
          a: { label: "Today", color: "#94A3B8" },
          b: { label: "End of 2028", color: "#4ADE80" },
          rows: [
            {
              id: "pass",
              label: "Passers-by",
              a: 24,
              b: 23,
              tail: "−1",
              note: "One in a hundred passers-by becomes a product fan — the child's Pink Drink and the four-dollar size are the doors.",
            },
            {
              id: "prod",
              label: "Product fans",
              a: 52,
              b: 47,
              tail: "−5",
              note: "Five in every hundred product fans climb: one more drink in the repertoire, one drop joined, one cup bought — converted by their drink, never by the brand.",
            },
            {
              id: "brand",
              label: "Brand fans",
              a: 19,
              b: 23,
              tail: "+4",
              note: "Nine visits a month at a ticket a fifth higher, plus four dollars of merch: the rung that turns the base's arithmetic.",
            },
            {
              id: "coll",
              label: "Superfans",
              a: 5,
              b: 7,
              tail: "+2",
              note: "Two in a hundred brand fans become superfans through an owned house on a calendar — the Bearista cast, member-first, small runs.",
            },
          ],
        },
        {
          kind: "plays",
          insight:
            "Two-thirds of the gain is in the first three moves, and they share one principle: the product fan is converted by their drink, never by the brand — anchor the cultural product to the drink, make the app reward the second drink, and let the Hype Hunters do the posting.",
          title: "Five moves, priced",
          provenance: "projected",
          plays: [
            {
              name: "A drink at the centre of every cultural product — Pink Vibes for the Pink Drink, the Great Pumpkin for the PSL, a matcha house for the matcha",
              itemType: "merch",
              window:
                "Every cup, collab, film and event from the holiday drop on — the hero drink named on the object",
              strategies: [
                { code: "fandom", fit: 0.9 },
                { code: "identity_signal", fit: 0.7 },
                { code: "scarcity", fit: 0.5 },
              ],
              why: "The bridge pairs say it three times: the Pink Drink's cup beat the football cup 40 to 27, the same merch partner got 90 with a drink attached and 53 without, the Unicorn's return out-drew a brand film four to one. A cultural product carries a product fan when it carries their drink. Merch reception moves from 20 to the Pink Bearista's 35, and launch-day traffic lands on the drink, not just the cup.",
              effect: {
                label: "CIQ",
                delta: 1.2,
                series: [0, 0.5, 0.85, 1.05, 1.2],
              },
              precedents: 6,
              reception: "your drink-anchored plays are received at 40 to 90",
            },
            {
              name: "The app from a till to a club — Mod Mondays on my drink, a second-drink offer for one-order members, drop access, and the Reserve tier as the visible top of the ladder",
              itemType: "community",
              window:
                "Spring, with the Rewards tiers' first anniversary — members first, then every app",
              strategies: [
                { code: "identity_signal", fit: 0.8 },
                { code: "scarcity", fit: 0.5 },
                { code: "fandom", fit: 0.4 },
              ],
              why: "The community assessment scores the app as a transactional till; owned spaces, owned channels and exclusivity all sit in the twenties while the crowd's own mechanics score in the nineties. Five points of product fans climbing one rung is worth six percent more visits and nine percent more sales, and it moves the one lane that did not move all year — community, from 71 into the eighties. The Reserve tier is the first owned status mechanic in years; this is the rest of it.",
              effect: {
                label: "CIQ",
                delta: 1.5,
                series: [0, 0.4, 0.9, 1.3, 1.5],
              },
              precedents: 4,
              reception:
                "member-first plays on your shelf are received at 40 to 80",
            },
            {
              name: "Recruit the Hype Hunters — an owned Bearista universe on a fixed drop calendar, member-first, small runs, no resale apology needed; the creator network over agency films",
              itemType: "collab",
              window:
                "The holiday drop, then a published calendar — four drops a year, each with a named character",
              strategies: [
                { code: "fandom", fit: 0.9 },
                { code: "scarcity", fit: 0.7 },
                { code: "opacity", fit: 0.5 },
              ],
              why: "Hype Hunters are 18% of coffee drinkers, three-quarters of them brand fans once inside, and the one segment where Starbucks holds less than its fair share — index 85. Every Bearista queue was a Hype Hunter queue; the brand does not own the segment between drops. Taking the index from 85 to 105 is about three points of the segment and a percent of traffic, most of it afternoon. Fandom is the best-received strategy on the shelf and the least used; merch is where it works hardest.",
              effect: {
                label: "CIQ",
                delta: 0.9,
                series: [0, 0.3, 0.55, 0.75, 0.9],
              },
              precedents: 11,
              reception:
                "your fandom merch is received at 26 on average, the Bearista at 40",
            },
            {
              name: "Give the Clean Connoisseurs their chair back — seats, ceramic, the handwritten cup, Reserve: the brand-fan path for the segment that runs the company",
              itemType: "experience",
              window:
                "With the thousand remodels — the third place restored where the craft drinkers already are",
              strategies: [
                { code: "identity_signal", fit: 0.8 },
                { code: "aesthetic_innovation", fit: 0.4 },
              ],
              why: "Your strongest segment, over-indexed at 130, and the one sixteen of nineteen brand leaders belong to. They become brand fans through the café and the coffee itself, never through a cup drop; places are the warmest-received type on the shelf at +12. Dwell and food attach lift the morning ticket by about two percent.",
              effect: {
                label: "CIQ",
                delta: 0.6,
                series: [0, 0.2, 0.35, 0.5, 0.6],
              },
              precedents: 13,
              reception:
                "your places are received warmest of any type — +12, Reserve Hyderabad at +73",
            },
            {
              name: "Make the second drink the product — every hero gets a family: PSL to Pumpkin Cream Cold Brew and Pumpkin Chai, Pink Drink to Pink Cannon Ball and the pink energy line, matcha to protein and strawberry matcha",
              itemType: "capsule",
              window:
                "Each hero's season — the family launches beside the return, not instead of it",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.8 },
                { code: "fandom", fit: 0.6 },
              ],
              why: "Product fans move sideways before they move up: from the Pumpkin Spice Latte to the Pumpkin Cream Cold Brew, from a matcha latte to a protein matcha. Each of those steps is a menu decision, not a marketing one. Repertoire moves from 1.4 to 2.0 drinks per product fan and ticket by about three and a half percent, the way modifiers did this year — the one want already answered, protein, is the one that grew fastest.",
              effect: {
                label: "CIQ",
                delta: 0.6,
                series: [0, 0.25, 0.4, 0.5, 0.6],
              },
              precedents: 38,
              reception:
                "your menu drops are received at 16 on average, the pink family at 32 to 60",
            },
          ],
          footnote:
            "Projected effects are priced on your measured record and compound with your current course, not on top of it; the three drink-level opportunities — the pink house, matcha for the kitchen, PSL Day — are the detail of the first and last moves, not additions to them.",
        },
        {
          kind: "trendCompare",
          insight:
            "On today's course the brand-fan share barely moves, because collector drops recruit the same people each time and burn some of them out; with the moves it reaches thirty in a hundred by the end of 2028 — and the fan changes character, from a collector of borrowed characters to a member of an owned house.",
          title: "Brand fans and superfans, by 2028",
          provenance: "projected",
          note: "The share of all customers who are brand fans or superfans, every half-year to the end of 2028: with the five moves, and on today's course. The shaded window is the first owned drop calendar.",
          highlight: {
            from: "2026-11-01",
            to: "2027-01-15",
            label: "the first owned drop calendar",
          },
          series: [
            {
              key: "plan",
              label: "With the five moves",
              color: "#4ADE80",
              points: [
                { d: "2026-08-24", v: 24 },
                { d: "2027-03-31", v: 25 },
                { d: "2027-09-30", v: 26.5 },
                { d: "2028-03-31", v: 28 },
                { d: "2028-09-30", v: 30 },
              ],
            },
            {
              key: "base",
              label: "Today's course",
              color: "#94A3B8",
              ghost: true,
              points: [
                { d: "2026-08-24", v: 24 },
                { d: "2027-03-31", v: 24.5 },
                { d: "2027-09-30", v: 25 },
                { d: "2028-03-31", v: 25 },
                { d: "2028-09-30", v: 25.5 },
              ],
            },
          ],
        },
        {
          kind: "impact",
          title: "What the five moves carry",
          provenance: "projected",
          delta: 4.8,
          body: "About five points on your published score, from 81.3 to roughly 86 — the same ceiling the generations analysis found, reached through the fan ladder rather than the product mix. It arrives as the base moves: the drink-anchored objects first, then the club and the second drink through spring. Most of it lands on lanes that do not reset.",
        },
        {
          kind: "modelView",
          body: "Most of Starbucks is loved one drink at a time. The product fan orders the same thing the same way and leaves; the brand fan loves the place — the cups, the drops, the café. The base runs two to one in the product fan's favour, and the money runs the other way: the minority that loves the place carries about half the spend.\n\nThe product fan is converted by their drink, never by the brand — the bridge pairs prove it three times over, the same cup and the same story landing far better with a named drink at the centre. So put a drink at the centre of every cultural product, turn the app from a till into a club that rewards the second drink, and give the craft drinkers who run the company their chair back. Move a few in every hundred one rung up and the base is worth more visits and more sales, with no price rise and no new store.",
        },
      ],
    },
    {
      id: "fandom-opportunity",
      // the platform is the cup — the owned character on a drop calendar
      itemType: "merch",
      type: "fandom",
      question: "Where is the biggest opportunity to capitalize on fandom?",
      aliases: [
        "where is the biggest opportunity to capitalize on fandom",
        "where is the biggest opportunity to capitalise on fandom",
        "biggest fandom opportunity",
        "where is our biggest fandom opportunity",
        "where can we capitalize on fandom",
        "how do we capitalize on fandom",
        "where is our fandom opportunity",
        "what is the best fandom play",
        "how should we capitalize on fandom",
        // the Weekly Perspective card's wording (2026-10-14)
        "where can fandom create the most value",
      ],
      askedOn: "2026-09-21",
      blocks: [
        {
          kind: "readout",
          provenance: "projected",
          verdict:
            "The bear, four times a year: the one fandom you own made $64M in two weeks — put it on a calendar, a drink in its hand.",
        },
        {
          kind: "arena",
          insight:
            "Two lanes carry the industry's fandom — the cup and the borrowed character — and on both the best play of the last year was yours or aimed at your customer; the club lane is where Dutch Bros beats you at your own game.",
          title: "How the industry builds fandom",
          provenance: "observed",
          unit: "fandom fit of the lane's plays · 0–100",
          note: "Bands are the field's range for fandom-tagged plays on each lane — typical to ceiling across the food and drink brands on the board, the last two years; the ring is the field's strongest play there, the dots are yours. Received = the best fandom pull measured on your shelf for that lane. Tap a lane for the read.",
          rows: [
            {
              id: "merch",
              label: "Cups & collectibles",
              sub: "merch",
              typical: 38,
              ceiling: 74,
              best: {
                brandName: "Dutch Bros",
                title: "State sticker & frog charm drops",
                pts: 70,
              },
              received: 62,
              own: [
                { title: "Glass Bearista cold cup", pts: 88 },
                { title: "Pink Bearista", pts: 80 },
                { title: "Mini Bearista blind boxes", pts: 52 },
              ],
              status: "recommended",
              note: "The industry's fandom lane, and the one you lead: the field's best cup play in two years is a sticker, yours is a bear that moved $64M of spend in two weeks and sells out by seven in the morning on its second run. Every other brand on the board rents a character for this lane; you own one.",
            },
            {
              id: "collab",
              label: "Borrowed characters",
              sub: "collab · film & IP",
              typical: 44,
              ceiling: 86,
              best: {
                brandName: "McDonald's",
                title: "KPop Demon Hunters dueling meals",
                pts: 90,
              },
              received: 76,
              own: [
                { title: "Snoopy Bearista · Peanuts", pts: 76 },
                { title: "POP MART MOLLY", pts: 68 },
                { title: "Miffy", pts: 54 },
              ],
              note: "The loudest lane and the least kept: McDonald's Minecraft meal turned a quarter, then turned the next April negative when it lapped; KPop sold out faster than planned and the quarter still came in under one percent. Your best collab of the year is the one that put the partner in a bear suit.",
            },
            {
              id: "archive",
              label: "Returns & anniversaries",
              sub: "archive",
              typical: 36,
              ceiling: 70,
              best: {
                brandName: "Taco Bell",
                title: "Baja Blast, year-round after twenty years",
                pts: 66,
              },
              received: 90,
              own: [
                { title: "Unicorn Frappuccino returns", pts: 90 },
                { title: "Pumpkin Spice Latte, year twenty-three", pts: 72 },
              ],
              note: "The lane where product fans queue: the field's returns are received well and quietly. The Unicorn's return is the best-received fandom play on your shelf — and it was played as a menu item, never as a fandom.",
            },
            {
              id: "capsule",
              label: "Menu drops",
              sub: "capsule",
              typical: 30,
              ceiling: 62,
              best: {
                brandName: "McDonald's",
                title: "Grimace birthday shake",
                pts: 78,
              },
              received: 90,
              own: [
                { title: "Orange Cream with the Dandy drop", pts: 90 },
                { title: "Energy Refreshers", pts: 44 },
              ],
              note: "A drink becomes fandom when it has a character or a crowd attached: the Grimace shake carried a ten-percent quarter in 2023; your Orange Cream carried the Dandy collab from 53 to 90. Alone, a menu drop is a menu drop.",
            },
            {
              id: "event",
              label: "Fan days",
              sub: "event",
              typical: 24,
              ceiling: 58,
              best: {
                brandName: "Taco Bell",
                title: "Live Más Live",
                pts: 60,
              },
              received: 40,
              own: [
                { title: "Coachella Unicorn drop", pts: 40 },
                { title: "Fanatics Fest", pts: 30 },
              ],
              note: "Taco Bell holds a fan convention for its menu; your events are appearances at other people's. The lane is small everywhere — it pays as a stage for a drop, not as a play of its own.",
            },
            {
              id: "community",
              label: "The club",
              sub: "community",
              typical: 28,
              ceiling: 64,
              best: {
                brandName: "Dutch Bros",
                title: "Dutch Rewards — 73% of transactions",
                pts: 64,
              },
              received: 41,
              own: [{ title: "Starbucks Rewards, 35.8M actives", pts: 41 }],
              note: "Dutch Bros runs three-quarters of its transactions through a club that drops stickers; your app runs the largest loyalty base in coffee as a till. The lane that never resets is the one you are received worst on.",
            },
            {
              id: "content",
              label: "Films & feeds",
              sub: "content",
              typical: 22,
              ceiling: 50,
              best: {
                brandName: "McDonald's",
                title: "Grimace relaunch, Germany — 57M views",
                pts: 50,
              },
              received: 34,
              own: [{ title: "Martha Stewart's PSL film", pts: 34 }],
              note: "Content carries fandom only when the fandom makes it — Grimace's German views were the crowd's edits, not an agency film. Your one fandom-tagged film is the least received play in the set.",
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "Every borrowed crowd sits bottom-right — loud, gone by the eighth week; the only dots that climbed are the ones with your character on them, and the haloed one is where they go on a calendar.",
          title: "What each play paid, and kept",
          provenance: "projected",
          x: { label: "Launch-week lift", low: "quiet", high: "loud" },
          y: {
            label: "What stayed eight weeks on",
            low: "rented",
            high: "kept",
          },
          quadrants: {
            tr: "Paid and stayed",
            tl: "Stayed quietly",
            br: "Paid and left",
            bl: "Neither",
          },
          note: "Launch-week lift reads spend and transactions against the week before (card-panel data where it exists, comps and press where it does not); what stayed reads visits and attention eight weeks on. Dot size is the money moved. Colour is who owned the crowd. Tap a dot for the read.",
          points: [
            {
              id: "bearista",
              label: "Glass Bearista cold cup · Nov 2025",
              short: "Bearista",
              era: "Owned character",
              color: "#4ADE80",
              x: 92,
              y: 70,
              w: 60,
              status: "current",
              note: "$64M of incremental spend in two weeks on a $29.95 cup: launch day +58% spend and +37% transactions, ticket +16%, five days over four million transactions against one the year before. Week one $53M, week two $11M — and the week after that still ran sixteen percent over the week before launch.",
            },
            {
              id: "pink-bearista",
              label: "Pink Bearista · Jul 2026",
              era: "Owned character",
              color: "#4ADE80",
              x: 80,
              y: 62,
              w: 40,
              status: "recent",
              note: "Mostly gone by seven in the morning across North America on the day it went on sale; a summer drop with the Pink Drink in its hand — the first proof the bear works out of season.",
            },
            {
              id: "snoopy-bearista",
              label: "Snoopy Bearista · Sep 2026",
              era: "Owned character",
              color: "#4ADE80",
              x: 76,
              y: 58,
              w: 36,
              status: "recent",
              note: "A borrowed character inside your own: two per customer, resale to $150 on a ten-piece Peanuts line. The partner's crowd came for Snoopy and left holding a Bearista.",
            },
            {
              id: "stanley",
              label: "Starbucks × Stanley pink Quencher · Jan 2024",
              era: "Borrowed object",
              color: "#94A3B8",
              x: 88,
              y: 30,
              w: 48,
              note: "The Target queue, $200 resale, a brand that went from $73M to $750M on it — and the cup left with Stanley. Teens now list the Quencher among the trends on their way out.",
            },
            {
              id: "minecraft",
              label: "McDonald's × A Minecraft Movie meal · Mar 2025",
              era: "Borrowed IP",
              color: "#B388FF",
              x: 84,
              y: 26,
              w: 70,
              note: "McDonald's largest global campaign: US comps swung from −3.6% to +2.5% in a quarter. Lapping it turned April 2026 negative — the crowd went home with the film.",
            },
            {
              id: "kpop",
              label: "McDonald's × KPop Demon Hunters meals · Mar 2026",
              era: "Borrowed IP",
              color: "#B388FF",
              x: 86,
              y: 34,
              w: 55,
              status: "current",
              note: "Dueling adult meals with photocards: sold out faster than planned, card sets resell at $100. The quarter still came in at +0.8% US comps — a sold-out launch week is not a quarter.",
            },
            {
              id: "fifa",
              label: "McDonald's FIFA campaign · Jun 2026",
              era: "Borrowed IP",
              color: "#B388FF",
              x: 40,
              y: 18,
              w: 30,
              note: "'Underperformed versus expectations' on the Q2 call — a tournament's crowd is the tournament's, and it watched the games somewhere else.",
            },
            {
              id: "grimace-de",
              label: "Grimace relaunch, Germany · 2026",
              era: "Owned character",
              color: "#4ADE80",
              x: 58,
              y: 44,
              w: 28,
              note: "57M views across social — the one owned character in the field, played once a year in one market at a time. It kept more than any borrowed one at a fraction of the volume.",
            },
            {
              id: "db-sticker",
              label: "Dutch Bros state sticker · 2026",
              era: "Drop",
              color: "#FFB74D",
              x: 46,
              y: 66,
              w: 26,
              note: "'The strongest merchant sticker drop of the year' with a meaningful lift to the quarter — small, dated, given with a drink, sold through a club that carries 73% of transactions.",
            },
            {
              id: "db-charm",
              label: "Dutch Bros frog charm · 2026",
              era: "Drop",
              color: "#FFB74D",
              x: 42,
              y: 60,
              w: 22,
              note: "The second of the two drops the CFO named; a charm for the cup, not a cup — the fandom object costs almost nothing and stays on the customer.",
            },
            {
              id: "live-mas",
              label: "Taco Bell Live Más Live · 2026",
              era: "Fan event",
              color: "#FF5A76",
              x: 36,
              y: 54,
              w: 20,
              note: "A fan convention for a menu — quiet on the week, long on what stays: the menu it previews carries the year.",
            },
            {
              id: "four-drops",
              label: "The bear, four times a year",
              short: "Four drops",
              era: "Owned character",
              color: "#4ADE80",
              x: 78,
              y: 84,
              w: 50,
              status: "recommended",
              note: "Four dated owned-character drops, each with a drink in its hand, gated through Rewards: the launch week of the Bearista, four times, and a calendar the crowd waits for instead of a queue it fights.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "The week after is where fandom is measured — the Bearista kept a sixth of its launch week, a borrowed film a third of much less; only stickers, small and dated, kept half.",
          title: "Launch week, and the week after",
          provenance: "projected",
          mode: "pair",
          a: { label: "Launch-week lift", color: "#4ADE80" },
          b: { label: "Week-after lift", color: "#94A3B8" },
          note: "Spend against the week before the play, in percent: the launch week, then the week after it. The Bearista pair is card-panel data; the others are modelled from comps, sell-through and press. The tail is the public number each play is known by. Tap a row for the read.",
          rows: [
            {
              id: "bearista",
              label: "Glass Bearista",
              sub: "Starbucks · Nov 2025",
              a: 58,
              b: 16,
              tail: "$64M",
              note: "Launch day +58% spend against the day before; the following week still ran 16% over the pre-launch week. $53M of the $64M came in week one — the decay of a drop, even an owned one.",
            },
            {
              id: "stanley",
              label: "Pink Quencher",
              sub: "Starbucks × Stanley · Jan 2024",
              a: 52,
              b: 12,
              tail: "$200 resale",
              note: "The Target frenzy, resale to $200 — a launch week almost the Bearista's size. It kept less because the object was Stanley's: the crowd's next cup was a Stanley too.",
            },
            {
              id: "pink-bearista",
              label: "Pink Bearista",
              sub: "Starbucks · Jul 2026",
              a: 41,
              b: 9,
              tail: "by 7 a.m.",
              note: "Smaller run, summer daypart, mostly sold out before the morning rush across North America. The week after is the Pink Drink's, which is the point.",
            },
            {
              id: "snoopy",
              label: "Snoopy Bearista",
              sub: "Starbucks × Peanuts · Sep 2026",
              a: 36,
              b: 10,
              tail: "capped at 2",
              note: "A ten-piece line capped at two cups a customer; resale to $150 within the week. A borrowed crowd that kept as well as an owned drop because it left holding your character.",
            },
            {
              id: "kpop",
              label: "KPop Demon Hunters meals",
              sub: "McDonald's · Mar 2026",
              a: 30,
              b: 8,
              tail: "sold out",
              note: "Sold out faster than planned in parts of the US; photocard sets at $100 on resale. US comps for the quarter: +0.8%. The cards were the fandom; the meal was the ticket.",
            },
            {
              id: "minecraft",
              label: "Minecraft Movie meal",
              sub: "McDonald's · Mar 2025",
              a: 24,
              b: 9,
              tail: "comps +2.5%",
              note: "The largest global campaign in McDonald's history swung a quarter from −3.6% to +2.5%. Twelve months later the same weeks ran negative against it — the rent came due.",
            },
            {
              id: "grimace",
              label: "Grimace, Germany",
              sub: "McDonald's · 2026",
              a: 20,
              b: 6,
              tail: "57M views",
              note: "An owned character relaunched in one market: a modest week, a long tail of the crowd's own edits. The character is the asset; the market is a choice.",
            },
            {
              id: "db-sticker",
              label: "State sticker",
              sub: "Dutch Bros · 2026",
              a: 14,
              b: 7,
              tail: "best drop",
              note: "Half of the launch week kept — the best ratio on the board. A sticker costs cents, ships with a drink and goes to members; the drop is small so the week after is the same size.",
            },
            {
              id: "fifa",
              label: "FIFA campaign",
              sub: "McDonald's · Jun 2026",
              a: 9,
              b: 2,
              tail: "under plan",
              tailLow: true,
              note: "Named on the Q2 call as underperforming expectations. A tournament's fans are the tournament's; the sponsor buys the logo placement, not the crowd.",
            },
          ],
        },
        {
          kind: "stacks",
          insight:
            "Eleven of your eighteen fandom plays are cups, and the two that were drinks were received better than any cup — the platform is the cup, the anchor is the drink.",
          title: "Where your own fandom comes from",
          provenance: "measured",
          note: "Your eighteen fandom-tagged plays of the last year sorted by product type — how many there were, the attention they bought, the reception they earned, and the money they moved. Tap a row for the read.",
          legend: [
            { key: "merch", label: "Cups & collectibles", color: "#FFB74D" },
            { key: "event", label: "Fan days", color: "#FF5A76" },
            { key: "capsule", label: "Menu drops", color: "#FDE047" },
            { key: "collab", label: "Borrowed characters", color: "#B388FF" },
            { key: "content", label: "Films", color: "#4FC3F7" },
          ],
          rows: [
            {
              id: "plays",
              label: "Plays",
              sub: "18 fandom-tagged plays",
              parts: [
                { key: "merch", v: 11 },
                { key: "event", v: 3 },
                { key: "capsule", v: 2 },
                { key: "collab", v: 1 },
                { key: "content", v: 1 },
              ],
              note: "Eleven cups — the Bearista family is nine of them — three fan days, two drinks, one collab, one film. Fandom is the least used strategy on your shelf and the one played almost entirely through merch.",
            },
            {
              id: "attention",
              label: "Attention bought",
              sub: "share of the set",
              parts: [
                { key: "merch", v: 62 },
                { key: "event", v: 8 },
                { key: "capsule", v: 16 },
                { key: "collab", v: 9 },
                { key: "content", v: 5 },
              ],
              note: "The cups bought most of the attention because they bought a queue: every Bearista launch is a news day. The two drinks bought a sixth of it on two plays.",
            },
            {
              id: "reception",
              label: "Reception earned",
              sub: "share of net reception",
              parts: [
                { key: "merch", v: 58 },
                { key: "event", v: 6 },
                { key: "capsule", v: 22 },
                { key: "collab", v: 9 },
                { key: "content", v: 5 },
              ],
              note: "Two drinks earned a fifth of the set's reception: the Unicorn's return and the Orange Cream with the Dandy drop are the two best-received fandom plays you have. A drink, when it is played as fandom, out-receives a cup.",
            },
            {
              id: "money",
              label: "Money moved",
              sub: "share of incremental spend",
              parts: [
                { key: "merch", v: 81 },
                { key: "event", v: 3 },
                { key: "capsule", v: 9 },
                { key: "collab", v: 6 },
                { key: "content", v: 1 },
              ],
              note: "Four cups in five of every incremental dollar — the Bearista's $64M alone is most of it. The drinks moved a tenth on two plays; the film moved almost nothing. The money is in the cup, the reception is in the drink: put them in the same hand.",
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "The owned cup is the only format hot on money and on staying; the club is hot on everything but the launch week — which is why the calendar is sold through Rewards.",
          title: "Which format pays which metric",
          provenance: "projected",
          note: "Seven ways the industry plays fandom, against the six numbers a CMO is asked for — how hard each format moves each one, on what the last two years of plays did. Tap a cell for the read.",
          cols: [
            "Launch-week sales",
            "Ticket",
            "Repeat visits",
            "Merch revenue",
            "Members won",
            "Stays 8 weeks",
          ],
          rows: [
            {
              label: "Owned character drop",
              cells: [
                {
                  v: 92,
                  note: "The Bearista's launch day: +58% spend, +37% transactions against the day before, five four-million-transaction days in a fortnight.",
                },
                {
                  v: 80,
                  note: "A $30 cup on a $7 order: ticket +16% on launch day. The one format that lifts the ticket instead of discounting it.",
                },
                {
                  v: 58,
                  note: "The week after ran 16% over the pre-launch week — the queue came back for the drink the cup was named after.",
                },
                {
                  v: 95,
                  note: "$64M in two weeks on one cup; merch is a rounding error in the revenue line until a character is on it.",
                },
                {
                  v: 72,
                  note: "The online drop needs a Rewards account and nothing else — every cup sold is a member won or re-activated.",
                },
                {
                  v: 70,
                  note: "The character is yours: the resale market, the replicas and the next drop's queue all point back at the brand.",
                },
              ],
            },
            {
              label: "Borrowed film IP meal",
              cells: [
                {
                  v: 88,
                  note: "Minecraft swung a McDonald's quarter; KPop sold out faster than planned. The loudest launch week the industry has.",
                },
                { v: 40 },
                { v: 30 },
                { v: 35 },
                {
                  v: 55,
                  note: "The meal is app-ordered and the collectible is app-gated — the film's crowd signs up, then lapses with the film.",
                },
                {
                  v: 22,
                  note: "Lapping Minecraft turned April negative; Pop Mart's Americas revenue fell 16.5% in the half the crowd moved on. The crowd is the IP's.",
                },
              ],
            },
            {
              label: "Collectible cards",
              cells: [
                {
                  v: 70,
                  note: "KPop's photocards sold the meal to adults; sets at $100 on resale within the week.",
                },
                { v: 45 },
                {
                  v: 62,
                  note: "A set is completed over visits — the card is a repeat mechanic the cup is not.",
                },
                { v: 40 },
                {
                  v: 68,
                  note: "Cards live in the app: the fandom's collection is the member's account.",
                },
                { v: 48 },
              ],
            },
            {
              label: "Sticker & charm drop",
              cells: [
                { v: 46 },
                { v: 30 },
                {
                  v: 74,
                  note: "Dutch Bros' drops come with a drink on a date — the customer comes back for the next one; 73% of transactions run through the club that announces it.",
                },
                { v: 38 },
                {
                  v: 64,
                  note: "The drop is the reason to join: Dutch Rewards' strongest contribution to comps on record came the quarter of the state sticker.",
                },
                {
                  v: 66,
                  note: "A charm stays on the cup for a year; a sticker on the laptop longer. Cheap, owned, and carried.",
                },
              ],
            },
            {
              label: "Drink return / anniversary",
              cells: [
                {
                  v: 66,
                  note: "The Unicorn's return and Baja Blast's year-round comeback — product fans queue for a drink they already love.",
                },
                { v: 52 },
                { v: 60 },
                {
                  v: 20,
                  note: "A return with nothing to take home: the PSL's twenty-third year sold coffee and no object.",
                },
                { v: 34 },
                { v: 52 },
              ],
            },
            {
              label: "Club tier / paid pass",
              cells: [
                {
                  v: 24,
                  note: "No launch week — a club is joined, not queued for. The one format the calendar has to carry, and the one that carries the calendar.",
                },
                { v: 48 },
                {
                  v: 86,
                  note: "Taco Bell's Taco Lover's Pass and Dutch Rewards run on visits; your Rewards actives spend two and a half to three times a non-member.",
                },
                { v: 10 },
                {
                  v: 90,
                  note: "The format IS members: 35.8M actives are about 60% of US company revenue — the only fandom number on the board that already sits in the P&L.",
                },
                {
                  v: 84,
                  note: "The lane that never resets: a member won in November is a member in June, a cup sold in November is a cup.",
                },
              ],
            },
            {
              label: "Fan event",
              cells: [
                { v: 36 },
                { v: 28 },
                { v: 40 },
                { v: 22 },
                { v: 30 },
                {
                  v: 50,
                  note: "Live Más Live previews the menu the fans then wait for; the event is a stage for the drops, not a play of its own.",
                },
              ],
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "One column is yours alone — the owned character — and one is empty for every coffee brand: the collectible card, which McDonald's just proved sells a meal to an adult.",
          title: "Who owns which fandom lane",
          provenance: "observed",
          note: "The six brands your customer chooses between, on the seven ways the industry plays fandom: how much of each lane each brand occupied over the last two years, from its own plays. A cold cell in a hot column is a gap. Tap a cell for the read.",
          cols: [
            "Owned character",
            "Borrowed film IP",
            "Collectible cards",
            "Sticker & charm",
            "Drink fandom",
            "Club / pass",
            "Fan event",
          ],
          rows: [
            {
              label: "Starbucks",
              cells: [
                {
                  v: 88,
                  note: "The Bearista, three drops in ten months, each sold out — the only owned character in coffee and the hottest cell on the board.",
                },
                {
                  v: 60,
                  note: "Peanuts, POP MART, Miffy, Dandy — a full collab calendar, and the best of it is the one that wore the bear.",
                },
                {
                  v: 10,
                  note: "Nothing. No card, no set, no chase in the app — the empty cell next to the hottest column.",
                },
                { v: 26 },
                {
                  v: 84,
                  note: "The Pink Drink, the matcha and the PSL are three fandoms with names; none has ever been played as one.",
                },
                {
                  v: 48,
                  note: "The largest loyalty base in coffee, run as a till: drops need an account, but the account has no drop calendar, no tier that shows, no chase.",
                },
                { v: 30 },
              ],
            },
            {
              label: "McDonald's",
              cells: [
                {
                  v: 54,
                  note: "Grimace — relaunched market by market (57M views in Germany), once a year. The field's only other owned character, and it is not on a calendar either.",
                },
                {
                  v: 96,
                  note: "Minecraft, KPop Demon Hunters, FIFA in fifteen months: the borrowed lane is McDonald's lane — and the Q2 call blamed the rent on it.",
                },
                {
                  v: 74,
                  note: "KPop photocards: the first collectible card in a US fast-food meal in years, resold in sets within the week.",
                },
                { v: 12 },
                { v: 40 },
                {
                  v: 62,
                  note: "220M 90-day actives across 70 markets, $40B of loyalty sales — the club is a discount engine, and the Q2 call blamed the removal of offers for two-thirds of the traffic miss.",
                },
                { v: 28 },
              ],
            },
            {
              label: "Dunkin'",
              cells: [
                { v: 20 },
                {
                  v: 34,
                  note: "The DunKings and the Sabrina Carpenter drink — borrowed faces over borrowed characters; the merch sold out and the lane went quiet.",
                },
                { v: 6 },
                { v: 22 },
                {
                  v: 58,
                  note: "The drink fandom you share the morning with — the winter merch line is the clash the forecast entry priced.",
                },
                { v: 40 },
                { v: 18 },
              ],
            },
            {
              label: "Dutch Bros",
              cells: [
                { v: 16 },
                { v: 8 },
                { v: 12 },
                {
                  v: 92,
                  note: "The state sticker and the frog charm — the strongest drops of its year, given with a drink to members. The lane is Dutch Bros' the way the character is yours.",
                },
                { v: 46 },
                {
                  v: 86,
                  note: "73% of transactions through Dutch Rewards, the club's strongest contribution to comps on record, 8.3% company comps in the quarter.",
                },
                { v: 44 },
              ],
            },
            {
              label: "Taco Bell",
              cells: [
                { v: 22 },
                { v: 40 },
                { v: 18 },
                { v: 30 },
                {
                  v: 70,
                  note: "Baja Blast, year-round after two decades, and the Mexican Pizza brought back by petition — a menu the fans campaign for.",
                },
                {
                  v: 78,
                  note: "The Taco Lover's Pass — a paid pass on the menu itself; the club is the product.",
                },
                {
                  v: 72,
                  note: "Live Más Live — a fan convention that previews the year's menu. The only real fan event in the field.",
                },
              ],
            },
            {
              label: "Chick-fil-A",
              cells: [
                { v: 12 },
                { v: 10 },
                { v: 4 },
                { v: 14 },
                { v: 30 },
                { v: 50 },
                { v: 20 },
              ],
            },
          ],
        },
        {
          kind: "shift",
          insight:
            "The crowd is leaving the borrowed character and the borrowed cup at the same time, and paying more for the one you own — the window to put it on a calendar is this holiday.",
          title: "Early signals",
          provenance: "observed",
          note: "Seven public numbers from the last two quarters, read as signals rather than results. Sparklines are the last six readings where a series exists.",
          rows: [
            {
              label: "Pop Mart in the Americas",
              context:
                "first-half revenue; Labubu's family down to 26% of the group",
              value: "−16.5%",
              delta: -16.5,
              spark: [100, 118, 124, 112, 96, 84],
            },
            {
              label: "Borrowed film IP at McDonald's",
              context:
                "Minecraft lapped negative in April · FIFA under plan · KPop sold out, then +0.8% comps",
              value: "under plan",
            },
            {
              label: "Bearista resale premium",
              context: "the Snoopy at $150 on $29.95; the original to $1,000",
              value: "5×",
              spark: [1, 4, 8, 6, 5, 5],
            },
            {
              label: "Photocards in a meal",
              context: "KPop Demon Hunters sets on resale, within the week",
              value: "$100 a set",
            },
            {
              label: "Sticker & charm drops",
              context:
                "Dutch Bros' state sticker and frog charm — 'meaningful sales lift'",
              value: "strongest of the year",
              spark: [40, 44, 48, 55, 62, 70],
            },
            {
              label: "The Quencher among teens",
              context: "listed among the trends on their way out; Owala rising",
              value: "on its way out",
              spark: [92, 88, 80, 70, 58, 46],
            },
            {
              label: "Loyalty share of visits",
              context:
                "Dutch Bros' share of transactions vs your Rewards' share of US company revenue",
              value: "73% vs ~60%",
            },
          ],
        },
        {
          kind: "playsTimeline",
          insight:
            "The field drops when a film opens; you drop when a season turns — four dates you own, and the film can rent a bear suit in between.",
          title: "The drop calendar",
          provenance: "projected",
          today: "2026-09-21",
          note: "Left of NOW: the fandom plays that moved the industry's numbers, by fandom fit — yours and the field's. Right of NOW: the five dated drops of the platform on their own projected-CIQ scale — height is impact. Tap a dot for the read.",
          points: [
            {
              d: "2024-01-03",
              v: 74,
              label: "Starbucks × Stanley pink Quencher",
              itemType: "merch",
              note: "The Target queue; $200 resale; the cup left with Stanley.",
            },
            {
              d: "2025-03-26",
              v: 82,
              label: "McDonald's × A Minecraft Movie meal",
              itemType: "collab",
              note: "The largest campaign in McDonald's history — a quarter turned, then lapped negative.",
            },
            {
              d: "2025-11-06",
              v: 88,
              label: "Glass Bearista cold cup",
              itemType: "merch",
              note: "$64M in two weeks; a record holiday launch week; the character you own.",
            },
            {
              d: "2026-03-31",
              v: 84,
              label: "McDonald's × KPop Demon Hunters meals",
              itemType: "collab",
              note: "Dueling meals with photocards, sold out faster than planned; the quarter came in at +0.8%.",
            },
            {
              d: "2026-05-12",
              v: 62,
              label: "Dutch Bros state sticker",
              itemType: "merch",
              note: "The strongest sticker drop of the year, with a meaningful lift to the quarter.",
            },
            {
              d: "2026-06-10",
              v: 38,
              label: "McDonald's FIFA campaign",
              itemType: "collab",
              note: "Underperformed expectations, on the company's own account.",
            },
            {
              d: "2026-07-13",
              v: 80,
              label: "Pink Bearista",
              itemType: "merch",
              note: "Gone by seven in the morning; the Pink Drink in the bear's hand.",
            },
            {
              d: "2026-09-15",
              v: 76,
              label: "Snoopy Bearista · Peanuts",
              itemType: "collab",
              note: "Two per customer; resale to $150; a partner's crowd holding your character.",
            },
            {
              d: "2026-11-05",
              v: 0,
              label: "Holiday Bearista cast — the first dated drop",
              itemType: "merch",
              projected: true,
              detail: "projected +0.8 CIQ",
              effect: 0.8,
              note: "The holiday cup, with a named cast and the winter drink in its hand; Rewards-gated, quantities said out loud.",
            },
            {
              d: "2027-02-09",
              v: 0,
              label: "Pick a side — Pink v Matcha, a card per side",
              itemType: "event",
              projected: true,
              detail: "projected +0.7 CIQ",
              effect: 0.7,
              note: "The dueling-meal format on your two youngest fandoms; the coffee lane's first collectible card, in the app.",
            },
            {
              d: "2027-04-13",
              v: 0,
              label: "Spring matcha Bearista",
              itemType: "merch",
              projected: true,
              detail: "projected +0.6 CIQ",
              effect: 0.6,
              note: "The fastest-growing fandom's first object — a bear for the kitchen, with the matcha it whisks.",
            },
            {
              d: "2027-06-15",
              v: 0,
              label: "A borrowed character, in a bear suit",
              itemType: "collab",
              projected: true,
              detail: "projected +0.4 CIQ",
              effect: 0.4,
              note: "The summer partner ships as a Bearista costume — the Snoopy rule, made the rule.",
            },
            {
              d: "2027-08-24",
              v: 0,
              label: "PSL Day Bearista",
              itemType: "merch",
              projected: true,
              detail: "projected +0.7 CIQ",
              effect: 0.7,
              note: "The return played as fandom for the first time in twenty-four years: a pumpkin bear on the day the drink comes back.",
            },
          ],
        },
        {
          kind: "plays",
          insight:
            "One platform and two rules: the bear on a calendar with a drink in its hand, the fan-versus-fan format on your own drink fandoms, and no borrowed crowd without a bear suit — priced together at a little over three points.",
          title: "Three moves, priced",
          provenance: "projected",
          plays: [
            {
              name: "Four dated drops, a drink in each hand — the Bearista on a published calendar: holiday, spring matcha, PSL Day and one more, each with its drink named on the cup, quantities said out loud, Rewards-gated, Reserve first",
              itemType: "merch",
              window:
                "The holiday drop in November, then the calendar published with it — four dates a year through next August",
              strategies: [
                { code: "fandom", fit: 0.9 },
                { code: "scarcity", fit: 0.7 },
                { code: "identity_signal", fit: 0.6 },
              ],
              why: "The only fandom play on the board that paid the launch week and stayed: $64M in two weeks in November, sold out by seven in the morning in July, two per customer in September — three drops, three queues, no calendar. A calendar turns the queue into a wait, the apology into a date, and the cup into a member: every online drop needs a Rewards account. Priced on the Bearista's own card-panel week, decayed for the smaller runs — about a hundred million dollars a year over the current course, and the fandom stays because the character is yours.",
              effect: {
                label: "CIQ",
                delta: 2.1,
                series: [0, 0.8, 1.2, 1.7, 2.1],
              },
              precedents: 3,
              reception: "your three Bearista drops are received at 76 to 88",
              similar: [
                {
                  brand: "dutch-bros",
                  brandName: "Dutch Bros",
                  title: "State sticker & frog charm drops",
                  pts: 70,
                },
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "Grimace birthday shake",
                  pts: 78,
                },
              ],
            },
            {
              name: "Pick a side — the dueling format on your own drink fandoms: Pink v Matcha in February, PSL v Pumpkin Cream in the fall, a collectible card per side in the app, the set completed over visits",
              itemType: "event",
              window:
                "February, on the Pink Drink's and the matcha's shared season — then every season with two heroes",
              strategies: [
                { code: "fandom", fit: 0.8 },
                { code: "opacity", fit: 0.5 },
                { code: "aesthetic_innovation", fit: 0.4 },
              ],
              why: "McDonald's just proved a collectible card sells a meal to an adult — KPop's photocards resold in sets within the week — and no coffee brand has a card. You have three drink fandoms with names and a customer who already argues Pink against matcha on the feed; a side to pick gives the argument a prize, and a set completed over visits is the repeat mechanic a cup does not have. The cards live in the app, so the collection is the member's account.",
              effect: {
                label: "CIQ",
                delta: 0.7,
                series: [0, 0.2, 0.45, 0.6, 0.7],
              },
              precedents: 2,
              reception:
                "your drink returns are received at 72 to 90 when a crowd is attached",
              similar: [
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "KPop Demon Hunters dueling meals",
                  pts: 90,
                },
              ],
            },
            {
              name: "Borrow only in a bear suit — every partner character ships as a Bearista costume, the Snoopy rule made the rule; no film, toy or tournament crowd without your character in the picture",
              itemType: "collab",
              window:
                "From the summer partner on — the rule for every collab already on the calendar",
              strategies: [
                { code: "fandom", fit: 0.7 },
                { code: "scarcity", fit: 0.5 },
              ],
              why: "Pop Mart's Americas revenue fell by a sixth the half the crowd moved on; McDonald's lapped Minecraft into negative comps and called FIFA under plan. A borrowed crowd is the partner's — unless it leaves holding yours. The Snoopy Bearista kept as much of its week as the owned drops did, at two per customer; MOLLY, played as MOLLY, kept a collab's share. The rule costs a mould.",
              effect: {
                label: "CIQ",
                delta: 0.4,
                series: [0, 0.1, 0.25, 0.35, 0.4],
              },
              precedents: 4,
              reception:
                "your collabs are received at 54 to 68 — 76 in a bear suit",
            },
          ],
          footnote:
            "Projected effects are priced on your measured record and the public card-panel week, and compound with your current course, not on top of it; the first move is the fans question's first and third moves as one platform — the drink at the centre of the object, the owned character on a calendar — priced once here so the two answers agree.",
        },
        {
          kind: "trendCompare",
          insight:
            "The calendar is not a bigger launch week — it is four of them, and the difference by next September is about what one holiday quarter's merch line makes today.",
          title: "Fandom revenue, cumulative",
          provenance: "projected",
          note: "Incremental spend from fandom plays, in millions of dollars, added up from October: with the calendar, the card and the bear-suit rule; and on today's course — the three drops of this year repeated when a season asks for one. The shaded month is the first dated drop.",
          highlight: {
            from: "2026-11-01",
            to: "2026-11-30",
            label: "the holiday Bearista cast",
          },
          series: [
            {
              key: "plan",
              label: "Four drops, a card, the rule",
              color: "#4ADE80",
              points: [
                { d: "2026-10-01", v: 0 },
                { d: "2026-11-01", v: 70 },
                { d: "2026-12-01", v: 88 },
                { d: "2027-01-01", v: 92 },
                { d: "2027-02-01", v: 128 },
                { d: "2027-03-01", v: 138 },
                { d: "2027-04-01", v: 172 },
                { d: "2027-05-01", v: 182 },
                { d: "2027-06-01", v: 205 },
                { d: "2027-07-01", v: 212 },
                { d: "2027-08-01", v: 258 },
                { d: "2027-09-01", v: 265 },
              ],
            },
            {
              key: "base",
              label: "Today's course",
              color: "#94A3B8",
              ghost: true,
              points: [
                { d: "2026-10-01", v: 0 },
                { d: "2026-11-01", v: 64 },
                { d: "2026-12-01", v: 76 },
                { d: "2027-01-01", v: 78 },
                { d: "2027-02-01", v: 78 },
                { d: "2027-03-01", v: 78 },
                { d: "2027-04-01", v: 78 },
                { d: "2027-05-01", v: 78 },
                { d: "2027-06-01", v: 78 },
                { d: "2027-07-01", v: 118 },
                { d: "2027-08-01", v: 122 },
                { d: "2027-09-01", v: 150 },
              ],
            },
          ],
        },
        {
          kind: "impact",
          title: "What the three moves carry",
          provenance: "projected",
          delta: 3.2,
          body: "About three points on your published score, from 81.3 to roughly 84.5 and past McDonald's 83.0, on the one fandom the field cannot copy. In money it is roughly a hundred million dollars a year of incremental spend — four launch weeks instead of one — plus the Rewards accounts every gated drop opens. Unlike a borrowed crowd's week, the character that earned it is still yours the month after.",
        },
        {
          kind: "modelView",
          body: "Fandom in this industry is rented by the week. A film, a toy or a tournament lends its crowd to a cup, the launch week is enormous, and the crowd goes home with its owner — a rival's biggest campaign turned a quarter, then turned the same weeks negative a year later. Every borrowed crowd on the board sits in that corner: loud, and gone by the eighth week.\n\nThe one fandom you own behaves differently. A glass bear moved more money in a fortnight than any merch line in your history and lifted the ticket instead of discounting it, and its second run was gone before the morning rush. It was never planned as a platform, and that is the opportunity: put the bear on a published calendar, four dates a year, each with a drink in its hand, gated through the club so every cup sold is a member won. Borrow only in a bear suit — a partner's crowd may come for its character, but it should leave holding yours.",
        },
      ],
    },
    {
      id: "community-activation",
      // the platform is the club — a standing, a room, a day
      itemType: "community",
      type: "community",
      question: "How to activate Starbucks community?",
      aliases: [
        "how to activate starbucks community",
        "how to activate the starbucks community",
        "how do we activate our community",
        "how do we activate the community",
        "how do we activate the starbucks community",
        "how to activate our community",
        "how can we activate our community",
        "how to activate community",
        "activate our community",
        "how do we activate community",
        // the Weekly Perspective card's wording (2026-10-14)
        "how can starbucks bring its community together",
      ],
      askedOn: "2026-09-21",
      blocks: [
        {
          kind: "readout",
          provenance: "projected",
          verdict:
            "Give the crowd the keys: a rank earned by visits, a members' hour in the café, one day a year the cup gives back.",
        },
        {
          kind: "arena",
          insight:
            "The strongest communities in food and drink are built on two things: a rank customers earn by showing up, and one day a year they show up for. Nobody on the board owns a rank — every rival scores 20 on it, you 41 — and the only fan day on the board, 7-Eleven's, is sixty years old and barely noticed. Your entries on those two lanes are a tier launched in March and a free red cup.",
          title: "How the industry builds community",
          provenance: "observed",
          unit: "community pull of the lane's plays · 0–100",
          note: "Seven ways a food or drink brand builds community. Each grey band shows how strong the field's plays on that lane usually are (left end) and at their best (right end) — the food and drink brands on the board (McDonald's, Coca-Cola, Red Bull, 7-Eleven) plus the public record of the brands the board does not measure yet (Dutch Bros, Dunkin', Blank Street, Erewhon, Chick-fil-A); the dashed ring is the single best play any of them made there; the yellow dots are your own plays. Tap a lane to read it.",
          rows: [
            {
              id: "ladder",
              label: "The standing",
              sub: "a rank earned by showing up",
              typical: 34,
              ceiling: 78,
              best: {
                brandName: "Dunkin'",
                title: "Boosted Status — twelve visits a month",
                pts: 62,
              },
              received: 48,
              own: [
                { title: "Reserve tier · the Tokyo drawing", pts: 48 },
                { title: "Gold at 500 Stars", pts: 40 },
              ],
              status: "recommended",
              note: "A rank a customer earns by visiting and can show to others. It is the weakest brand-owned part of your community assessment (status progression scores 41 of 100) — and nobody on the board is better: McDonald's, Coca-Cola, Red Bull and 7-Eleven all score 20 on it. The lane is empty across the industry. The only live plays are off the board: Dunkin' makes anyone who visits twelve times in a month a 'Boosted' member with extra perks; Blank Street sells the rank outright for $22 a week. Your own tiers launched in March and are earned by Stars spent, not visits made; the Reserve trip to Tokyo is the first status in years a member could actually show off.",
            },
            {
              id: "club",
              label: "The club",
              sub: "the loyalty program as a membership",
              typical: 46,
              ceiling: 84,
              best: {
                brandName: "McDonald's",
                title: "The app — 220M actives, $40B of loyalty sales",
                pts: 76,
              },
              received: 66,
              own: [
                { title: "Starbucks Rewards, 35.8M actives", pts: 66 },
                { title: "Free Mod Monday", pts: 58 },
                { title: "Mofusand — member early access", pts: 50 },
              ],
              note: "The loyalty program, when members treat it as belonging rather than as a discount. You built the biggest one in coffee — 35.8 million active members, six of every ten dollars in your own cafés — and McDonald's the biggest in food, 220 million actives on deals and a film a quarter. Both assessments score member retention high and status low. Off the board, Dutch Bros puts three of every four transactions through its app with a fifth of your members: the gap is not the size of the club, it is what being a member is for.",
            },
            {
              id: "paid",
              label: "The paid room",
              sub: "a membership people pay for",
              typical: 28,
              ceiling: 72,
              best: {
                brandName: "Blank Street",
                title: "Regulars — $22 a week, 4,000 waiting",
                pts: 70,
              },
              note: "A membership with a price on it. You have never played this lane. Blank Street charges $22 a week for its 'Regulars' plan and has a waiting list of about 4,000 people; Erewhon sells a $200-a-year membership to some 60,000 people and gives them a free drink every month. Nothing on your shelf costs anything to join — your exclusivity score is 23 of 100.",
            },
            {
              id: "room",
              label: "The room",
              sub: "a space that belongs to members",
              typical: 30,
              ceiling: 70,
              best: {
                brandName: "Coca-Cola",
                title: "Jacob's Soda Fountain pop-up, The Battery Atlanta",
                pts: 73,
              },
              received: 64,
              own: [
                { title: "1,000 uplifts — stays in a mug 3×", pts: 64 },
                { title: "Community Stores, 150 of 1,000", pts: 38 },
              ],
              note: "A physical place the community can call its own. The best-received room on the board this year is a rival's pop-up: Coca-Cola's soda fountain at The Battery in Atlanta, received at 73 — a room with a ritual in it. You have spent the most on rooms: a thousand cafés re-seated in a year, ceramic mugs back, three times as many customers choosing to sit and stay within three weeks. Yet owned spaces score only 22 in your assessment (and 20 for McDonald's and Red Bull, 60 for Coca-Cola with its World of Coca-Cola), because a café has chairs but no moment that belongs to members.",
            },
            {
              id: "day",
              label: "The day",
              sub: "one date a year fans show up for",
              typical: 26,
              ceiling: 74,
              best: {
                brandName: "Dutch Bros",
                title: "Drink One for Dane — $1.7M in a day",
                pts: 74,
              },
              received: 52,
              own: [{ title: "Red Cup Day", pts: 52 }],
              note: "One date a year the community shows up for. On the board, 7-Eleven has the oldest one — Slurpee Day, sixty years old this July — and it was received at 11, a free drink the crowd has stopped noticing; Red Bull's best-received event of the year was a day it hosted for one fan, received at 73. Off the board, Dutch Bros' Drink One for Dane gives a dollar from every drink to ALS research and a sticker with every cup: $1.7 million in one day this May, $20 million over nineteen years, and a queue that comes to give rather than to get. Your only fixed date is Red Cup Day — a free cup, which is a promotion the crowd attends, not a day it owns.",
            },
            {
              id: "canon",
              label: "The crowd's canon",
              sub: "the crowd's own drinks, printed by you",
              typical: 32,
              ceiling: 68,
              best: {
                brandName: "Erewhon",
                title: "The smoothie collab — $40K a slot",
                pts: 60,
              },
              received: 70,
              own: [
                { title: "Secret Menu in the app", pts: 70 },
                { title: "The $25,000 drink contest", pts: 54 },
              ],
              note: "Taking what customers invented and making it official. This is your best lane: the secret menu customers wrote for fifteen years is now in the app (since July 2025), and a $25,000 contest paid a customer for the next drink on it. Erewhon runs the same idea as a business — brands pay $40,000 to be the smoothie the crowd talks about. Your fan-creativity score is 96 of 100; you have owned it for only fourteen months.",
            },
            {
              id: "hosts",
              label: "The hosts",
              sub: "the baristas as the community's hosts",
              typical: 38,
              ceiling: 80,
              best: {
                brandName: "Chick-fil-A",
                title: "'My pleasure' — ACSI 83, eleven years",
                pts: 80,
              },
              received: 56,
              own: [
                { title: "Green Apron Service", pts: 56 },
                { title: "Barista Championship", pts: 46 },
                { title: "Cup writing, back", pts: 44 },
              ],
              note: "The staff as the people who make it feel like a community. Every other lane rests on this one, and no brand on the board plays it as a play: Chick-fil-A, off the board, has led customer satisfaction for eleven straight years on a two-word script ('my pleasure'); Dutch Bros' 'broistas' are a name its crowd uses. Your one play here is the Barista Championship — the hosts on a stage, received at 25 in North America. Two-thirds of your cafés now hold the Green Apron Service standard and store-leader tenure is up seven points — but your baristas came off the longest strike in your history without a contract, and bargaining only resumed in April.",
            },
          ],
        },
        {
          kind: "stacks",
          insight:
            "Nobody wins on one mechanic — Dutch Bros is hosts plus a ritual plus a cause, Blank Street a standing with a room — and on the board every rival is a ritual first: McDonald's rents one by the meal, Coca-Cola and Red Bull stage one, 7-Eleven's is sixty years old. Yours is the only shape where the crowd's own canon is the largest part and the standing the smallest.",
          title: "What strong communities are made of",
          provenance: "observed",
          note: "Each brand's community plays of the last year sorted by the mechanic they run on — the hosts, a ritual, a standing, a room, a cause, the crowd's canon — as a share of the brand's community pull. The board's food and drink brands are read from their measured plays and assessments; Dutch Bros, Chick-fil-A and Blank Street from their public record. Tap a brand for the read.",
          legend: [
            { key: "hosts", label: "Hosts", color: "#2DD4BF" },
            { key: "ritual", label: "Ritual", color: "#FDE047" },
            { key: "standing", label: "Standing", color: "#FB923C" },
            { key: "room", label: "Room", color: "#4FC3F7" },
            { key: "cause", label: "Cause", color: "#4ADE80" },
            { key: "canon", label: "Canon", color: "#B388FF" },
          ],
          rows: [
            {
              id: "dutch-bros",
              label: "Dutch Bros",
              sub: "73% of transactions from members · off the board",
              parts: [
                { key: "hosts", v: 30 },
                { key: "ritual", v: 25 },
                { key: "standing", v: 15 },
                { key: "room", v: 8 },
                { key: "cause", v: 17 },
                { key: "canon", v: 5 },
              ],
              note: "The broista at the window, a sticker drop that is a ritual, a founder's day that gives — three mechanics the crowd can name, and comps of eight percent with visits up a tenth while yours fell. Not on the board yet; read from its public record.",
            },
            {
              id: "chick-fil-a",
              label: "Chick-fil-A",
              sub: "ACSI 83, eleven straight years · off the board",
              parts: [
                { key: "hosts", v: 45 },
                { key: "ritual", v: 20 },
                { key: "standing", v: 10 },
                { key: "room", v: 15 },
                { key: "cause", v: 10 },
                { key: "canon", v: 0 },
              ],
              note: "Almost half of it is the hosts — a script, a smile, a name — and a closed Sunday that is the country's best-known ritual of absence. No canon: the crowd is not asked to write anything.",
            },
            {
              id: "mcdonalds",
              label: "McDonald's",
              sub: "superfan depth 100, status 20",
              parts: [
                { key: "hosts", v: 10 },
                { key: "ritual", v: 35 },
                { key: "standing", v: 25 },
                { key: "room", v: 5 },
                { key: "cause", v: 5 },
                { key: "canon", v: 20 },
              ],
              note: "The largest club in food runs on rituals bought by the meal — a film, a band, a purple character — and a canon the crowd edits for it (insider language 80). Its assessment scores superfan depth at 100 and status at 20: the deepest fans on the board, with nothing to climb. Its best-received events this year were a Spider-Man cinema takeover and a game show.",
            },
            {
              id: "coca-cola",
              label: "Coca-Cola",
              sub: "the bottle is the badge · membership signalling 100",
              parts: [
                { key: "hosts", v: 5 },
                { key: "ritual", v: 40 },
                { key: "standing", v: 5 },
                { key: "room", v: 25 },
                { key: "cause", v: 10 },
                { key: "canon", v: 15 },
              ],
              note: "Rituals and rooms: the Christmas truck, the FIFA trophy tour, the World of Coca-Cola (owned spaces 60, the board's highest), and this summer's best-received room anywhere — a pop-up soda fountain in Atlanta, received at 73. Share a Coke came back in July and was received at zero: a canon replayed is not a canon.",
            },
            {
              id: "red-bull",
              label: "Red Bull",
              sub: "48 events since September · no membership program",
              parts: [
                { key: "hosts", v: 20 },
                { key: "ritual", v: 45 },
                { key: "standing", v: 2 },
                { key: "room", v: 13 },
                { key: "cause", v: 0 },
                { key: "canon", v: 20 },
              ],
              note: "The most events on the board and the thinnest club: membership program 20, rituals 40, no standing at all. The community is the sport and the athletes are the hosts; its best-received event of the year was a day it hosted for one fan, received at 73 — scarcity of one.",
            },
            {
              id: "7-eleven",
              label: "7-Eleven",
              sub: "Slurpee Day at sixty · barrier to entry 0",
              parts: [
                { key: "hosts", v: 5 },
                { key: "ritual", v: 45 },
                { key: "standing", v: 15 },
                { key: "room", v: 10 },
                { key: "cause", v: 5 },
                { key: "canon", v: 20 },
              ],
              note: "The oldest fan day on the board — Slurpee Day, sixty years this July — received at 11; a run in Singapore and a FanLand received in the twenties. A ritual the crowd has stopped noticing, a rewards program the assessment scores at 60, and nothing exclusive (0).",
            },
            {
              id: "blank-street",
              label: "Blank Street",
              sub: "Regulars — a waiting list to pay · off the board",
              parts: [
                { key: "hosts", v: 15 },
                { key: "ritual", v: 20 },
                { key: "standing", v: 40 },
                { key: "room", v: 20 },
                { key: "cause", v: 0 },
                { key: "canon", v: 5 },
              ],
              note: "A standing you buy by the week, redeemed in a room the size of a kiosk — five thousand paying and four thousand in line for the right to be a regular somewhere. Not on the board yet; read from its public record.",
            },
            {
              id: "starbucks",
              label: "Starbucks",
              sub: "lane 71 — canon-heavy, standing-light",
              parts: [
                { key: "hosts", v: 20 },
                { key: "ritual", v: 22 },
                { key: "standing", v: 8 },
                { key: "room", v: 12 },
                { key: "cause", v: 6 },
                { key: "canon", v: 32 },
              ],
              note: "A third of your community pull is what the crowd wrote — the secret menu, the cup in hand, the resale market — and less than a tenth is a standing anyone can climb. The room is a thousand new chairs with no hour; the cause is a foundation nobody queues for.",
            },
          ],
        },
        {
          kind: "radar",
          insight:
            "Your shape is a fan's — everything the crowd could build alone is in the nineties, everything that needs a door, a rank or a channel from you is under forty-five. Every rival on the board has the same hole: status progression scores 20 for all four and exclusivity nothing — the rank is a gap the whole industry has left open.",
          title: "Your community's anatomy",
          provenance: "measured",
          note: "The community assessments on the board, August 2026 — yours and the four food and drink rivals' — on eight of their dimensions: the first four are what the crowd built without the brand (resale depth, the crowd's own creations, the cup or bottle as a badge, rituals), the last four what only a brand can build (a status to climb, exclusivity, owned spaces, an owned channel). The dashed outline is the average of the four rivals. Tap a brand to morph the shape.",
          axes: [
            "Resale",
            "Canon",
            "The cup",
            "Rituals",
            "Status",
            "Exclusive",
            "Spaces",
            "Channel",
          ],
          baseline: {
            id: "field",
            label: "The field",
            values: [70, 75, 70, 65, 20, 5, 30, 30],
          },
          series: [
            {
              id: "starbucks",
              label: "Starbucks",
              values: [97, 96, 94, 84, 41, 23, 22, 19],
            },
            {
              id: "mcdonalds",
              label: "McDonald's",
              values: [80, 80, 60, 80, 20, 0, 20, 40],
            },
            {
              id: "coca-cola",
              label: "Coca-Cola",
              values: [80, 80, 100, 60, 20, 0, 60, 40],
            },
            {
              id: "red-bull",
              label: "Red Bull",
              values: [60, 60, 60, 40, 20, 20, 20, 20],
            },
            {
              id: "7-eleven",
              label: "7-Eleven",
              values: [60, 80, 60, 80, 20, 0, 20, 20],
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "The deep plays are all small — a soda fountain, a day for one fan, a waiting list — and the wide plays are all shallow — the app, a sixty-year-old free drink; only the Dutch Bros club is both, and that is where the haloed dot sits: a standing that thirty-six million can climb.",
          title: "Reach and depth of community plays",
          provenance: "projected",
          x: { label: "Reach", low: "a few", high: "millions" },
          y: {
            label: "Depth in the week's habits",
            low: "a nudge",
            high: "a ritual",
          },
          quadrants: {
            tr: "Wide and deep",
            tl: "Deep, small",
            br: "Wide, shallow",
            bl: "Neither",
          },
          note: "Reach counts the people a play touched — members, tickets, cups; depth reads what it changed in their week — visits, stays, what they do unpaid — from the board's measured reception where the play is on it and from the public record where it is not. Dot size is the money or the members it moved. Colour is the mechanic. Tap a dot for the read.",
          points: [
            {
              id: "dutch-rewards",
              label: "Dutch Rewards · 73% of transactions",
              era: "Club",
              color: "#4ADE80",
              x: 88,
              y: 76,
              w: 60,
              note: "Fifteen million members putting three of every four transactions through the app, with visits up thirteen percent in a year the field's fell — the one play on the board that is wide and deep at once.",
            },
            {
              id: "dane",
              label: "Drink One for Dane · May 2026",
              era: "Day",
              color: "#FDE047",
              x: 62,
              y: 88,
              w: 40,
              status: "current",
              note: "One day, a dollar a drink, a sticker with every cup: $1.7M for a cause with the founder's name on it, $20M over nineteen years, and a queue that comes to give.",
            },
            {
              id: "regulars-bs",
              label: "Blank Street Regulars",
              era: "Paid",
              color: "#FB923C",
              x: 18,
              y: 92,
              w: 22,
              note: "$22 a week for up to fourteen drinks, invite only, in the app: five thousand paying to be a regular and four thousand waiting to. The deepest habit on the board, at the smallest reach.",
            },
            {
              id: "erewhon",
              label: "Erewhon membership",
              era: "Paid",
              color: "#FB923C",
              x: 14,
              y: 86,
              w: 26,
              note: "Sixty thousand people paying up to $200 a year to belong to a grocer, with a drink a month as the receipt.",
            },
            {
              id: "chipotle",
              label: "Chipotle Rewards on Repeat · Apr 2026",
              era: "Club",
              color: "#4ADE80",
              x: 80,
              y: 36,
              w: 44,
              status: "current",
              note: "Twenty-one million actives and a monthly free drop — wide, and shallow by design: a discount on a schedule is a nudge, not a standing.",
            },
            {
              id: "boosted",
              label: "Dunkin' Boosted Status",
              era: "Status",
              color: "#2DD4BF",
              x: 58,
              y: 64,
              w: 28,
              note: "Twelve visits in a month unlock three months of more — the first standing in coffee earned by showing up rather than by spending. The visit becomes the currency.",
            },
            {
              id: "mcd-app",
              label: "McDonald's app · 220M actives",
              era: "Club",
              color: "#4ADE80",
              x: 96,
              y: 22,
              w: 70,
              note: "The widest club in food and the shallowest: $40B of loyalty sales on deals, a film a quarter, and nothing a member climbs.",
            },
            {
              id: "free-mod",
              label: "Free Mod Monday · Mar 2026",
              era: "Canon",
              color: "#B388FF",
              x: 88,
              y: 54,
              w: 36,
              status: "current",
              note: "Every member, one free modifier, one Monday a month: a third of those who tried one reordered it in the weeks after. A nudge that became a habit — your best depth-per-dollar play of the year.",
            },
            {
              id: "secret-menu",
              label: "Secret Menu in the app · Jul 2025",
              era: "Canon",
              color: "#B388FF",
              x: 86,
              y: 60,
              w: 34,
              note: "The crowd's canon printed by the brand for the first time, four drinks at a launch and a $25,000 contest to write the next — wide because every member sees it, deep because they wrote it.",
            },
            {
              id: "uplifts",
              label: "The uplifts — 1,000 cafés, mugs 3×",
              era: "Room",
              color: "#4FC3F7",
              x: 70,
              y: 66,
              w: 48,
              status: "current",
              note: "A thousand cafés re-seated overnight at $150K each, ceramic back, and stays in a mug tripled inside three weeks. The room is built; it has no hour.",
            },
            {
              id: "tokyo",
              label: "Reserve trip to Tokyo · fall 2026",
              era: "Status",
              color: "#2DD4BF",
              x: 4,
              y: 80,
              w: 8,
              status: "recent",
              note: "Twelve members drawn from the Reserve tier and flown to the Roastery — the deepest thing the club has ever done, for twelve people.",
            },
            {
              id: "odyssey",
              label: "Odyssey · closed Mar 2024",
              era: "Club",
              color: "#94A3B8",
              x: 8,
              y: 12,
              w: 10,
              note: "The last owned community you built: a token programme that never held hands with Rewards, its $100 collection unsold, closed after fifteen months. The lesson is the bridge, not the technology.",
            },
            {
              id: "community-stores",
              label: "Community Stores · 150 of 1,000",
              era: "Room",
              color: "#4FC3F7",
              x: 22,
              y: 46,
              w: 18,
              note: "A hundred and fifty cafés with a neighbourhood programme, on the way to a thousand by the end of the decade — a cause with a room, quiet by design.",
            },
            {
              id: "soda-fountain",
              label: "Coca-Cola · Jacob's Soda Fountain pop-up · Aug 2026",
              era: "Room",
              color: "#4FC3F7",
              x: 12,
              y: 72,
              w: 16,
              status: "recent",
              note: "A pop-up soda fountain at The Battery in Atlanta — the best-received room on the board this year at 73, on aesthetic innovation alone. Small, and a ritual: people came to sit at a counter.",
            },
            {
              id: "spiderman",
              label: "McDonald's × Spider-Man cinema takeover · Jul 2026",
              era: "Day",
              color: "#FDE047",
              x: 40,
              y: 40,
              w: 20,
              status: "recent",
              note: "A fan night in cinemas, played as fandom and identity, received at 56 — McDonald's best event of the year, and a night rather than a date: nothing to come back to.",
            },
            {
              id: "david",
              label: "Red Bull · a day hosted for one fan · Aug 2026",
              era: "Day",
              color: "#FDE047",
              x: 2,
              y: 96,
              w: 6,
              status: "recent",
              note: "Red Bull New York hosted a special day for a single fan and it was the brand's best-received event of the year, at 73, on scarcity alone — the deepest and smallest dot on the board.",
            },
            {
              id: "slurpee",
              label: "7-Eleven · Slurpee Day at sixty · Jul 2026",
              era: "Day",
              color: "#FDE047",
              x: 66,
              y: 30,
              w: 24,
              note: "The oldest fan day on the board, a free drink every July since the sixties — wide, and received at 11 this year. A ritual the crowd has stopped noticing, because it gives nothing but the drink.",
            },
            {
              id: "regulars",
              label: "Regulars — a standing earned by visits",
              short: "Regulars",
              era: "Status",
              color: "#2DD4BF",
              x: 78,
              y: 88,
              w: 50,
              status: "recommended",
              note: "Dunkin's rule on Dutch Bros' scale: twelve visits in a month make a Regular, the Regular gets a drink with their name in the Secret Menu, every Monday's modifier, first access to drops and the Reserve drawings. Thirty-six million can see it; the visit is the currency.",
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "The standing is the only mechanic hot on visits and on staying; the hour is what fills the afternoon you said you wanted; the day is the one that buys engagement nobody is paid for.",
          title: "Which mechanic moves which metric",
          provenance: "projected",
          note: "Seven ways to run a community, six things a CMO is measured on. Cell heat is the mechanic's pull on that metric, modelled from the field's plays and your shelf. Tap a cell for the read.",
          cols: [
            "Visits a week",
            "Ticket",
            "The afternoon",
            "Members won",
            "Unpaid engagement",
            "Stays a year",
          ],
          rows: [
            {
              label: "A standing earned by visits",
              cells: [
                {
                  v: 88,
                  note: "Twelve visits a month is Dunkin's bar and Blank Street's fourteen drinks a week is the ceiling — a standing counted in visits makes the visit the thing worth having.",
                },
                { v: 44 },
                { v: 62 },
                {
                  v: 70,
                  note: "Four in ten of your transactions are non-members; a standing you can see from the queue is the reason to sign in.",
                },
                { v: 74 },
                {
                  v: 94,
                  note: "A rank you can lose is a rank you keep visiting for — the one mechanic still working a year later without a new offer, and the hottest cell on the board.",
                },
              ],
            },
            {
              label: "A paid membership",
              cells: [
                {
                  v: 92,
                  note: "Fourteen drinks a week for $22 — the heaviest habit in coffee, bought upfront.",
                },
                { v: 30 },
                { v: 54 },
                { v: 18 },
                { v: 60 },
                { v: 80 },
              ],
            },
            {
              label: "A members' hour in the café",
              cells: [
                { v: 56 },
                { v: 48 },
                {
                  v: 90,
                  note: "The afternoon is the daypart you named on the call; an hour that belongs to members fills it with people who came to stay and buy twice.",
                },
                { v: 40 },
                {
                  v: 82,
                  note: "Stays in a ceramic mug tripled in three weeks with no incentive at all — the room already does this; the hour gives it a date.",
                },
                { v: 72 },
              ],
            },
            {
              label: "A day that gives back",
              cells: [
                { v: 40 },
                { v: 52 },
                { v: 34 },
                { v: 58 },
                {
                  v: 92,
                  note: "$1.7M in a day at a dollar a drink — the queue comes for the cause and keeps the sticker; nobody is paid to be there.",
                },
                { v: 64 },
              ],
            },
            {
              label: "The crowd's canon in the app",
              cells: [
                { v: 60 },
                {
                  v: 58,
                  note: "A third of the members who tried a free modifier reordered it — a canon drink is a ticket lift the customer chose.",
                },
                { v: 48 },
                { v: 66 },
                { v: 70 },
                { v: 58 },
              ],
            },
            {
              label: "Hosts who know your name",
              cells: [
                { v: 64 },
                { v: 36 },
                { v: 50 },
                { v: 30 },
                { v: 84 },
                {
                  v: 90,
                  note: "Eleven straight years at the top of satisfaction on a two-word script — the host is the mechanic that never expires, and the one a strike switches off.",
                },
              ],
            },
            {
              label: "A fan event",
              cells: [
                { v: 30 },
                { v: 22 },
                { v: 38 },
                { v: 44 },
                { v: 60 },
                { v: 42 },
              ],
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "The first two crowds are already in the app and already in the chair — the standing and the hour cost them nothing to join; the last row is the one every other row depends on.",
          title: "The crowds that would answer first",
          provenance: "projected",
          note: "Six crowds inside your community, scored for how fast they would take up a standing, an hour and a day — from your Rewards and café record and the public signals. Tap a crowd for what moves it.",
          rows: [
            {
              id: "climbers",
              label: "The Gold and Reserve climbers",
              context: "already counting",
              score: 88,
              role: "First-month engine",
              horizon: "short",
              drivers: [
                "Members levelled up Green to Gold to Reserve inside four months of the relaunch — the ladder is already being climbed; a standing by visits gives the climb a reason a Star cannot.",
                "Twelve members won a trip to Tokyo — the first tier benefit in years worth being seen to hold.",
                "Five hundred Stars is roughly a visit a week — the same people the Regulars rule would name on its first day.",
              ],
            },
            {
              id: "stayers",
              label: "The sit-and-stay regulars",
              context: "the Clean Connoisseurs",
              score: 82,
              role: "The afternoon's tenants",
              horizon: "long",
              drivers: [
                "Stays in a ceramic mug tripled inside three weeks of the uplifts, with free refills for staying — the chair was the offer and they took it.",
                "Your strongest segment at index 130, sixteen of nineteen brand leaders among them; they become brand fans through the café and the coffee, never through a cup drop.",
                "An hour that is theirs turns the afternoon they already spend into a date they keep.",
              ],
            },
            {
              id: "authors",
              label: "The secret-menu crowd",
              context: "authors of the canon",
              score: 76,
              role: "Authors of the canon",
              horizon: "short",
              drivers: [
                "A $25,000 contest drew the drinks, four made the app, the crowd voted on Instagram — the canon is written; a Regular's named drink is the next page.",
                "One in three who tried a free modifier reordered it: this crowd changes its habit when the app suggests, not when it is paid to.",
              ],
            },
            {
              id: "hype",
              label: "The Hype Hunters between drops",
              context: "the queue with nowhere to go",
              score: 68,
              role: "The queue with nowhere to go",
              horizon: "short",
              drivers: [
                "Every 7 a.m. Bearista queue is this crowd, and between drops the brand does not own it — index 85, the one segment below fair share.",
                "First access to drops is the one standing benefit they would visit twelve times a month for; the resale market at 97 says they already keep score.",
              ],
            },
            {
              id: "clubs",
              label: "The morning clubs",
              context: "the run that ends at a café",
              score: 64,
              role: "The run that ends at a café",
              horizon: "long",
              drivers: [
                "Coffee clubbing up nearly five-fold in a year and run-club membership up six in ten — the crowd is forming clubs at a café's door, and a shoe brand just gave one a grocer's room.",
                "Drinking is at its lowest since the thirties; the morning is the new night out and it ends holding a cup.",
              ],
            },
            {
              id: "baristas",
              label: "The baristas",
              context: "the hosts — or the picket",
              score: 58,
              role: "The hosts, or the picket",
              horizon: "long",
              drivers: [
                "Four and a half thousand of them struck for three months this winter and bargaining only resumed in April — no community is activated over the heads of its hosts.",
                "Leader tenure up seven points and the service standard held in two-thirds of cafés: the hosts are steadier than a year ago, and the day that gives to a cause they choose is the first play that is theirs.",
              ],
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "Five strategies, a hundred and eighty plays this year, and not one of them on the community lane — your club and your cafés are scored as places, never as plays. Identity signal, the strategy the engine weighs highest, is the one you receive worst against McDonald's; a rank people can earn is an identity signal by construction.",
          title: "Five strategies, you vs McDonald's",
          provenance: "measured",
          mode: "pair",
          a: { label: "Starbucks", color: "#4ADE80" },
          b: { label: "McDonald's", color: "#94A3B8" },
          note: "Every play on your shelf since last September is tagged with the strategies it runs on; this is the average reception each strategy earned, next to McDonald's on the same scale. The tail is how many of your plays carried the strategy. Neither of your two community items carries any strategy at all. Tap a row for the read.",
          rows: [
            {
              id: "identity",
              label: "Identity signal",
              sub: "the play says who the customer is",
              a: 13,
              b: 21,
              tail: "29 plays",
              note: "Your widest gap against McDonald's, on the strategy the engine weighs highest. Your best identity plays this year were all objects — the Dandy road-trip collection (80), the MOLLY collab, the Joyful capsule — none of them the club or the café. A rank earned by visits is the first identity signal that is not something to buy.",
            },
            {
              id: "scarcity",
              label: "Scarcity",
              sub: "limited, dated, capped",
              a: 17,
              b: 19,
              tail: "29 plays",
              note: "Level with McDonald's and your second-best-received strategy. The best of it was the Unicorn's return (80) and a capped collab; on collabs scarcity earned your highest valence of any pairing. A rank you can lose and one day a year are scarcity without a cap on a shelf.",
            },
            {
              id: "aesthetic",
              label: "Aesthetic innovation",
              sub: "the play looks new",
              a: 17,
              b: 12,
              tail: "85 plays",
              note: "Your most-played strategy — nearly half of everything on the shelf — and better received than McDonald's. Its best-received pairing this year was on a place, not a product: aesthetic innovation on your café experiences earned a valence near twenty, the highest of any strategy on any type. The uplifts are that pairing; the members' hour is what gives it a date.",
            },
            {
              id: "fandom",
              label: "Fandom",
              sub: "the play belongs to a crowd",
              a: 17,
              b: 18,
              tail: "21 plays",
              note: "Well received on average and badly received where it was a fan day: the fandom-tagged events (Coachella, Fanatics Fest) earned a valence of minus sixteen, your worst pairing of the year, while the Unicorn's return earned 80. Fandom carries a drink and a cup for you; it does not carry an event — which is why the day is not played as one.",
            },
            {
              id: "opacity",
              label: "Opacity",
              sub: "only insiders know how",
              a: 9,
              b: 14,
              tail: "16 plays",
              note: "Your least-played and worst-received strategy — except where it is a drink only the crowd knows how to order: the caramelised-banana matcha, the Fanatics Fest Refresher and the Barista Championship are its best plays. Opacity belongs in the canon lane and nowhere else on the club.",
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "Each move is played on the strategy the record says it can carry: the rank is an identity signal with scarcity in it, the hour is aesthetic innovation on a place — your best-received pairing this year — and the day is a badge and a date, not a fandom; opacity goes only where it already works, in a drink only the crowd knows how to order.",
          title: "Which strategy carries each mechanic",
          provenance: "projected",
          note: "The seven ways to build community against the five strategies the engine scores. Cell heat is how well the strategy carries the mechanic — modelled from how each strategy was received on your shelf, type by type, and from the field's plays. Tap a cell for the read.",
          cols: [
            "Identity signal",
            "Fandom",
            "Aesthetic innovation",
            "Scarcity",
            "Opacity",
          ],
          rows: [
            {
              label: "A rank earned by visits",
              cells: [
                {
                  v: 92,
                  note: "A rank people can earn and show is an identity signal by construction — the strategy the engine weighs highest, the one you receive worst, and one the club has never carried. The hottest cell on the board, and the first move.",
                },
                { v: 40 },
                { v: 30 },
                {
                  v: 70,
                  note: "A rank you can lose is scarcity that never sells out: twelve visits a month keeps it, and first access to drops is the reward.",
                },
                {
                  v: 55,
                  note: "The Regular's own drink in the Secret Menu — opacity where it already works for you, a drink only insiders know how to order.",
                },
              ],
            },
            {
              label: "The club",
              cells: [{ v: 60 }, { v: 50 }, { v: 25 }, { v: 45 }, { v: 40 }],
            },
            {
              label: "A paid membership",
              cells: [
                { v: 66 },
                { v: 30 },
                { v: 30 },
                {
                  v: 88,
                  note: "A waiting list is scarcity on a membership — Blank Street's four thousand in line are the proof.",
                },
                { v: 50 },
              ],
            },
            {
              label: "A members' hour in the café",
              cells: [
                { v: 58 },
                { v: 35 },
                {
                  v: 84,
                  note: "Aesthetic innovation on a place earned the best valence of any strategy on any type on your shelf this year — the uplifts are that pairing, and the hour gives it a date. The second move.",
                },
                { v: 40 },
                {
                  v: 62,
                  note: "A tasting only members hear about — opacity's kind of play, on the one type where you receive it.",
                },
              ],
            },
            {
              label: "A day that gives back",
              cells: [
                {
                  v: 74,
                  note: "The sticker on the cup and the cause the baristas chose — a badge on the day, worn in the queue. The third move is played as identity, not fandom.",
                },
                {
                  v: 36,
                  note: "Fan days tagged fandom were your worst-received pairing of the year, at a valence of minus sixteen — which is why the day is not played as one.",
                },
                { v: 44 },
                {
                  v: 78,
                  note: "One day a year is scarcity by calendar: Dutch Bros' day has held nineteen years on it.",
                },
                { v: 20 },
              ],
            },
            {
              label: "The crowd's canon",
              cells: [
                { v: 50 },
                { v: 56 },
                { v: 62 },
                { v: 30 },
                {
                  v: 90,
                  note: "The secret menu is opacity made official — every best-received opacity play on your shelf is a drink only the crowd knew how to order.",
                },
              ],
            },
            {
              label: "The hosts",
              cells: [
                {
                  v: 70,
                  note: "A name on the cup and a barista who knows it — the cheapest identity signal in the building.",
                },
                { v: 30 },
                { v: 36 },
                { v: 10 },
                { v: 34 },
              ],
            },
          ],
        },
        {
          kind: "shift",
          insight:
            "The crowd is forming clubs at a café's door and paying to be a regular — while your one open lane is a contract that has not been signed; the window is the winter the uplifts finish.",
          title: "Early signals",
          provenance: "observed",
          rows: [
            {
              label: "Coffee clubbing",
              context: "events listed, year on year",
              value: "+478%",
              spark: [10, 14, 22, 36, 58, 100],
            },
            {
              label: "Run-club membership",
              context: "new clubs three and a half times over",
              value: "+59%",
              spark: [40, 48, 55, 66, 84, 100],
            },
            {
              label: "A paid coffee standing",
              context: "Blank Street Regulars, the waiting list",
              value: "4,000 waiting",
              spark: [20, 34, 50, 66, 88, 100],
            },
            {
              label: "Stays in a ceramic mug",
              context: "three weeks after the uplift, US",
              value: "3×",
              spark: [30, 32, 34, 60, 88, 100],
            },
            {
              label: "A free modifier reordered",
              context: "Free Mod Monday, the weeks after",
              value: "1 in 3",
              spark: [0, 20, 28, 31, 33, 33],
            },
            {
              label: "Members' share of the till",
              context: "Dutch Bros transactions vs your revenue",
              value: "73% vs 59%",
              spark: [58, 60, 62, 66, 70, 73],
            },
            {
              label: "The hosts' contract",
              context: "the longest strike; bargaining since April",
              value: "unsigned",
              spark: [50, 50, 50, 50, 50, 50],
            },
          ],
          note: "Public signals from the last year: the crowd's own club-forming, the field's paid standings, and your café and app record. The last row is the precondition, not a metric.",
        },
        {
          kind: "playsTimeline",
          insight:
            "The field's best-received community plays of the year were small and dated — a soda fountain, a day for one fan — and its widest were old dates gone stale; yours have landed on app releases. Three dates the crowd can plan around, beside the drop calendar the fandom answer already set.",
          title: "The community calendar",
          provenance: "projected",
          today: "2026-09-21",
          note: "Left of NOW: the community plays that moved the industry's habits, by community pull — yours, the board's rivals' (measured reception) and the public record's. Right of NOW: the three dated moves on their own projected-CIQ scale — height is impact. Tap a dot for the read.",
          points: [
            {
              d: "2024-03-15",
              v: 24,
              label: "Odyssey closed",
              itemType: "community",
              note: "The token community shut after fifteen months; it never held hands with Rewards.",
            },
            {
              d: "2025-07-14",
              v: 70,
              label: "Secret Menu in the app",
              itemType: "community",
              note: "The crowd's canon printed by the brand; a $25,000 contest to write the next drink.",
            },
            {
              d: "2025-10-01",
              v: 56,
              label: "Green Apron Service",
              itemType: "experience",
              note: "The service standard, now held in two-thirds of company cafés.",
            },
            {
              d: "2025-11-06",
              v: 88,
              label: "The Bearista queue",
              itemType: "merch",
              note: "The crowd's own ritual — a 7 a.m. line the brand did not organise.",
            },
            {
              d: "2026-03-10",
              v: 74,
              label: "Rewards relaunch — Green, Gold, Reserve",
              itemType: "community",
              note: "Tiers, Free Mod Monday, the Reserve card; members levelled up within four months.",
            },
            {
              d: "2026-04-13",
              v: 52,
              label: "Chipotle Rewards on Repeat",
              itemType: "community",
              note: "Twenty-one million actives and a monthly free drop.",
            },
            {
              d: "2026-05-15",
              v: 74,
              label: "Dutch Bros Drink One for Dane",
              itemType: "event",
              note: "$1.7M in a day at a dollar a drink; a sticker with every cup.",
            },
            {
              d: "2026-07-09",
              v: 56,
              label: "McDonald's × Spider-Man cinema takeover",
              itemType: "event",
              note: "A fan night in cinemas — McDonald's best-received event of the year, at 56.",
            },
            {
              d: "2026-07-11",
              v: 40,
              label: "7-Eleven Slurpee Day, the sixtieth",
              itemType: "event",
              note: "The board's oldest fan day, received at 11 — a free drink the crowd has stopped noticing.",
            },
            {
              d: "2026-07-15",
              v: 72,
              label: "The thousandth uplift",
              itemType: "experience",
              note: "A thousand cafés re-seated ahead of plan; stays in a mug tripled.",
            },
            {
              d: "2026-08-10",
              v: 73,
              label: "Red Bull's day for one fan",
              itemType: "event",
              note: "Red Bull New York hosted a day for a single fan — its best-received event of the year, at 73.",
            },
            {
              d: "2026-08-11",
              v: 73,
              label: "Coca-Cola's soda fountain pop-up",
              itemType: "experience",
              note: "Jacob's Soda Fountain at The Battery Atlanta — the best-received room on the board, at 73.",
            },
            {
              d: "2026-11-10",
              v: 0,
              label: "Regulars — the standing launches",
              itemType: "community",
              projected: true,
              detail: "projected +1.5 CIQ",
              effect: 1.5,
              note: "Twelve visits a month make a Regular: a named drink in the Secret Menu, the Monday modifier, first access to drops, the Reserve drawings.",
            },
            {
              d: "2027-01-19",
              v: 0,
              label: "The Regulars' Hour — the first members' hour",
              itemType: "experience",
              projected: true,
              detail: "projected +0.6 CIQ",
              effect: 0.6,
              note: "One hour a month in the uplifted cafés — a tasting, the ceramic mug, the run club's end point.",
            },
            {
              d: "2027-04-14",
              v: 0,
              label: "Starbucks Day — the cup gives back",
              itemType: "event",
              projected: true,
              detail: "projected +0.5 CIQ",
              effect: 0.5,
              note: "A dollar a drink to a cause each café's baristas choose; a sticker with every cup.",
            },
          ],
        },
        {
          kind: "plays",
          insight:
            "A standing, a room and a day — each played on the strategy your record says it can carry: the rank as an identity signal with scarcity in it, the hour as aesthetic innovation on a place, the day as a badge and a date rather than a fandom — priced together at a little over two and a half points.",
          title: "Three moves, priced",
          provenance: "projected",
          plays: [
            {
              name: "Regulars — a standing earned by visits, not Stars: twelve visits in a month make a Regular; a Regular gets a drink with their name in the Secret Menu, every Monday's free modifier, first access to drops and the Reserve drawings",
              itemType: "community",
              window:
                "November, when the holiday cast drops through the club — then the standing counted every month",
              strategies: [
                { code: "identity_signal", fit: 0.8 },
                { code: "scarcity", fit: 0.5 },
                { code: "opacity", fit: 0.4 },
              ],
              why: "Status progression scores 41 and an owned channel 19 against a crowd that scores its own mechanics in the nineties — and every rival on the board scores 20 on status: the lane is empty across the industry. Dunkin' has shown the rule works in coffee, Blank Street that people will queue to pay for it, Dutch Bros that a club members climb runs three of four transactions. Played as an identity signal first: it is the strategy the engine weighs highest and the one you receive worst against McDonald's (13 to 21), and it has never been tried on the club — every identity play on your shelf this year was an object. A rank you can lose is scarcity that never sells out; the Regular's own drink in the Secret Menu is opacity on the one lane where it already works for you. Counting visits instead of Stars makes the visit the prize, and four in ten of your transactions are still anonymous. This is the fans question's second move — the app from a till to a club — priced there and here alike.",
              effect: {
                label: "CIQ",
                delta: 1.5,
                series: [0, 0.5, 0.9, 1.25, 1.5],
              },
              precedents: 3,
              reception:
                "your club plays are received at 58 to 66 — the tiers at 48",
              similar: [
                {
                  brand: "dunkin",
                  brandName: "Dunkin'",
                  title: "Boosted Status — twelve visits a month",
                  pts: 62,
                },
                {
                  brand: "blank-street",
                  brandName: "Blank Street",
                  title: "Regulars — $22 a week, 4,000 waiting",
                  pts: 70,
                },
                {
                  brand: "dutch-bros",
                  brandName: "Dutch Bros",
                  title: "Dutch Rewards — 73% of transactions",
                  pts: 84,
                },
              ],
            },
            {
              name: "The Regulars' Hour — the uplifted café as the members' room: one hour a month, a tasting on the bar, the ceramic mug, the run club's end point, the afternoon's tenants given a date",
              itemType: "experience",
              window:
                "January, in the fifteen hundred uplifted cafés — the afternoon hour, once a month, then weekly where it fills",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.7 },
                { code: "identity_signal", fit: 0.5 },
                { code: "opacity", fit: 0.3 },
              ],
              why: "Owned spaces score 22 while stays in a ceramic mug tripled inside three weeks of the seats coming back — the room is built and it has no hour. Played on aesthetic innovation, because on your shelf this year aesthetic innovation on a place was the best-received strategy pairing of any strategy on any type (a valence near twenty on the café experiences), and because the board's best-received room of the year — Coca-Cola's soda fountain pop-up, 73 — was that pairing too. The crowd is forming clubs at a café's door: coffee clubbing up nearly five-fold, run clubs up six in ten, a shoe brand and a grocer opening a wellness club with a juice at the end. An hour that belongs to members fills the afternoon daypart you named, with the segment that runs the company; a tasting only members hear about is opacity's kind of play. This is the fans question's fourth move — the chair back — given a date.",
              effect: {
                label: "CIQ",
                delta: 0.6,
                series: [0, 0.15, 0.35, 0.5, 0.6],
              },
              precedents: 2,
              reception: "places are the warmest-received type on your shelf",
              similar: [
                {
                  brand: "coca-cola",
                  brandName: "Coca-Cola",
                  title: "Jacob's Soda Fountain pop-up",
                  pts: 73,
                },
                {
                  brand: "erewhon",
                  brandName: "Erewhon",
                  title: "On × Erewhon Wellness Club",
                  pts: 56,
                },
              ],
            },
            {
              name: "Starbucks Day — one day a year the cup gives back: a dollar a drink to a cause each café's baristas choose, a sticker with every cup, the date on the calendar beside the drops",
              itemType: "event",
              window:
                "April, between the winter and the spring drops — the same date every year after",
              strategies: [
                { code: "identity_signal", fit: 0.6 },
                { code: "scarcity", fit: 0.5 },
                { code: "fandom", fit: 0.3 },
              ],
              why: "Engagement without incentives scores 38 — nobody comes to a Starbucks for a reason that is not a drink or a cup. Dutch Bros' one day raises $1.7M at a dollar a drink and has for nineteen years, with a queue that comes to give and keeps the sticker; on the board, 7-Eleven's sixty-year-old free-drink day was received at 11 and Red Bull's day for one fan at 73 — a date pays when it gives something, not when it gives something away. Played as a badge and a date, not as a fandom: fan days tagged fandom were your worst-received pairing of the year (a valence of minus sixteen on Coachella and Fanatics Fest), while the sticker on the cup is an identity signal worn in the queue and one day a year is scarcity by calendar. A day the hosts choose the cause for is the first play that is the baristas' as much as the crowd's — after the longest strike in your history, that is the point. Priced on the Dutch Bros day at your scale, decayed for a first year.",
              effect: {
                label: "CIQ",
                delta: 0.5,
                series: [0, 0.1, 0.2, 0.4, 0.5],
              },
              precedents: 2,
              reception: "your fan days are received at 40 to 52",
              similar: [
                {
                  brand: "dutch-bros",
                  brandName: "Dutch Bros",
                  title: "Drink One for Dane",
                  pts: 74,
                },
                {
                  brand: "red-bull",
                  brandName: "Red Bull",
                  title: "A day hosted for one fan",
                  pts: 73,
                },
              ],
            },
          ],
          footnote:
            "Projected effects are priced on your measured record, the board's measured reception of the rivals' plays and the public numbers of the brands the board does not measure yet, and compound with your current course, not on top of it; the strategy fits are read from how each strategy was received on your shelf this year, type by type; the first two moves are the fans question's second and fourth moves at the same price, so the answers agree; the day is new here; the fandom question's drop calendar runs alongside, through the same club.",
        },
        {
          kind: "trendCompare",
          insight:
            "The lowest persistent lane becomes a top-three lane in a year, and the curve steps on three dates rather than sloping — that is what an owned community looks like in the record.",
          title: "The community lane, month by month",
          provenance: "projected",
          note: "Your community lane score, the persistent lane the assessment scored at 71: with the standing, the hour and the day — each step is a launch month, read a month after it; and on today's course — the uplifts finishing and the tiers maturing alone. The shaded month is the standing's launch.",
          highlight: {
            from: "2026-11-01",
            to: "2026-11-30",
            label: "the Regulars standing launches",
          },
          series: [
            {
              key: "plan",
              label: "A standing, an hour, a day",
              color: "#4ADE80",
              points: [
                { d: "2026-10-01", v: 71 },
                { d: "2026-11-01", v: 72 },
                { d: "2026-12-01", v: 77 },
                { d: "2027-01-01", v: 77 },
                { d: "2027-02-01", v: 81 },
                { d: "2027-03-01", v: 81 },
                { d: "2027-04-01", v: 82 },
                { d: "2027-05-01", v: 85 },
                { d: "2027-06-01", v: 85 },
                { d: "2027-07-01", v: 85 },
                { d: "2027-08-01", v: 85 },
                { d: "2027-09-01", v: 85 },
              ],
            },
            {
              key: "base",
              label: "Today's course",
              color: "#94A3B8",
              ghost: true,
              points: [
                { d: "2026-10-01", v: 71 },
                { d: "2026-11-01", v: 71 },
                { d: "2026-12-01", v: 72 },
                { d: "2027-01-01", v: 72 },
                { d: "2027-02-01", v: 72 },
                { d: "2027-03-01", v: 73 },
                { d: "2027-04-01", v: 73 },
                { d: "2027-05-01", v: 73 },
                { d: "2027-06-01", v: 74 },
                { d: "2027-07-01", v: 74 },
                { d: "2027-08-01", v: 74 },
                { d: "2027-09-01", v: 74 },
              ],
            },
          ],
        },
        {
          kind: "impact",
          title: "What the three moves carry",
          provenance: "projected",
          delta: 2.6,
          body: "About two and a half points on your published score, from 81.3 to roughly 83.9 and past McDonald's 83.0, on the lane that did not move all year: community, from 71 into the mid-eighties. In the ladder it is brand fans and superfans from 24 to about 27 in every hundred customers, the three points that carry half your merch. It arrives on three dates, and unlike a drop none of it leaves when the season does.",
        },
        {
          kind: "modelView",
          body: "Your community was built by the crowd. Everything the assessment scores highest is something customers made without you — the secret menu they wrote, the cup that says who they are before they order. Everything only a brand can build — a rank to climb, a reason to show up that is not a discount, a door that is theirs — scores in the bottom half. The largest club in coffee runs as a till.\n\nSo the move is not to build a community but to give the one you have what it cannot build itself: a standing earned by visits, an hour in the café that is the members', and a day the cup gives back to a cause the baristas choose. Each is played on the strategy your record says it can carry: the rank as an identity signal, the hour as aesthetic innovation, the day as a date rather than a fandom. And the hosts are the precondition: yours came off the longest strike in the company's history without a contract, and a community activated over their heads reads as a promotion.",
        },
      ],
    },
    {
      id: "collab-partner",
      // the partnership itself is the product — the lane the engine weighs
      // joint-heaviest and the best thing this brand makes
      itemType: "collab",
      type: "collab",
      question: "Who should we collaborate with?",
      aliases: [
        "who should we collaborate with",
        "who should starbucks collaborate with",
        "who should we partner with",
        "who should we collaborate with next",
        "which partner should we choose",
        "who should we do a collab with",
        "which collab should we do next",
        "what collaboration should we run next",
        "who to collaborate with",
        "which brands should we collaborate with",
      ],
      askedOn: "2026-09-22",
      blocks: [
        {
          kind: "readout",
          provenance: "projected",
          verdict:
            "Buy the odds, not the name: Pokémon on the cup, a BT21 drop rhythm, a coffee master in residence for a year.",
        },
        {
          kind: "quadrant",
          insight:
            "You are not bad at making collaborations. You are bad at choosing them. When one of yours lands it lands harder than anyone's on the board, 63 against McDonald's 55, but only 31 in a hundred are noticed at all where they manage 52. Pick at their hit rate and the same eighteen attempts turn into three more noticed a year, on the same budget.",
          title: "Where the board's collabs land",
          provenance: "measured",
          x: {
            label: "How often it's noticed",
            low: "rarely",
            high: "most times",
            hint: "how many of its collaborations our reads found coverage for",
          },
          y: {
            label: "How hard it lands",
            low: "a ripple",
            high: "a moment",
            hint: "how strong that coverage was, on the ones we found",
          },
          quadrants: {
            tr: "Often, and hard",
            tl: "Rarely, but hard",
            br: "Often, not hard",
            bl: "Neither",
          },
          note: "Every brand on the board with at least eight collaborations read since launch. Across: the share of them the world noticed at all. Up: how strongly those measured when it did. Dot size is how many the brand ran this year; colour is its industry. Tap a dot for the read.",
          points: [
            {
              id: "starbucks",
              label: "Starbucks",
              era: "Food",
              color: "#FB923C",
              x: 31,
              y: 63,
              w: 40,
              note: "Eighteen collaborations, five noticed — and those five measured 62.6, the hardest landing of any brand in food and the highest on the board outside luxury. You are picking partners that often produce nothing, and beating everyone when you pick well.",
            },
            {
              id: "mcdonalds",
              label: "McDonald's",
              era: "Food",
              color: "#FB923C",
              x: 52,
              y: 55,
              w: 60,
              note: "Thirty-two collaborations and half of them noticed, on a partner list that is almost entirely characters and fandoms: BT21 at 60, One Piece at 49.5, Spider-Man at 46.5, Pokémon at 42.3. It lands less hard than you and nearly twice as often.",
            },
            {
              id: "hermes",
              label: "Hermès",
              era: "Fashion",
              color: "#B388FF",
              x: 82,
              y: 58,
              w: 30,
              note: "Four collaborations in five noticed — the most disciplined partner list on the board. Every one of them is a named maker with their hands on the object: a table by Barber and Osgerby, a collection drawn by Katie Scott.",
            },
            {
              id: "louis-vuitton",
              label: "Louis Vuitton",
              era: "Fashion",
              color: "#B388FF",
              x: 50,
              y: 59,
              w: 40,
              note: "Half its collaborations land, and they land hard: the World Cup trophy trunk at 70, a crayon designed by Konstantin Grcic at 43.5. Scarce, made, and impossible to mistake for anyone else's.",
            },
            {
              id: "zara",
              label: "Zara",
              era: "Fashion",
              color: "#B388FF",
              x: 47,
              y: 58,
              w: 40,
              note: "A mass-market brand with a luxury hit rate, built on named people rather than partner brands — Aaron Levine's second drop, a collection with Karl Templer at 53.",
            },
            {
              id: "jacquemus",
              label: "Jacquemus",
              era: "Fashion",
              color: "#B388FF",
              x: 45,
              y: 59,
              w: 28,
            },
            {
              id: "nike",
              label: "Nike",
              era: "Fashion",
              color: "#B388FF",
              x: 38,
              y: 54,
              w: 70,
              note: "The largest collaboration programme on the board, forty of them, and a hit rate a third better than yours on a much longer list. Volume with a discipline behind it.",
            },
            {
              id: "netflix",
              label: "Netflix",
              era: "Tech",
              color: "#4FC3F7",
              x: 35,
              y: 38,
              w: 45,
            },
            {
              id: "ferrari",
              label: "Ferrari",
              era: "Cars",
              color: "#94A3B8",
              x: 28,
              y: 43,
              w: 50,
            },
            {
              id: "uniqlo",
              label: "Uniqlo",
              era: "Fashion",
              color: "#B388FF",
              x: 26,
              y: 46,
              w: 55,
            },
            {
              id: "coca-cola",
              label: "Coca-Cola",
              era: "Food",
              color: "#FB923C",
              x: 24,
              y: 51,
              w: 50,
              note: "Twenty-seven collaborations and a quarter of them noticed — the same shape of problem as yours, without the hard landing to compensate.",
            },
            {
              id: "tiffany",
              label: "Tiffany",
              era: "Jewellery",
              color: "#F472B6",
              x: 22,
              y: 60,
              w: 25,
            },
            {
              id: "red-bull",
              label: "Red Bull",
              era: "Food",
              color: "#FB923C",
              x: 17,
              y: 60,
              w: 50,
              note: "Eighteen collaborations, three noticed — but the three measured 60. A partner list of teams and athletes that lands rarely and never quietly.",
            },
            {
              id: "7-eleven",
              label: "7-Eleven",
              era: "Food",
              color: "#FB923C",
              x: 17,
              y: 31,
              w: 80,
              note: "Forty-three collaborations, the most in food, and the worst corner of the chart on both counts. Running more of them is not the lever on its own.",
            },
          ],
        },
        {
          kind: "arena",
          insight:
            "Four kinds of collaboration beat the average both on how often they are noticed and on how hard they land: something made to be kept by a named crowd, something with real craft in it, something the partner actually made, and two brands with a reason to be together. Across all four you have three plays. The other fifteen sit in the three weakest lanes on the chart — a big name, a template, or a brand from the next aisle.",
          title: "The shapes that get noticed",
          provenance: "measured",
          unit: "share of plays the world noticed · %",
          note: "The 451 read collaborations on the board, sorted by the qualities they were graded on. The grey band runs from how often that shape is noticed to how often the best brand running it is noticed; the dashed ring is that brand's own rate with its strongest play named; the yellow dots are where your eighteen went. Tap a shape to read it.",
          rows: [
            {
              id: "kept-scene",
              label: "A keepsake for a scene",
              sub: "made to be kept, for a named crowd",
              typical: 57,
              ceiling: 78,
              best: {
                brandName: "McDonald's",
                title: "The SpongeBob × One Piece Happy Meal",
                pts: 72,
              },
              own: [{ title: "three of your eighteen · two landed", pts: 67 }],
              status: "recommended",
              note: "The strongest single signal on the whole board: a thing built to be kept, made for a crowd that already exists. Twenty-one plays, and 57 in every hundred were noticed — nearly double the average — while 29 of every hundred were still being talked about six weeks later, the best retention measured anywhere. You have run three: MOLLY, Mofusand and the Dandy road trip. Two of them are the best-measured collaborations in your year. This is the first move.",
            },
            {
              id: "craft",
              label: "Real craft in the product",
              sub: "the thing is genuinely well made",
              typical: 42,
              ceiling: 66,
              best: {
                brandName: "Hermès",
                title: "The Stadium table by Barber and Osgerby",
                pts: 60,
              },
              note: "Seventy-two plays graded for craft in the object itself, and four in ten were noticed. They also hold 27 in a hundred of their launch at six weeks. You have never run one: not a single collaboration in your year was graded above 40 for how well the thing was made.",
            },
            {
              id: "partner-made",
              label: "The partner made it",
              sub: "a named person, hands on the work",
              typical: 41,
              ceiling: 68,
              best: {
                brandName: "Zara",
                title: "Aaron Levine's second drop",
                pts: 58,
              },
              note: "A named person beats a partner brand on the odds of being noticed at all, 40 in a hundred to 27 — the clearest cut in the whole table. Seventy-five plays on the board were graded for the partner's own involvement. Three of your eighteen were with a person at all, and none of them was graded above 40 for whether that person made anything.",
            },
            {
              id: "scarce",
              label: "Genuinely scarce",
              sub: "capped, dated, gone",
              typical: 40,
              ceiling: 62,
              best: {
                brandName: "Louis Vuitton",
                title: "The World Cup trophy trunk",
                pts: 55,
              },
              note: "Four in ten noticed — but the weakest retention of the four good shapes, 17 in a hundred at six weeks. Scarcity buys the moment and does not keep it. Four of your eighteen carried it, and they were your best-received pairing of the year at 21.8.",
            },
            {
              id: "authentic",
              label: "A documented shared story",
              sub: "a real reason the two are together",
              typical: 31,
              ceiling: 60,
              best: {
                brandName: "Giorgio Armani",
                title: "The biographical series",
                pts: 58,
              },
              note: "Only average on the odds — and the second-best thing on the board for what survives: 25 in a hundred still there at six weeks, against 15 for a pairing that reads like a template. This is your worst grade in the entire rubric, 23.3 against the board's 36.8.",
            },
            {
              id: "formulaic",
              label: "Reads formulaic",
              sub: "a template anyone could have run",
              typical: 29,
              ceiling: 40,
              best: {
                brandName: "7-Eleven",
                title: "The Hot Wheels die-cast",
                pts: 34,
              },
              own: [{ title: "five of your eighteen", pts: 20 }],
              note: "A hundred and eleven plays that could have been run by anyone with anyone. Below-average odds, and the worst retention on the board — 15 in a hundred at six weeks, less than half what a keepsake holds. Five of your eighteen were graded here.",
            },
            {
              id: "tier",
              label: "A top-tier partner brand",
              sub: "the biggest name available",
              typical: 27,
              ceiling: 45,
              best: {
                brandName: "Netflix",
                title: "The Stranger Things box set",
                pts: 40,
              },
              own: [{ title: "eight of your eighteen", pts: 25 }],
              note: "The most comfortable choice on the board and one of the worst. A hundred and sixty plays with a top-tier partner brand were noticed 27 times in a hundred — below average — and when a tier-100 partner does land it produces the lowest reception in the whole table, 45.8. Fame buys a slightly better ticket and a slightly worse prize. Eight of your eighteen are here.",
            },
            {
              id: "adjacent",
              label: "An adjacent world",
              sub: "a partner with no distance to travel",
              typical: 22,
              ceiling: 34,
              best: {
                brandName: "Red Bull",
                title: "The Trackhouse Racing snapback",
                pts: 33,
              },
              own: [
                { title: "Stanley and BrüMate · two of eighteen", pts: 10 },
              ],
              note: "The bottom of the table: a hundred and twenty-three plays where the two worlds share a customer and nothing has to travel, and barely one in five was noticed. The Stanley tumblers are the case. Graded 42.9 on paper — the highest you gave any collaboration all year — and measured exactly zero across five separate reads. Two coffee-adjacent drinkware brands sharing a shopper produced nothing at all.",
            },
          ],
        },
        {
          kind: "radar",
          insight:
            "The names you sign are the best in food, ahead of McDonald's, Coca-Cola, Red Bull and 7-Eleven. What is missing is the reason. Asked why these two are together you grade 23 where the board grades 37, and asked whether the partner actually made the thing, 33 against 50 — and those are the two answers that decide whether anything is still there six weeks later.",
          title: "How your pairings grade",
          provenance: "measured",
          note: "Your eighteen collaborations of the year graded on eight of the questions the board asks of every partnership — against the whole food cohort on the same eight, and the board's own average as the dashed outline. The first three are about the pairing, the next three about the thing you make, the last two about how scarce it is and how big the name is. Tap a brand to morph the shape.",
          axes: [
            "Shared story",
            "They made it",
            "Distance",
            "Keepsake",
            "A scene",
            "Real craft",
            "Scarce",
            "Partner tier",
          ],
          baseline: {
            id: "board",
            label: "The board",
            values: [37, 50, 39, 48, 47, 42, 38, 62],
          },
          series: [
            {
              id: "starbucks",
              label: "Starbucks",
              values: [23, 33, 47, 57, 52, 30, 35, 80],
            },
            {
              id: "mcdonalds",
              label: "McDonald's",
              values: [36, 40, 35, 60, 50, 28, 30, 75],
            },
            {
              id: "red-bull",
              label: "Red Bull",
              values: [28, 42, 50, 40, 55, 24, 34, 70],
            },
            {
              id: "coca-cola",
              label: "Coca-Cola",
              values: [30, 36, 38, 48, 44, 26, 33, 72],
            },
            {
              id: "7-eleven",
              label: "7-Eleven",
              values: [25, 30, 42, 52, 38, 22, 28, 68],
            },
          ],
        },
        {
          kind: "stacks",
          insight:
            "Almost every collaboration on the board is doing one of two jobs: saying who the brand is, or making something look new. Yours lean on those two harder than anyone's, eighty-three tags in a hundred. The interesting ones are the two nobody spends — fandom takes three tags in a hundred of yours, and opacity, the if-you-know-you-know play the secret menu already runs for you, never appears on a collaboration at all.",
          title: "What a collab is used for",
          provenance: "measured",
          note: "Every collaboration on the board is tagged with the strategies it runs on. These are the shares of those tags — yours, the whole board's, food's, and everything brands make that is not a collaboration. Tap a row for the read.",
          legend: [
            { key: "identity", label: "Identity signal", color: "#FB923C" },
            {
              key: "aesthetic",
              label: "Aesthetic innovation",
              color: "#4FC3F7",
            },
            { key: "scarcity", label: "Scarcity", color: "#FDE047" },
            { key: "fandom", label: "Fandom", color: "#B388FF" },
            { key: "opacity", label: "Opacity", color: "#94A3B8" },
          ],
          rows: [
            {
              id: "sbux",
              label: "Your collabs",
              sub: "eighteen plays, thirty tags",
              parts: [
                { key: "identity", v: 43 },
                { key: "aesthetic", v: 40 },
                { key: "scarcity", v: 14 },
                { key: "fandom", v: 3 },
                { key: "opacity", v: 0 },
              ],
              note: "The board's own mix, pushed further. Identity signal and aesthetic innovation carry eight of every ten tags you put on a collaboration, and they return the least of the four: 13.5 and 14.4. The four scarcity tags returned 21.8, your best pairing of the year. The single fandom tag returned nothing at all.",
            },
            {
              id: "board",
              label: "Every collab on the board",
              sub: "537 plays, 721 tags",
              parts: [
                { key: "identity", v: 43 },
                { key: "aesthetic", v: 33 },
                { key: "scarcity", v: 16 },
                { key: "fandom", v: 8 },
                { key: "opacity", v: 0 },
              ],
              note: "Collaborations are what brands reach for when they want to say who a customer is. Identity signal carries more than four tags in ten and fandom fewer than one — the exact inverse of what the two return.",
            },
            {
              id: "food",
              label: "Food collabs",
              sub: "165 plays across the cohort",
              parts: [
                { key: "identity", v: 44 },
                { key: "aesthetic", v: 32 },
                { key: "scarcity", v: 17 },
                { key: "fandom", v: 7 },
                { key: "opacity", v: 0 },
              ],
              note: "Food is the most collaboration-dense industry measured — 18 in every hundred plays it makes is a partnership, against 14 in fashion and 6 in beauty — and it runs the board's mix almost exactly. The fandom tags it does carry return 14.2, the best of any cut in the cohort.",
            },
            {
              id: "shelf",
              label: "Everything else brands make",
              sub: "3,745 tags off the collab shelf",
              parts: [
                { key: "identity", v: 22 },
                { key: "aesthetic", v: 42 },
                { key: "scarcity", v: 14 },
                { key: "fandom", v: 11 },
                { key: "opacity", v: 11 },
              ],
              note: "Off the collaboration shelf the mix inverts: aesthetic innovation leads, identity signal halves, and opacity — a drink only the crowd knows how to order, a drop nobody announced — is a tenth of everything. A partnership is the one thing brands never keep quiet about.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "Building for a fandom is the best-paying thing a collaboration can do, and it is the one almost nobody does: it measures 14 where every other strategy measures 10 or 11, on eight tags in a hundred. One of your eighteen was built that way, and it measured nothing. The strategy you spend most, saying who you are, pays no better than average.",
          title: "What each strategy returns",
          provenance: "measured",
          mode: "pair",
          a: { label: "On a collab", color: "#4ADE80" },
          b: { label: "Everywhere else", color: "#94A3B8" },
          note: "Average reception of every play on the board carrying each strategy — on a collaboration, and on everything that is not one. The tail is the strategy's share of collaboration tags. Tap a row for the read.",
          rows: [
            {
              id: "fandom",
              label: "Fandom",
              sub: "a crowd that already organises itself",
              a: 14,
              b: 8,
              tail: "8 tags in 100",
              note: "The best-performing strategy on a collaboration anywhere on the board — 13.8 against 10.0 to 10.8 for every other tag — on the joint-highest multiplier the engine carries, and the least spent of the four. McDonald's × BT21 is the reference: the best-received food collaboration in the record at 60. You have tagged one of eighteen for fandom. It measured zero.",
            },
            {
              id: "identity",
              label: "Identity signal",
              sub: "the play says who the customer is",
              a: 11,
              b: 9,
              tail: "43 tags in 100",
              note: "Four tags in ten and the largest share of your own collaboration spend, on a middling return. It is the right strategy for a partnership and the wrong one to run alone — every one of your identity collaborations this year was an object somebody could buy.",
            },
            {
              id: "scarcity",
              label: "Scarcity",
              sub: "capped, dated, gone",
              a: 11,
              b: 8,
              tail: "16 tags in 100",
              note: "Level with identity signal across the board, and your own best pairing of the year: the four collaborations you capped returned 21.8, well above your other fourteen. Scarcity buys the moment; it is the retention table that says it does not keep it.",
            },
            {
              id: "aesthetic",
              label: "Aesthetic innovation",
              sub: "the thing looks like nothing else",
              a: 10,
              b: 9,
              tail: "33 tags in 100",
              note: "A third of collaboration tags and the weakest return of the three strategies in real use. Off the collaboration shelf it is the largest tag brands carry — this is the one strategy a partnership does not improve.",
            },
            {
              id: "opacity",
              label: "Opacity",
              sub: "only the crowd knows",
              a: 0,
              b: 6,
              tail: "never tagged on a collab",
              note: "The one strategy a collaboration never runs on — not once in 537 of them. Off that shelf it is a tenth of everything brands make and the weakest-received of the five. It is where your secret menu lives, and a partnership announced on a billboard is its opposite by construction.",
            },
          ],
        },
        {
          kind: "shift",
          insight:
            "The biggest results in coffee and tea came from putting the partner in the cup rather than on a tumbler: a liquor in a latte sold five and a half million cups on its first day, a luxury house on a fruit tea one and a half million in three days. None of these chains is measured on the board, so none of this is in the charts above. What every one of them bought is distance between two worlds — the one question you already grade above average.",
          title: "Off the board",
          provenance: "observed",
          note: "The coffee and tea chains the index does not measure yet, read from their own published figures. Not comparable with the graded plays above; kept here because the category's largest collaboration results all came from outside the measured set.",
          rows: [
            {
              label: "Luckin × Moutai",
              context: "a 1,500-yuan liquor in a 19-yuan latte",
              value: "5.42M cups, day one",
              spark: [5, 12, 30, 62, 88, 100],
            },
            {
              label: "Luckin × Black Myth",
              context: "a games IP at its exact peak",
              value: "ordering went down",
              spark: [8, 20, 44, 70, 92, 100],
            },
            {
              label: "Heytea × Fendi",
              context: "seven hours at the top of Weibo",
              value: "1.5M cups in three days",
              spark: [10, 26, 48, 74, 100, 86],
            },
            {
              label: "Tim Hortons × Bieber",
              context: "a named-person menu, run as a programme",
              value: "four seasons on",
              spark: [30, 52, 70, 66, 80, 74],
            },
            {
              label: "Dunkin' × e.l.f.",
              context: "a makeup line off a coffee menu",
              value: "sold out in a week",
              spark: [12, 34, 60, 84, 100, 70],
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "Two partners own the next quarter, a character licence and an idol fandom, and a third owns the year: a named coffee maker with a small audience. Under them sits the one you have already proved, POP MART, the best-measured play you have ever run. The bottom three are the comfortable choices — the biggest audience, the platform everyone is already on, and the brand from the next aisle you tried once and heard nothing back.",
          title: "Nine partners, ranked",
          provenance: "projected",
          note: "Nine named partners and partner shapes, each graded on the same questions the board asks of every collaboration. The score blends what it would move this quarter, what it would move in a year, and how much of the case the board's own record already proves. Tap a partner for the evidence.",
          rows: [
            {
              id: "pokemon",
              label: "The Pokémon Company",
              context: "the quarter",
              score: 88,
              role: "Top-tier character IP",
              horizon: "short",
              drivers: [
                "It is the shape with the best measured odds on the whole board — made to be kept and built for a scene, noticed 57 times in a hundred against an average of 31, and still read 29 times in a hundred six weeks later.",
                "It is proven in food by the brand you are chasing: McDonald's character plays are the top of the cohort's reception table, Pokémon at 42.3, One Piece at 49.5, Spider-Man at 46.5 — and they are why it lands half the time.",
                "It is proven inside your own record. POP MART MOLLY is the strongest-scoring item in your entire index at 58.5, and it is exactly this shape.",
              ],
            },
            {
              id: "hybe",
              label: "HYBE — BT21 and the idol IP",
              context: "the fandom",
              score: 84,
              role: "Music fandom IP",
              horizon: "short",
              drivers: [
                "Fandom is the best-performing strategy on collaborations measured anywhere on the board, 13.8 against 10.0 to 10.8, and it carries eight tags in a hundred.",
                "You have tagged one of eighteen collaborations for fandom and it measured zero — the single largest unused lever in your record.",
                "The reference case is the best-measured food collaboration in the database: McDonald's × BT21 at 60, on a partner graded 80 for credibility with a documented shared story.",
              ],
            },
            {
              id: "craft",
              label: "A coffee master in residence",
              context: "the year",
              score: 79,
              role: "A named maker, real craft",
              horizon: "long",
              drivers: [
                "It repairs your two worst grades in the whole rubric at once: a documented shared story, 23.3 against the board's 36.8, and whether the partner made the thing, 33.3 against 50.3.",
                "Both are the questions that decide what survives — a documented story holds 25 in a hundred of its launch at six weeks against 15 for a template, and real craft is noticed 42 times in a hundred.",
                "A named person is noticed 40 times in a hundred against 27 for a partner brand, and you have run three person collaborations out of eighteen, none of them graded above 40 for the person's own involvement.",
              ],
            },
            {
              id: "popmart",
              label: "POP MART — act two",
              context: "the bench",
              score: 76,
              role: "Deepen the proven partner",
              horizon: "long",
              drivers: [
                "Your best-measured collaboration of the year, 43.5 across its reads and 58.5 in the index, and the partner is still at the top of its own category.",
                "Cheap, repeatable and already understood inside the building — the only candidate that needs no argument.",
                "It belongs on the calendar whichever of the three above is chosen; it is not a substitute for any of them.",
              ],
            },
            {
              id: "animation",
              label: "A prestige animation house",
              context: "the long shot",
              score: 58,
              role: "Craft IP, rarely licensed",
              horizon: "long",
              drivers: [
                "Scarce by temperament — it almost never licenses, which is the entire value of the pairing.",
                "Graded highest of the nine on the thing itself, and the slowest of the nine to agree to anything.",
                "Worth opening a conversation that takes a year, not a quarter.",
              ],
            },
            {
              id: "drinkware",
              label: "An adjacent drinkware brand",
              context: "the control",
              score: 42,
              role: "The trap you already ran",
              horizon: "short",
              drivers: [
                "It posts the third-best sales figure of the nine, because half a million tumblers at fifty dollars is real revenue.",
                "It sits in the worst cut on the board — an adjacent world with nothing to travel, noticed 22 times in a hundred.",
                "You ran it in June. The Stanley tumblers were graded the highest on paper of any collaboration in your year and measured exactly zero across five reads.",
              ],
            },
            {
              id: "gaming",
              label: "A gaming platform",
              context: "where they already are",
              score: 35,
              role: "Borrowed audience",
              horizon: "short",
              drivers: [
                "A large live audience that has already seen a great many brand activations.",
                "Nothing is kept, so nothing survives the launch fortnight.",
                "Graded lowest of the nine on the thing itself — there is no object at the end of it.",
              ],
            },
            {
              id: "sports",
              label: "A global sports property",
              context: "the biggest audience",
              score: 32,
              role: "The quiet trap",
              horizon: "short",
              drivers: [
                "The largest audience of any candidate, and the lowest retention of any candidate.",
                "You already ran the World Cup cup sleeve in June. Five reads, nothing measured.",
                "A tournament year makes the partner's own moment loud and leaves nothing behind when it ends.",
              ],
            },
            {
              id: "designer",
              label: "A designer or streetwear label",
              context: "scene credibility",
              score: 31,
              role: "Deep, and narrow",
              horizon: "long",
              drivers: [
                "Strong inside its scene and largely unknown outside it — the smallest quarter of the nine.",
                "The thisisneverthat collaboration is the measured version of this bet: eight reads, nothing.",
                "Worth running when there is a scene you are trying to enter, and not before.",
              ],
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "Only one partner is strong on both clocks, and it is the one that ages fastest: the idol fandom, 84 this quarter against 69 over the year. Every other name trades one clock for the other — the character licence takes the quarter, the residency takes the year. The useful warning is the pairing you already ran: the drinkware brand posts the third-best sales number of the nine and finishes in the bottom half of both clocks. It sells cups and changes nothing.",
          title: "The two clocks",
          provenance: "projected",
          x: {
            label: "What it moves this quarter",
            low: "little",
            high: "a lot",
            hint: "sales, traffic, app and web, reach in the launch quarter",
          },
          y: {
            label: "What it moves in a year",
            low: "little",
            high: "a lot",
            hint: "sentiment, equity, recall and awareness over a year",
          },
          quadrants: {
            tr: "Pays on both",
            tl: "Pays in the year",
            br: "Pays in the quarter",
            bl: "Neither",
          },
          note: "The same nine partners on the two clocks. Across: sales, traffic, app and web, reach and media value in the launch quarter. Up: sentiment, equity, recall and awareness over a year. Dot size is the projected gain to your published score. Tap a dot for the read.",
          points: [
            {
              id: "pokemon",
              label: "Pokémon",
              short: "Pokémon",
              era: "Character IP",
              color: "#FB923C",
              x: 84,
              y: 57,
              w: 55,
              status: "recommended",
              note: "The best quarter of the nine, on the shape with the best measured odds on the board. It is graded lower on the year than the fandom play because a licence does not change what a brand means — which is why the residency is the third move and not an alternative to this one.",
            },
            {
              id: "hybe",
              label: "BT21",
              era: "Borrowed audience",
              color: "#B388FF",
              x: 84,
              y: 69,
              w: 58,
              note: "Level with Pokémon on the quarter and twelve points better on the year, because a fandom brings sentiment with it. The measured risk is shelf life: an act's moment is finite in a way a character's is not, and this is the lowest retention of the three recommendations.",
            },
            {
              id: "craft",
              label: "The residency",
              era: "A named maker",
              color: "#2DD4BF",
              x: 49,
              y: 82,
              w: 58,
              note: "The best year of the nine and a modest quarter. Judged on launch-week impressions it will look like the weakest thing on this chart, and it is the only one still paying in twelve months.",
            },
            {
              id: "popmart",
              label: "POP MART again",
              era: "Proven partner",
              color: "#4FC3F7",
              x: 45,
              y: 73,
              w: 58,
              note: "Fourth on both clocks and the cheapest thing on the chart to say yes to. It belongs on the calendar whichever of the three above is chosen.",
            },
            {
              id: "animation",
              label: "An animation house",
              era: "Character IP",
              color: "#FB923C",
              x: 42,
              y: 58,
              w: 62,
            },
            {
              id: "sports",
              label: "A sports property",
              era: "Borrowed audience",
              color: "#B388FF",
              x: 37,
              y: 25,
              w: 43,
              note: "The quiet trap: the largest audience of any candidate and last place on the year. You already ran the World Cup cup sleeve in June and five reads found nothing.",
            },
            {
              id: "drinkware",
              label: "Adjacent drinkware",
              era: "The control",
              color: "#94A3B8",
              x: 34,
              y: 34,
              w: 48,
              note: "The control, drawn from the Stanley pairing you ran in June: the third-best sales figure of the nine, the bottom half of both clocks, and exactly zero measured across five reads when you actually ran it.",
            },
            {
              id: "gaming",
              label: "A gaming platform",
              era: "Borrowed audience",
              color: "#B388FF",
              x: 31,
              y: 40,
              w: 53,
            },
            {
              id: "designer",
              label: "A designer label",
              era: "A named maker",
              color: "#2DD4BF",
              x: 23,
              y: 39,
              w: 60,
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "Nothing wins the whole grid, because the two halves reward different things: a big name and reach move sales and traffic now, a real story and real craft move how people feel about you later. Neither kind of partner does the other's job. The drinkware brand makes it plain — third-best on sales, last on everything that is still true in a year.",
          title: "What each partner moves",
          provenance: "projected",
          note: "The six strongest candidates against the six things a collaboration can move — three inside the launch quarter, three across a year. Cell heat is the projected movement, scaled across the six. Modelled as the odds the play is noticed at all, multiplied by the near-constant result a collaboration produces when it is. Tap a cell for the read.",
          cols: [
            "Sales",
            "Traffic",
            "App and web",
            "Reach",
            "Sentiment",
            "Equity",
          ],
          rows: [
            {
              label: "Pokémon on the cup",
              cells: [
                {
                  v: 95,
                  note: "The hottest cell on the grid: a character in the drink and a keepsake beside it moves the whole basket, not just the merchandise. Anchored against a promotional quarter's measured ceiling — the largest single food activation on record moved visits by a tenth for a week.",
                },
                { v: 78 },
                {
                  v: 62,
                  note: "App-exclusive early access for members lifts the launch window by about a third again, on the published response to large quick-service collaborations.",
                },
                { v: 66 },
                { v: 35 },
                { v: 40 },
              ],
            },
            {
              label: "BT21, with a rhythm",
              cells: [
                { v: 72 },
                { v: 80 },
                { v: 62 },
                {
                  v: 88,
                  note: "The largest reach of the six, and the only one where the partner's own audience does most of the carrying — sixty million followers with a release calendar they already organise around.",
                },
                { v: 52 },
                { v: 52 },
              ],
            },
            {
              label: "The coffee residency",
              cells: [
                { v: 40 },
                { v: 58 },
                { v: 38 },
                { v: 40 },
                {
                  v: 92,
                  note: "The best year on the grid, and it comes from the two questions you grade worst on. A documented story keeps a quarter of its launch reading at six weeks against 15 in a hundred for a template — the durable half of a collaboration is graded on craft and reason, never on reach.",
                },
                { v: 78 },
              ],
            },
            {
              label: "POP MART again",
              cells: [
                { v: 40 },
                { v: 22 },
                { v: 52 },
                { v: 42 },
                { v: 60 },
                { v: 60 },
              ],
            },
            {
              label: "A sports property",
              cells: [
                { v: 30 },
                { v: 32 },
                { v: 28 },
                { v: 48 },
                { v: 18 },
                { v: 20 },
              ],
            },
            {
              label: "Adjacent drinkware",
              cells: [
                {
                  v: 60,
                  note: "The trap in one cell: the third-best sales figure of the nine candidates, because half a million tumblers at fifty dollars is real revenue — beside the bottom of the table on everything that is still true in a year.",
                },
                { v: 12 },
                { v: 22 },
                { v: 30 },
                { v: 28 },
                { v: 35 },
              ],
            },
          ],
        },
        {
          kind: "playsTimeline",
          insight:
            "Your year is a shelf, not a calendar: nine collaborations since the end of April, three of them measured at nothing, and the two that landed hardest arrived six days apart. McDonald's spread one character across a film, a manga and an anniversary over the same summer and landed every beat. Three dates with beats inside them are worth more than nine drops that collide.",
          title: "The collab calendar",
          provenance: "projected",
          today: "2026-09-22",
          note: "Left of NOW: the collaborations of the year, yours and the food cohort's, by the reception their reads measured. Right of NOW: the three dated moves on their own projected-CIQ scale — height is impact. Tap a dot for the read.",
          points: [
            {
              d: "2026-04-28",
              v: 0,
              label: "thisisneverthat",
              itemType: "collab",
              note: "A Seoul label with a shared audience and no distance to travel. Eight reads, nothing measured.",
            },
            {
              d: "2026-04-30",
              v: 0,
              label: "Harry Potter keychains",
              itemType: "collab",
              note: "A blind box on a top-tier licence, graded 38.6 on paper. Eight reads, nothing measured.",
            },
            {
              d: "2026-05-19",
              v: 11,
              label: "Miffy",
              itemType: "collab",
              note: "The character shape done as merchandise alone — barely read, twice.",
            },
            {
              d: "2026-06-02",
              v: 32,
              label: "McDonald's × POP MART",
              itemType: "collab",
              note: "The cohort's answer to your own best play, two months earlier.",
            },
            {
              d: "2026-06-15",
              v: 0,
              label: "Stanley tumblers",
              itemType: "collab",
              note: "Graded 42.9 on paper — the highest you gave any collaboration all year — and exactly zero across five reads.",
            },
            {
              d: "2026-06-26",
              v: 23,
              label: "Jay Chou",
              itemType: "collab",
              note: "Your best-read person pairing of the year, and the only one above single figures.",
            },
            {
              d: "2026-07-06",
              v: 42,
              label: "Dandy Worldwide",
              itemType: "collab",
              note: "A small label, a real object, four reads and a reception of 41.5 — the shape working at a fraction of the budget.",
            },
            {
              d: "2026-07-14",
              v: 60,
              label: "McDonald's × BT21",
              itemType: "collab",
              note: "The best-measured food collaboration in the whole record, on the strategy you have tagged once.",
            },
            {
              d: "2026-07-20",
              v: 42,
              label: "McDonald's × Pokémon",
              itemType: "collab",
              note: "A character in the meal in Japan, read three times and never below 40.",
            },
            {
              d: "2026-07-28",
              v: 53,
              label: "Dandy road trip",
              itemType: "collab",
              note: "The same partner again with drinks attached to the merchandise — and the reception rose from 41.5 to 53.",
            },
            {
              d: "2026-07-28",
              v: 50,
              label: "McDonald's × One Piece",
              itemType: "collab",
              note: "The keepsake-for-a-scene shape at national scale, and the reason the cohort lands half the time.",
            },
            {
              d: "2026-08-03",
              v: 44,
              label: "POP MART MOLLY",
              itemType: "collab",
              note: "Measured 43.5, and the strongest-scoring item in your whole index at 58.5.",
            },
            {
              d: "2026-08-21",
              v: 40,
              label: "Mofusand, member access",
              itemType: "collab",
              note: "The right shape with the right door — too new to have been read yet.",
            },
            {
              d: "2027-01-26",
              v: 84,
              label: "Pokémon on the cup",
              itemType: "collab",
              projected: true,
              effect: 1.1,
              detail: "projected +1.1 CIQ",
              note: "A drink that carries the character, a range built to be kept beside it, and early access in the app — the first of at least three beats in the year, not a drop.",
            },
            {
              d: "2027-04-13",
              v: 84,
              label: "BT21, with a rhythm",
              itemType: "collab",
              projected: true,
              effect: 0.8,
              detail: "projected +0.8 CIQ",
              note: "A named drink and a collectible series on a calendar the fandom can organise around — the strategy you have spent once, on the partner the record says performs best.",
            },
            {
              d: "2027-07-06",
              v: 49,
              label: "The coffee residency",
              itemType: "collab",
              projected: true,
              effect: 0.5,
              detail: "projected +0.5 CIQ",
              note: "A named authority with their hands on the work for a year — the quietest launch of the three and the only one still being read next summer.",
            },
          ],
        },
        {
          kind: "plays",
          insight:
            "The three are deliberately not the same bet: the character buys the quarter, on the shape with the best odds on the board; the fandom spends the strategy you have never spent; the residency repairs the two grades that decide what survives. One of them would move a quarter. Together they are worth about two and a half points on the year.",
          title: "Three partners, priced",
          provenance: "projected",
          plays: [
            {
              name: "Pokémon on the cup — a character year, not a keychain: a drink that carries the character, a collectible range built to be kept beside it, and early access in the app for members, across at least three beats",
              itemType: "collab",
              window:
                "January, into the anniversary year — then a beat a quarter",
              strategies: [
                { code: "identity_signal", fit: 0.8 },
                { code: "scarcity", fit: 0.6 },
              ],
              why: "This is the shape with the best measured odds on the whole board: a thing made to be kept, built for a crowd that already exists, is noticed 57 times in a hundred against an average of 31, and is still read 29 times in a hundred six weeks later. It is proven in food by the brand you are chasing — McDonald's character plays are the top of the cohort's reception table and the reason it lands half the time — and it is proven inside your own record, because POP MART MOLLY is the strongest-scoring item in your entire index and it is exactly this shape. Played as an identity signal with scarcity in it: a character on the cup says who the customer is, and a capped range is the only scarcity that never has to discount. The risk is running it as a merchandise licence — if the character never reaches the drink and the product is a keychain, it drops into the formulaic cut, 29 in a hundred noticed and 15 in a hundred kept, and the difference between the two outcomes is entirely in the brief.",
              effect: {
                label: "CIQ",
                delta: 1.1,
                series: [0, 0.4, 0.7, 0.95, 1.1],
              },
              precedents: 4,
              reception:
                "your one play of this shape measured 43.5 and scores 58.5 — the highest item you own",
              similar: [
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "The SpongeBob × One Piece Happy Meal",
                  pts: 50,
                },
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "The Japan Pokémon summer campaign",
                  pts: 42,
                },
                {
                  brand: "starbucks",
                  brandName: "Starbucks",
                  title: "POP MART MOLLY, twenty years",
                  pts: 44,
                },
              ],
            },
            {
              name: "BT21 with a drop rhythm — the fandom lever you have never pulled: a named drink, a collectible series on a calendar the fandom can organise around, and member-only access as the reward for organising",
              itemType: "collab",
              window:
                "April, then a beat every eight weeks while the act is live",
              strategies: [
                { code: "identity_signal", fit: 0.8 },
                { code: "scarcity", fit: 0.4 },
                { code: "fandom", fit: 0.3 },
              ],
              why: "Fandom is the best-performing strategy on collaborations measured anywhere on the board — 13.8 against 10.0 to 10.8 for every other tag — on the joint-highest multiplier the engine carries, and it takes only eight tags in a hundred. You have tagged one of eighteen collaborations for fandom and that one measured zero, which makes this the single largest unused lever in your record. The reference case is the best-measured food collaboration in the database: McDonald's × BT21 at 60, on a partner graded 80 for credibility with a documented shared story behind the pairing. The failure mode is borrowing the fandom and giving it nothing to do — a cup sleeve with a face on it rents the audience for a week, while a rhythm the fandom can plan around is what produces the 13.8. This is also the shortest shelf life of the three: an act's moment is finite in a way a character's is not, which is why it is the second move and not the first.",
              effect: {
                label: "CIQ",
                delta: 0.8,
                series: [0, 0.3, 0.55, 0.7, 0.8],
              },
              precedents: 3,
              reception:
                "the board's fandom collabs are received at 13.8 — your one attempt at zero",
              similar: [
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "The BT21 Happy Meals",
                  pts: 60,
                },
                {
                  brand: "uniqlo",
                  brandName: "Uniqlo",
                  title: "UT × ZO&FRIENDS with G-DRAGON",
                  pts: 45,
                },
                {
                  brand: "netflix",
                  brandName: "Netflix",
                  title: "KPop Demon Hunters skincare",
                  pts: 32,
                },
              ],
            },
            {
              name: "A coffee master in residence — a year, not a launch: a named authority with their hands on a small number of genuinely co-developed releases, the roast, the equipment around it and the ritual it belongs to",
              itemType: "collab",
              window:
                "July, and then quarterly for a year — a residency, not a drop",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.6 },
                { code: "identity_signal", fit: 0.6 },
                { code: "scarcity", fit: 0.4 },
              ],
              why: "These are your two worst grades in the whole rubric: a documented reason the two are together scores 23.3 against a board average of 36.8, and whether the partner actually made the thing scores 33.3 against 50.3. Your partner tiers are fine — the names are fine. The pairings are thin. And both of those questions are the ones that decide what survives: a documented shared story keeps 25 in a hundred of its launch reading at six weeks against 15 for a pairing that reads like a template, and real craft in the product is noticed 42 times in a hundred against the board's 31. A named person also beats a partner brand on the odds of landing at all, 40 to 27, and you have run three person collaborations out of eighteen, none of them graded above 40 for that person's own involvement. The failure mode is casting for reach: the whole value here is an audience that is deep rather than wide, and a bigger name inverts both numbers. Judged on launch-week impressions it will look like the weakest of the three, and it is the one still paying in a year.",
              effect: {
                label: "CIQ",
                delta: 0.5,
                series: [0, 0.15, 0.3, 0.42, 0.5],
              },
              precedents: 3,
              reception:
                "person-made pairings on the board land 40 times in a hundred and keep a quarter of it",
              similar: [
                {
                  brand: "hermes",
                  brandName: "Hermès",
                  title: "The Stadium table by Barber and Osgerby",
                  pts: 46,
                },
                {
                  brand: "louis-vuitton",
                  brandName: "Louis Vuitton",
                  title: "The LV Crayon by Konstantin Grcic",
                  pts: 44,
                },
                {
                  brand: "zara",
                  brandName: "Zara",
                  title: "Aaron Levine's second drop",
                  pts: 34,
                },
              ],
            },
          ],
          footnote:
            "Together +2.4. One collaboration on its own is worth about two tenths of a point, because this lane already scores highest of the seven things you make — the gain here is a year of beats plus the lift every play on the shelf takes when the brand stops missing. Borrowed fandom rents an audience rather than owning one, which is why the bear calendar from the fandom answer runs alongside these three: the borrowed character buys the quarter, your own keeps the year.",
        },
        {
          kind: "trendCompare",
          insight:
            "The line steps rather than slopes, once a month after each launch, because a collaboration is read about a fortnight after it lands and either registers or does not. Left alone the lane drifts from twelve and a half to fourteen. With the three partners it reaches the low twenties, past McDonald's seventeen.",
          title: "Your collabs, month by month",
          provenance: "projected",
          note: "The average reception of your collaboration shelf, read every fortnight: with the three partners — each step is a launch read a month after the date — and on today's course, the current partner list running at its current hit rate. The shaded month is the first launch.",
          highlight: {
            from: "2027-01-01",
            to: "2027-01-31",
            label: "the character year opens",
          },
          series: [
            {
              key: "plan",
              label: "The three partners",
              color: "#4ADE80",
              points: [
                { d: "2026-10-01", v: 12 },
                { d: "2026-11-01", v: 12 },
                { d: "2026-12-01", v: 13 },
                { d: "2027-01-01", v: 13 },
                { d: "2027-02-01", v: 16 },
                { d: "2027-03-01", v: 16 },
                { d: "2027-04-01", v: 16 },
                { d: "2027-05-01", v: 19 },
                { d: "2027-06-01", v: 19 },
                { d: "2027-07-01", v: 19 },
                { d: "2027-08-01", v: 22 },
                { d: "2027-09-01", v: 22 },
              ],
            },
            {
              key: "base",
              label: "Today's course",
              color: "#94A3B8",
              ghost: true,
              points: [
                { d: "2026-10-01", v: 12 },
                { d: "2026-11-01", v: 12 },
                { d: "2026-12-01", v: 12 },
                { d: "2027-01-01", v: 13 },
                { d: "2027-02-01", v: 13 },
                { d: "2027-03-01", v: 13 },
                { d: "2027-04-01", v: 13 },
                { d: "2027-05-01", v: 13 },
                { d: "2027-06-01", v: 13 },
                { d: "2027-07-01", v: 14 },
                { d: "2027-08-01", v: 14 },
                { d: "2027-09-01", v: 14 },
              ],
            },
          ],
        },
        {
          kind: "impact",
          title: "What the three partners carry",
          provenance: "projected",
          delta: 2.4,
          body: "About two and a half points on your published score, from 81.3 to roughly 83.7 and past McDonald's 83.0 — the lane the engine weighs joint-heaviest and the one you already make best. It arrives as frequency, not volume: about ten of your eighteen collaborations a year noticed instead of five. One caution — a single collaboration is worth about two tenths on its own; the rest is the lift every play takes when the brand stops missing.",
        },
        {
          kind: "modelView",
          body: "Collaborations are the best thing you make — graded highest on paper, highest again after the reads, on the heaviest weight the engine carries — and you run fewer than almost anyone in food. Sort every collaboration on the board by the grade it was given before launch and only one thing moves: how often the reads find anything to measure. What they find, when they find it, is much the same size either way. A partner is a bet on whether there will be something to read, not on how loud it will be.\n\nYour own record is the sharpest version of that: you land harder than any brand in food and less often than the brand you are chasing. The making is fine; the picking is where the money goes. So the three partners do different jobs: a character licence buys the quarter on the shape with the best measured odds, a fandom fills the strategy that returns most and you have spent once, and a named maker in residence repairs the two questions that decide what is still being read six weeks later.",
        },
      ],
    },
    {
      id: "next-store-usa",
      // a store opening is an experience shelf
      itemType: "experience",
      type: "retail",
      question: "Where should we open our next store in USA?",
      aliases: [
        "where should we open our next store in the usa",
        "where should we open the next store in usa",
        "where to open our next store in usa",
        "where should our next us store be",
      ],
      askedOn: "2026-09-01",
      blocks: [
        {
          kind: "readout",
          provenance: "observed",
          verdict: "East Austin is your next door — go before the rent knows.",
        },
        {
          kind: "geoHotspots",
          title: "Where the signals are lighting up",
          provenance: "observed",
          note: "Dot brightness is composite site-signal strength — café-culture velocity, creator-economy leases, resale and pop-up activity, audience inflow. Tap a city for its early signals.",
          spots: [
            {
              id: "austin",
              city: "Austin",
              state: "TX",
              area: "East Austin",
              x: 47.0,
              y: 48.4,
              score: 87,
              momentum: 12,
              signals: [
                "Matcha and third-wave café openings up sharply year over year",
                "Creator-studio and vinyl-bar leases clustering east of I-35",
                "Fastest Gen Z inflow of any large US metro",
                "Café geo-tag content velocity in the national top three",
              ],
            },
            {
              id: "nashville",
              city: "Nashville",
              state: "TN",
              area: "Wedgewood-Houston",
              x: 65.9,
              y: 33.2,
              score: 84,
              momentum: 10,
              signals: [
                "Gallery-crawl and boutique-fitness density doubling",
                "Music-industry studios pulling daytime creative traffic",
                "Retail rents still a fraction of the Gulch next door",
              ],
            },
            {
              id: "miami",
              city: "Miami",
              state: "FL",
              area: "Little River",
              x: 77.3,
              y: 59.9,
              score: 81,
              momentum: 9,
              signals: [
                "Wynwood's creative overflow moving one neighborhood north",
                "Design-fair pop-ups running year-round, not seasonally",
                "Fashion flagships already scouting the corridor",
              ],
            },
            {
              id: "denver",
              city: "Denver",
              state: "CO",
              area: "RiNo",
              x: 34.5,
              y: 23.9,
              score: 78,
              momentum: 7,
              signals: [
                "Craft-roaster and taproom density at coastal levels",
                "Mural district drawing steady weekend footfall",
                "Outdoor-brand doors validating the corridor",
              ],
            },
            {
              id: "phoenix",
              city: "Phoenix",
              state: "AZ",
              area: "Roosevelt Row",
              x: 22.3,
              y: 40.2,
              score: 76,
              momentum: 8,
              signals: [
                "First Friday art walks among the largest in the country",
                "Drive-thru-free urban café formats piloting here first",
                "Population inflow without a premium café anchor yet",
              ],
            },
            {
              id: "columbus",
              city: "Columbus",
              state: "OH",
              area: "Short North",
              x: 72.4,
              y: 23.4,
              score: 74,
              momentum: 6,
              signals: [
                "The classic test-market instinct: national brands pilot here",
                "University adjacency with year-round daytime traffic",
                "Arch-district retail turning over to first-party doors",
              ],
            },
            {
              id: "slc",
              city: "Salt Lake City",
              state: "UT",
              area: "Granary District",
              x: 22.6,
              y: 21.3,
              score: 71,
              momentum: 7,
              signals: [
                "Warehouse-to-studio conversions accelerating",
                "Climbing gyms and roasteries sharing blocks",
              ],
            },
            {
              id: "charlotte",
              city: "Charlotte",
              state: "NC",
              area: "South End",
              x: 76.1,
              y: 35.6,
              score: 70,
              momentum: 5,
              signals: [
                "Rail-trail footfall compounding every quarter",
                "Athleisure doors clustering ahead of café culture",
              ],
            },
            {
              id: "la",
              city: "Los Angeles",
              state: "CA",
              area: "Silver Lake",
              x: 11.6,
              y: 38.5,
              score: 66,
              momentum: 2,
              signals: [
                "The pattern's proof case — already priced for flagships",
                "Still the reference profile the emerging spots rhyme with",
              ],
            },
          ],
        },
        {
          kind: "shift",
          title: "Your cultural pull, by US region",
          provenance: "measured",
          rows: [
            {
              label: "Sun Belt",
              context: "Austin · Nashville · Phoenix · Miami",
              value: "72",
              delta: 8.4,
              spark: [52, 55, 58, 61, 66, 72],
            },
            {
              label: "Mountain West",
              context: "Denver · Salt Lake City",
              value: "61",
              delta: 5.2,
              spark: [48, 50, 53, 55, 58, 61],
            },
            {
              label: "Midwest",
              context: "Chicago · Columbus — the quiet climber",
              value: "58",
              delta: 4.6,
              spark: [47, 49, 50, 53, 55, 58],
            },
            {
              label: "Northeast",
              context: "New York · Boston — flagship-priced",
              value: "64",
              delta: 2.1,
              spark: [59, 60, 61, 62, 63, 64],
            },
            {
              label: "Pacific Northwest",
              context: "Seattle · Portland — home turf, saturated",
              value: "68",
              delta: 1.2,
              spark: [66, 66, 67, 67, 68, 68],
            },
          ],
          note: "Pull index blends your measured cultural capital with regional attention share — where a new door borrows the most existing momentum.",
        },
        {
          kind: "openings",
          title: "Where the field is opening doors",
          provenance: "observed",
          note: "Recent first-party doors by culture-forward brands — when apparel scouts and coffee scouts agree on a neighborhood, the window is short. Filter by industry.",
          rows: [
            {
              brand: "glossier",
              brandName: "Glossier",
              industry: "beauty",
              city: "Brooklyn, NY",
              area: "Williamsburg",
              when: "2026-05-14",
              note: "Second Brooklyn door; opening-weekend queues",
            },
            {
              brand: "gentle-monster",
              brandName: "Gentle Monster",
              industry: "fashion",
              city: "Miami, FL",
              area: "Design District",
              when: "2026-03-20",
              note: "Installation-first flagship",
            },
            {
              brand: "nike",
              brandName: "Nike",
              industry: "fashion",
              city: "Austin, TX",
              area: "South Congress",
              when: "2026-06-05",
              note: "Run-club community format, not a mall box",
            },
            {
              brand: "aritzia",
              brandName: "Aritzia",
              industry: "fashion",
              city: "Austin, TX",
              area: "Domain NORTHSIDE",
              when: "2026-02-12",
            },
            {
              brand: "alo-yoga",
              brandName: "Alo Yoga",
              industry: "fashion",
              city: "Nashville, TN",
              area: "12South",
              when: "2026-04-09",
              note: "Studio-plus-café hybrid door",
            },
            {
              brand: "apple",
              brandName: "Apple",
              industry: "tech",
              city: "Miami, FL",
              area: "Worldcenter",
              when: "2026-01-24",
            },
            {
              brand: "arcteryx",
              brandName: "Arc'teryx",
              industry: "fashion",
              city: "Denver, CO",
              area: "RiNo",
              when: "2026-05-02",
              note: "Climbing wall in-store",
            },
            {
              brand: "blank-street",
              brandName: "Blank Street",
              industry: "food",
              city: "Chicago, IL",
              area: "Wicker Park",
              when: "2026-07-18",
              note: "First Midwest cluster — watch this one",
            },
            {
              brand: "pop-mart",
              brandName: "POP MART",
              industry: "lifestyle",
              city: "Los Angeles, CA",
              area: "The Grove",
              when: "2026-06-27",
              note: "Robo-shop vending format",
            },
            {
              brand: "uniqlo",
              brandName: "Uniqlo",
              industry: "fashion",
              city: "Columbus, OH",
              area: "Short North",
              when: "2026-08-08",
              note: "The test-market instinct again",
            },
            {
              brand: "lululemon",
              brandName: "Lululemon",
              industry: "fashion",
              city: "Charlotte, NC",
              area: "South End",
              when: "2026-03-06",
            },
            {
              brand: "dutch-bros",
              brandName: "Dutch Bros",
              industry: "food",
              city: "Phoenix, AZ",
              area: "Roosevelt Row",
              when: "2026-07-30",
              note: "Walk-up urban pilot, no drive-thru",
            },
          ],
        },
        {
          kind: "modelView",
          body: "Open where culture is still cheap. East Austin has the exact profile Silver Lake had before it priced out — creator leases first, cafés second, flagships last — and you want to be second, never last.\n\nWedgewood-Houston is the fast follower if Austin's corridor closes. The openings feed says fashion is already moving: when apparel scouts and coffee scouts agree on a neighborhood, the window is short. One door, placed early, beats three placed safely.",
        },
      ],
    },
    {
      id: "creatine-genz",
      // the answer frames it as a limited drop
      itemType: "capsule",
      type: "audience",
      question: "How would a drink with creatine perform with Gen Z?",
      aliases: [
        "how would a new drink with creatine perform with gen z",
        "how would a creatine drink perform with gen z",
        "would a creatine drink work with gen z",
        "how would a drink with creatine land with gen z",
      ],
      askedOn: "2026-09-02",
      blocks: [
        {
          kind: "readout",
          provenance: "projected",
          verdict:
            "Gen Z carries the spike; Millennials decide if it compounds.",
        },
        {
          kind: "cohorts",
          title: "Who moves first — and who makes it last",
          provenance: "projected",
          note: "Resonance is modeled receptivity to THIS concept per cohort, from your measured record with each. The fast clock spikes launch week; the slow clock compounds into habit and recall.",
          rows: [
            {
              id: "gen-z",
              label: "Gen Z",
              context: "the order they post",
              score: 84,
              role: "First-week engine",
              horizon: "short",
              drivers: [
                "Gym-tok already made creatine a lifestyle word, not a supplement word",
                "The performance-café occasion is theirs: pre-lift, post-class, before the shift",
                "Ingredient literacy means the story needs no translation",
                "A drink you can defend in the comments is a drink that gets posted",
              ],
            },
            {
              id: "millennials",
              label: "Millennials",
              context: "the order they repeat",
              score: 71,
              role: "Slow compounder",
              horizon: "long",
              drivers: [
                "Already buy protein coffee without calling it that",
                "Morning-routine loyalty converts a stunt into a staple",
                "App-order behavior makes the habit measurable within weeks",
              ],
            },
            {
              id: "gen-x",
              label: "Gen X",
              context: "follows the normalization",
              score: 46,
              role: "Quiet adopter",
              horizon: "long",
              drivers: [
                "Adopts once Millennials make it ordinary",
                "Trusts menu permanence over limited drops",
              ],
            },
            {
              id: "gen-alpha",
              label: "Gen Alpha",
              context: "sibling gravity only",
              score: 38,
              role: "Watches, doesn't buy",
              drivers: [
                "Sees it through older siblings' orders, not through ads",
                "A performance ingredient aimed younger would read wrong — leave them out of the story",
              ],
            },
            {
              id: "boomers",
              label: "Boomers",
              context: "not the audience",
              score: 21,
              role: "Sits this one out",
              drivers: [
                "Creatine still reads gym-shop, not café",
                "No cost to skipping them — this story doesn't need to be universal",
              ],
            },
          ],
        },
        {
          kind: "trendCompare",
          title: "Projected cultural pull, by generation",
          provenance: "projected",
          highlight: {
            from: "2026-09-14",
            to: "2026-10-12",
            label: "launch window",
          },
          note: "Pull index — the share of each cohort's cultural attention the drink captures, modeled from your cohort record. Gen Z spikes and cools; Millennials build slower and hold the level.",
          series: [
            {
              key: "genz",
              label: "Gen Z",
              color: "#8b7bff",
              points: [
                { d: "2026-09-15", v: 12 },
                { d: "2026-10-15", v: 64 },
                { d: "2026-11-15", v: 78 },
                { d: "2026-12-15", v: 61 },
                { d: "2027-01-15", v: 49 },
                { d: "2027-02-15", v: 42 },
              ],
            },
            {
              key: "millennials",
              label: "Millennials",
              color: "#4FC3F7",
              points: [
                { d: "2026-09-15", v: 8 },
                { d: "2026-10-15", v: 22 },
                { d: "2026-11-15", v: 37 },
                { d: "2026-12-15", v: 47 },
                { d: "2027-01-15", v: 55 },
                { d: "2027-02-15", v: 61 },
              ],
            },
            {
              key: "genx",
              label: "Gen X",
              color: "#94A3B8",
              ghost: true,
              points: [
                { d: "2026-09-15", v: 4 },
                { d: "2026-10-15", v: 9 },
                { d: "2026-11-15", v: 14 },
                { d: "2026-12-15", v: 19 },
                { d: "2027-01-15", v: 23 },
                { d: "2027-02-15", v: 26 },
              ],
            },
          ],
        },
        {
          kind: "modelView",
          body: "Treat creatine as a ritual, not a flavor. Gen Z will carry the launch if it lands as performance culture — the gym-adjacent morning, the order you post, the ingredient you can defend — and they will drop it the week it feels like a menu extension.\n\nMillennials are the quiet second act: they already buy protein coffee without calling it that, and habit is their love language. So ship the drop loud for the first cohort, then translate it into permanence for the second before the spike cools. Skip the youngest cohort entirely — a performance ingredient marketed downward is a story you don't want written.",
        },
      ],
    },
    {
      id: "audience-map",
      // who carries the brand, by generation
      itemType: "community",
      type: "audience",
      question: "Which generations carry which of our cultural products?",
      aliases: [
        "which generation carries which cultural product",
        "which audiences carry our cultural products",
        "who responds to which of our cultural products",
        "which generations respond to our cultural products",
      ],
      askedOn: "2026-09-01",
      blocks: [
        {
          kind: "readout",
          provenance: "measured",
          verdict:
            "You run on two engines: Gen Z powers the drops, Millennials hold the rituals.",
        },
        {
          kind: "matrix",
          title: "Product-type resonance, by generation",
          provenance: "measured",
          cols: ["Gen Alpha", "Gen Z", "Millennials", "Gen X"],
          note: "Cell brightness is modeled cohort resonance per product family, zero to one hundred. Tap a cell for the read.",
          rows: [
            {
              label: "Collabs",
              cells: [
                {
                  v: 72,
                  note: "Character collabs travel down through younger siblings first",
                },
                {
                  v: 92,
                  note: "The POP MART MOLLY window — your hottest cohort moment on record",
                },
                { v: 61 },
                { v: 34 },
              ],
            },
            {
              label: "Capsules",
              cells: [
                { v: 55 },
                {
                  v: 84,
                  note: "Pink Bearista™ resold in hours — scarcity reads as sport here",
                },
                { v: 58 },
                { v: 30 },
              ],
            },
            {
              label: "Content",
              cells: [
                { v: 38 },
                { v: 66 },
                {
                  v: 71,
                  note: "Story-form outperforms feed-form with this cohort",
                },
                { v: 52 },
              ],
            },
            {
              label: "Events",
              cells: [{ v: 30 }, { v: 58 }, { v: 69 }, { v: 47 }],
            },
            {
              label: "Experiences",
              cells: [
                { v: 26 },
                { v: 54 },
                {
                  v: 77,
                  note: "Reserve Roastery visits are planned pilgrimages — ritual behavior",
                },
                { v: 58 },
              ],
            },
            {
              label: "Merch",
              cells: [{ v: 44 }, { v: 76 }, { v: 63 }, { v: 41 }],
            },
            {
              label: "Community",
              cells: [
                { v: 22 },
                { v: 49 },
                {
                  v: 81,
                  note: "Your longest-hold signal — app streaks and store regulars",
                },
                { v: 64, note: "Quietly your second community cohort" },
              ],
            },
          ],
        },
        {
          kind: "modelView",
          body: "Read the map as two engines, not one audience. Your drops run on the cohort that treats scarcity as sport, and your rituals run on the one that treats your app as a morning habit — different products, different clocks, both yours.\n\nThe mistake would be pointing every product at the loudest cohort: collab energy doesn't convert into community capital, and community capital is what survives a quiet quarter. Feed each engine its own fuel and let the portfolio do the balancing.",
        },
      ],
    },
    {
      id: "markets-compare",
      // how the house's codes read market to market
      itemType: "brand_codes",
      type: "geo",
      question: "How does our cultural impact compare across markets?",
      aliases: [
        "compare our cultural impact across markets",
        "comparison of geographical impact",
        "how does our cultural pull differ by market",
        "which markets carry our cultural score",
      ],
      askedOn: "2026-08-31",
      blocks: [
        {
          kind: "readout",
          provenance: "measured",
          verdict:
            "North America pays the bills; the Pacific moves the culture.",
        },
        {
          kind: "shift",
          title: "Cultural pull, by market",
          provenance: "measured",
          rows: [
            {
              label: "North America",
              context: "home market · broad and slow",
              value: "74",
              delta: 2.1,
              spark: [70, 71, 71, 72, 73, 74],
            },
            {
              label: "Japan",
              context: "Seoul-adjacent halo, Tokyo flagships",
              value: "69",
              delta: 6.4,
              spark: [58, 60, 62, 64, 66, 69],
            },
            {
              label: "South Korea",
              context: "thisisneverthat still compounding",
              value: "66",
              delta: 7.2,
              spark: [55, 57, 59, 61, 64, 66],
            },
            {
              label: "Southeast Asia",
              context: "the Kopi Kolektif effect",
              value: "63",
              delta: 9.8,
              spark: [48, 51, 54, 57, 60, 63],
            },
            {
              label: "UK & Ireland",
              context: "steady, promotion-driven",
              value: "57",
              delta: 3.0,
              spark: [52, 53, 54, 55, 56, 57],
            },
            {
              label: "Middle East",
              context: "new flagships pulling ahead of retail",
              value: "52",
              delta: 5.6,
              spark: [44, 46, 47, 49, 50, 52],
            },
          ],
          note: "Pull index blends your measured cultural capital with each market's attention share — how hard your cultural record works there.",
        },
        {
          kind: "matrix",
          title: "What travels where",
          provenance: "measured",
          cols: ["Collabs", "Capsules", "Content", "Community"],
          note: "Cell brightness is how strongly a product family lands in that market. Tap a cell for the read.",
          rows: [
            {
              label: "North America",
              cells: [
                { v: 70 },
                { v: 64 },
                { v: 58 },
                {
                  v: 72,
                  note: "Home-market habit — the app is the third place",
                },
              ],
            },
            {
              label: "Japan",
              cells: [
                {
                  v: 88,
                  note: "The thisisneverthat pattern — local creator collabs outrun global campaigns",
                },
                { v: 81 },
                { v: 39 },
                { v: 41, note: "Your biggest format gap in any market" },
              ],
            },
            {
              label: "South Korea",
              cells: [{ v: 85 }, { v: 78 }, { v: 52 }, { v: 61 }],
            },
            {
              label: "Southeast Asia",
              cells: [
                { v: 66 },
                { v: 58 },
                {
                  v: 82,
                  note: "Kopi Creator Kolektif — creator series outrun paid content",
                },
                { v: 74 },
              ],
            },
            {
              label: "UK & Ireland",
              cells: [{ v: 58 }, { v: 49 }, { v: 61 }, { v: 63 }],
            },
            {
              label: "Middle East",
              cells: [
                { v: 61 },
                { v: 72, note: "Scarcity formats clear fastest here" },
                { v: 44 },
                { v: 38 },
              ],
            },
          ],
        },
        {
          kind: "radar",
          title: "The five capitals, market by market",
          provenance: "measured",
          note: "Each axis is one of the five cultural capitals your record builds, scored per market. Tap a market to morph the shape — the dashed outline is your all-markets profile.",
          axes: [
            "Scarcity",
            "Opacity",
            "Aesthetic innovation",
            "Fandom",
            "Identity signal",
          ],
          baseline: {
            id: "all",
            label: "All markets",
            values: [65, 47, 59, 61, 61],
          },
          series: [
            {
              id: "na",
              label: "North America",
              values: [62, 38, 51, 78, 66],
            },
            { id: "japan", label: "Japan", values: [81, 63, 72, 49, 70] },
            {
              id: "korea",
              label: "South Korea",
              values: [74, 55, 69, 58, 64],
            },
            {
              id: "sea",
              label: "Southeast Asia",
              values: [55, 41, 57, 82, 61],
            },
            { id: "uk", label: "UK & Ireland", values: [51, 39, 48, 57, 55] },
            { id: "me", label: "Middle East", values: [69, 47, 58, 41, 52] },
          ],
        },
        {
          kind: "modelView",
          body: "The home market buys permanence; the Pacific buys motion. Japan and Korea are doing what your biggest market can't — turning local creator collabs into items the rest of the world imports — while North America compounds slowly on habit and scale.\n\nThe play is direction, not volume: export the Seoul pattern westward before a rival does, and let content lead in Southeast Asia, where creator series outrun anything paid. A score that grows everywhere the same way is a score nobody is steering.",
        },
      ],
    },
    {
      id: "japan-community",
      itemType: "community",
      type: "geo",
      question: "How do we grow community capital in Japan?",
      aliases: [
        "how to grow community capital in japan",
        "how do we increase community capital in japan",
        "how to build community in japan",
      ],
      askedOn: "2026-08-30",
      blocks: [
        {
          kind: "readout",
          provenance: "projected",
          verdict: "Japan doesn't join brands — it joins rituals.",
        },
        {
          kind: "geoCompare",
          title: "Community capital, market by market",
          provenance: "measured",
          metric: "Community pull",
          focus: "japan",
          note: "Every market's community pull on one map — Japan is the ringed focus dot. Tap a market to read it against Japan; the arc is the gap you're closing.",
          markets: [
            {
              id: "japan",
              label: "Japan",
              x: 91.5,
              y: 12.6,
              value: 41,
              delta: 0.4,
              note: "Your biggest format gap anywhere — collabs run at 88 here while community sits at 41.",
            },
            {
              id: "sea",
              label: "Southeast Asia",
              x: 80.3,
              y: 25.3,
              value: 74,
              delta: 3.1,
              note: "The model to import — Kopi Kolektif turned creator series into store regulars.",
            },
            {
              id: "na",
              label: "North America",
              x: 26.3,
              y: 11.4,
              value: 72,
              delta: 1.9,
              note: "Home-market habit — the app is the third place.",
            },
            {
              id: "uk",
              label: "UK & Ireland",
              x: 50.9,
              y: 7.5,
              value: 63,
              delta: 1.4,
              note: "Steady club-of-regulars energy, promotion-led.",
            },
            {
              id: "korea",
              label: "South Korea",
              x: 87.0,
              y: 12.8,
              value: 61,
              delta: 2.6,
              note: "Seoul collab energy converting into regulars — the path Japan hasn't taken yet.",
            },
            {
              id: "me",
              label: "Middle East",
              x: 67.1,
              y: 17.2,
              value: 38,
              delta: 2.2,
              note: "Community younger than the doors — new flagships still forming their crowd.",
            },
          ],
        },
        {
          kind: "plays",
          title: "Three moves, built for Japan",
          provenance: "projected",
          plays: [
            {
              name: "Kissaten Hours — a members' quiet-morning ritual, one hour before open",
              itemType: "community",
              window: "October · Tokyo & Osaka flagships",
              strategies: [
                { code: "identity_signal", fit: 0.8 },
                { code: "fandom", fit: 0.6 },
              ],
              why: "Kissaten culture is the native shape of belonging here — a slow hour with its own etiquette borrows a century of meaning your app can't.",
              effect: {
                label: "CIQ",
                delta: 0.5,
                series: [0, 0.15, 0.3, 0.42, 0.5],
              },
            },
            {
              name: "The Stamp Rally — a neighborhood café crawl with local artist stamps",
              itemType: "event",
              window: "November · Shimokitazawa, Koenji, Nakameguro",
              strategies: [
                { code: "fandom", fit: 0.75 },
                { code: "aesthetic_innovation", fit: 0.6 },
              ],
              why: "Stamp rallies are Japan's own collection mechanic — completion culture that turns visiting a circuit of stores into a story, not a promotion.",
              effect: {
                label: "CIQ",
                delta: 0.3,
                series: [0, 0.1, 0.18, 0.25, 0.3],
              },
            },
            {
              name: "Club Matcha Lab — members co-create a Japan-only seasonal drink",
              itemType: "community",
              window: "January · app members, Japan only",
              strategies: [
                { code: "fandom", fit: 0.8 },
                { code: "scarcity", fit: 0.55 },
              ],
              why: "Co-creation converts customers into stakeholders — a drink members voted into existence is defended like their own work.",
              effect: {
                label: "CIQ",
                delta: 0.4,
                series: [0, 0.1, 0.22, 0.32, 0.4],
              },
            },
          ],
          footnote:
            "Projected effects assume typical execution on your measured record; they compound with your current Japan course, not on top of it.",
        },
        {
          kind: "trendCompare",
          title: "Japan community pull, with the slate",
          provenance: "projected",
          note: "Community pull index in Japan, modeled with and without the three moves — the distance between the lines is the slate.",
          series: [
            {
              key: "with",
              label: "With the slate",
              color: "#8b7bff",
              points: [
                { d: "2026-09-15", v: 41 },
                { d: "2026-10-15", v: 43 },
                { d: "2026-11-15", v: 45 },
                { d: "2026-12-15", v: 47 },
                { d: "2027-01-15", v: 50 },
                { d: "2027-02-15", v: 52 },
              ],
            },
            {
              key: "without",
              label: "Current course",
              color: "#94A3B8",
              ghost: true,
              points: [
                { d: "2026-09-15", v: 41 },
                { d: "2026-10-15", v: 41 },
                { d: "2026-11-15", v: 42 },
                { d: "2026-12-15", v: 42 },
                { d: "2027-01-15", v: 42 },
                { d: "2027-02-15", v: 43 },
              ],
            },
          ],
        },
        {
          kind: "modelView",
          body: "Japan won't join a fan club, but it will keep a ritual. Community there has to feel like a neighborhood habit with its own etiquette — the quiet morning, the stamp in the book, the seasonal drink members helped make — not a loyalty program wearing culture as a costume.\n\nStart small and specific in the flagships, let regulars set the tone, and resist scaling it the moment it works; the scarcity of it is the point. Your collabs opened the door in Japan — community is how you get invited to stay.",
        },
      ],
    },
    {
      id: "merch-clash",
      itemType: "merch",
      type: "forecast",
      question:
        "Dunkin is dropping a similar merch line — what happens to our holiday drop?",
      aliases: [
        "what happens to our holiday merch if dunkin drops a similar line",
        "dunkin is releasing similar merch what does it do to our drop",
        "what happens to our merch if dunkin copies it",
      ],
      askedOn: "2026-09-02",
      blocks: [
        {
          kind: "readout",
          provenance: "projected",
          verdict:
            "Ship first or ship stranger — second-and-similar is the only losing lane.",
        },
        {
          kind: "trendCompare",
          title: "Your drop's carry, clear vs contested",
          provenance: "projected",
          highlight: {
            from: "2026-11-16",
            to: "2026-12-14",
            label: "holiday window",
          },
          note: "Projected CIQ carry of the same drop in a clear window vs landing second — the distance between the lines is the similarity tax.",
          series: [
            {
              key: "clear",
              label: "Clear window",
              color: "#8b7bff",
              points: [
                { d: "2026-11-02", v: 0 },
                { d: "2026-11-16", v: 0.6 },
                { d: "2026-11-30", v: 1.3 },
                { d: "2026-12-14", v: 1.9 },
                { d: "2026-12-28", v: 1.7 },
                { d: "2027-01-11", v: 1.4 },
              ],
            },
            {
              key: "contested",
              label: "After Dunkin's line",
              color: "#94A3B8",
              ghost: true,
              points: [
                { d: "2026-11-02", v: 0 },
                { d: "2026-11-16", v: 0.2 },
                { d: "2026-11-30", v: 0.5 },
                { d: "2026-12-14", v: 0.8 },
                { d: "2026-12-28", v: 0.7 },
                { d: "2027-01-11", v: 0.6 },
              ],
            },
          ],
        },
        {
          kind: "plays",
          title: "Three ways to defend the drop",
          provenance: "projected",
          plays: [
            {
              name: "Move the window — teaser now, drop before theirs opens",
              itemType: "merch",
              window: "late September · all channels",
              strategies: [{ code: "scarcity", fit: 0.7 }],
              why: "The first mover owns the comparison; the calendar is the whole tax.",
            },
            {
              name: "Ship stranger — pull the Mofusand collab language into the line",
              itemType: "merch",
              window: "unchanged date · reworked drop",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.75 },
                { code: "fandom", fit: 0.7 },
              ],
              why: "Character-world merch can't be read as an echo of a color-story line — similarity is a choice, not a fate.",
            },
            {
              name: "Bundle the habit — the drop unlocks through app streaks",
              itemType: "community",
              window: "with the drop · app members first",
              strategies: [
                { code: "fandom", fit: 0.8 },
                { code: "opacity", fit: 0.5 },
              ],
              why: "A rival can copy a cup; they can't copy a streak. Anchoring merch to the ritual moves the fight onto ground only you hold.",
            },
          ],
        },
        {
          kind: "modelView",
          body: "Similarity is only a tax if you pay it. Second-and-similar is the one losing lane, and it's the lane the calendar is steering you into — so either move first or stop being similar.\n\nMoving first is cleaner: their teaser cadence gives you the window, and the first mover owns the comparison. If the date can't move, change the language instead — fold your character-collab world into the line until a side-by-side stops being possible. And whatever ships, tie it to the habit only you own; a rival can copy a cup, not a streak.",
        },
      ],
    },
    {
      id: "collagen-signal",
      // the lead move is a foam on a drink — the capsule is the format the
      // signal lands in
      itemType: "capsule",
      type: "signal",
      question: "Is collagen about to shake up the beverage bar?",
      aliases: [
        "is collagen shaking up the beverage bar",
        "is collagen the next cold foam",
        "collagen is shaking things up",
        "collagen is shaking up the beverage bar",
        "should we put collagen on the menu",
        "how would a collagen drink perform",
        "is the collagen drink a real signal",
        "is collagen a real signal",
        "collagen drinks",
        "collagen",
      ],
      askedOn: "2026-10-23",
      blocks: [
        {
          kind: "readout",
          provenance: "observed",
          verdict:
            "Yes. The signal is real, it is early, and it is arriving through the beauty aisle. The crowd is large, the money is small, and no chain at your scale has claimed the café ritual yet.",
        },
        {
          kind: "stats",
          provenance: "observed",
          insight:
            "The crowd already drinks for a function, and most of the collagen money is still in a jar at home. The drink is the smaller half and the faster-growing one. So far it has shipped as a season's celebrity matcha at a juice chain and a two-dollar boost at a drive-thru, not yet as a ritual. The people who would buy it are already in your line for a matcha.",
          items: [
            {
              label: "Collagen drinks, worldwide",
              value: "$431M",
              hint: "this year's market in collagen drinks, growing about nine percent a year toward $671M by 2031; the wider collagen-infused beverage category was $1.66B last year",
            },
            {
              label: "Gen Z on functional drinks",
              value: "80%",
              hint: "drink a functional beverage at least every two weeks, against 65% of all adults — this spring's read of the US and Brazil",
            },
            {
              label: "Function-forward, Gen A and Z",
              value: "71%",
              hint: "look for drinks that do something — energy, protein, gut, skin — the beverage industry's own read of the two youngest generations this spring",
            },
            {
              label: "Beauty as the reason",
              value: "33%",
              hint: "name skin, hair or beauty as the main reason they take a supplement; collagen is now the second most common ingredient on beauty-claim launches, 'mainstream like protein or fiber'",
            },
          ],
        },
        {
          kind: "shift",
          insight:
            "The door is already open. Protein went from a gym powder to the most popular add-on on your menu inside a year, on the same crowd that now asks what a drink does for them. Collagen is the next ingredient with that shape. Two in five people doubt the claim, so the ritual sells and the promise does not.",
          title: "What the crowd is doing",
          provenance: "observed",
          note: "Seven public numbers from the past year. Each line says what the figure counts and why it matters here; the last one is the risk, not a signal.",
          rows: [
            {
              label: "Protein reaches your menu",
              context:
                "Share of the drinks on your menu that can take protein milk or protein foam, one year after the foam launched. The option reaches nearly the whole menu; the foam is already your most-popular add-on.",
              value: "~90%",
            },
            {
              label: "Add-ons as a business",
              context:
                "What customisation is worth to you as a business, the add-ons and modifiers included. A new foam walks through a door that already takes a billion a year.",
              value: "≈ $1B",
            },
            {
              label: "Users want a new form",
              context:
                "Share of people who already take collagen and say they want to try it in a new format. The crowd exists and is asking for the next form; a drink is the nearest one.",
              value: "1 in 3",
            },
            {
              label: "Drinks that reflect identity",
              context:
                "Share of Gen Z and Gen Alpha, aged 13 to 29, who say they choose a drink because it reflects who they are. A glow drink says something; a gym shake says gym.",
              value: "58%",
            },
            {
              label: "The feed picks the drink",
              context:
                "Share of Gen Z and Gen Alpha who say what friends, creators and their feeds are drinking shapes what they order. Over half of them also want limited-edition drops.",
              value: "63%",
            },
            {
              label: "#collagen on TikTok",
              context:
                "Views on the tag today, across about 846,000 posts. The drink tag alone holds 313,000 posts: the ritual already exists on the feed.",
              value: "4.6B",
            },
            {
              label: "Skeptics of functional claims",
              context:
                "Share of consumers who doubt that functional food and drink deliver the health benefit they promise. Europe's regulator has approved no collagen health claim, so the drink sells on ritual, never on a promise.",
              value: "~40%",
            },
          ],
        },
        {
          kind: "trendCompare",
          insight:
            "Collagen coffee is searched about eight times as often as two years ago and is still a sliver of protein coffee. That is what protein looked like before it was on your menu. The curve says early, not the level. The spring spike followed the launches, and this winter is the test.",
          title: "Attention, three words",
          provenance: "observed",
          note: "Worldwide Google search interest for the three phrases, monthly means from January 2024 to September 2026, on one scale — the busiest month of any of the three reads 100. Protein coffee is the door that opened on your menu; matcha latte the ritual already in your cup; collagen coffee the signal, small and climbing. The shaded months are the collagen café opening at Ulta, the sparkling can and your protein foam reaching Europe. October is left off as a part month.",
          highlight: {
            from: "2026-03-01",
            to: "2026-04-30",
            label: "the launches land",
          },
          series: [
            {
              key: "collagen",
              label: "collagen coffee",
              color: "#22D3EE",
              points: [
                { d: "2024-01-01", v: 2 },
                { d: "2024-02-01", v: 2 },
                { d: "2024-03-01", v: 1 },
                { d: "2024-04-01", v: 1 },
                { d: "2024-05-01", v: 1 },
                { d: "2024-06-01", v: 2 },
                { d: "2024-07-01", v: 2 },
                { d: "2024-08-01", v: 2 },
                { d: "2024-09-01", v: 2 },
                { d: "2024-10-01", v: 2 },
                { d: "2024-11-01", v: 1 },
                { d: "2024-12-01", v: 1 },
                { d: "2025-01-01", v: 2 },
                { d: "2025-02-01", v: 2 },
                { d: "2025-03-01", v: 2 },
                { d: "2025-04-01", v: 2 },
                { d: "2025-05-01", v: 2 },
                { d: "2025-06-01", v: 2 },
                { d: "2025-07-01", v: 3 },
                { d: "2025-08-01", v: 5 },
                { d: "2025-09-01", v: 5 },
                { d: "2025-10-01", v: 5 },
                { d: "2025-11-01", v: 6 },
                { d: "2025-12-01", v: 4 },
                { d: "2026-01-01", v: 5 },
                { d: "2026-02-01", v: 6 },
                { d: "2026-03-01", v: 10 },
                { d: "2026-04-01", v: 17 },
                { d: "2026-05-01", v: 15 },
                { d: "2026-06-01", v: 11 },
                { d: "2026-07-01", v: 5 },
                { d: "2026-08-01", v: 6 },
                { d: "2026-09-01", v: 8 },
              ],
            },
            {
              key: "protein",
              label: "protein coffee",
              color: "#FB923C",
              points: [
                { d: "2024-01-01", v: 10 },
                { d: "2024-02-01", v: 9 },
                { d: "2024-03-01", v: 9 },
                { d: "2024-04-01", v: 10 },
                { d: "2024-05-01", v: 9 },
                { d: "2024-06-01", v: 11 },
                { d: "2024-07-01", v: 10 },
                { d: "2024-08-01", v: 10 },
                { d: "2024-09-01", v: 10 },
                { d: "2024-10-01", v: 9 },
                { d: "2024-11-01", v: 9 },
                { d: "2024-12-01", v: 9 },
                { d: "2025-01-01", v: 15 },
                { d: "2025-02-01", v: 14 },
                { d: "2025-03-01", v: 16 },
                { d: "2025-04-01", v: 17 },
                { d: "2025-05-01", v: 16 },
                { d: "2025-06-01", v: 20 },
                { d: "2025-07-01", v: 22 },
                { d: "2025-08-01", v: 30 },
                { d: "2025-09-01", v: 29 },
                { d: "2025-10-01", v: 24 },
                { d: "2025-11-01", v: 28 },
                { d: "2025-12-01", v: 23 },
                { d: "2026-01-01", v: 37 },
                { d: "2026-02-01", v: 37 },
                { d: "2026-03-01", v: 57 },
                { d: "2026-04-01", v: 100 },
                { d: "2026-05-01", v: 99 },
                { d: "2026-06-01", v: 71 },
                { d: "2026-07-01", v: 41 },
                { d: "2026-08-01", v: 34 },
                { d: "2026-09-01", v: 44 },
              ],
            },
            {
              key: "matcha",
              label: "matcha latte",
              color: "#4ADE80",
              ghost: true,
              points: [
                { d: "2024-01-01", v: 19 },
                { d: "2024-02-01", v: 20 },
                { d: "2024-03-01", v: 23 },
                { d: "2024-04-01", v: 23 },
                { d: "2024-05-01", v: 22 },
                { d: "2024-06-01", v: 22 },
                { d: "2024-07-01", v: 24 },
                { d: "2024-08-01", v: 26 },
                { d: "2024-09-01", v: 25 },
                { d: "2024-10-01", v: 25 },
                { d: "2024-11-01", v: 23 },
                { d: "2024-12-01", v: 23 },
                { d: "2025-01-01", v: 29 },
                { d: "2025-02-01", v: 32 },
                { d: "2025-03-01", v: 42 },
                { d: "2025-04-01", v: 45 },
                { d: "2025-05-01", v: 46 },
                { d: "2025-06-01", v: 43 },
                { d: "2025-07-01", v: 47 },
                { d: "2025-08-01", v: 54 },
                { d: "2025-09-01", v: 42 },
                { d: "2025-10-01", v: 36 },
                { d: "2025-11-01", v: 34 },
                { d: "2025-12-01", v: 34 },
                { d: "2026-01-01", v: 42 },
                { d: "2026-02-01", v: 69 },
                { d: "2026-03-01", v: 43 },
                { d: "2026-04-01", v: 51 },
                { d: "2026-05-01", v: 52 },
                { d: "2026-06-01", v: 53 },
                { d: "2026-07-01", v: 51 },
                { d: "2026-08-01", v: 57 },
                { d: "2026-09-01", v: 53 },
              ],
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "Collagen is already in cups, at the small end of the grid. A juice chain sold it as a celebrity matcha for a season. A drive-thru sells it as protein and never says the word. A Los Angeles grocer has made it a glow smoothie for four years. Each of them holds one piece of the play: the ritual, the add-on, the beauty code. The two pieces you already own, matcha and function, sit in the first row beside an empty cell. The beauty cell between them is dark only because nobody in food and drink has more than two loose plays there. No chain at your scale has put the whole play in one cup. That empty cell is the signal, and it is yours to fill first.",
          title: "Who on the board is near the signal",
          provenance: "measured",
          note: "Every play on the record since April for eight food and drink brands, counted by what it was near — matcha, a functional claim, a beauty code, collagen itself — each column scaled to its busiest brand. Collagen alone is counted over every cup a brand has ever named, since this year alone would read one apiece. Tap a cell for the plays.",
          cols: ["Matcha", "Function", "Beauty-coded", "Collagen"],
          rows: [
            {
              label: "Starbucks",
              cells: [
                {
                  v: 100,
                  note: "Six matcha drinks since spring — lavender, blue coconut, mango cream, caramelised banana, pumpkin cream, the orange cream collection — the most on the board, and the best-read drinks you own.",
                },
                {
                  v: 48,
                  note: "Ten functional plays: the energy refreshers, the protein coffee in India, the protein pocket, the weighted vest. Second only to Red Bull, whose whole shelf is function.",
                },
                {
                  v: 100,
                  note: "Two plays near a beauty code — the Inner Light aura room for a spring drink and the Dandy orange cream drop — the most on this grid, and only because nobody in food and drink has more. The code is not yours yet.",
                },
                {
                  v: 0,
                  note: "No collagen play in any market — the US, Europe, Asia-Pacific, Korea and Japan newsrooms all checked. The lane is open.",
                },
              ],
            },
            {
              label: "7-Eleven",
              cells: [
                {
                  v: 50,
                  note: "Three — a matcha latte launch with a coconut-water brand, a Magnum matcha crumble, matcha donuts under a Kyoto tea house's name. Read by nobody.",
                },
                {
                  v: 5,
                  note: "One: the Replenish electrolyte Slurpee.",
                },
                {
                  v: 50,
                  note: "One, loosely — the same H2Coco matcha launch, staged as a live brand experience.",
                },
                { v: 0, note: "None." },
              ],
            },
            {
              label: "Red Bull",
              cells: [
                { v: 0, note: "None — the can has never gone green." },
                {
                  v: 100,
                  note: "Twenty-one plays, every one of them a functional claim by definition: the editions, the McDonald's energizers, the racing cans. Function is the whole brand, and the editions were mostly not read.",
                },
                { v: 0, note: "None." },
                { v: 0, note: "None." },
              ],
            },
            {
              label: "Coca-Cola",
              cells: [
                { v: 0, note: "None." },
                {
                  v: 14,
                  note: "Three — the caffeine-free and zero-sugar packs, a soda flavour for a fast-food chain.",
                },
                {
                  v: 50,
                  note: "One, loosely — a limited pack tied to a film.",
                },
                { v: 0, note: "None." },
              ],
            },
            {
              label: "McDonald's",
              cells: [
                { v: 0, note: "None." },
                {
                  v: 5,
                  note: "One: the Red Bull energizers — read at seventy-three for presence, the best-received functional drink on the food board, and it was a partner's can.",
                },
                { v: 0, note: "None." },
                { v: 0, note: "None." },
              ],
            },
            {
              label: "Joe & the Juice",
              cells: [
                {
                  v: 17,
                  note: "One: a limited-edition matcha with a TV presenter's name on it, this spring, in every one of the chain's ninety-one British bars — matcha, black sesame, sea salt and agave on a pea milk.",
                },
                {
                  v: 5,
                  note: "One, the same drink: collagen is the function, sold in the chain's own words as an answer to demand for functional options.",
                },
                {
                  v: 50,
                  note: "One, the same drink again: a celebrity face, a plumping pun in the headline, a drink made to be posted. The nearest thing to your Glow play anyone has shipped.",
                },
                {
                  v: 25,
                  note: "One cup: the one named collagen drink a café chain has put on every menu — a season's limited edition in one country, by a juice bar with five hundred shops in twenty-five markets. Matcha first, collagen second, the glow implied and never claimed.",
                },
              ],
            },
            {
              label: "7 Brew",
              cells: [
                {
                  v: 0,
                  note: "None — a drive-thru coffee chain, no matcha on the sign.",
                },
                {
                  v: 5,
                  note: "One: a two-dollar protein boost on any drink — coffee, energy, soda, lemonade, shake — fifteen grams a serving, Dallas–Fort Worth from August and every one of its nine hundred stands from October. Sold as protein.",
                },
                {
                  v: 0,
                  note: "None — the boost is unflavoured and unglamorous by design.",
                },
                {
                  v: 25,
                  note: "One cup, and the boost is collagen: unflavoured collagen protein, the whole fifteen grams, and the word left the nationwide release — guests asked for protein, so protein is what it is called. Collagen in the cup, not on the sign: the exact opposite of a glow.",
                },
              ],
            },
            {
              label: "Erewhon",
              cells: [
                {
                  v: 0,
                  note: "None with collagen in it — the matcha smoothie on the standing menu is a matcha, not a glow.",
                },
                {
                  v: 5,
                  note: "One: July's limited-edition silver smoothie with a home-goods brand — electrolytes, creatine, collagen, magnesium — twelve dollars at select stores.",
                },
                {
                  v: 50,
                  note: "The skin smoothie that started the ritual is still on the menu — the strawberry glaze Hailey Bieber built on vanilla collagen peptides, the cup the glow-smoothie feed was made from. The Rhode founder's own precedent for the third move.",
                },
                {
                  v: 100,
                  note: "Four cups, the most anywhere: the grocer's smoothie bar has had collagen in a named cup for four years — the strawberry glaze, a coconut cloud, a model's island glow, this summer's silver smoothie with creatine — every one a limited drop with a face or a brand on it. A bar, not a chain: fourteen stores in Southern California.",
                },
              ],
            },
          ],
        },
        {
          kind: "arena",
          insight:
            "Your crowd reads function when it arrives as a flavour and a colour. The matcha drops and the energy refreshers are the best-read drinks you own. The same crowd turns on function when it arrives as a gym claim. Collagen has to land in the first lane, as a glow and not a dose. The beauty lane is empty across the whole board, so the band there is nobody's record yet.",
          title: "Your functional shelf",
          provenance: "measured",
          unit: "strength of the play · 0–100",
          note: "Every wellness-adjacent play on your shelf since April, sorted by the function it carried, on one scale. Bands are the food board's range for drops on that lane — typical to ceiling; the ring is the field's strongest play there, the dots are yours. Received = the best presence the crowd gave any of them. Tap a lane for the read.",
          rows: [
            {
              id: "matcha",
              label: "Matcha drops",
              sub: "the seasonal cup",
              typical: 30,
              ceiling: 33,
              best: {
                brandName: "7-Eleven",
                title: "H2Coco matcha latte launch",
                pts: 20,
              },
              received: 73,
              own: [
                { title: "Orange Cream collection", pts: 35 },
                { title: "Lavender Matcha", pts: 33 },
                { title: "Blue Coconut Matcha", pts: 33 },
                { title: "Iced Pumpkin Cream Matcha", pts: 30 },
                { title: "Iced Mango Cream Matcha", pts: 30 },
                { title: "Caramelised Banana Matcha", pts: 29 },
              ],
              note: "Six matcha drinks since spring, every one graded at or above the food board's band, and the best-read drinks you own — the orange cream collection and the pumpkin matcha praised at seventy-three. Matcha is the one wellness ingredient the crowd already reads as a Starbucks ritual; the only other matcha drink on the board is a convenience-store launch nobody read.",
            },
            {
              id: "energy",
              label: "Energy refreshers",
              sub: "caffeine as a flavour",
              typical: 26,
              ceiling: 29,
              best: {
                brandName: "McDonald's",
                title: "Red Bull Dragonberry Energizer",
                pts: 32,
              },
              received: 60,
              own: [
                { title: "New York Energy Refresher", pts: 50 },
                { title: "Fanatics Fest Refresher", pts: 41 },
                { title: "Blended Pink Energy", pts: 25 },
                { title: "Passionfruit Guava Energy", pts: 24 },
                { title: "Mango Dragonfruit Energy", pts: 21 },
              ],
              note: "The busiest functional lane on the food board — eighteen drops, half of them yours — and the one where you sit above the field: the New York refresher is the strongest drink on the board this year. Energy sold as a fruit and a colour is received; Red Bull's own editions, sold as energy, were not read once.",
            },
            {
              id: "protein",
              label: "Protein",
              sub: "the gym claim",
              typical: 26,
              ceiling: 26,
              received: 27,
              own: [
                { title: "SuperYou protein coffee, India", pts: 32 },
                { title: "Starbucks Weighted Vest", pts: 27 },
                { title: "Chicken Bacon Protein Pocket", pts: 25 },
              ],
              note: "The protein foam itself is older than this record, so the shelf holds what came after it: a protein coffee in India read with more criticism than praise, a weighted vest the crowd mocked, a protein pocket nobody mentioned. No other food brand on the board has a protein drop at all. The add-on in nine of ten drinks is received as a ritual; protein as a claim is not.",
            },
            {
              id: "wellness",
              label: "Wellness objects",
              sub: "the vest, the aura room",
              typical: 25,
              ceiling: 27,
              best: {
                brandName: "Dior",
                title: "Haute Wellness collection (beauty board)",
                pts: 26,
              },
              received: 47,
              own: [
                { title: "Weighted Vest, with KBB by Kahlana", pts: 34 },
                { title: "Inner Light aura activation", pts: 30 },
                { title: "Weighted Vest, the drop", pts: 27 },
              ],
              note: "Wellness as an object sits in a narrow band everywhere — Dior's yoga mats and bottles graded the same as your vest — and your vest is the one play on this shelf the crowd read below zero. Wellness the crowd wears is a costume; wellness it drinks is a habit.",
            },
            {
              id: "beauty",
              label: "Beauty in a cup",
              sub: "the lane nobody holds",
              typical: 25,
              ceiling: 27,
              best: {
                brandName: "Dior",
                title: "Facials and Matcha day (beauty board)",
                pts: 25,
              },
              status: "recommended",
              note: "Empty on your shelf and nearly empty on the board: a facials-and-matcha day at Dior, a kombucha at Aesop, a free matcha pop-up at Sephora — beauty houses borrowing the cup for an afternoon, each read once or not at all. The band is low because nobody has brought the whole ritual: a drink, a name, a cup worth posting. This is the row the moves are about.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "Flavour wins and claims lose. Every drink that landed was sold as a season, a colour or a day out. The two plays sold as fitness are the only ones read below zero. A collagen drink has to be a glow you can taste and post, never a supplement with a straw.",
          title: "Strength against reception",
          provenance: "measured",
          mode: "pair",
          a: { label: "Strength", color: "#FDE047" },
          b: { label: "Reception", color: "#4ADE80" },
          note: "Eight functional plays from your own shelf: how strongly each was graded on paper against the net reception the crowd gave it in its launch window. The two rows with a tail were read below zero. Tap a row for the read.",
          rows: [
            {
              id: "pumpkin",
              label: "Iced Pumpkin Cream Matcha",
              sub: "August · matcha, a season",
              a: 30,
              b: 46,
              note: "A seasonal matcha praised at forty-six in its first window — a wellness ingredient sold as autumn, and the crowd read the season, not the ingredient.",
            },
            {
              id: "fanatics",
              label: "Fanatics Fest Refresher",
              sub: "July · energy, a day out",
              a: 41,
              b: 53,
              note: "Energy sold as a day out: the best-received drink on this list, a refresher that existed for one festival.",
            },
            {
              id: "pink",
              label: "Blended Pink Energy",
              sub: "July · energy, a colour",
              a: 25,
              b: 47,
              note: "Graded low on paper, received high — a pink drink with caffeine in it reads as a Pink Drink first and an energy drink second.",
            },
            {
              id: "banana",
              label: "Caramelised Banana Matcha",
              sub: "June · matcha, a flavour",
              a: 29,
              b: 27,
              note: "Read three windows running, never below fourteen: the most durable matcha of the summer.",
            },
            {
              id: "coconut",
              label: "Blue Coconut Matcha",
              sub: "June · matcha, a colour",
              a: 33,
              b: 27,
              note: "Four reads, one of them negative — the colour carried it and the flavour divided it.",
            },
            {
              id: "dragonfruit",
              label: "Mango Dragonfruit Energy",
              sub: "July · energy, sold as energy",
              a: 21,
              b: 13,
              note: "Low on paper, low in the crowd: the one energy drink on the list sold as energy rather than as a fruit.",
            },
            {
              id: "protein-india",
              label: "Protein coffee, India",
              sub: "May · protein, an actor's name",
              a: 32,
              b: 0,
              tail: "read −6",
              tailLow: true,
              note: "Graded the strongest play on this list — a protein coffee with an actor's name on it — and the only drink here with more criticism than praise. The gym claim came before the drink.",
            },
            {
              id: "vest",
              label: "The Weighted Vest",
              sub: "May · wellness, a costume",
              a: 27,
              b: 0,
              tail: "read −13",
              tailLow: true,
              note: "The one play on your record the crowd mocked. Wellness worn is a joke; wellness poured is a ritual.",
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "Your drinks run on one strategy, the aesthetic, and it works. The empty half of the chart is where a collagen drink gets its edge. Opacity is one door, because a secret-menu order is the one route protein foam never used. The identity signal is the other. You have never once put it in a cup, even though the crowd picks a drink for what it says about them.",
          title: "Which strategy carries a drink",
          provenance: "measured",
          x: {
            label: "How often you play it",
            low: "never",
            high: "every drop",
            hint: "your drink drops since April that carry each cultural strategy, as a share of the busiest one",
          },
          y: {
            label: "How the crowd received it",
            low: "silent",
            high: "praised",
            hint: "the presence and praise the crowd gave the drops carrying each strategy on your measured record, as a share of the best-received one (fandom, sixty)",
          },
          quadrants: {
            tr: "Your habit, and it works",
            tl: "Rare, and it works",
            br: "Your habit, ignored",
            bl: "Never played",
          },
          note: "The five cultural strategies on your fifty-five drink drops since April. Across is how often each one is played on a drink; up is how the crowd received the drops that carried it, against the best-received strategy. Dot size is the valence the strategy earned. Tap a dot for the read.",
          wLabel: "valence",
          points: [
            {
              id: "aesthetic",
              label: "Aesthetic innovation",
              era: "The strategy you play",
              color: "#FDE047",
              x: 97,
              y: 73,
              w: 50,
              status: "current",
              note: "Fifty-five drinks, almost every one: the colour, the foam, the new flavour. Received at forty-four on the record — your default, and second only to fandom in reception: the strategy a collagen drink naturally wears.",
            },
            {
              id: "opacity",
              label: "Opacity",
              short: "Secret menu",
              era: "The door protein never used",
              color: "#B388FF",
              x: 24,
              y: 57,
              w: 48,
              status: "recommended",
              note: "Thirteen drinks — the secret-menu orders, the unannounced ones — received at thirty-four on the record. Opacity is the strategy of withholding, and the one whose best Starbucks plays are drinks. Protein foam was announced; a glow foam that leaks as a secret-menu order a week before it is announced is opacity played on a drink.",
            },
            {
              id: "scarcity",
              label: "Scarcity",
              era: "The seasonal shape",
              color: "#FB923C",
              x: 13,
              y: 63,
              w: 60,
              note: "Seven drinks, received at thirty-eight on the record with the best valence of the five — a drink for one festival, one summer. The seasonal drop is the shape your matcha already runs on.",
            },
            {
              id: "fandom",
              label: "Fandom",
              era: "A crowd attached",
              color: "#FF7AB8",
              x: 5,
              y: 90,
              w: 75,
              note: "Three drinks and the highest reception on the chart — the Pink Energy and the Fanatics refreshers, drinks with a crowd already attached. Rare, and it works; a beauty partner's crowd is the fandom a collagen drink could borrow.",
            },
            {
              id: "identity",
              label: "Identity signal",
              era: "Never in a cup",
              color: "#60A5FA",
              x: 2,
              y: 3,
              w: 10,
              note: "Never played on a drink. Forty-four identity plays on your record and not one of them a cup — strange for a brand whose cup is the identity. The crowd choosing a drink for what it says about them is the opening.",
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "Gen Z carries the first week and Millennials keep it. Millennials already stir collagen into coffee at home, and the foam moves that habit into your café. The generations that doubt the claim still drink the coffee, which is one more reason to sell the drink as a glow and never as a dose.",
          title: "Who carries it, who keeps it",
          provenance: "projected",
          note: "Five generations scored for how much of a collagen drink's first year each would carry — from your shelf's record, what the crowd says it drinks and the collagen buyer's profile — and the clock each runs on, a launch spike or capital that compounds. Tap a crowd for the drivers.",
          rows: [
            {
              id: "gen-z",
              label: "Gen Z",
              context: "the matcha line, the feed, the glow",
              score: 82,
              role: "First-week engine",
              horizon: "short",
              drivers: [
                "Eight in ten drink something functional at least every other week, more than any other generation",
                "Chooses a drink for what it says about them and buys what the feed drinks — a glow foam is a post before it is a drink",
                "Already in your line for the matcha and the pink drinks, the best-read things you make",
                "The collagen feed is already enormous — billions of views on the tag, hundreds of thousands of posts on the drink alone — and this is the crowd that buys what the feed drinks",
              ],
            },
            {
              id: "millennials",
              label: "Millennials",
              context: "the scoop already in their coffee",
              score: 74,
              role: "Slow compounder",
              horizon: "long",
              drivers: [
                "The collagen-in-coffee habit already exists at home: the biggest powder brand's whole business is a scoop stirred into a morning cup",
                "Your largest generation of product fans, and the base the spikes sit on",
                "Pays for protein foam today; a second foam is a modifier, not a conversion",
              ],
            },
            {
              id: "gen-x",
              label: "Gen X",
              context: "collagen's supplement core",
              score: 52,
              role: "Quiet adopter",
              horizon: "long",
              drivers: [
                "The supplement aisle's biggest collagen buyer — joints and skin, bought for a decade",
                "Will order it without posting it: the money without the moment",
              ],
            },
            {
              id: "boomers",
              label: "Boomers",
              context: "the claim, not the cup",
              score: 34,
              role: "Reads the label",
              horizon: "long",
              drivers: [
                "Doubts functional claims at the highest rate of any generation, and the regulator agrees",
                "Drinks the coffee, skips the foam",
              ],
            },
            {
              id: "gen-alpha",
              label: "Gen Alpha",
              context: "watching the older sibling's cup",
              score: 30,
              role: "Watches first",
              horizon: "short",
              drivers: [
                "Function-forward at the same rate as Gen Z, with no money of their own yet",
                "The pink-drink crowd of the next five years; the glow cup is what they will have seen first",
              ],
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "Everything collagen is sold in today is either big and far from a café, like a Japanese pharmacy bottle or a scoop at the kitchen counter, or café-shaped and tiny inside a beauty store. The corner where a café order meets scale has one occupant, your protein foam. That is the shape the ingredient is waiting for.",
          title: "Where collagen already lives",
          provenance: "observed",
          x: {
            label: "From the aisle to the cup",
            low: "a jar at home",
            high: "a café order",
            hint: "how close each product is to a drink someone orders and carries — a supplement at home, a can on a shelf, a café",
          },
          y: {
            label: "How big it is",
            low: "a launch",
            high: "a category",
            hint: "the product's scale today — sales, doors, years on the market — from the public record",
          },
          quadrants: {
            tr: "A café order, at scale",
            tl: "At scale, at home",
            br: "A café order, new",
            bl: "New, on a shelf",
          },
          note: "Where collagen is sold today, by how close it is to a café order and how big it is, from the public record. Colour is the aisle it came from; dot size is scale; the haloed dot is the empty corner. Tap a dot for the read.",
          wLabel: "scale",
          open: "glow",
          points: [
            {
              id: "shiseido",
              label: "Shiseido and Meiji, Japan",
              era: "Beauty aisle",
              color: "#F472B6",
              x: 22,
              y: 92,
              w: 70,
              note: "The origin: Shiseido's collagen drink since the nineties, Meiji's powder the top seller in Japan — beauty-from-within is thirty years old in Asia, and the region grows fastest. Drunk at home from a small bottle, bought in a pharmacy.",
            },
            {
              id: "vital-scoop",
              label: "Vital Proteins, the scoop",
              era: "Supplement",
              color: "#94A3B8",
              x: 48,
              y: 86,
              w: 66,
              note: "The category's biggest name, owned by Nestlé: a scoop stirred into a morning coffee at home. The habit a café foam would move into your line already exists at the kitchen counter.",
            },
            {
              id: "vital-can",
              label: "Vital Proteins sparkling",
              era: "Can",
              color: "#60A5FA",
              x: 40,
              y: 38,
              w: 30,
              note: "Launched in March: a collagen sparkling water with vitamin C, three flavours, no sugar — the supplement brand becoming a drink. A can on a shelf, not yet an order.",
            },
            {
              id: "cans",
              label: "Monster, Unwell",
              era: "Can",
              color: "#60A5FA",
              x: 36,
              y: 44,
              w: 28,
              note: "An energy giant and a podcast brand both put collagen in a can this year — the ingredient reads as mainstream enough to sit beside caffeine.",
            },
            {
              id: "cloud",
              label: "Clöud Café at Ulta",
              era: "Beauty aisle",
              color: "#F472B6",
              x: 76,
              y: 22,
              w: 24,
              note: "A Korean-inspired collagen café — coffee, matcha, milk tea with marine collagen — opened inside a beauty retailer in March, with a strawberry matcha and a pistachio matcha as exclusives. Café-shaped, sold where the skincare is: the closest anyone has come to the corner you own, from the other side.",
            },
            {
              id: "nexus",
              label: "Nexus collagen cold foam",
              era: "Supplement",
              color: "#94A3B8",
              x: 62,
              y: 10,
              w: 14,
              note: "A powder sold to make collagen cold foam at home — somebody has already named the product; nobody has put it on a menu.",
            },
            {
              id: "protein-foam",
              label: "Your protein cold foam",
              era: "Your cup",
              color: "#FDE047",
              x: 96,
              y: 80,
              w: 60,
              status: "current",
              note: "The precedent in the corner: the same shape — an ingredient from the supplement aisle, sold as a foam on any cold drink — became your most-popular add-on inside a year and went to Europe and Asia in its second. Collagen is the next ingredient with that shape.",
            },
            {
              id: "glow",
              label: "Glow Cold Foam",
              short: "Glow",
              era: "Your cup",
              color: "#FDE047",
              x: 92,
              y: 56,
              w: 40,
              status: "recommended",
              note: "The empty seat: a collagen foam sold as a glow, on the menu that already turned protein into a ritual. The first move below.",
            },
          ],
        },
        {
          kind: "plays",
          insight:
            "Three moves and one rule. Sell the glow, never the claim. The foam does the volume, the named drink does the season, and the beauty partner does the crowd. Together they are worth about two points, most of it from an add-on you already know how to launch.",
          title: "Three moves, priced",
          provenance: "projected",
          plays: [
            {
              name: "Glow Cold Foam — a collagen and vitamin C foam on any cold drink, seeded a week early as a secret-menu order, then launched the way protein foam was",
              itemType: "capsule",
              window:
                "Secret-menu seeding in late January, on the menu for the spring launch; Europe and Asia a season later, the protein route",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.6 },
                { code: "opacity", fit: 0.4 },
              ],
              why: "Protein foam proved the door: an ingredient from the supplement aisle became the most-popular add-on on your menu inside a year, with no health claim. A second foam is a modifier your customers already pay for, and the secret-menu week is the one door protein never used — opacity's best plays on your record are drinks. Sold as a glow you can taste, never as a dose.",
              effect: {
                label: "CIQ",
                delta: 0.9,
                series: [0, 0.3, 0.6, 0.8, 0.9],
              },
              precedents: 3,
              reception:
                "function sold as a flavour reads 46 to 53 on your shelf; the beauty field's wellness drops are received at 40 to 47",
              similar: [
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "McDonald's x Red Bull Dragonberry Energizer",
                  pts: 32.0,
                },
                {
                  brand: "netflix",
                  brandName: "Netflix",
                  title: "Anua x KPop Demon Hunters Skincare Collection",
                  pts: 31.9,
                },
                {
                  brand: "dior",
                  brandName: "Dior",
                  title: "Haute Wellness Dior Collection",
                  pts: 25.7,
                },
              ],
            },
            {
              name: "Strawberry Matcha Glow — the Ulta café's drink in your cup: a strawberry matcha under the glow foam, a named seasonal with a cup worth posting",
              itemType: "capsule",
              window:
                "A spring seasonal, March to May — the window collagen searches peaked in this year; back as a summer drop if it is read",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.6 },
                { code: "scarcity", fit: 0.4 },
              ],
              why: "Matcha is the one wellness ingredient the crowd already reads as your ritual — six drinks since spring, the best-read on your shelf — and the beauty-store café's exclusives are a strawberry matcha and a pistachio matcha. A named seasonal is the shape your matcha already runs on, and scarcity carries the best valence of your five strategies on drinks.",
              effect: {
                label: "CIQ",
                delta: 0.6,
                series: [0, 0.2, 0.4, 0.55, 0.6],
              },
              precedents: 3,
              reception:
                "your matcha seasonals read 27 to 46 in their first window; the board's other matcha drinks were not read at all",
              similar: [
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "7-Eleven Limited Edition Magnum Matcha Crumble",
                  pts: 29.4,
                },
                {
                  brand: "dior",
                  brandName: "Dior",
                  title: "A Day with Dior Beauty: Facials and Matcha",
                  pts: 24.5,
                },
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title:
                    "H2Coco Matcha Latte and Strawberry Matcha Launch Live Brand Experience",
                  pts: 20.2,
                },
              ],
            },
            {
              name: "Rhode × Starbucks Glow Cup — the glow foam in a Rhode-coded cup, one limited drop with the lip case's crowd: beauty meeting the beverage bar in a single object",
              itemType: "collab",
              brand: "rhode",
              window:
                "One drop in the spring window, app members first; a second colour for summer if the first sells out",
              strategies: [
                { code: "identity_signal", fit: 0.4 },
                { code: "fandom", fit: 0.4 },
              ],
              why: "You have never put an identity signal in a cup, and your three fandom drinks are the best-received on the chart; Rhode has twenty-three beauty-coded plays and the fandom your drinks lack. The board's one beauty-meets-fandom drop — a skincare line for a Netflix cartoon — was read in four windows running. A collab is the lane you land hardest in and play least.",
              effect: {
                label: "CIQ",
                delta: 0.7,
                series: [0, 0.25, 0.5, 0.65, 0.7],
              },
              precedents: 3,
              reception:
                "beauty-meets-crowd drops are read at 40 to 53 on the board; your own best collab read 58",
              similar: [
                {
                  brand: "sephora",
                  brandName: "Sephora",
                  title: "Sephora Summer Club",
                  pts: 42.2,
                },
                {
                  brand: "netflix",
                  brandName: "Netflix",
                  title: "Anua x KPop Demon Hunters Skincare Collection",
                  pts: 31.9,
                },
                {
                  brand: "aesop",
                  brandName: "Aesop",
                  title: "Aesop x Wonderbrew Custom Kombucha Flavors",
                  pts: 27.1,
                },
              ],
            },
          ],
          footnote:
            "Projected effects are priced on your measured record and the public facts in the file header, and compound with your current course. No move carries a skin claim. Europe's regulator declines collagen health claims and two in five consumers doubt them, so each move is sold as a ritual.",
        },
        {
          kind: "impact",
          title: "What the three moves carry",
          provenance: "projected",
          delta: 2.2,
          body: "About two points on your published score, from 84.95 to roughly 87.2. Most of it comes from a foam that follows the protein route, the one add-on that already turned a supplement into a ritual. The qualifier is the claim. None of the three moves says 'skin', because two in five people doubt it and the regulator agrees. Sold as a glow, the moves earn their price. Sold as a dose, the weighted vest is the precedent.",
        },
        {
          kind: "modelView",
          body: "Collagen is real and it is early, and the tell is where it is arriving from. Very little of it comes through coffee. A juice chain sold a celebrity matcha for a season, and a drive-thru sells a two-dollar boost as protein. Most of it comes through the beauty aisle. A Korean café is opening inside a beauty retailer, a supplement brand has turned itself into a sparkling water, and the ingredient has been a drink in Japan for a generation. The crowd that buys beauty from within is large and already in your line for a matcha.\n\nThe money is small, and that is the point of an early signal. No chain at your scale has played it, so there is no reading at your scale yet. What can be read is the shape. Protein took the same route, a supplement-aisle ingredient sold as a foam with no claim attached, and became the add-on most of your drinks now carry. Your own shelf says how it has to land. Function received as a flavour, a colour or a season is praised. Function received as a gym claim is the only thing your crowd has read below zero.\n\nSo the answer is a ritual, not a supplement. Own the aesthetic of the glow before a beauty retailer owns the café. Seed it the way your secret menu seeds everything. Skip the claim entirely, because the regulator will not let you make it and the crowd does not need it.",
        },
      ],
    },
    {
      id: "micro-orders-signal",
      // the lead move is a drink size, the Short on the menu board, so the
      // capsule is the format the signal lands in
      itemType: "capsule",
      type: "signal",
      question: "Are micro-orders the next shape of the cup?",
      aliases: [
        // aliases are PRE-normalized (utils/labText): a hyphen is a space
        "micro orders are having a moment",
        "are micro orders a real signal",
        "are micro orders the next shape of the cup",
        "is the small cup a real signal",
        "should we sell smaller drinks",
        "tiny cups big energy",
        "is the aerocano a real signal",
        "should the short size go on the menu",
        "micro orders",
        "small cups",
        "espresso shots",
      ],
      askedOn: "2026-10-23",
      blocks: [
        {
          kind: "readout",
          provenance: "observed",
          verdict:
            "Yes. The micro-order is real, and it is already your best new drink, a smaller, stronger, faster cup. Nobody owns the ritual around it yet. That ritual is the small cup at a small price, bought in the afternoon as a little treat.",
        },
        {
          kind: "stats",
          provenance: "observed",
          insight:
            "The crowd moved to the shot before any chain moved the cup. Nearly half of coffee drinkers have an espresso drink in a given week, three in ten say they want it smaller and stronger, and half of Gen Z buy themselves a small treat every week. Your aerated shot sold a million cups in its first week in Korea. The order got smaller on its own. The cup and the price have not followed it yet.",
          items: [
            {
              label: "Espresso drinks, past week",
              value: "47%",
              hint: "of US coffee drinkers had an espresso-based drink in the past week this autumn, a record, up from four in ten four years ago. The past-day figure is at its high too.",
            },
            {
              label: "Prefer it smaller and stronger",
              value: "30%",
              hint: "of US adults in a summer poll like their coffee smaller and stronger. Two in five want it larger and milkier. Most had never had a cortado, and two in three were interested once it was described.",
            },
            {
              label: "Gen Z buying little treats weekly",
              value: "52%",
              hint: "of Gen Z adults buy themselves a small treat every week or more, and nine in ten do it at all. Food and drink is the top category, and coffee is the third-favourite drink to treat yourself to.",
            },
            {
              label: "The Aerocano's first week",
              value: "1M cups",
              hint: "sold in Korea in the first seven days this February, the fastest iced launch in that market's history. Three shots in a small cup, with no new machine behind it.",
            },
          ],
        },
        {
          kind: "shift",
          insight:
            "Both halves of the signal are growing. Espresso shots are rising at the register and the little treat is a weekly habit, and both land in the afternoon window you have said you want. The one thing the crowd says it will not buy is a smaller drink for more money, and your new small strong drink is priced above the Americano. The order is the signal. The price is the risk.",
          title: "What the crowd is doing",
          provenance: "observed",
          note: "Seven public numbers from the past year. Each line says what the figure counts and why it matters here. The last one is the risk, not a signal.",
          rows: [
            {
              label: "Espresso shots on the till",
              context:
                "Change in espresso-shot sales across about a hundred and sixty thousand restaurant tills last year against the year before, while frappés, drip coffee and cold brew fell. The small strong order is growing at the register as well as in the surveys.",
              value: "+3.3%",
            },
            {
              label: "Beverage-only trips, afternoon",
              context:
                "Share of restaurant visits where a drink is the whole order that fall at lunch or in the mid-afternoon, from this year's industry read. Half of Gen Z make one every week. That window is the second peak you have said you want to build.",
              value: "69%",
            },
            {
              label: "A drink as the snack",
              context:
                "Share of US adults who say they sometimes have a beverage in place of a snack, up seven points in five years. The little cup competes with a bag of chips, not with a bigger cup.",
              value: "68%",
            },
            {
              label: "Gen Z at small coffee chains",
              context:
                "Growth in Gen Z card spending at small and mid-sized coffee chains this June against last June, the strongest of any generation. The crowd that buys small treats is spending more on coffee, and it is spending it with smaller chains.",
              value: "+25%",
            },
            {
              label: "The mini can, sold alone",
              context:
                "The price of a seven-and-a-half-ounce cola can sold as a single in US convenience stores since January. Mini-can volume grew high single digits in the first quarter, ahead of the brand as a whole. A small size at an entry price sells on the shelf next door.",
              value: "$1.29",
            },
            {
              label: "Smaller for a smaller price",
              context:
                "Share of GLP-1 users who say restaurants have not adapted and want smaller portions at proportionally lower prices. A fifth of US households now include a user. Small is a health read as much as a money one.",
              value: "35%",
            },
            {
              label: "Not worth the price",
              context:
                "Share of US adults in a summer poll who say a smaller coffee may not feel worth the price. Another fifth say the serving looks too small. This is the risk, because your new small strong drink costs more than the Americano it is built on.",
              value: "26%",
            },
          ],
        },
        {
          kind: "trendCompare",
          insight:
            "The small strong drink is the one line that climbs every year. It stepped up the month your cortado went on the menu, rose again in the Aerocano's Korea weeks, and is at its high now that the shot has landed at home. The big sweet cup spikes for two weeks when a unicorn comes back and then falls to where it was. The iced Americano barely registers in English search, because the world calls it an Americano and Korea searches in Korean. Interest moved to the small cup before the price did.",
          title: "Attention, three words",
          provenance: "observed",
          note: "Worldwide Google search interest for the three phrases, monthly means from January 2024 to September 2026, on one scale. The busiest month of any of the three reads 100. Cortado is the small strong cup. Iced americano is the base the Aerocano was built on, Korea's cup. Frappuccino is the big sweet cup, the one falling at the register. The shaded weeks are the Aerocano's launch in Korea. October is left off as a part month.",
          highlight: {
            from: "2026-02-01",
            to: "2026-03-31",
            label: "the Aerocano lands",
          },
          series: [
            {
              key: "cortado",
              label: "cortado",
              color: "#22D3EE",
              points: [
                { d: "2024-01-01", v: 49 },
                { d: "2024-02-01", v: 53 },
                { d: "2024-03-01", v: 53 },
                { d: "2024-04-01", v: 50 },
                { d: "2024-05-01", v: 51 },
                { d: "2024-06-01", v: 49 },
                { d: "2024-07-01", v: 50 },
                { d: "2024-08-01", v: 52 },
                { d: "2024-09-01", v: 57 },
                { d: "2024-10-01", v: 54 },
                { d: "2024-11-01", v: 52 },
                { d: "2024-12-01", v: 59 },
                { d: "2025-01-01", v: 77 },
                { d: "2025-02-01", v: 63 },
                { d: "2025-03-01", v: 65 },
                { d: "2025-04-01", v: 62 },
                { d: "2025-05-01", v: 57 },
                { d: "2025-06-01", v: 60 },
                { d: "2025-07-01", v: 62 },
                { d: "2025-08-01", v: 77 },
                { d: "2025-09-01", v: 75 },
                { d: "2025-10-01", v: 72 },
                { d: "2025-11-01", v: 71 },
                { d: "2025-12-01", v: 70 },
                { d: "2026-01-01", v: 76 },
                { d: "2026-02-01", v: 72 },
                { d: "2026-03-01", v: 78 },
                { d: "2026-04-01", v: 73 },
                { d: "2026-05-01", v: 79 },
                { d: "2026-06-01", v: 85 },
                { d: "2026-07-01", v: 77 },
                { d: "2026-08-01", v: 79 },
                { d: "2026-09-01", v: 88 },
              ],
            },
            {
              key: "frappuccino",
              label: "frappuccino",
              color: "#FB923C",
              points: [
                { d: "2024-01-01", v: 32 },
                { d: "2024-02-01", v: 33 },
                { d: "2024-03-01", v: 36 },
                { d: "2024-04-01", v: 34 },
                { d: "2024-05-01", v: 35 },
                { d: "2024-06-01", v: 36 },
                { d: "2024-07-01", v: 36 },
                { d: "2024-08-01", v: 38 },
                { d: "2024-09-01", v: 35 },
                { d: "2024-10-01", v: 32 },
                { d: "2024-11-01", v: 31 },
                { d: "2024-12-01", v: 30 },
                { d: "2025-01-01", v: 29 },
                { d: "2025-02-01", v: 33 },
                { d: "2025-03-01", v: 38 },
                { d: "2025-04-01", v: 38 },
                { d: "2025-05-01", v: 39 },
                { d: "2025-06-01", v: 41 },
                { d: "2025-07-01", v: 52 },
                { d: "2025-08-01", v: 50 },
                { d: "2025-09-01", v: 38 },
                { d: "2025-10-01", v: 36 },
                { d: "2025-11-01", v: 37 },
                { d: "2025-12-01", v: 35 },
                { d: "2026-01-01", v: 38 },
                { d: "2026-02-01", v: 35 },
                { d: "2026-03-01", v: 38 },
                { d: "2026-04-01", v: 43 },
                { d: "2026-05-01", v: 47 },
                { d: "2026-06-01", v: 49 },
                { d: "2026-07-01", v: 48 },
                { d: "2026-08-01", v: 100 },
                { d: "2026-09-01", v: 48 },
              ],
            },
            {
              key: "americano",
              label: "iced americano",
              color: "#4ADE80",
              ghost: true,
              points: [
                { d: "2024-01-01", v: 3 },
                { d: "2024-02-01", v: 3 },
                { d: "2024-03-01", v: 4 },
                { d: "2024-04-01", v: 5 },
                { d: "2024-05-01", v: 5 },
                { d: "2024-06-01", v: 5 },
                { d: "2024-07-01", v: 5 },
                { d: "2024-08-01", v: 5 },
                { d: "2024-09-01", v: 4 },
                { d: "2024-10-01", v: 4 },
                { d: "2024-11-01", v: 4 },
                { d: "2024-12-01", v: 2 },
                { d: "2025-01-01", v: 4 },
                { d: "2025-02-01", v: 4 },
                { d: "2025-03-01", v: 5 },
                { d: "2025-04-01", v: 5 },
                { d: "2025-05-01", v: 5 },
                { d: "2025-06-01", v: 5 },
                { d: "2025-07-01", v: 6 },
                { d: "2025-08-01", v: 7 },
                { d: "2025-09-01", v: 5 },
                { d: "2025-10-01", v: 4 },
                { d: "2025-11-01", v: 4 },
                { d: "2025-12-01", v: 4 },
                { d: "2026-01-01", v: 5 },
                { d: "2026-02-01", v: 5 },
                { d: "2026-03-01", v: 6 },
                { d: "2026-04-01", v: 7 },
                { d: "2026-05-01", v: 8 },
                { d: "2026-06-01", v: 9 },
                { d: "2026-07-01", v: 8 },
                { d: "2026-08-01", v: 8 },
                { d: "2026-09-01", v: 8 },
              ],
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "Read as one grid, the small cup is split four ways and nobody holds more than two of them. You hold the drink. Every espresso-forward play on the board is yours, and the one small cup with a crowd behind it is your Korean launch. A fast-food chain holds the price, with a one-dollar small coffee locked for the year. The cola holds the small can on the shelf. The convenience store holds the mini as an object. The British chain has sold a small size for years and never made it a moment. Your row has one dark cell, and it sits beside your fullest one. That cell is the small cup at an entry price. Nobody owns the ritual yet, because nobody has put the drink and the price in the same cup.",
          title: "Who on the board plays small",
          provenance: "measured",
          note: "Every play on the record since April for seven coffee and drink brands, counted by what it was near: an espresso-forward drink, a mini format, an entry price, a small cup sold as a moment. Each column is scaled to its busiest brand. One play on a column nobody else plays reads as a step, not a hundred. Tap a cell for the plays.",
          cols: [
            "Espresso-forward",
            "Mini format",
            "Entry price",
            "Small as a ritual",
          ],
          rows: [
            {
              label: "Starbucks",
              cells: [
                {
                  v: 100,
                  note: "Seven since spring: the Aerocano in four markets, the pecan cortado's return, the film's character orders, the Jay Chou Aerocano series. Every espresso-forward drink on the food board is one of yours. Nobody else plays the shot.",
                },
                {
                  v: 75,
                  note: "Three: the seasonal mini pies, the Mini Bearista blind box sold as an add-on with any drink, and the cortado that exists in the Short size only. The mini is an object or a pastry on your shelf, and the Short is still off the board.",
                },
                {
                  v: 0,
                  note: "None. The Aerocano arrived at home priced above the Americano it is built on, a small strong drink without the small price. This is the empty cell in your row.",
                },
                {
                  v: 100,
                  note: "One, and it is the only cell on the grid with a measured crowd: the Aerocano in Korea, a million cups in a week, read in four windows running. The small cup as a moment exists in one market, without its price.",
                },
              ],
            },
            {
              label: "McDonald's",
              cells: [
                {
                  v: 0,
                  note: "None. The coffee is drip, and the espresso is a latte.",
                },
                {
                  v: 75,
                  note: "Three: the Lil Happy Meal, the mini hash browns' return in Britain, the mini lamps. The hash browns were the lowest-graded play on this list and among the best read, a loved thing made smaller.",
                },
                {
                  v: 100,
                  note: "Two: a one-dollar small coffee locked for the whole year in Canada, and the under-three-dollar tier at home. The price promise is the play, and no brand on the grid states it as clearly.",
                },
                {
                  v: 0,
                  note: "None. The small coffee is a price, never a moment.",
                },
              ],
            },
            {
              label: "Coca-Cola",
              cells: [
                { v: 0, note: "None. No coffee on the shelf this year." },
                {
                  v: 75,
                  note: "Three: the cherry float in the small can, the collectible mini cans for the anniversary, the custom bottles. The crowd read the mini cans in five windows running.",
                },
                {
                  v: 50,
                  note: "One: the small can sold alone at the lowest entry price in the range, since January, with its volume growing ahead of the brand's. Small at an entry price, on a shelf.",
                },
                {
                  v: 0,
                  note: "None. A can on a shelf is a size, not a ritual.",
                },
              ],
            },
            {
              label: "7-Eleven",
              cells: [
                { v: 0, note: "None. The coffee is a self-serve urn." },
                {
                  v: 100,
                  note: "Four, the most on the board, and every one an object: the mini city, the mini sneaker blind boxes with a running brand, the Sanrio mini boxes, the anniversary minis. The mini sells as a thing to collect.",
                },
                {
                  v: 0,
                  note: "None that lasts. A large coffee at a small price for one day a year, then back to full price.",
                },
                { v: 0, note: "None." },
              ],
            },
            {
              label: "Red Bull",
              cells: [
                { v: 0, note: "None." },
                {
                  v: 0,
                  note: "None by name. The small can is its only size, so small is never a play. It is the brand.",
                },
                { v: 0, note: "None. The can is never discounted." },
                { v: 0, note: "None." },
              ],
            },
            {
              label: "Luckin",
              cells: [
                {
                  v: 14,
                  note: "One step: the whole menu is an espresso cup, sold by the app. A category rather than a play.",
                },
                { v: 0, note: "None. One size, one price." },
                {
                  v: 50,
                  note: "One: a two-dollar cup on the app, the hook that built thirty-six thousand stores. The price is the play, the cup is not small, and the same-store line has turned down this year.",
                },
                {
                  v: 0,
                  note: "None. The hook is the price, never the size.",
                },
              ],
            },
            {
              label: "Costa",
              cells: [
                {
                  v: 14,
                  note: "One step: the flat white is the house cup. It is on the board as a size, not a drop.",
                },
                {
                  v: 25,
                  note: "One: the eight-ounce Mini, on the menu for years, from about a pound and a half.",
                },
                {
                  v: 50,
                  note: "One: the Mini's price, lower still with a reusable cup. A bakery chain overtook it this year on a cheaper Americano.",
                },
                {
                  v: 25,
                  note: "A size on the board, never a moment. It is the nearest thing to the Short anyone sells, and nobody has ever queued for it.",
                },
              ],
            },
          ],
        },
        {
          kind: "arena",
          insight:
            "The smallest drinks you make are graded the lowest on paper, and the crowd reads them for the longest. The aerated shot sits under the band, and the crowd read it in four windows running. The big shaken cup sits above the band, and the crowd read it once, at volume. The crowd reads mini objects everywhere on the board. The last lane is nobody's record. No brand here has sold a small cup at an entry price as a drink, so its band is borrowed from the mini format and is waiting for a first play.",
          title: "Your small shelf",
          provenance: "measured",
          unit: "strength of the play · 0–100",
          note: "Every small, strong or mini play on your shelf since April, sorted by lane, on one scale. Bands are the food board's range for drops on that lane, typical to ceiling. The ring is the field's strongest play there, and the dots are yours. Received is the best presence the crowd gave any of them. The last lane borrows the mini format's band, since no brand has a play on it. Tap a lane for the read.",
          rows: [
            {
              id: "aerocano",
              label: "Aerocano",
              sub: "the aerated shot",
              typical: 28,
              ceiling: 29,
              received: 67,
              own: [
                { title: "Aerocano, North America", pts: 28 },
                { title: "Aerocano, Latin America", pts: 26 },
                { title: "Aerocano, Asia-Pacific", pts: 25 },
                { title: "Honey Ruby Grapefruit Aerocano", pts: 24 },
              ],
              note: "Four launches of one drink, every one graded under the band. The crowd read the Asian one in four windows, which makes it the most durable drink on this shelf. No other food brand has an espresso-forward drop, so the band is your own record. The home launch is too new to have been read.",
            },
            {
              id: "cortado",
              label: "Cortado and the Short",
              sub: "the small cup you sell",
              typical: 28,
              ceiling: 29,
              received: 67,
              own: [
                { title: "The Devil Wears Prada orders", pts: 30 },
                { title: "Pecan Cortado return", pts: 27 },
              ],
              note: "The one drink on your menu that comes in the Short only. Its appearances on the record are a film's character orders, which the crowd read at sixty-seven and then mocked, and this autumn's pecan return, not yet read. The small cup exists on your board as a seasonal flavour, never as a size with a name.",
            },
            {
              id: "shaken",
              label: "Shaken espresso",
              sub: "the big espresso cup",
              typical: 31,
              ceiling: 36,
              received: 73,
              own: [
                { title: "Iced Pumpkin Cream Shaken Espresso", pts: 41 },
                { title: "Iced Horchata Shaken Espresso", pts: 31 },
                { title: "Crème Brûlée Shaken Espresso", pts: 26 },
              ],
              note: "For scale, the espresso drink sold big and sweet. The pumpkin one is the strongest play on this shelf and your biggest fall launch day. The crowd read the horchata one in five windows running. The shot carries the big cup too. The crowd is buying espresso in every size you sell it.",
            },
            {
              id: "mini",
              label: "Mini objects",
              sub: "the pies, the blind box",
              typical: 30,
              ceiling: 32,
              best: {
                brandName: "Coca-Cola",
                title: "Cherry Float in 7.5-oz mini cans",
                pts: 29,
              },
              received: 53,
              own: [
                { title: "Seasonal Mini Pies", pts: 27 },
                { title: "Mini Bearista Blind Box", pts: 27 },
              ],
              note: "The busiest small lane on the board, with twelve drops. One of yours was read: the Mini Bearista sold as an add-on with any drink, which the crowd read in three windows running. The cola's small can is the field's best here. Mini as an object is received everywhere. Mini as a drink is only the can.",
            },
            {
              id: "entry",
              label: "The small cup at an entry price",
              sub: "the lane nobody holds",
              typical: 30,
              ceiling: 32,
              best: {
                brandName: "McDonald's",
                title: "Mini Hash Browns return",
                pts: 19,
              },
              received: 67,
              status: "recommended",
              note: "Empty on your shelf and empty as a drink across the board. The nearest play is a fast-food chain bringing back a small, cheap, loved thing in Britain. It is the lowest-graded play on this shelf and one of the best read. The band is borrowed because nobody has sold a small coffee at a small price as a moment. This is the row the moves are about.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "The crowd reads the small strong drink where it is new and has a texture. It reads a character's order once and then mocks it. It reads the add-on with a small object in every window. It grades the big espresso cup highest and reads it once. The pattern says the small cup needs a texture, a name and a thing to hold, not a film and not a size on a board.",
          title: "Strength against reception",
          provenance: "measured",
          mode: "pair",
          a: { label: "Strength", color: "#FDE047" },
          b: { label: "Reception", color: "#4ADE80" },
          note: "Eight small or espresso-forward plays from your own shelf. Each row shows how strongly the play was graded on paper against the best net reception the crowd gave it in a read window. A grey tail marks a play too new to have been read. A red one was read below zero later. Tap a row for the read.",
          rows: [
            {
              id: "aerocano-apac",
              label: "Aerocano, Asia-Pacific",
              sub: "June · the aerated shot",
              a: 25,
              b: 67,
              note: "Graded under the band, and the crowd read it in four windows running, the most durable drink on your small shelf. A shot with a texture and a name, in a market that drinks its Americano iced.",
            },
            {
              id: "honey-ruby",
              label: "Honey Ruby Grapefruit Aerocano",
              sub: "June · a fruit on the shot",
              a: 24,
              b: 53,
              note: "Climbed window by window from silence to fifty-three. The flavoured small cup took three reads to land, and then it landed.",
            },
            {
              id: "aerocano-latam",
              label: "Aerocano, Latin America",
              sub: "May · the same shot, divided",
              a: 26,
              b: 40,
              tail: "then −14",
              tailLow: true,
              note: "Praised at forty in its first window and read below zero in the second. This is the one market where the small strong drink divided the room. Same drink, different coffee culture.",
            },
            {
              id: "aerocano-na",
              label: "Aerocano, North America",
              sub: "September · the home launch",
              a: 28,
              b: 0,
              tail: "too new to read",
              note: "The strongest of the four launches on paper, and not yet read. It is priced above the Americano at home. The first window will say whether the crowd minds.",
            },
            {
              id: "pecan-cortado",
              label: "Pecan Cortado return",
              sub: "October · the small cup, back",
              a: 27,
              b: 0,
              tail: "not yet read",
              note: "The Short-only drink returning as a flavour, launched beside the pumpkin cream. Not yet read. The autumn window will tell.",
            },
            {
              id: "dwp-orders",
              label: "The Devil Wears Prada orders",
              sub: "April · a character's order",
              a: 30,
              b: 67,
              tail: "then −13",
              tailLow: true,
              note: "A doppio and a cappuccino with characters' names on them. The crowd read them at sixty-seven in the first window and mocked them in the second. A small order sold as a film is a joke by the second week.",
            },
            {
              id: "bearista",
              label: "Mini Bearista add-on",
              sub: "June · a small object on any drink",
              a: 27,
              b: 53,
              note: "A blind box sold with any handcrafted drink. The crowd read it in three windows running and never below forty. No object on your shelf was read more often.",
            },
            {
              id: "pumpkin-shaken",
              label: "Iced Pumpkin Cream Shaken Espresso",
              sub: "August · the big espresso cup",
              a: 41,
              b: 60,
              note: "For scale, the big espresso cup. It is the strongest play here and your biggest fall launch day, and the crowd read it at sixty, once. Loud, large and seasonal, and not the shape the small cup needs to copy.",
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "Your drinks run on one strategy, the aesthetic, and it carries them. The identity signal has never been in a cup, and the small cup is the first drink that could carry it. A Short at an entry price says coffee, not dessert, about the person holding it. Fandom is rare on drinks and the best received, and that is the lane the treat drop borrows. Scarcity earns your best valence and is the shape of a dated drop.",
          title: "Which strategy carries a drink",
          provenance: "measured",
          x: {
            label: "How often you play it",
            low: "never",
            high: "every drop",
            hint: "your drink drops since April that carry each cultural strategy, as a share of the busiest one",
          },
          y: {
            label: "How the crowd received it",
            low: "silent",
            high: "praised",
            hint: "the presence and praise the crowd gave the drops carrying each strategy on your measured record, as a share of the best-received one (fandom, sixty)",
          },
          quadrants: {
            tr: "Your habit, and it works",
            tl: "Rare, and it works",
            br: "Your habit, ignored",
            bl: "Never played",
          },
          note: "The five cultural strategies on your fifty-six drink drops since April. Across is how often each one is played on a drink. Up is how the crowd received the drops that carried it, against the best-received strategy. Dot size is the valence the strategy earned. Tap a dot for the read.",
          wLabel: "valence",
          points: [
            {
              id: "aesthetic",
              label: "Aesthetic innovation",
              era: "The strategy you play",
              color: "#FDE047",
              x: 97,
              y: 74,
              w: 50,
              status: "current",
              note: "Fifty-six drinks, every one of them: the foam, the cascade, the season's flavour. The Aerocano is aesthetic innovation in its purest form, a texture made of air, and it is why the crowd read the small cup at all. The strategy carries the drink. It does not carry the price.",
            },
            {
              id: "identity",
              label: "Identity signal",
              era: "Never in a cup",
              color: "#60A5FA",
              x: 2,
              y: 3,
              w: 10,
              status: "recommended",
              note: "Fifty-eight identity plays on your record and not one of them a drink. The cup you carry is the identity signal, and a small strong one at an entry price says something a Venti cannot. It says coffee, not dessert, and a ritual, not a reward. The first move puts the strategy in a cup for the first time.",
            },
            {
              id: "scarcity",
              label: "Scarcity",
              era: "The dated drop",
              color: "#FB923C",
              x: 13,
              y: 63,
              w: 60,
              note: "Seven drinks with the best valence of the five, each a cup for one season or one week. The treat drop runs on this shape, with four dates a year and gone when it is gone.",
            },
            {
              id: "opacity",
              label: "Opacity",
              short: "Secret menu",
              era: "The order with a name",
              color: "#B388FF",
              x: 25,
              y: 55,
              w: 48,
              note: "Fourteen drinks, the orders you have to know to ask for. The espresso flight is opacity on a board, three small pours you order by name at the bar, not from the menu.",
            },
            {
              id: "fandom",
              label: "Fandom",
              era: "A crowd attached",
              color: "#FF7AB8",
              x: 5,
              y: 97,
              w: 85,
              note: "Three drinks and the highest reception on the chart, each with a crowd already attached. The bear's crowd is the fandom the treat drop borrows. The crowd read the blind box sold with a drink in every window.",
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "Gen Z carries the first week, and the generations above it keep the cup. The youngest buy the treat and post the shot. Millennials buy the afternoon second coffee by app. Gen X already orders the cortado and never posts it. Boomers wanted the small cup before it had a name. Only the very youngest are watching rather than buying.",
          title: "Who orders small, who stays",
          provenance: "projected",
          note: "Five generations scored for how much of a small cup's first year each would carry, from your shelf's record, what the crowd says it drinks and who buys the little treat. Each runs on its own clock, a launch spike or capital that compounds. Tap a crowd for the drivers.",
          rows: [
            {
              id: "gen-z",
              label: "Gen Z",
              context: "the weekly treat, the feed, the iced shot",
              score: 84,
              role: "First-week engine",
              horizon: "short",
              drivers: [
                "Buys itself a small treat every week, more than any other generation, and food and drink is the top category",
                "Its spending at small coffee chains grew faster this summer than any other generation's. The crowd is there, and it is spending with smaller chains",
                "Drinks espresso iced and posts the pour. The little-treat tag earns the most views a post of any coffee tag on the feed",
                "Half of them make a drink-only trip every week, most of those in the afternoon",
              ],
            },
            {
              id: "millennials",
              label: "Millennials",
              context: "the afternoon second cup",
              score: 70,
              role: "The afternoon second cup",
              horizon: "long",
              drivers: [
                "The app's core crowd. Four in ten coffee orders now go through an app, and the afternoon offer lands on their phone first",
                "The second peak you have said you want is their trip, a drink-only visit between lunch and the school run",
                "Your largest generation of product fans, and the base the spikes sit on",
              ],
            },
            {
              id: "gen-x",
              label: "Gen X",
              context: "orders the cortado already",
              score: 58,
              role: "Orders the cortado already",
              horizon: "long",
              drivers: [
                "The espresso drinker of record. The generations from their late twenties up carry the espresso line, and this one drinks it hot and small",
                "Will order the Short without posting it, which is the money without the moment",
              ],
            },
            {
              id: "boomers",
              label: "Boomers",
              context: "small is the size they wanted",
              score: 48,
              role: "Small is the size they wanted",
              horizon: "long",
              drivers: [
                "Asked for the Short for twenty years while it stayed off the board. Portion and sugar were their reasons before they were anyone's",
                "The GLP-1 household is theirs more than anyone's, and it wants smaller at a smaller price",
              ],
            },
            {
              id: "gen-alpha",
              label: "Gen Alpha",
              context: "the blind box, no money yet",
              score: 26,
              role: "Watches",
              horizon: "short",
              drivers: [
                "The mini object's crowd, the bear, the blind box and the collectible can, with no coffee money of its own yet",
                "The small cup is what they will have seen first, and the treat drop is the object they will ask for",
              ],
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "Everything small is either a can on a shelf or a size on a board. The cans are at scale and far from a café. The chain sizes and price promises are café orders and small. The one small cup with a crowd behind it is yours, the Aerocano after Korea, and it sits in the corner without its small price. The empty seat beside it is a café order, at scale, at an entry price. That seat is the Short.",
          title: "Where the small cup already lives",
          provenance: "observed",
          x: {
            label: "From a shelf to a café",
            low: "a shelf",
            high: "a café order",
            hint: "how close each product is to a drink someone orders over a counter and carries out: a can in a store, an app price, a bar",
          },
          y: {
            label: "How big it is",
            low: "a launch",
            high: "a category",
            hint: "the product's scale today, in cups, cans, stores and years on the menu, from the public record",
          },
          quadrants: {
            tr: "A café order, at scale",
            tl: "At scale, on a shelf",
            br: "A café order, small",
            bl: "Small, on a shelf",
          },
          note: "Where the small drink is sold today, by how close it is to a café order and how big it is, from the public record. Colour is where it came from. Dot size is scale. The haloed dot is the empty corner. Tap a dot for the read.",
          wLabel: "scale",
          open: "short",
          points: [
            {
              id: "italy",
              label: "Italy's standing espresso",
              era: "The bar",
              color: "#94A3B8",
              x: 92,
              y: 95,
              w: 80,
              note: "The origin. A shot drunk standing at the bar for the price of a coin, in every town in the country. The small cup is the whole category there, and it has never needed a name.",
            },
            {
              id: "korea",
              label: "Korea's iced Americano",
              era: "The bar",
              color: "#94A3B8",
              x: 86,
              y: 90,
              w: 76,
              note: "The cup the Aerocano was built for. In this market more than two in three Americanos are ordered iced, all year. A small strong iced coffee was already the daily order, which is why a million of the new one sold in a week.",
            },
            {
              id: "coke-mini",
              label: "Coca-Cola's small can",
              era: "The shelf",
              color: "#60A5FA",
              x: 12,
              y: 44,
              w: 36,
              note: "The small can sold alone at the lowest entry price in the range, since January, growing ahead of the brand. It proves that small at a small price sells as a single, on a shelf, with nobody to make it.",
            },
            {
              id: "red-bull",
              label: "Red Bull's small can",
              era: "The shelf",
              color: "#60A5FA",
              x: 8,
              y: 88,
              w: 70,
              note: "Fourteen billion small cans a year, growing by a tenth. The quick caffeine fix at scale is a can, not a cup. This is what the small cup competes with in the afternoon.",
            },
            {
              id: "luckin",
              label: "Luckin's app cup",
              era: "A chain's price",
              color: "#FB923C",
              x: 70,
              y: 60,
              w: 48,
              note: "A two-dollar cup on an app built thirty-six thousand stores. The play is the price, not the size, and the price war behind it is cooling. The scale was bought with the price.",
            },
            {
              id: "costa",
              label: "Costa's Mini",
              era: "A chain's price",
              color: "#FB923C",
              x: 64,
              y: 20,
              w: 18,
              note: "An eight-ounce size on a British menu for years, from about a pound and a half. A size, never a moment. Nobody queued for it, and a bakery overtook the chain this year on a cheaper Americano.",
            },
            {
              id: "mcd-canada",
              label: "McDonald's $1 small, Canada",
              era: "A chain's price",
              color: "#FB923C",
              x: 58,
              y: 26,
              w: 22,
              note: "A one-dollar small coffee locked for the whole year. A price promise on a drip coffee, the entry price without the drink.",
            },
            {
              id: "cortado",
              label: "Your Cortado",
              era: "Your cup",
              color: "#FDE047",
              x: 78,
              y: 18,
              w: 20,
              status: "current",
              note: "The small cup you already sell, in the Short only, hot only, since the start of last year. That was the first time the size had been on the board. It is a drink, not yet a ritual. It comes back as a seasonal flavour, and nobody orders it by size.",
            },
            {
              id: "aerocano",
              label: "Your Aerocano",
              era: "Your cup",
              color: "#FDE047",
              x: 84,
              y: 62,
              w: 50,
              status: "current",
              note: "The one small strong drink with a crowd behind it. A million cups in a week in Korea, five markets since, and home this autumn. A café order on its way to scale, priced above the Americano. The ritual without its price.",
            },
            {
              id: "short",
              label: "Aerocano Short",
              short: "The Short",
              era: "Your cup",
              color: "#FDE047",
              x: 95,
              y: 72,
              w: 40,
              status: "recommended",
              note: "The empty seat. The Aerocano in the Short, on the menu board, at an entry price, sold as the afternoon's second coffee. A café order, at scale, at the price the crowd says it will pay. This is the first move below.",
            },
          ],
        },
        {
          kind: "plays",
          insight:
            "Three moves and one rule. Sell the ritual, never the discount. The Short does the volume and puts the identity signal in a cup for the first time. The flight does the theatre. The treat drop does the crowd. Together they are worth about two points, most of it from a size you already pour.",
          title: "Three moves, priced",
          provenance: "projected",
          plays: [
            {
              name: "Aerocano Short, the eight-ounce Short on the menu board for the first time, as the Aerocano's own size at an entry price, sold as the afternoon's second coffee",
              itemType: "capsule",
              window:
                "A winter test in the two markets that tested the cortado, national by spring. App members get the afternoon offer first, the one-more-coffee shape Korea already drinks",
              strategies: [
                { code: "identity_signal", fit: 0.5 },
                { code: "aesthetic_innovation", fit: 0.4 },
              ],
              why: "The drink is proven. It sold a million cups in a week in Korea, and the crowd read it in four windows running. The price is the one thing the crowd says it will not pay more for. The Short has been off your board for twenty years. Putting it on as the Aerocano's size at an entry price is the identity signal you have never put in a cup. Sold as a ritual with a time of day, never as a value item.",
              effect: {
                label: "CIQ",
                delta: 0.9,
                series: [0, 0.3, 0.6, 0.8, 0.9],
              },
              precedents: 3,
              reception:
                "the crowd read the Aerocano at 40 to 67 in its first windows abroad, and mini formats on the board at 47 to 73",
              similar: [
                {
                  brand: "coca-cola",
                  brandName: "Coca-Cola",
                  title: "Cherry Float in 7.5-oz mini cans",
                  pts: 28.8,
                },
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "Lil McDonald's Happy Meal",
                  pts: 35.2,
                },
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "Mini Hash Browns return",
                  pts: 18.8,
                },
              ],
            },
            {
              name: "The Espresso Flight, three small pours on one board: the Blonde Aerocano, the cortado and the season's cup, the barista's theatre as a small object worth posting",
              itemType: "experience",
              window:
                "Reserve bars first, in the winter. Core stores by spring, as an order you ask for by name at the bar",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.6 },
                { code: "opacity", fit: 0.4 },
              ],
              why: "Your tasting events were graded in the twenties and thirties, and the crowd never read them. The crowd reads the board's small-object drops in every window. A flight turns three small cups into one thing to hold and post. Ordering it by name at the bar is opacity played on a drink, the strategy whose best plays on your record are orders you have to know about.",
              effect: {
                label: "CIQ",
                delta: 0.6,
                series: [0, 0.2, 0.4, 0.55, 0.6],
              },
              precedents: 3,
              reception:
                "your tasting events were graded 20 to 40 and never read, and the crowd reads the board's small-object drops at 47 to 67",
              similar: [
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "Slurpee Day 60th anniversary",
                  pts: 36.8,
                },
                {
                  brand: "coca-cola",
                  brandName: "Coca-Cola",
                  title: "Burger King x Coca-Cola KingCam mini camera",
                  pts: 31.3,
                },
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "Trionda mini ball competition",
                  pts: 27.7,
                },
              ],
            },
            {
              name: "Little Treat Drop, a Short drink, a mini pie and a Mini Bearista charm as one dated drop, app members first, four a year on the Bearista calendar",
              itemType: "merch",
              window:
                "Four dates a year on the bear's calendar, the first in the winter window. App members a day early, and gone when it is gone",
              strategies: [
                { code: "fandom", fit: 0.5 },
                { code: "scarcity", fit: 0.4 },
              ],
              why: "The measured precedent is your own. The Mini Bearista blind box sold as an add-on with any handcrafted drink, and the crowd read it in three windows running and never below forty. Fandom is the best-received strategy on your drinks, and scarcity earns the best valence. The little treat is the occasion the youngest crowd buys every week. The small cup arrives as the thing the bear comes with.",
              effect: {
                label: "CIQ",
                delta: 0.7,
                series: [0, 0.25, 0.5, 0.65, 0.7],
              },
              precedents: 3,
              reception:
                "the crowd reads mini objects at 47 to 67 across the board, and read your own blind box in three windows running",
              similar: [
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "7-Eleven x New Balance mini sneaker blind boxes",
                  pts: 42.3,
                },
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "Mini-City collectibles",
                  pts: 32.7,
                },
                {
                  brand: "coca-cola",
                  brandName: "Coca-Cola",
                  title: "America250 collectible mini cans",
                  pts: 25.8,
                },
              ],
            },
          ],
          footnote:
            "Projected effects are priced on your measured record and the public facts in the file header, and compound with your current course. No move is a value menu. Each is a ritual at an entry price, and none cuts the price of a drink already on the board.",
        },
        {
          kind: "impact",
          title: "What the three moves carry",
          provenance: "projected",
          delta: 2.2,
          body: "About two points on your published score, from 86.1 to roughly 88.3. Most of it comes from a size you already pour, sold at the price the crowd says it will pay. The comparison is Korea, where a million cups in a week was the Aerocano without a small price. The qualifier is that the price is an entry, not a discount. If the small cup is sold as less for more, it is the one thing the crowd says it will not buy.",
        },
        {
          kind: "modelView",
          body: "The micro-order is real, and it is already your best new drink. It came from a market that drinks its Americano iced, and from a barista's trick with no new machine behind it. A smaller, stronger, faster cup sold a million times in a week before anyone at home had tasted it. The order got smaller on its own. The crowd moved to the shot at the register, the little treat became a weekly habit, and both happen in the afternoon you have said you want.\n\nWhat you do not own is the ritual around the cup. The small cup at a small price belongs to nobody. The afternoon second coffee belongs to nobody. The treat belongs to a bear you already make. Your own shelf says how it lands. The crowd reads the small strong drink where it is new and has a texture. It reads the small object in every window. It mocks a small order sold as a joke by the second week. The shot in a bottle is shrinking while the shot in a cup is growing, and the drive-thru is answering the afternoon with a can.\n\nSo put the Short on the board before a drive-thru puts a shot in a can. Give it the Aerocano's name, the afternoon's hour and an entry price, and never call it value. The crowd will pay a small price for a small cup. It has said that it will not pay a large one.",
        },
      ],
    },
    {
      id: "swavory-signal",
      // the lead moves are drinks (a foam, a cortado, a shaken espresso),
      // so the capsule is the format the signal lands in
      itemType: "capsule",
      type: "signal",
      question: "Is swavory about to reshape the flavour bar?",
      aliases: [
        // aliases are PRE-normalized (utils/labText): a hyphen is a space;
        // the Lab pack is British, the Home card is US, both spellings carry
        "is swavory about to reshape the flavor bar",
        "are savoury sweet flavours the next cup",
        "are savory sweet flavors the next cup",
        "is the swavory cup a real signal",
        "is swavory a real signal",
        "swavory is seasoning the cup",
        "swavory opens up functional opportunities",
        "the rise of swavory flavors",
        "the rise of swavory flavours",
        "should we put miso caramel on the menu",
        "how would a swavory drink perform",
        "sweet and savory drinks",
        "sweet and savoury drinks",
        "salted pistachio",
        "miso caramel",
        "swavory",
      ],
      askedOn: "2026-10-28",
      blocks: [
        {
          kind: "readout",
          provenance: "observed",
          verdict:
            "Yes, and half of it already happened in your cup. Salt has finished your pistachio drinks for five winters. The chef flavours behind it, miso caramel, black sesame and salted pistachio, are doubling on search and reached a specialty café's menu this autumn. No chain at your scale has named the drink after them yet.",
        },
        {
          kind: "stats",
          provenance: "observed",
          insight:
            "The crossover has already crossed once. Hot honey went from a pizzeria squeeze bottle to one menu in nine, and the nut that carries the second wave doubled on menus in four years. The chef end is the next room, and the crowd that will order it already asks for unexpected flavours. The signal is not whether the palate is ready. It is who names the drink first.",
          items: [
            {
              label: "Hot honey on US menus",
              value: "11%",
              hint: "share of US restaurant menus carrying hot honey this year, about three times four years ago. Three in four US consumers know it and half have tried it. This is the sweet-and-savoury crossover that already went mainstream.",
            },
            {
              label: "Pistachio drinks on menus",
              value: "×2",
              hint: "pistachio beverages on US menus doubled over the past four years and are forecast to more than double again by 2029. Your January launch is the biggest single reason the word spikes.",
            },
            {
              label: "Want unexpected flavours",
              value: "58%",
              hint: "share of Gen Z and Gen Alpha drinkers, aged 13 to 29, who say they want flavours they did not expect. Another 57% want globally inspired ones. This is this spring's read of the two youngest generations.",
            },
            {
              label: "Black sesame matcha, searched",
              value: "+147%",
              hint: "growth in US searches for black sesame matcha over the two years to last autumn, the chef flavour that grew fastest on a review site. Black sesame lattes grew by a quarter in the same read.",
            },
          ],
        },
        {
          kind: "shift",
          insight:
            "The door is open and the crowd is already walking through it. Cold foam is the fastest-growing thing on your menu, salt has sat on your pistachio drinks for five winters, and half the country has tried hot honey. The chef flavours are small and doubling, and two in three adults say they pick lower-sugar drinks, which is what a salted finish lets a treat claim. The one thing that fails is savoury as the name of the drink. Your own olive-oil coffee is the proof.",
          title: "What the crowd is doing",
          provenance: "observed",
          note: "Seven numbers from the past year and your own menu's history. Each line says what the figure counts and why it is here. The last one is the risk, not a signal.",
          rows: [
            {
              label: "Cold foam, your leading modifier",
              context:
                "Growth in cold-foam platform sales across your US company-operated stores in the spring quarter, the modifier your finance chief calls the leading one. A salted, nutty foam walks through a door that is already the fastest-growing thing on the menu.",
              value: "+40%",
            },
            {
              label: "Salt on your cup since",
              context:
                "The winter the Pistachio Latte first came with a salted brown-butter topping. The salted cold foam on cold brew has been permanent since 2018. Swavory is not a new ingredient for you. It is a topping waiting to become the name of the drink.",
              value: "2021",
            },
            {
              label: "Hot honey, tried",
              context:
                "Share of US consumers who have tried hot honey, with three in four aware of it. The crowd has already accepted salt and heat on something sweet. The question left is the cup.",
              value: "48%",
            },
            {
              label: "Your January spike",
              context:
                "Worldwide search interest in 'pistachio latte' over the past twelve months against the twelve before, peaking every January when your pistachio drinks return. The nut is yours on the calendar. The salt is not yet yours by name.",
              value: "×1.8",
            },
            {
              label: "The chef end, searched",
              context:
                "Growth over two years in worldwide searches for miso caramel, black sesame latte and hojicha latte, each from a small base. Miso caramel set its peak in September, the month a specialty café put it on the menu. Small, climbing, and not yet owned.",
              value: "×2 to ×3",
            },
            {
              label: "Choose lower-sugar drinks",
              context:
                "Share of US adults who say they pick lower-sugar or lower-calorie drinks, this spring's read of fifteen hundred of them. Salt is how a sweet drink reads as less sweet without losing the treat. It is the reason salted caramel outlived every other dessert flavour.",
              value: "66%",
            },
            {
              label: "Savoury that led the drink",
              context:
                "Olive-oil coffee left your US menu within a year of going nationwide, and a spiced cola left shelves after six months. A savoury drink sold as a savoury drink has twice been withdrawn. A salted finish on a sweet one never has been.",
              value: "2 pulled",
            },
          ],
        },
        {
          kind: "trendCompare",
          insight:
            "Your own January is the loudest thing on the chart, and it is the same height two years running. The nut is a habit, not a climb. The climb is underneath it. Every chef flavour doubled or tripled in two years, and miso caramel set its peak in the month a café shipped it. That is what hot honey looked like before it was on one menu in nine. The curve says early. The January spike says the audience is already in your line.",
          title: "Four flavours, one scale",
          provenance: "observed",
          note: "Worldwide Google search interest for the four phrases, monthly means from January 2024 to September 2026, on one scale. Your pistachio latte's busiest month reads 100. The faint line is the door you already own, spiking every January. The three solid lines are the chef end, small and climbing. The shaded months are a specialty café putting miso caramel on its menu. October is left off as a part month.",
          highlight: {
            from: "2026-08-01",
            to: "2026-09-30",
            label: "the chef flavour ships",
          },
          series: [
            {
              key: "pistachio-latte",
              label: "pistachio latte",
              color: "#4ADE80",
              ghost: true,
              points: [
                { d: "2024-01-01", v: 52 },
                { d: "2024-02-01", v: 30 },
                { d: "2024-03-01", v: 20 },
                { d: "2024-04-01", v: 18 },
                { d: "2024-05-01", v: 15 },
                { d: "2024-06-01", v: 15 },
                { d: "2024-07-01", v: 18 },
                { d: "2024-08-01", v: 18 },
                { d: "2024-09-01", v: 17 },
                { d: "2024-10-01", v: 18 },
                { d: "2024-11-01", v: 20 },
                { d: "2024-12-01", v: 31 },
                { d: "2025-01-01", v: 95 },
                { d: "2025-02-01", v: 69 },
                { d: "2025-03-01", v: 50 },
                { d: "2025-04-01", v: 47 },
                { d: "2025-05-01", v: 39 },
                { d: "2025-06-01", v: 35 },
                { d: "2025-07-01", v: 40 },
                { d: "2025-08-01", v: 38 },
                { d: "2025-09-01", v: 31 },
                { d: "2025-10-01", v: 29 },
                { d: "2025-11-01", v: 33 },
                { d: "2025-12-01", v: 36 },
                { d: "2026-01-01", v: 100 },
                { d: "2026-02-01", v: 76 },
                { d: "2026-03-01", v: 52 },
                { d: "2026-04-01", v: 50 },
                { d: "2026-05-01", v: 48 },
                { d: "2026-06-01", v: 40 },
                { d: "2026-07-01", v: 37 },
                { d: "2026-08-01", v: 33 },
                { d: "2026-09-01", v: 32 },
              ],
            },
            {
              key: "hojicha-latte",
              label: "hojicha latte",
              color: "#FB923C",
              points: [
                { d: "2024-01-01", v: 10 },
                { d: "2024-02-01", v: 11 },
                { d: "2024-03-01", v: 11 },
                { d: "2024-04-01", v: 12 },
                { d: "2024-05-01", v: 12 },
                { d: "2024-06-01", v: 12 },
                { d: "2024-07-01", v: 13 },
                { d: "2024-08-01", v: 13 },
                { d: "2024-09-01", v: 13 },
                { d: "2024-10-01", v: 14 },
                { d: "2024-11-01", v: 16 },
                { d: "2024-12-01", v: 13 },
                { d: "2025-01-01", v: 17 },
                { d: "2025-02-01", v: 18 },
                { d: "2025-03-01", v: 22 },
                { d: "2025-04-01", v: 23 },
                { d: "2025-05-01", v: 26 },
                { d: "2025-06-01", v: 24 },
                { d: "2025-07-01", v: 30 },
                { d: "2025-08-01", v: 32 },
                { d: "2025-09-01", v: 31 },
                { d: "2025-10-01", v: 32 },
                { d: "2025-11-01", v: 32 },
                { d: "2025-12-01", v: 26 },
                { d: "2026-01-01", v: 31 },
                { d: "2026-02-01", v: 29 },
                { d: "2026-03-01", v: 37 },
                { d: "2026-04-01", v: 36 },
                { d: "2026-05-01", v: 37 },
                { d: "2026-06-01", v: 34 },
                { d: "2026-07-01", v: 35 },
                { d: "2026-08-01", v: 38 },
                { d: "2026-09-01", v: 41 },
              ],
            },
            {
              key: "salted-pistachio",
              label: "salted pistachio",
              color: "#B388FF",
              points: [
                { d: "2024-01-01", v: 9 },
                { d: "2024-02-01", v: 7 },
                { d: "2024-03-01", v: 9 },
                { d: "2024-04-01", v: 9 },
                { d: "2024-05-01", v: 9 },
                { d: "2024-06-01", v: 10 },
                { d: "2024-07-01", v: 10 },
                { d: "2024-08-01", v: 11 },
                { d: "2024-09-01", v: 9 },
                { d: "2024-10-01", v: 9 },
                { d: "2024-11-01", v: 11 },
                { d: "2024-12-01", v: 12 },
                { d: "2025-01-01", v: 11 },
                { d: "2025-02-01", v: 10 },
                { d: "2025-03-01", v: 13 },
                { d: "2025-04-01", v: 10 },
                { d: "2025-05-01", v: 12 },
                { d: "2025-06-01", v: 11 },
                { d: "2025-07-01", v: 13 },
                { d: "2025-08-01", v: 13 },
                { d: "2025-09-01", v: 12 },
                { d: "2025-10-01", v: 15 },
                { d: "2025-11-01", v: 15 },
                { d: "2025-12-01", v: 14 },
                { d: "2026-01-01", v: 12 },
                { d: "2026-02-01", v: 13 },
                { d: "2026-03-01", v: 15 },
                { d: "2026-04-01", v: 18 },
                { d: "2026-05-01", v: 17 },
                { d: "2026-06-01", v: 14 },
                { d: "2026-07-01", v: 12 },
                { d: "2026-08-01", v: 14 },
                { d: "2026-09-01", v: 17 },
              ],
            },
            {
              key: "miso-caramel",
              label: "miso caramel",
              color: "#22D3EE",
              points: [
                { d: "2024-01-01", v: 4 },
                { d: "2024-02-01", v: 5 },
                { d: "2024-03-01", v: 4 },
                { d: "2024-04-01", v: 5 },
                { d: "2024-05-01", v: 6 },
                { d: "2024-06-01", v: 4 },
                { d: "2024-07-01", v: 5 },
                { d: "2024-08-01", v: 4 },
                { d: "2024-09-01", v: 6 },
                { d: "2024-10-01", v: 7 },
                { d: "2024-11-01", v: 7 },
                { d: "2024-12-01", v: 6 },
                { d: "2025-01-01", v: 7 },
                { d: "2025-02-01", v: 7 },
                { d: "2025-03-01", v: 6 },
                { d: "2025-04-01", v: 7 },
                { d: "2025-05-01", v: 6 },
                { d: "2025-06-01", v: 6 },
                { d: "2025-07-01", v: 7 },
                { d: "2025-08-01", v: 9 },
                { d: "2025-09-01", v: 10 },
                { d: "2025-10-01", v: 9 },
                { d: "2025-11-01", v: 10 },
                { d: "2025-12-01", v: 11 },
                { d: "2026-01-01", v: 9 },
                { d: "2026-02-01", v: 9 },
                { d: "2026-03-01", v: 9 },
                { d: "2026-04-01", v: 9 },
                { d: "2026-05-01", v: 11 },
                { d: "2026-06-01", v: 9 },
                { d: "2026-07-01", v: 10 },
                { d: "2026-08-01", v: 11 },
                { d: "2026-09-01", v: 15 },
              ],
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "Everyone here holds one piece. The honey is yours and McDonald's, on a plate for them and in a cup for you. The pistachio is a convenience store's ice cream and your January. Savoury in the cup is a soy latte at a Chinese chain and a miso caramel at a specialty café. Your own strongest cell is honey, the swavory flavour you already pour. The salted finish you invented for your pistachio drinks shows as an empty cell only because it is older than this record. No chain at your scale has put salt, nut and the chef's name in one drink. That is the cell to fill.",
          title: "Who on the board is near the signal",
          provenance: "measured",
          note: "Every play on the record since April for eight food and drink brands, counted by what it was near: a pistachio, salt as a finish on something sweet, honey and hot honey, savoury inside a drink. Each column is scaled to its busiest brand. One salted finish on the whole grid is a step, not a hundred. Tap a cell for the plays.",
          cols: [
            "Pistachio",
            "Salt as a finish",
            "Honey",
            "Savoury in the cup",
          ],
          rows: [
            {
              label: "Starbucks",
              cells: [
                {
                  v: 50,
                  note: "One: the pistachio oat-milk crunch Frappuccino this autumn, too new to read. The January pistachio platform, with its salted brown butter, is older than this record.",
                },
                {
                  v: 0,
                  note: "None on the record since April. The salted brown butter on your pistachio drinks predates it. The oldest swavory play on any menu here is yours, and it has never been the name of the drink.",
                },
                {
                  v: 100,
                  note: "Four, the most on the grid: the Pikachu Honey Cream Latte, the honey-grapefruit Aerocano the crowd read three windows running and climbing, and the Japan Pokémon collab and its launch event. Honey is the swavory flavour you already pour.",
                },
                {
                  v: 0,
                  note: "None. Nothing savoury has been inside one of your drinks since April. The olive-oil coffee that was is history, and it was withdrawn.",
                },
              ],
            },
            {
              label: "McDonald's",
              cells: [
                { v: 0, note: "None. No pistachio on the shelf this year." },
                { v: 0, note: "None. The salt is on the fries." },
                {
                  v: 75,
                  note: "Three: the British hot-honey takeover the crowd read twice at nothing, the honey brown-butter breakfast biscuit read at fourteen, and the spring drop. Honey on a plate, read quietly.",
                },
                {
                  v: 0,
                  note: "None. The hot honey never reached a cup, and the coffee is the coffee.",
                },
              ],
            },
            {
              label: "7-Eleven",
              cells: [
                {
                  v: 100,
                  note: "Two, the most on the grid: the Dubai pistachio ice-cream exclusive the crowd read at thirty-three, and a Dubai-style chocolate bar not yet read. The pistachio wave on the board is a freezer, not a cup.",
                },
                { v: 0, note: "None. The salt is on the egg, not the sweet." },
                { v: 0, note: "None." },
                {
                  v: 100,
                  note: "Two, the board's only savoury drinks: a roasted-corn smoothie the crowd read once at thirteen and a fruit gazpacho smoothie it did not read at all. Savoury in the cup, at a convenience store, met with a shrug.",
                },
              ],
            },
            {
              label: "Red Bull",
              cells: [
                { v: 0, note: "None." },
                { v: 0, note: "None. The can has no finish." },
                {
                  v: 25,
                  note: "One loose mention of honey in a summer partnership. The flavour play of the year was a Japanese lime, and the crowd read it well. Citrus, not salt.",
                },
                { v: 0, note: "None." },
              ],
            },
            {
              label: "Coca-Cola",
              cells: [
                { v: 0, note: "None." },
                { v: 0, note: "None." },
                { v: 0, note: "None. The nearest flavour play is a lime." },
                {
                  v: 0,
                  note: "None. The spiced cola that tried a savoury edge left shelves after six months.",
                },
              ],
            },
            {
              label: "Blue Bottle",
              cells: [
                { v: 0, note: "None." },
                { v: 0, note: "None. The miso does the work, not a salt." },
                { v: 0, note: "None." },
                {
                  v: 50,
                  note: "One cup: a miso caramel latte, iced and as a shakerato, in its US cafés this autumn and in Japan with a red miso. The exact flavour on your card, at a café with a few dozen doors.",
                },
              ],
            },
            {
              label: "Blank Street",
              cells: [
                {
                  v: 50,
                  note: "One: a salted pistachio matcha on pistachio milk, a New York special that reached the summer menu and left it by autumn.",
                },
                {
                  v: 25,
                  note: "The same drink, finished with pink salt. The one salted finish on the whole grid, shown as a step. A scoop in a cup, a season long, and gone.",
                },
                { v: 0, note: "None." },
                { v: 0, note: "None. The salt finishes, it never leads." },
              ],
            },
            {
              label: "Luckin",
              cells: [
                { v: 0, note: "None." },
                {
                  v: 0,
                  note: "None. The cheese is the drink, not its finish.",
                },
                { v: 0, note: "None." },
                {
                  v: 100,
                  note: "Two: a salted-cheese soy-milk latte in its first winter and the series back this September across thirty-six thousand stores. Savoury in the cup at a scale beyond yours, and the one proof it can be a volume drink.",
                },
              ],
            },
          ],
        },
        {
          kind: "arena",
          insight:
            "Your crowd takes a flavour when it comes with a story: a season, a place, a pastry. The honey and the nut lanes are read, and savoury on a plate is read as lunch. The savoury-in-the-cup lane is empty on your shelf and nearly empty on the board, and the band there is a convenience store's record, not yours. The swavory drink lands in the nut lane wearing a salted finish, and that is the lane you already lead.",
          title: "Your flavour shelf",
          provenance: "measured",
          unit: "strength of the play · 0–100",
          note: "Every flavour play on your shelf since April, sorted by lane, on one scale. Bands are the food board's range for drinks on that lane, typical to ceiling. The ring is the field's strongest play there, and the dots are yours. Received is the best presence the crowd gave any of them. Tap a lane for the read.",
          rows: [
            {
              id: "nut",
              label: "Nut and caramel",
              sub: "the pistachio door",
              typical: 29,
              ceiling: 32,
              best: {
                brandName: "McDonald's",
                title: "Stroopwafel McFlurry",
                pts: 31,
              },
              received: 67,
              own: [
                { title: "Pikachu Honey Cream Latte", pts: 34 },
                { title: "Caramelised Banana drinks", pts: 29 },
                { title: "Tiramisu Velvet", pts: 28 },
                { title: "Pistachio Oatmilk Crunch Frappuccino", pts: 27 },
                { title: "Crème Brûlée range", pts: 26 },
              ],
              note: "The lane the swavory drink lands in. This autumn's pastry-chef drinks are too new to read, except the tiramisu, which the crowd praised at forty on its first read. The caramelised banana was read four windows running. A nut with a salted finish belongs here, not in a savoury lane.",
            },
            {
              id: "honey",
              label: "Honey",
              sub: "the swavory you pour",
              typical: 31,
              ceiling: 31,
              best: {
                brandName: "McDonald's",
                title: "UK Hot Honey range",
                pts: 31,
              },
              received: 67,
              own: [
                { title: "Pikachu Honey Cream Latte", pts: 34 },
                { title: "Honey Ruby Grapefruit Aerocano", pts: 24 },
              ],
              note: "Both of your honey drinks were read, the grapefruit one in three windows running and climbing each time. The field's hot honey sits on a plate and the crowd read it at nothing. Honey in a cup is yours already.",
            },
            {
              id: "spice",
              label: "Spice sold as a season",
              sub: "pumpkin, horchata, chai",
              typical: 31,
              ceiling: 35,
              best: {
                brandName: "7-Eleven",
                title: "Pumpkin Spice Slurpee",
                pts: 36,
              },
              received: 73,
              own: [
                { title: "Fall 2026 menu", pts: 43 },
                { title: "Iced Pumpkin Cream Shaken Espresso", pts: 41 },
                { title: "Iced Horchata Shaken Espresso", pts: 31 },
                { title: "Pumpkin Spice Chai", pts: 30 },
                { title: "Chaider", pts: 30 },
              ],
              note: "The best-read lane you own. A spice with a season or a place attached is how your crowd takes a flavour: the horchata was read five windows running, the pumpkin shelf at sixty and seventy-three. The chef's cortado borrows this shape, one flavour with a month on it.",
            },
            {
              id: "plate",
              label: "Savoury on a plate",
              sub: "the sandwich, the pocket",
              typical: 30,
              ceiling: 32,
              best: {
                brandName: "7-Eleven",
                title: "El GOAL-AZO Tacos",
                pts: 37,
              },
              received: 60,
              own: [
                { title: "Braised Short Rib Grilled Cheese", pts: 28 },
                { title: "Chicken Bacon Protein Pocket", pts: 25 },
              ],
              note: "Savoury food is read as food, once and quietly. The grilled cheese was read at thirteen and the protein pocket not at all. It never crosses into the cup's conversation, which is why the move is a finish on a drink, not a savoury menu.",
            },
            {
              id: "cup",
              label: "Savoury in the cup",
              sub: "empty at your scale",
              typical: 30,
              ceiling: 32,
              best: {
                brandName: "7-Eleven",
                title: "Roasted Corn Smoothie",
                pts: 30,
              },
              received: 53,
              status: "recommended",
              note: "Two savoury drinks on the whole board, both a convenience store's smoothies, one read at thirteen. A specialty café's miso caramel and a Chinese chain's salted-cheese latte sit outside this record. This is the row the moves fill, with salt as the finish and never as the drink.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "A flavour with a story is praised and a savoury plate is read as lunch, but the one drink your crowd pushed below zero this autumn was neither. It was sweet stacked on sweet. Your crowd is not asking for less treat. It is asking for the treat to taste of something. Salt and a chef's flavour are the cheapest way to give a sweet drink an edge, which is why the pistachio topping has survived five winters.",
          title: "Strength against reception",
          provenance: "measured",
          mode: "pair",
          a: { label: "Strength", color: "#FDE047" },
          b: { label: "Reception", color: "#4ADE80" },
          note: "Eight flavour plays from your own shelf. Each row shows how strongly the play was graded on paper against the best net reception the crowd gave it in a read window. A red tail marks a play the crowd read below zero. Tap a row for the read.",
          rows: [
            {
              id: "horchata",
              label: "Iced Horchata Shaken Espresso",
              sub: "May · a place in a cup",
              a: 31,
              b: 73,
              note: "A cinnamon-rice drink from a Mexican street, read five windows running and climbing to seventy-three. The most durable flavour drop you own. A flavour with a place attached is the shape your crowd keeps.",
            },
            {
              id: "orange-cream",
              label: "Orange Cream collection",
              sub: "July · a season, five drinks",
              a: 35,
              b: 73,
              note: "A summer flavour across five drinks, read at seventy-three in its first window. The season did the storytelling and the crowd took it.",
            },
            {
              id: "pumpkin-shaken",
              label: "Iced Pumpkin Cream Shaken Espresso",
              sub: "August · the calendar's spice",
              a: 41,
              b: 60,
              note: "The strongest play here and your biggest fall launch day, read at sixty once. The calendar is the story, and the spice rides it.",
            },
            {
              id: "yuzu",
              label: "Yuzu Citrus soda",
              sub: "April · a Japanese citrus",
              a: 24,
              b: 60,
              note: "A Japanese citrus in a soda for the Asia-Pacific shelf, read at sixty and then forty-seven. Graded low on paper, received well. A flavour with a country on it.",
            },
            {
              id: "honey-ruby",
              label: "Honey Ruby Grapefruit Aerocano",
              sub: "June · honey against bitter",
              a: 24,
              b: 53,
              note: "Honey set against bitter grapefruit on the aerated shot, read in three windows and climbing from nothing to fifty-three. The nearest thing to a swavory contrast on your shelf, and the crowd warmed to it.",
            },
            {
              id: "tiramisu",
              label: "Tiramisu Velvet",
              sub: "October · the pastry chef's drink",
              a: 28,
              b: 40,
              note: "The one pastry-chef drink the crowd has read this autumn, praised at forty on its first window. A dessert with a name is received. A chef's flavour with a name should be too.",
            },
            {
              id: "short-rib",
              label: "Braised Short Rib Grilled Cheese",
              sub: "October · savoury on a plate",
              a: 28,
              b: 13,
              note: "Savoury food, read once at thirteen. The crowd reads a sandwich as lunch and moves on. Savoury earns attention in your cup, not on your plate.",
            },
            {
              id: "charlie-brown",
              label: "Charlie Brown Mocha Frappuccino",
              sub: "September · sweet on sweet",
              a: 37,
              b: 0,
              tail: "read −27",
              tailLow: true,
              note: "Pumpkin spice on mocha on pumpkin foam, a secret-menu order graded well on paper and the one autumn drink the crowd read below zero. Sweet stacked on sweet with nothing to cut it. The case for a salted finish in one row.",
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "Your drinks run on one strategy and it works, but the chef flavour wants the one you play least on a cup. A miso caramel is a special by nature: one flavour, one month, a chef's name, then gone. Scarcity carries the best valence of anything you do with a drink and you have used it seven times since spring. The identity door is the other opening. No Starbucks drink has yet let the person holding it say 'I have taste', and that is exactly what the swavory order says.",
          title: "Which strategy carries a drink",
          provenance: "measured",
          x: {
            label: "How often you play it",
            low: "never",
            high: "every drop",
            hint: "your drink drops since April that carry each cultural strategy, as a share of the busiest one",
          },
          y: {
            label: "How the crowd received it",
            low: "silent",
            high: "praised",
            hint: "the presence and praise the crowd gave the drops carrying each strategy on your measured record, as a share of the best-received one (fandom, sixty)",
          },
          quadrants: {
            tr: "Your habit, and it works",
            tl: "Rare, and it works",
            br: "Your habit, ignored",
            bl: "Never played",
          },
          note: "The five cultural strategies on your fifty-six drink drops since April. Across is how often each one is played on a drink. Up is how the crowd received the drops that carried it, against the best-received strategy. Dot size is the valence the strategy earned. Tap a dot for the read.",
          wLabel: "valence",
          points: [
            {
              id: "aesthetic",
              label: "Aesthetic innovation",
              era: "The strategy you play",
              color: "#FDE047",
              x: 97,
              y: 68,
              w: 50,
              status: "current",
              note: "Fifty-six drinks, every one of them: the colour, the foam, the new flavour. Received at forty-one. The strategy carries the drink and it carries the salted foam too, because a foam is a texture before it is a taste.",
            },
            {
              id: "scarcity",
              label: "Scarcity",
              era: "The dated drop",
              color: "#FB923C",
              x: 13,
              y: 66,
              w: 60,
              status: "recommended",
              note: "Seven drinks with the best valence of the five, one festival, one summer. The chef's special is scarcity by nature: one flavour, one month, then gone. It is the shape the specialty cafés use and the shape your horchata and tiramisu already ran on.",
            },
            {
              id: "opacity",
              label: "Opacity",
              short: "Secret menu",
              era: "The order with a name",
              color: "#B388FF",
              x: 25,
              y: 52,
              w: 48,
              note: "Fourteen secret-menu drinks. Your most viral drink last year was a customer's off-menu Dubai chocolate matcha before it was a menu item. A salted foam seeded a week early as an order you have to know is this strategy on a drink.",
            },
            {
              id: "fandom",
              label: "Fandom",
              era: "A crowd attached",
              color: "#FF7AB8",
              x: 5,
              y: 90,
              w: 75,
              note: "Three drinks and the highest reception on the chart, each with a crowd already attached. Not the shape of a flavour signal, but the reason the Pikachu honey latte was read at all.",
            },
            {
              id: "identity",
              label: "Identity signal",
              era: "Never in a cup",
              color: "#60A5FA",
              x: 2,
              y: 3,
              w: 10,
              note: "Fifty-eight identity plays on your record and not one of them a drink. Ordering miso caramel or a hot-honey sesame cup says something about the drinker. It is the door you have never opened in a cup, and the third move opens it.",
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "Millennials carry this one and Gen Z lights it. The chef palate is a millennial habit from their bakeries, and the same generation is the one cutting sugar, which makes a salted finish the treat they can justify. Gen Z wants the unexpected but still wants it sweet, so the salt sharpens the drink rather than replacing the sugar. The generations that would read miso as a mistake will read salted caramel as dessert. The name does the work.",
          title: "Who carries it, who keeps it",
          provenance: "projected",
          note: "Five generations scored for how much of a swavory drink's first year each would carry, from your shelf's record, what the crowd says it drinks and who already orders the chef's flavours. Each runs on its own clock, a launch spike or capital that compounds. Tap a crowd for the drivers.",
          rows: [
            {
              id: "millennials",
              label: "Millennials",
              context: "the bakery palate, cutting sugar",
              score: 82,
              role: "Slow compounder",
              horizon: "long",
              drivers: [
                "The heaviest specialty-coffee drinkers, seven in ten in a given week. The brown-butter and miso-caramel palate was built at their bakeries and ice-cream counters",
                "Half of them say they are cutting sugar, and salt is how a treat reads as less sweet without giving up the treat",
                "The app's core crowd, and the one that reorders a flavour after the feed has moved on",
              ],
            },
            {
              id: "gen-z",
              label: "Gen Z",
              context: "wants it unexpected, still wants it sweet",
              score: 76,
              role: "First-week engine",
              horizon: "short",
              drivers: [
                "Six in ten want flavours they did not expect, and more than half want globally inspired ones",
                "Three in four still want it sweet or indulgent, so the salt finishes a treat, it does not replace one",
                "The feed made Dubai chocolate a menu item before you did, and it will make the next pistachio",
              ],
            },
            {
              id: "gen-x",
              label: "Gen X",
              context: "salted caramel's first buyer",
              score: 54,
              role: "Quiet adopter",
              horizon: "long",
              drivers: [
                "Salted caramel's original buyer, the generation that made sweet-and-salt a dessert flavour",
                "The least likely to post the cup and the most likely to reorder it",
              ],
            },
            {
              id: "boomers",
              label: "Boomers",
              context: "reads the name before the drink",
              score: 34,
              role: "Reads the name",
              horizon: "long",
              drivers: [
                "Miso in a coffee reads as a mistake. Sea salt caramel reads as dessert. The words on the board decide",
                "Will take the salted foam on a drink already ordered, and never ask for the chef's name",
              ],
            },
            {
              id: "gen-alpha",
              label: "Gen Alpha",
              context: "the pistachio-chocolate generation",
              score: 30,
              role: "Watches first",
              horizon: "short",
              drivers: [
                "The pistachio-chocolate generation, raised on the Dubai bar, with no coffee money of its own yet",
                "The salted pistachio foam is what they will have seen first, on someone else's cup",
              ],
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "The chef's flavour lives on a chef's counter and the volume lives on a plate. The one time your cup held savoury at volume it was olive oil leading the drink, and it was withdrawn. The corner where a chef's flavour meets your scale has one dot, your salted pistachio drinks, and they have never said the chef's name. That is the empty seat, and it is a finish, not a new drink.",
          title: "Where swavory already lives",
          provenance: "observed",
          x: {
            // short on purpose: at 390px the two end reads and the axis
            // label share one line
            label: "From chef to chain",
            low: "a counter",
            high: "a chain's cup",
            hint: "how far each play is from a drink ordered at scale: a scoop at a chef's counter, a special at a café, a cup at a chain",
          },
          y: {
            label: "How big it is",
            low: "a special",
            high: "a volume drink",
            hint: "the play's scale today, in doors, months on the menu and what the company said about it, from the public record",
          },
          quadrants: {
            tr: "A chain's cup, at volume",
            tl: "On a plate",
            br: "A chain's special",
            bl: "A chef's special",
          },
          note: "Where the swavory flavour is sold today, by how close it is to a chain's cup and how big it is, from the public record. Colour is where it lives. Dot size is scale. The haloed dot is the empty seat. Tap a dot for the read.",
          wLabel: "scale",
          open: "miso",
          points: [
            {
              id: "scoops",
              label: "Miso scoops at the ice-cream counter",
              era: "Chef's counter",
              color: "#94A3B8",
              x: 8,
              y: 20,
              w: 18,
              note: "A miso butterscotch brownie at one scoop shop and a sweet-potato miso swirl in another's chef series this summer. The flavour was born at a chef's counter, a pint at a time.",
            },
            {
              id: "gregorys",
              label: "Gregorys' salted-egg-yolk latte",
              era: "Chef's counter",
              color: "#94A3B8",
              x: 30,
              y: 12,
              w: 12,
              note: "A salted-egg-yolk latte with sesame foam and a tahini mocha, an autumn menu at a New York chain of a few dozen cafés. Savoury-sweet in the cup, small and seasonal.",
            },
            {
              id: "blue-bottle",
              label: "Blue Bottle's Miso Caramel Latte",
              era: "Chef's counter",
              color: "#94A3B8",
              x: 38,
              y: 26,
              w: 22,
              note: "The card's flavour on a specialty menu this autumn, hot, iced and as a shakerato, and in Japan with a red miso. A few dozen doors, a season long. The chef's flavour reached a café menu and stopped there.",
            },
            {
              id: "blank-street",
              label: "Blank Street's Salted Pistachio Matcha",
              era: "Chef's counter",
              color: "#94A3B8",
              x: 44,
              y: 22,
              w: 18,
              note: "Pistachio milk, matcha and pink salt, a New York special that made the summer menu and left it by autumn. The other flavour on your card, as a season at a small chain.",
            },
            {
              id: "hot-honey",
              label: "McDonald's hot honey",
              era: "On a plate",
              color: "#60A5FA",
              x: 70,
              y: 88,
              w: 60,
              note: "One menu in nine carries hot honey, and the biggest chain of all put it on a sandwich in two countries this year. Savoury-sweet at volume, on a plate, never in the cup.",
            },
            {
              id: "shake-shack",
              label: "Shake Shack's Dubai Pistachio Shake",
              era: "A chain's cup",
              color: "#FB923C",
              x: 78,
              y: 62,
              w: 44,
              note: "One of its strongest shakes ever by its own account, back for a second summer. The pistachio wave in a chain's cup, sweet all the way through, no salt in the name.",
            },
            {
              id: "luckin",
              label: "Luckin's Salted Cheese Soy Latte",
              era: "A chain's cup",
              color: "#FB923C",
              x: 88,
              y: 80,
              w: 56,
              note: "Savoury in the cup at thirty-six thousand stores, back this September with millions of views in days. The one proof that a savoury-sweet drink can be a volume drink, at a scale beyond yours.",
            },
            {
              id: "oleato",
              label: "Your Oleato",
              era: "Your cup, withdrawn",
              color: "#B388FF",
              x: 92,
              y: 34,
              w: 24,
              note: "Olive oil led the drink and the drink led the launch. It went nationwide and was gone from the US menu inside a year. Savoury as the identity of the cup is the one shape your own record says to avoid.",
            },
            {
              id: "pistachio-cortado",
              label: "Your Pistachio Cortado",
              era: "Your cup",
              color: "#FDE047",
              x: 94,
              y: 72,
              w: 50,
              status: "current",
              note: "Salted brown butter on a pistachio drink, every January for five winters and on the small cup since this one. The chef's flavour at your scale, already. It has never said the chef's name.",
            },
            {
              id: "miso",
              label: "Miso Caramel Cortado",
              short: "The chef's cortado",
              era: "Your cup",
              color: "#FDE047",
              x: 90,
              y: 52,
              w: 40,
              status: "recommended",
              note: "The empty seat. The chef's flavour on your small strong cup, salt as the finish, one month, a pastry chef's name on the card. A chain's cup sold as a special. This is the second move below.",
            },
          ],
        },
        {
          kind: "plays",
          insight:
            "Three moves and one rule. Salt finishes the drink, savoury never leads it. The foam does the volume all year, the cortado does the chef's month, and the honey-sesame shaken espresso brings the Reserve's best idea to the main menu. Together about two points, most of it from a modifier you already know how to launch.",
          title: "Three moves, priced",
          provenance: "projected",
          plays: [
            {
              name: "Salted Pistachio Cold Foam, a pistachio cream foam finished with pink salt and brown-butter crunch on any cold drink, year-round, seeded a week early as a secret-menu order",
              itemType: "capsule",
              window:
                "Seeded in late December as an order you have to know, on the menu with the January pistachio return. The foam stays when the drinks go",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.6 },
                { code: "opacity", fit: 0.3 },
              ],
              why: "Cold foam is your leading modifier and platform sales grew by forty percent in a quarter. The salted brown butter has topped the pistachio latte for five winters, so the flavour is proven and the texture is new. Your most viral drink last year was an off-menu Dubai chocolate matcha before it was on the board, and the off-menu week is that strategy played on purpose. Pistachio costs more than it did two winters ago, so the foam is priced as a modifier, not a free topping.",
              effect: {
                label: "CIQ",
                delta: 0.9,
                series: [0, 0.3, 0.6, 0.8, 0.9],
              },
              precedents: 3,
              reception:
                "a flavour sold as a season reads 46 to 73 on your shelf, and your honey drinks 20 to 53",
              similar: [
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "Magnum Dubai Pistachio & Kunafa",
                  pts: 30.8,
                },
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "Cadbury Dairy Milk Dubai Inspired Chocolate",
                  pts: 31.7,
                },
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "Stroopwafel McFlurry",
                  pts: 31.1,
                },
              ],
            },
            {
              name: "Miso Caramel Cortado, the chef's flavour on your small strong cup: a red-miso caramel on two ristretto shots, one month, one flavour, a pastry chef's name on the card",
              itemType: "capsule",
              window:
                "A four-week special in late winter, after the pistachio month. Back in autumn if the crowd reads it",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.5 },
                { code: "scarcity", fit: 0.5 },
              ],
              why: "The cortado is the drink the micro-orders crowd already chose. The horchata and the tiramisu prove your crowd praises a flavour with a story, and scarcity carries the best valence of your five strategies on drinks. The specialty café that shipped miso caramel this autumn has a few dozen doors. The caramel is the name and the miso is the finish, which is the order of words that keeps it off the Oleato shelf.",
              effect: {
                label: "CIQ",
                delta: 0.8,
                series: [0, 0.25, 0.5, 0.7, 0.8],
              },
              precedents: 3,
              reception:
                "your place-and-pastry drinks read 40 to 73, and savoury sold as savoury has been withdrawn twice",
              similar: [
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "Stroopwafel McFlurry",
                  pts: 31.1,
                },
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "Roasted Corn Smoothie",
                  pts: 29.5,
                },
                {
                  brand: "red-bull",
                  brandName: "Red Bull",
                  title: "Summer Edition Sudachi Lime",
                  pts: 30.2,
                },
              ],
            },
            {
              name: "Hot Honey Sesame Shaken Espresso, the Reserve's hot-honey-and-black-sesame idea on the main menu as a spring shaken espresso: hot honey, black sesame cold foam, a chilli flake you can see",
              itemType: "capsule",
              window:
                "Spring, six weeks, Rewards members a week early. The Reserve keeps the cocktail",
              strategies: [
                { code: "identity_signal", fit: 0.4 },
                { code: "scarcity", fit: 0.3 },
              ],
              why: "Hot honey is on one menu in nine and half the country has tried it. The pairing has been yours at the Reserve for two years, and both of your honey drinks on the record were read. You have never let a drink carry an identity signal. A visible chilli and a black foam say 'I have taste' in the hand, which is the one thing a Venti caramel cannot say.",
              effect: {
                label: "CIQ",
                delta: 0.6,
                series: [0, 0.2, 0.4, 0.55, 0.6],
              },
              precedents: 3,
              reception:
                "your honey drinks read 20 to 53, and the field's hot honey on a plate was read at nothing",
              similar: [
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "McDonald's UK Hot Honey Range",
                  pts: 30.9,
                },
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "Honey Brown Butter Bacon Egg & Cheese Biscuit",
                  pts: 26.2,
                },
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "Roasted Corn Smoothie",
                  pts: 29.5,
                },
              ],
            },
          ],
          footnote:
            "Projected effects are priced on your measured record and the public facts in the file header, and compound with your current course. No move leads with savoury. Salt and the chef's flavour finish a sweet drink. Pistachio costs more than it did two winters ago, so the foam is priced as a modifier, not a free topping.",
        },
        {
          kind: "impact",
          title: "What the three moves carry",
          provenance: "projected",
          delta: 2.3,
          body: "A little over two points on your published score, from 86.1 to roughly 88.4. Most of it is a salted foam that rides the modifier already growing fastest on your menu. The qualifier is the order of the words. Salt finishes the drink and the chef's flavour names the month, and savoury never leads. Led by olive oil, the same idea was withdrawn inside a year.",
        },
        {
          kind: "modelView",
          body: "Swavory is real, and on your menu it is older than its name. The tell is the salted brown butter on your pistachio drinks, which has outlived every seasonal around it, while hot honey went from a squeeze bottle to one menu in nine. The crowd accepted salt on something sweet years ago. What is new is the chef end, miso caramel and black sesame and salted pistachio, doubling on search from a small base and reaching a specialty café's menu this autumn.\n\nThere is no reading at your scale yet, because the chef end lives at a café with a few dozen doors and a Chinese chain that sells cheese in a latte. What can be read is your own shelf. A flavour with a story is praised. Savoury on a plate is lunch. The one drink your crowd pushed under this autumn was sweet stacked on sweet, with nothing to cut it. Your crowd is not asking for less treat. It is asking for the treat to taste of something.\n\nSo finish, do not lead. A salted pistachio foam all year, a chef's cortado for a month, the Reserve's honey and sesame on the main menu. Never call it savoury coffee, and never let the oil or the miso be the headline, because the last time savoury led the cup you took it off the menu yourself.",
        },
      ],
    },
    {
      id: "cafe-mixology-signal",
      // the lead moves are drinks (a tonic, a shot on a matcha, a spritz),
      // so the capsule is the format the signal lands in
      itemType: "capsule",
      type: "signal",
      question: "Is café mixology about to remix the cold bar?",
      aliases: [
        // aliases are PRE-normalized (utils/labText): a hyphen is a space
        // and an accented letter is a space too, so "café" is stored as
        // "caf " beside the plain "cafe"; the Lab pack is British, the
        // Home card is US, both spellings carry
        "is cafe mixology about to remix the cold bar",
        "is caf mixology about to remix the cold bar",
        "is the coffee bar about to become a mocktail bar",
        "are espresso tonics the next shape of the cup",
        "caf mixology is remixing the cup",
        "cafe mixology is remixing the cup",
        "caf mixology is the next opportunity for extending your coffee presence",
        "cafe mixology is the next opportunity for extending your coffee presence",
        "is caf mixology a real signal",
        "is cafe mixology a real signal",
        "should we put an espresso tonic on the menu",
        "should we sell coffee sodas",
        "how would a coffee mocktail perform",
        "is the dirty matcha a signal",
        "coffee sodas espresso tonics and cold brew mocktails",
        "caf mixology",
        "cafe mixology",
        "espresso tonic",
        "coffee soda",
        "dirty matcha",
        "coffee mocktail",
      ],
      askedOn: "2026-10-28",
      blocks: [
        {
          kind: "readout",
          provenance: "observed",
          verdict:
            "Yes, and the fizz has already reached your cup from two directions. The chains built the non-alcoholic bar as a dirty soda this spring, and your own sparkling test is pouring a cold brew spritz in three cities while your Spanish stores sold an espresso tonic all summer. The signal is the afternoon the bar is losing. No chain at your scale puts coffee in the fizz in the US yet.",
        },
        {
          kind: "stats",
          provenance: "observed",
          insight:
            "The crowd moved before the menu did. Half of under-thirty-fives do not drink, and the young plan the coffee shop over the bar two to one. What they search for is the fizz, not a mocktail, and the cup they put it in is an espresso drink more often than ever. The question is not whether the afternoon wants a bar drink. It is whether yours has coffee in it.",
          items: [
            {
              label: "Adults who drink",
              value: "54%",
              hint: "share of US adults who drink alcohol as of this summer, a tie with last year's record low and down from two in three four years ago. Only half of under-thirty-fives do, the crowd whose evening the coffee shop inherits.",
            },
            {
              label: "Coffee soda, searched",
              value: "×1.9",
              hint: "worldwide search interest in coffee soda over the past twelve months against the twelve before, with a spike this April, the month two chains put coffee and foam into a soda. The fizz is what people type.",
            },
            {
              label: "Espresso drinks, past week",
              value: "47%",
              hint: "record share of US adults who drank an espresso-based drink in the past week this summer, up by a sixth since 2022. The cup the fizz goes into is already the strong one.",
            },
            {
              label: "Gen Z: the café over the bar",
              value: "70%",
              hint: "share of Gen Z adults who planned a coffee-shop visit in Thanksgiving week this winter, against a third who planned a bar or pub. The ritual has moved rooms before anyone named the drink.",
            },
          ],
        },
        {
          kind: "shift",
          insight:
            "The bar is emptying and the coffee shop is the room the evening lands in. The young halved their bar-going in a year, one adult in six already swaps a drink for an alcohol-free one, and the menus that list a mocktail grew by a third. The fizz that sells at the register is caffeinated, and the dirty soda owns the feed before the espresso tonic has a tag. The one line that cuts the other way is the price. Half of coffee drinkers will not pay five dollars, and a bar drink costs nine.",
          title: "What the crowd is doing",
          provenance: "observed",
          note: "Seven numbers from the past year. Each line says what the figure counts and why it is here. The last one is the risk, not a signal.",
          rows: [
            {
              label: "Gen Z drinking at bars",
              context:
                "Share of US Gen Z adults, aged eighteen to twenty-seven, who say bars and clubs are where they mainly drink, down from forty-five percent a year earlier. The evening occasion is leaving the bar, and the coffee shop is the room it lands in.",
              value: "23%",
            },
            {
              label: "Chose a non-alcoholic instead",
              context:
                "Share of US adults who picked an alcohol-free beer, wine or spirit instead of a drink in the past year, the first year the question was asked. The swap is already a habit, and a coffee drink is the cheapest version of it.",
              value: "17%",
            },
            {
              label: "US alcohol, by volume",
              context:
                "The fall in US beverage-alcohol volume last year while no-alcohol beer grew by fifteen percent. The bar is shrinking faster than the no-alcohol shelf is growing, and the gap is an afternoon.",
              value: "−5%",
            },
            {
              label: "Menus with a mocktail",
              context:
                "Growth in the share of US restaurant menus listing a mocktail or zero-proof drink over the past year. A chain that lists one has a bar drink. Yours lists none.",
              value: "+35%",
            },
            {
              label: "Energy drinks at the register",
              context:
                "Same-store growth of energy drinks sold across one hundred sixty thousand restaurants last year, while regular soda fell and cold brew slipped. The fizz that sells is caffeinated and the plain one is not.",
              value: "+8.7%",
            },
            {
              label: "Dirty soda, views per post",
              context:
                "Average TikTok views per post on the dirty soda tag, the highest per post of any drink tag we read, on only a few thousand posts. The espresso tonic's own tags are too small to be counted, so the fizz has the feed before it has a vocabulary.",
              value: "45K",
            },
            {
              label: "Will not pay five dollars",
              context:
                "Share of US coffee drinkers who refuse to pay five dollars or more for one drink this summer, while a mocktail's median menu price is nine. A bar drink priced like a bar drink does not fit your cup.",
              value: "55%",
            },
          ],
        },
        {
          kind: "trendCompare",
          insight:
            "The fizz is the signal, not the word. Coffee soda doubled on search over two years and spiked this spring, the weeks two chains shipped a dirty soda. The espresso tonic has had three summers in a row, each higher than the last, and falls back every winter. The hybrid the bar calls a dirty matcha is the fastest grower from the smallest base, and the phrase coffee mocktail is barely typed. People want the drink before they have a name for it, and the window is the warm half of the year.",
          title: "Four names for the fizz, one scale",
          provenance: "observed",
          note: "Worldwide Google search interest for the four phrases, monthly means from January 2024 to September 2026, scaled so the busiest month of any term reads one hundred. Against cold brew every one of these reads under six, so this is a remix of a drink, not a rival to it. People search iced matcha latte, not the bar's name for it, which is why the hybrid line sits low. The faint line is the word nobody types yet. The shaded months are the spring the chains shipped the soda. October is left off as a part month.",
          highlight: {
            from: "2026-04-01",
            to: "2026-06-30",
            label: "the sparkling spring",
          },
          series: [
            {
              key: "coffee-soda",
              label: "coffee soda",
              color: "#FB923C",
              points: [
                { d: "2024-01-01", v: 31 },
                { d: "2024-02-01", v: 32 },
                { d: "2024-03-01", v: 48 },
                { d: "2024-04-01", v: 38 },
                { d: "2024-05-01", v: 31 },
                { d: "2024-06-01", v: 29 },
                { d: "2024-07-01", v: 27 },
                { d: "2024-08-01", v: 30 },
                { d: "2024-09-01", v: 32 },
                { d: "2024-10-01", v: 28 },
                { d: "2024-11-01", v: 26 },
                { d: "2024-12-01", v: 27 },
                { d: "2025-01-01", v: 30 },
                { d: "2025-02-01", v: 31 },
                { d: "2025-03-01", v: 32 },
                { d: "2025-04-01", v: 34 },
                { d: "2025-05-01", v: 36 },
                { d: "2025-06-01", v: 51 },
                { d: "2025-07-01", v: 40 },
                { d: "2025-08-01", v: 45 },
                { d: "2025-09-01", v: 40 },
                { d: "2025-10-01", v: 39 },
                { d: "2025-11-01", v: 44 },
                { d: "2025-12-01", v: 41 },
                { d: "2026-01-01", v: 44 },
                { d: "2026-02-01", v: 56 },
                { d: "2026-03-01", v: 65 },
                { d: "2026-04-01", v: 100 },
                { d: "2026-05-01", v: 93 },
                { d: "2026-06-01", v: 76 },
                { d: "2026-07-01", v: 49 },
                { d: "2026-08-01", v: 45 },
                { d: "2026-09-01", v: 58 },
              ],
            },
            {
              key: "espresso-tonic",
              label: "espresso tonic",
              color: "#22D3EE",
              points: [
                { d: "2024-01-01", v: 5 },
                { d: "2024-02-01", v: 6 },
                { d: "2024-03-01", v: 8 },
                { d: "2024-04-01", v: 14 },
                { d: "2024-05-01", v: 17 },
                { d: "2024-06-01", v: 19 },
                { d: "2024-07-01", v: 25 },
                { d: "2024-08-01", v: 20 },
                { d: "2024-09-01", v: 12 },
                { d: "2024-10-01", v: 7 },
                { d: "2024-11-01", v: 6 },
                { d: "2024-12-01", v: 7 },
                { d: "2025-01-01", v: 7 },
                { d: "2025-02-01", v: 7 },
                { d: "2025-03-01", v: 10 },
                { d: "2025-04-01", v: 14 },
                { d: "2025-05-01", v: 15 },
                { d: "2025-06-01", v: 27 },
                { d: "2025-07-01", v: 28 },
                { d: "2025-08-01", v: 25 },
                { d: "2025-09-01", v: 14 },
                { d: "2025-10-01", v: 11 },
                { d: "2025-11-01", v: 10 },
                { d: "2025-12-01", v: 10 },
                { d: "2026-01-01", v: 10 },
                { d: "2026-02-01", v: 9 },
                { d: "2026-03-01", v: 14 },
                { d: "2026-04-01", v: 21 },
                { d: "2026-05-01", v: 27 },
                { d: "2026-06-01", v: 34 },
                { d: "2026-07-01", v: 28 },
                { d: "2026-08-01", v: 25 },
                { d: "2026-09-01", v: 17 },
              ],
            },
            {
              key: "dirty-matcha",
              label: "dirty matcha",
              color: "#4ADE80",
              points: [
                { d: "2024-01-01", v: 3 },
                { d: "2024-02-01", v: 3 },
                { d: "2024-03-01", v: 3 },
                { d: "2024-04-01", v: 3 },
                { d: "2024-05-01", v: 3 },
                { d: "2024-06-01", v: 3 },
                { d: "2024-07-01", v: 4 },
                { d: "2024-08-01", v: 4 },
                { d: "2024-09-01", v: 4 },
                { d: "2024-10-01", v: 3 },
                { d: "2024-11-01", v: 4 },
                { d: "2024-12-01", v: 4 },
                { d: "2025-01-01", v: 4 },
                { d: "2025-02-01", v: 5 },
                { d: "2025-03-01", v: 6 },
                { d: "2025-04-01", v: 6 },
                { d: "2025-05-01", v: 7 },
                { d: "2025-06-01", v: 7 },
                { d: "2025-07-01", v: 7 },
                { d: "2025-08-01", v: 9 },
                { d: "2025-09-01", v: 7 },
                { d: "2025-10-01", v: 7 },
                { d: "2025-11-01", v: 6 },
                { d: "2025-12-01", v: 6 },
                { d: "2026-01-01", v: 7 },
                { d: "2026-02-01", v: 7 },
                { d: "2026-03-01", v: 7 },
                { d: "2026-04-01", v: 9 },
                { d: "2026-05-01", v: 9 },
                { d: "2026-06-01", v: 8 },
                { d: "2026-07-01", v: 8 },
                { d: "2026-08-01", v: 8 },
                { d: "2026-09-01", v: 7 },
              ],
            },
            {
              key: "coffee-mocktail",
              label: "coffee mocktail",
              color: "#B388FF",
              ghost: true,
              points: [
                { d: "2024-01-01", v: 2 },
                { d: "2024-02-01", v: 1 },
                { d: "2024-03-01", v: 2 },
                { d: "2024-04-01", v: 2 },
                { d: "2024-05-01", v: 2 },
                { d: "2024-06-01", v: 2 },
                { d: "2024-07-01", v: 2 },
                { d: "2024-08-01", v: 2 },
                { d: "2024-09-01", v: 2 },
                { d: "2024-10-01", v: 1 },
                { d: "2024-11-01", v: 2 },
                { d: "2024-12-01", v: 2 },
                { d: "2025-01-01", v: 2 },
                { d: "2025-02-01", v: 1 },
                { d: "2025-03-01", v: 1 },
                { d: "2025-04-01", v: 2 },
                { d: "2025-05-01", v: 2 },
                { d: "2025-06-01", v: 2 },
                { d: "2025-07-01", v: 2 },
                { d: "2025-08-01", v: 3 },
                { d: "2025-09-01", v: 2 },
                { d: "2025-10-01", v: 2 },
                { d: "2025-11-01", v: 3 },
                { d: "2025-12-01", v: 2 },
                { d: "2026-01-01", v: 2 },
                { d: "2026-02-01", v: 2 },
                { d: "2026-03-01", v: 3 },
                { d: "2026-04-01", v: 3 },
                { d: "2026-05-01", v: 4 },
                { d: "2026-06-01", v: 4 },
                { d: "2026-07-01", v: 3 },
                { d: "2026-08-01", v: 3 },
                { d: "2026-09-01", v: 2 },
              ],
            },
          ],
        },
        {
          kind: "matrix",
          insight:
            "The grid splits the signal in two. The soda is everyone's: a convenience store throws a birthday for a slush, a cola builds a fountain, an energy can lands on a burger chain's counter, a drive-thru chain runs on it. The remix is yours alone: thirteen plays on espresso and cold brew and no rival has one, and your matcha is the best-read lane on your shelf. What nobody at your scale has done is put the two together, coffee in the fizz, in the US. Your Spanish stores did it for a summer and your three-city test is doing it now. The cell is empty on the grid because it is still a test.",
          title: "Who on the board is near the signal",
          provenance: "measured",
          note: "Every play on the record since April for eight food and drink brands, counted by what it was near: a soda or anything sparkling, espresso or cold brew remixed with fruit, air or fizz, matcha built into something else, and the mocktail named as one. Each column is scaled to its busiest brand. A convenience store's frozen soda's sixtieth birthday fills the first column, and one play on a column nobody else plays is a step, not a hundred. Tap a cell for the plays.",
          cols: [
            "Sparkling & soda",
            "Espresso remixed",
            "Matcha hybrid",
            "The mocktail itself",
          ],
          rows: [
            {
              label: "Starbucks",
              cells: [
                {
                  v: 6,
                  note: "Two, both Japan: a yuzu citrus soda praised at sixty and a strawberry return read at nothing. Japan's menu lets a customer add espresso to either. No soda in the US since your handcrafted sodas left the menu a decade ago.",
                },
                {
                  v: 100,
                  note: "Thirteen, and no rival has one: the Aerocano in four regions, the honey and grapefruit Aerocano climbing three windows, the horchata and pumpkin shaken espressos, a peach and apple cold brew praised twice. The remix is yours already. The fizz is not.",
                },
                {
                  v: 100,
                  note: "Seven, the best-read lane on your shelf, from the blue coconut matcha at eighty to the pumpkin cream at seventy-three. Every one is matcha plus foam or fruit. None is matcha plus espresso, the order the crowd makes off-menu.",
                },
                {
                  v: 0,
                  note: "None. No play of yours on the record names a mocktail, a cocktail or a bar, though your Roasteries have poured spirit-free spritzes for years and your three-city sparkling test is pouring a cold brew spritz now. The empty cell is the signal.",
                },
              ],
            },
            {
              label: "McDonald's",
              cells: [
                {
                  v: 3,
                  note: "One on our record since April, a beaded drink carrier. Its crafted sodas and caffeinated Refreshers went to every US restaurant in May and our shelf did not catch them, so read this cell as a limit of our search, not a quiet rival.",
                },
                {
                  v: 0,
                  note: "None on the record. Its drinks concept that sold a matcha latte and a churro cold brew closed last summer as too complex to run.",
                },
                {
                  v: 0,
                  note: "None on the record. The matcha latte it did sell was at the drinks concept that closed last summer.",
                },
                {
                  v: 0,
                  note: "None on the record. The nearest thing is a burger chain's first energy drink, which is a can, not a bar order.",
                },
              ],
            },
            {
              label: "7-Eleven",
              cells: [
                {
                  v: 100,
                  note: "Thirty-six, almost all one frozen soda's sixtieth birthday: flavours, straws, a craft-brewery soda, a date campaign read at eighty-seven. The busiest column on the grid is a party for a slush, not a bar.",
                },
                {
                  v: 0,
                  note: "None on the record. The cold brew is a pumpkin cream, never remixed.",
                },
                {
                  v: 43,
                  note: "Three: a matcha ice cream, matcha donuts, a matcha latte launch event, none read above nothing.",
                },
                {
                  v: 100,
                  note: "Two, one of them a bottled fruit juice sold as a cocktail, read at forty-seven. A step, shown as the column's hundred because nobody else has one.",
                },
              ],
            },
            {
              label: "Red Bull",
              cells: [
                {
                  v: 6,
                  note: "Two canned editions. The can is the bar drink here, and it travels to other people's counters: a burger chain's first energy drink in August.",
                },
                { v: 0, note: "None on the record." },
                { v: 0, note: "None on the record." },
                {
                  v: 0,
                  note: "None on the record. The can is never called a cocktail.",
                },
              ],
            },
            {
              label: "Coca-Cola",
              cells: [
                {
                  v: 19,
                  note: "Seven, led by a soda fountain pop-up that recreated the eighteen eighty-six counter and was read at seventy-three, and a permanent tea-in-soda. The one soda bar on the board, and it was praised.",
                },
                {
                  v: 0,
                  note: "None on the record. The coffee cola left US shelves years ago.",
                },
                { v: 0, note: "None on the record." },
                { v: 50, note: "One, a listening bar in Bali. A step." },
              ],
            },
            {
              label: "Blue Bottle",
              cells: [
                {
                  v: 0,
                  note: "None. The fizz is in the tonic, not a soda of its own.",
                },
                {
                  v: 25,
                  note: "One platform: a cold-extracted espresso built for ice across every café worldwide since June, the base for its espresso tonic and shakerato, the tonic about a dollar and a half over its cold brew. The specialty answer, at a few dozen doors.",
                },
                { v: 0, note: "None on the record." },
                {
                  v: 0,
                  note: "None. The tonic is sold as coffee, never as a bar drink.",
                },
              ],
            },
            {
              label: "Dutch Bros",
              cells: [
                {
                  v: 50,
                  note: "Two platforms, an energy soda line that is about a quarter of its sales and a plant-caffeine refresher made permanent in August across twelve hundred shops. Shown as half the first column because the slush party has more plays, not because it is smaller.",
                },
                {
                  v: 0,
                  note: "None. The coffee and the soda bar are two menus there.",
                },
                { v: 0, note: "None on the record." },
                { v: 0, note: "None. The soda bar never calls itself one." },
              ],
            },
            {
              label: "Luckin",
              cells: [
                { v: 0, note: "None. No soda on the menu we could verify." },
                {
                  v: 25,
                  note: "A fruity Americano series on its New York menu and a grapefruit Americano in a bottle that sold a million in a day in China. A step, at thirty-six thousand stores.",
                },
                {
                  v: 14,
                  note: "An iced matcha on the New York menu, priced above yours. A step.",
                },
                {
                  v: 0,
                  note: "None. Its two bar drinks this May had alcohol in them.",
                },
              ],
            },
          ],
        },
        {
          kind: "arena",
          insight:
            "Your crowd reads the remix and ignores the plain fizz. Espresso with fruit or air on it is praised every time, matcha with a story is the best-read lane you own, and the Refresher is already the drink they order like a bar drink. The sodas are two Japanese specials and the mocktail lane is empty on your shelf and nearly empty on the board. The move is not a new lane. It is the lane you lead, poured over tonic.",
          title: "Your cold shelf",
          provenance: "measured",
          unit: "strength of the play · 0–100",
          note: "Every cold play on your shelf since April near the signal, sorted by lane, on one scale. Bands are the food board's range for drinks on that lane, typical to ceiling. The ring is the field's strongest play there, and the dots are yours. Received is the best presence the crowd gave any of them. Tap a lane for the read.",
          rows: [
            {
              id: "soda",
              label: "Sparkling and soda",
              sub: "two sodas, Japan only",
              typical: 32,
              ceiling: 36,
              best: {
                brandName: "7-Eleven",
                title: "Blueberry Lemonade Slurpee",
                pts: 47,
              },
              received: 60,
              own: [
                { title: "Chillax Soda Strawberry", pts: 31 },
                { title: "Chillax Soda Yuzu Citrus", pts: 24 },
              ],
              note: "Two Japanese sodas on the whole shelf, the yuzu praised at sixty and the strawberry return read at nothing. The band is a slush's birthday. Nothing sparkling on the US menu outside the test.",
            },
            {
              id: "espresso",
              label: "Espresso remixed",
              sub: "the lane only you play",
              typical: 30,
              ceiling: 34,
              best: {
                brandName: "7-Eleven",
                title: "Pumpkin Cream Cold Brew",
                pts: 30,
              },
              received: 73,
              own: [
                { title: "Iced Pumpkin Cream Shaken Espresso", pts: 41 },
                { title: "Okayama Peach Apple Cold Brew", pts: 34 },
                { title: "Iced Horchata Shaken Espresso", pts: 31 },
                { title: "Aerocano", pts: 28 },
                { title: "Honey Ruby Grapefruit Aerocano", pts: 24 },
              ],
              note: "Fruit, spice or air on espresso and cold brew is read every time it ships: the horchata climbed five windows to seventy-three, the peach cold brew was praised twice, the grapefruit Aerocano is climbing. The field's best play here was never read. The tonic lands in this lane.",
            },
            {
              id: "matcha",
              label: "Matcha",
              sub: "the best-read lane",
              typical: 32,
              ceiling: 34,
              best: {
                brandName: "7-Eleven",
                title: "Magnum Matcha Crumble",
                pts: 29,
              },
              received: 80,
              own: [
                { title: "Protein Cold Foam Matcha", pts: 35 },
                { title: "Lavender Matcha", pts: 33 },
                { title: "Blue Coconut Matcha", pts: 33 },
                { title: "Iced Pumpkin Cream Matcha", pts: 30 },
                { title: "Iced Mango Cream Matcha", pts: 30 },
              ],
              note: "Every matcha with a colour or a season attached was read, from the blue coconut at eighty down. The one negative window on this shelf was the blue coconut's second week. The hybrid lands here with a shot poured over it.",
            },
            {
              id: "energy",
              label: "Energy and Refreshers",
              sub: "your bar drink already",
              typical: 27,
              ceiling: 30,
              best: {
                brandName: "Red Bull",
                title: "Summer Dragon Fruit Edition",
                pts: 29,
              },
              received: 80,
              own: [
                { title: "New York Energy Refresher", pts: 50 },
                { title: "Fanatics Fest Refresher", pts: 41 },
                { title: "Tropical Butterfly Refresher", pts: 30 },
                { title: "Blended Pink Energy", pts: 25 },
                {
                  title: "Blended Passionfruit Guava Energy Refresher",
                  pts: 24,
                },
              ],
              note: "The Refresher is the drink your crowd already treats as a bar order: event exclusives, a sampling tour read at sixty-seven, a pop-up at seventy-three. The sparkling version is in your test. The band is low because the field's cans are read at nothing.",
            },
            {
              id: "bar",
              label: "The mocktail itself",
              sub: "no chain at your scale",
              typical: 36,
              ceiling: 36,
              best: {
                brandName: "7-Eleven",
                title: "AriZona Slurpee Fruit Juice Cocktail",
                pts: 36,
              },
              received: 47,
              status: "recommended",
              note: "One play in the whole band, a bottled juice sold as a cocktail, so the typical and the ceiling are the same drink. Your Roasteries' spritz bars and the cold brew spritz in your test sit outside this record. The row the moves fill, with coffee in the glass and a platform price on the cup.",
            },
          ],
        },
        {
          kind: "pairs",
          insight:
            "Size and reception do not travel together on this shelf. Your biggest play here, the pumpkin shaken espresso, was read well but not best. The best-read was a blue matcha that also had the only bad week. The drink closest to a tonic, honey and grapefruit on aerated espresso, is small and climbing. The one plain soda return was read at nothing. A remix with coffee in it and a fruit on it is the shape your crowd rewards, and the Aerocano is the base that was just handed to every US store.",
          title: "Strength against reception",
          provenance: "measured",
          mode: "pair",
          a: { label: "Strength", color: "#FDE047" },
          b: { label: "Reception", color: "#4ADE80" },
          note: "Eight of your cold plays nearest the signal. The left bar is how big the play was, the right how well it was received at its best window. A grey tail is a play we have not read yet, a red one a window that went under zero. Tap a row for the read.",
          rows: [
            {
              id: "horchata",
              label: "Iced Horchata Shaken Espresso",
              sub: "May · a place on espresso",
              a: 31,
              b: 73,
              tail: "climbed five windows",
              note: "A cinnamon-rice drink on shaken espresso, read five windows running and climbing to seventy-three. The most durable remix you own. Coffee with a flavour and a place attached is what your crowd keeps.",
            },
            {
              id: "pumpkin-shaken",
              label: "Iced Pumpkin Cream Shaken Espresso",
              sub: "August · the calendar's spice",
              a: 41,
              b: 60,
              note: "The biggest play on this shelf and your largest autumn launch day, read at sixty once. Read well, not best. Size buys the first window, not the praise.",
            },
            {
              id: "okayama",
              label: "Okayama Peach Apple Cold Brew",
              sub: "Philippines · fruit on cold brew",
              a: 34,
              b: 60,
              tail: "fruit on cold brew, praised twice",
              note: "A peach and apple cold brew for the Philippines, praised at sixty in both windows it was read. Fruit on cold brew is the cold brew spritz without the fizz, and the crowd already takes it.",
            },
            {
              id: "honey-ruby",
              label: "Honey Ruby Grapefruit Aerocano",
              sub: "June · the nearest to a tonic",
              a: 24,
              b: 53,
              tail: "climbing, the nearest to a tonic",
              note: "Honey and grapefruit on aerated espresso, read in three windows and climbing from nothing to fifty-three. Bitter citrus on a cold shot is the espresso tonic's flavour without the tonic. Small, and the crowd warmed to it.",
            },
            {
              id: "blue-coconut",
              label: "Blue Coconut Matcha",
              sub: "summer · the best-read matcha",
              a: 33,
              b: 80,
              tail: "one window under zero",
              tailLow: true,
              note: "A blue coconut matcha and its ocean drink, read at eighty in the first window and under zero in the second. The best-read drink on this shelf and the only one your crowd turned on. A colour earns the first week and the second week asks what it tastes of.",
            },
            {
              id: "yuzu",
              label: "Chillax Soda Yuzu Citrus",
              sub: "April · Japan's soda, add espresso",
              a: 24,
              b: 60,
              note: "A yuzu citrus soda for Japan, read at sixty and then forty-seven, small on paper and received well. The menu lets a customer add espresso to it. The one plain fizz your crowd praised had a Japanese citrus on it.",
            },
            {
              id: "strawberry",
              label: "Chillax Soda Strawberry",
              sub: "April · a return with more pulp",
              a: 31,
              b: 0,
              tail: "read once at nothing",
              note: "The strawberry soda's return with more fruit pulp, read once at nothing. A soda coming back as a soda does not move your crowd. Fizz with nothing new in it is the shape to avoid.",
            },
            {
              id: "aerocano-us",
              label: "Aerocano, US",
              sub: "September · the base, every store",
              a: 28,
              b: 0,
              tail: "too new to read",
              note: "Aerated Blonde espresso over ice, on the core menu of every US store since the end of September and not yet read. The pour the espresso tonic needs is already behind every counter. The tonic is the next thing to put under it.",
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "Every drink you ship is an aesthetic play and it is read well, and the few that let a fan order feel like a badge are read best. No drink on your record carries an identity signal, the strategy where the cup says who the drinker is. The non-alcoholic bar order is exactly that: I am here, I am not drinking, and this is mine. The tonic, the glass and the bitters do the talking. That is the strategy the fizz needs, and it is the one you have never put on a drink.",
          title: "Which strategy carries a drink",
          provenance: "measured",
          x: {
            label: "How often you play it",
            low: "rarely",
            high: "most drinks",
            hint: "share of your drink plays since April carrying the strategy, scaled to the busiest",
          },
          y: {
            label: "Reception, share of best",
            low: "ignored",
            high: "praised",
            hint: "average reception of the drinks carrying it, as a share of the best strategy's (fandom, sixty). A strategy with no drink sits on the midline",
          },
          quadrants: {
            tr: "Your house style",
            tl: "Rare and praised",
            br: "Often, quietly",
            bl: "Rare and unread",
          },
          note: "The five cultural strategies on your fifty-six drink drops since April. Across is how often each one is played on a drink. Up is how the crowd received the drops that carried it, against the best-received strategy. Dot size is the valence the strategy earned. The haloed dot is the one no drink carries. Tap a dot for the read.",
          wLabel: "valence",
          open: "identity",
          points: [
            {
              id: "aesthetic",
              label: "Aesthetic innovation",
              era: "The look and the flavour",
              color: "#FDE047",
              x: 97,
              y: 68,
              w: 50,
              status: "current",
              note: "Fifty-six drinks, every one of them: the colour, the foam, the new flavour, the cascade in the cup. Received at forty-one. The strategy that carries every drink you ship, and it will carry a tonic's clear cup and a shot cascading through a matcha too.",
            },
            {
              id: "opacity",
              label: "Opacity",
              short: "Secret menu",
              era: "The secret menu",
              color: "#B388FF",
              x: 25,
              y: 52,
              w: 48,
              note: "Fourteen secret-menu drinks, received in the middle. The dirty matcha has been an off-menu order at your counter for years, which is this strategy played by the crowd on your behalf. Naming it is the move.",
            },
            {
              id: "scarcity",
              label: "Scarcity",
              era: "The limited drop",
              color: "#FB923C",
              x: 13,
              y: 66,
              w: 60,
              note: "Seven drinks, the best valence of the five. The espresso tonic is a summer drink on search, so the window is scarcity by nature: a season, then gone, then back.",
            },
            {
              id: "fandom",
              label: "Fandom",
              era: "Three drinks, all read",
              color: "#FF7AB8",
              x: 6,
              y: 90,
              w: 75,
              note: "Three drinks and the highest reception on the chart, each with a crowd already attached. The dirty matcha has a crowd attached before it has a name, the people who already order it.",
            },
            {
              id: "identity",
              label: "Identity signal",
              era: "No drink carries one",
              color: "#60A5FA",
              x: 2,
              y: 50,
              w: 10,
              status: "recommended",
              note: "Fifty-eight identity plays on your record and not one of them a drink. The zero-proof order is an identity: I am out, I am not drinking, and this is mine. The spritz hour is the first drink of yours to carry it.",
            },
          ],
        },
        {
          kind: "cohorts",
          insight:
            "The young light it and the thirty-somethings keep it. Gen Z moved the evening out of the bar in a single year and will order the drink that says so. Millennials are the espresso-drinking majority and the afternoon regular, so they decide whether a tonic becomes a habit or a summer. Gen Alpha wants the sparkle and not the coffee, which is why the Refresher version has to exist beside the tonic, not instead of it.",
          title: "Who carries it, who keeps it",
          provenance: "projected",
          note: "Four generations scored for how much of the signal each would carry at your counter, modelled from the record and the public reads above, not measured. Each runs on its own clock, a launch spike or capital that compounds. Tap a crowd for the drivers.",
          rows: [
            {
              id: "gen-z",
              label: "Gen Z",
              context: "lights it, this summer",
              score: 84,
              role: "Lights it",
              horizon: "short",
              drivers: [
                "Bars and clubs halved as their main drinking place in a single year, and a quarter of them do not drink at all",
                "Seven in ten planned the coffee shop over the bar in a holiday week, two to one",
                "The dirty soda owns their feed, with more views per post than any drink tag we read",
              ],
            },
            {
              id: "millennials",
              label: "Millennials",
              context: "carries it, year-round",
              score: 78,
              role: "Carries it",
              horizon: "long",
              drivers: [
                "The heaviest specialty drinkers, seven in ten in a given week, and the espresso-based majority",
                "The afternoon second drink is theirs, the day part where your gains are strongest",
                "The home-bar habit that already mixes tonic, bitters and a peel, and will recognise the drink",
              ],
            },
            {
              id: "gen-alpha",
              label: "Gen Alpha",
              context: "the fizz without the coffee, two summers",
              score: 60,
              role: "The fizz without the coffee",
              horizon: "short",
              drivers: [
                "The sparkling Refresher is theirs, bright, cold and shaken in front of them",
                "The caffeine is the parent's problem, which is why the spritz needs a no-coffee twin",
                "The colour is the point, and the clear cup shows it",
              ],
            },
            {
              id: "gen-x",
              label: "Gen X",
              context: "the tonic drinker, slow",
              score: 44,
              role: "The tonic drinker",
              horizon: "long",
              drivers: [
                "The espresso tonic's original crowd, who met it at a specialty café and will pay for the real thing",
                "Small, loyal and unlikely to post the cup, the generation that reorders without telling anyone",
              ],
            },
          ],
        },
        {
          kind: "quadrant",
          insight:
            "The top right is full of fizz with no coffee in it and the bottom left is full of coffee with no scale. The burger chain and the donut chain took the soda to every store this spring, and your Refreshers already sit there. The espresso tonic sits with the specialty cafés and your own Spanish summer. The corner between them is where your three-city test is pointed. Two of your own dots are withdrawn, and both were fizz sold as its own menu.",
          title: "Where café mixology already lives",
          provenance: "observed",
          x: {
            // short on purpose: at 390px the two end reads and the axis
            // label share one line
            label: "Bar to chain",
            low: "a bartender",
            high: "every cup",
            hint: "how far each play is from a bartender's idea to a chain's cup: a specialty café's special, a chain's test, a chain's core menu",
          },
          y: {
            label: "Scale",
            low: "a few doors",
            high: "every store",
            hint: "how many doors pour it today, from the public record",
          },
          quadrants: {
            tr: "The chain's cup",
            tl: "A bar's idea",
            br: "A chain's one-off",
            bl: "A café's special",
          },
          note: "Where coffee meets the fizz today, by how far it has travelled from a bartender's idea and how many doors pour it, from the public record. Colour is whose it is. Dot size is scale. The haloed dot is the empty seat. Tap a dot for the read.",
          wLabel: "scale",
          open: "tonic",
          points: [
            {
              id: "origin",
              label: "The specialty tonic",
              era: "A bar's idea",
              color: "#94A3B8",
              x: 8,
              y: 6,
              w: 10,
              note: "First mixed in Oslo and put on a Swedish roaster's menu in two thousand seven, then carried by barista competitions to the specialty cafés of every continent. The origin, and still a bartender's drink.",
            },
            {
              id: "blue-bottle",
              label: "Blue Bottle Espresso Tonic",
              era: "A café's special",
              color: "#94A3B8",
              x: 20,
              y: 18,
              w: 16,
              note: "A cold-extracted espresso built for ice across every café worldwide since June, the base for its tonic and shakerato, the tonic about a dollar and a half over its cold brew. The specialty answer at a few dozen doors.",
            },
            {
              id: "roastery",
              label: "Your Roastery spritz bars",
              era: "Your cup",
              color: "#FDE047",
              x: 12,
              y: 5,
              w: 10,
              note: "The Roasteries' bars have poured spirit-free spritzes beside the coffee cocktails since before the pandemic, a ten-dollar all-day spritz in Chicago among them. Café mixology has been yours for years, at four doors.",
            },
            {
              id: "spain",
              label: "Your Spanish Coffee Tonic",
              era: "Your cup",
              color: "#FDE047",
              x: 55,
              y: 22,
              w: 22,
              note: "An espresso tonic, a cold brew tonic and a matcha tonic with a tonic brand in about two hundred fifty Spanish and Portuguese stores for a summer, barista-made, launched at the stadium store. The chain's tonic exists. It has not crossed the Atlantic.",
            },
            {
              id: "japan-soda",
              label: "Japan Chillax Soda, add espresso",
              era: "Your cup",
              color: "#FDE047",
              x: 62,
              y: 45,
              w: 30,
              note: "A soda line across every Japanese store since April with an official customisation that adds a shot to the yuzu citrus. A coffee soda by the barista's hand, nationwide, in one country.",
            },
            {
              id: "spritzers",
              label: "Your Spritzers test",
              era: "Your cup",
              color: "#FDE047",
              x: 70,
              y: 14,
              w: 18,
              note: "About a hundred stores in three cities since July: sparkling energy Refreshers and spritzes, a cold brew sour spritz and a strawberry matcha spritz among them, built for the afternoon. The test is the dot pointed at the empty seat.",
            },
            {
              id: "cosmcs",
              label: "CosMc's",
              era: "Withdrawn",
              color: "#B388FF",
              x: 60,
              y: 8,
              w: 12,
              note: "A burger chain's drinks concept with a matcha latte, a turmeric latte and a churro cold brew, eight units, closed last summer because the drinks might be too complex. Fizz and remix as their own menu, and it did not run.",
            },
            {
              id: "fizzio",
              label: "Fizzio",
              era: "Withdrawn",
              color: "#B388FF",
              x: 80,
              y: 60,
              w: 28,
              note: "Your handcrafted sodas, a root beer, a ginger ale and a lemon ale with a fizz you could add to a tea, in thirty-five hundred cafés one summer and off the menu within two years. Sparkling as its own menu at your counter, and it went flat.",
            },
            {
              id: "dunkin",
              label: "Dunkin' Dirty Soda",
              era: "The chains' fizz",
              color: "#60A5FA",
              x: 85,
              y: 72,
              w: 44,
              note: "A cola with the chain's coffee milk and sweet foam, nationwide from the end of April, the coffee soda a chain shipped this spring and the month the search for coffee soda spiked. Coffee in the fizz, at a donut chain's scale, for a season.",
            },
            {
              id: "swig",
              label: "Swig",
              era: "The chains' fizz",
              color: "#60A5FA",
              x: 78,
              y: 30,
              w: 24,
              note: "The dirty soda chain, past a hundred and sixty stores with a new one every six days and visits up by a third in a winter month. Its chief says it is doing for soda what you did for coffee. There is no coffee on its menu.",
            },
            {
              id: "mcdonalds",
              label: "McDonald's crafted sodas",
              era: "The chains' fizz",
              color: "#60A5FA",
              x: 93,
              y: 91,
              w: 56,
              note: "Three crafted sodas with cold foam and three caffeinated Refreshers in every US restaurant since May, a new beverage crew role behind them, and more than half of that traffic after lunch. The fizz at the largest scale there is, with no coffee in it.",
            },
            {
              id: "refreshers",
              label: "Your Refreshers",
              era: "Your cup",
              color: "#FDE047",
              x: 97,
              y: 83,
              w: 60,
              status: "current",
              note: "A two-billion-dollar platform in every US store, the fastest-growing lineup you own and already the drink your crowd orders like a bar drink. No coffee, no fizz. The sparkling version is in the test.",
            },
            {
              id: "tonic",
              label: "Coffee in the fizz, every store",
              short: "The empty seat",
              era: "Your cup",
              color: "#FDE047",
              x: 85,
              y: 90,
              w: 40,
              status: "recommended",
              note: "The empty seat. Espresso or cold brew in a sparkling drink on a base every US store already pours, priced like a platform drink and never like a bar's. Your Spanish summer and your three-city test both point here. These are the moves below.",
            },
          ],
        },
        {
          kind: "plays",
          insight:
            "Three moves and one rule. The fizz rides a drink you already sell and a price you already charge. The tonic takes the summer on the base every US store just got, the dirty matcha names the order the crowd invented, and the spritz hour gives the afternoon a bar drink with coffee in it. About two points together, most of it from a platform already in test.",
          title: "Three moves, priced",
          provenance: "projected",
          plays: [
            {
              name: "Aerocano Tonic, the Spanish coffee tonic on the US summer menu: Blonde espresso aerated and poured over tonic and ice with a twist of citrus, one syrup, one new ingredient",
              itemType: "capsule",
              window:
                "Memorial Day to Labor Day, the Spanish window. Rewards members a week early. Back next summer if the crowd reads it",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.6 },
                { code: "scarcity", fit: 0.4 },
              ],
              why: "The espresso tonic has had three summers in a row on search, each higher than the last. The Aerocano reached every US store in September and is the pour the drink needs. Your Spanish stores sold the tonic for a summer and the honey and grapefruit version is climbing on your Asia-Pacific shelf. A tonic syrup is one new ingredient and the drink is built in the time a shaken espresso takes, so the busiest hour is safe.",
              effect: {
                label: "CIQ",
                delta: 0.9,
                series: [0, 0.3, 0.6, 0.8, 0.9],
              },
              precedents: 3,
              reception:
                "your fruit and air on espresso reads 53 to 73, and your yuzu soda 60",
              similar: [
                {
                  brand: "coca-cola",
                  brandName: "Coca-Cola",
                  title: "Sprite + Tea Permanent Summer Release",
                  pts: 34.2,
                },
                {
                  brand: "mcdonalds",
                  brandName: "McDonald's",
                  title: "McDonald's × Red Bull Dragonberry Energizer",
                  pts: 32.0,
                },
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "7-Eleven Canada × Collective Arts Slurpee Soda",
                  pts: 27.7,
                },
              ],
            },
            {
              name: "Iced Dirty Matcha, the off-menu order the crowd invented, named: iced matcha with a shot poured over it at the hand-off so the cascade is the content, on the year-round matcha menu",
              itemType: "capsule",
              window:
                "January with the matcha menu, permanent. The first month the shot is free for Rewards members",
              strategies: [
                { code: "aesthetic_innovation", fit: 0.5 },
                { code: "fandom", fit: 0.3 },
              ],
              why: "The dirty matcha is the fastest grower on search from the smallest base, and the crowd credits your stores with inventing it off-menu. Matcha is the best-read lane on your shelf and orders grew by a third on the sellers' registers last year. Matcha supply is tight and its price is up, so the move is a shot added to a matcha you already pour, never a new matcha. Naming a fan's order is the fandom play you have made three times on a drink, and all three were read.",
              effect: {
                label: "CIQ",
                delta: 0.8,
                series: [0, 0.25, 0.5, 0.7, 0.8],
              },
              precedents: 3,
              reception:
                "your matcha plays read 53 to 80, and your three fandom drinks were all read",
              similar: [
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "Itohkyuemon Supervised Matcha Donuts",
                  pts: 31.3,
                },
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "7-Eleven Limited Edition Magnum Matcha Crumble",
                  pts: 29.4,
                },
                {
                  brand: "coca-cola",
                  brandName: "Coca-Cola",
                  title: "Sprite + Tea Permanent Summer Release",
                  pts: 34.2,
                },
              ],
            },
            {
              name: "After Five Spritz, your test's cold brew spritz as the afternoon's bar drink: cold brew, tonic, a dash of bitters and a peel, built in a clear cup and served from three to close, with a zero-proof name on the board",
              itemType: "capsule",
              window:
                "Where the test lives first, three to close on weekdays, Rewards members only for the first fortnight. National with the test's read-out",
              strategies: [
                { code: "identity_signal", fit: 0.5 },
                { code: "opacity", fit: 0.3 },
              ],
              why: "Half of under-thirty-fives do not drink and the young plan the café over the bar two to one. Your afternoon is where the gains are and where customers take no caffeine, so a half-caffeine cold brew fits the hour. More than half of a burger chain's new drink traffic comes after lunch. Your Roasteries have poured a spirit-free spritz for years. The sober-curious order is an identity, and no drink of yours has ever carried one.",
              effect: {
                label: "CIQ",
                delta: 0.6,
                series: [0, 0.2, 0.4, 0.55, 0.6],
              },
              precedents: 3,
              reception:
                "your Refresher events read 67 to 73, and the board's one soda bar read 73",
              similar: [
                {
                  brand: "coca-cola",
                  brandName: "Coca-Cola",
                  title: "Jacob's Soda Fountain pop-up, The Battery Atlanta",
                  pts: 39.6,
                },
                {
                  brand: "7-eleven",
                  brandName: "7-Eleven",
                  title: "AriZona Blue Raspberry Slurpee Fruit Juice Cocktail",
                  pts: 36.0,
                },
                {
                  brand: "red-bull",
                  brandName: "Red Bull",
                  title: "Red Bull Energizers at McDonald's",
                  pts: 36.0,
                },
              ],
            },
          ],
          footnote:
            "Projected effects are priced on your measured record and the public facts in the file header, and compound with your current course. No move is a new menu. The fizz is a modifier on a drink you already sell, priced like one, and the last time it was its own menu it left in two years.",
        },
        {
          kind: "impact",
          title: "What the three moves carry",
          provenance: "projected",
          delta: 2.3,
          body: "A little over two points on your published score, from 86.1 to roughly 88.4. Most of it is a tonic on the espresso base every store just got and a shot on the matcha your crowd already orders. The qualifier is the cup. The fizz rides a platform you already price and never becomes its own menu. Sold as its own menu, your sodas left in two years.",
        },
        {
          kind: "modelView",
          body: "The fizz is real, and it arrived through the soda door, not the tonic door. The tell is that the chains shipped dirty sodas to every store this spring while your own test poured a cold brew spritz in three cities and your Spanish stores sold an espresso tonic all summer. The crowd moved rooms first. The young drink less, drink at home when they do, and plan the coffee shop over the bar.\n\nThere is no reading at your scale yet. The espresso tonic and the dirty matcha are small on search and live at specialty cafés, and your own test has not reported. What can be read is your shelf. Coffee remixed with fruit or air is praised every time, matcha with a story is your best-read lane, the Refresher is already ordered like a bar drink, and the one plain soda return was read at nothing.\n\nSo put the coffee in the fizz on a base you already pour, name the order the crowd invented, and give the afternoon a bar drink with a zero-proof name. Never call it a mocktail menu, never price it like a bar, and never let the fizz be its own board, because the last time sparkling was its own menu at your counter it went flat in two years.",
        },
      ],
    },
  ],
};
